/**
 * districts — the registry of curated HD "tiny planet" districts. Each is a small,
 * high-detail slice of Hong Kong baked from open data (OSM buildings/roads/coastline
 * + Copernicus DEM elevation), loaded into the same free-roam Engine A at `/planet?district=<id>`.
 *
 * The model is MANY small detailed planets, not one coarse whole-territory render:
 * curated limited areas you can walk/drive, each its own toy world. Add an entry here
 * after baking its assets (see scripts/bake-hk-district.mjs --coastline --dtm,
 * scripts/bake-hk-buildings.mjs, scripts/bake-hk-roads.mjs).
 */

import type { DistrictRenderTune } from './planetModel.ts';

/**
 * What a computed game planet declares about itself.
 *
 * `archetype` + `seed` are the whole planet: `bake-game-planet --check` regenerates from
 * them and byte-compares, which is what makes "reproducible from a seed" testable rather
 * than asserted.
 */
export interface GamePlanetSpec {
  /** Which generator produced it (`scripts/lib/worldgen/archetypes/`). */
  readonly archetype: string;
  /** The integer seed. Same archetype + same seed = byte-identical assets. */
  readonly seed: number;
  /** How it was made. `computed` = our generator; `open-map` = imported from an
   *  open-licensed source (route C, not yet shipped); `real-double` = real OSM geometry
   *  presented in a game mood, which is the one variant that is NOT fictional. */
  readonly source: 'computed' | 'open-map' | 'real-double';
  readonly form: 'cube' | 'tile';
  /** Arcade modes this world can actually honour. Rally needs a roads asset; Siege needs
   *  walkable ground and a spawn ring. Declaring a mode the planet cannot serve shows the
   *  player a button that does nothing. */
  readonly modes: ReadonlyArray<'courier' | 'survival' | 'rally' | 'kart'>;
}

export interface HdDistrict {
  /** Asset id → districts/<id>.json (+ optional buildings/<id>.json, roads/<id>.json). */
  id: string;
  /** Short display name for the picker. */
  name: string;
  /** One-line flavour of what the area shows. */
  blurb: string;
  /** What real data this district carries (drives the picker badges). */
  has: { cloud?: boolean; roads?: boolean; terrain?: boolean };
  /** Planet scale level: 1 = single district (default) · 2 = area/strip (multi-
   *  district) · 3 = downtown territory tile · 4 = whole territory (HK Island /
   *  Kowloon; the All-HK view is the level-4 ceiling). Drives picker grouping +
   *  the zoom-out chain. */
  level?: 1 | 2 | 3 | 4;
  /** Ultra-tile render tuning, merged onto the loaded PlanetData at load time
   *  (render-time TS constants — survives re-bakes; the planet's look-tuning
   *  convention). See DistrictRenderTune in data/planetModel. */
  render?: DistrictRenderTune;
  /** A 6-face cube-belt baked at HALF the standard crop — 2.80 m/cell instead of 5.63,
   *  ~3 km² of walkable ground instead of a belt's ~11.5. Files under the picker's
   *  Special > Ultra shelf rather than the Cube-belt tier, because the thing that makes
   *  it special is RESOLUTION, not face count.
   *
   *  This is an EXPLICIT opt-in on purpose. `isBeltDistrict` is `id.endsWith('-belt')`
   *  and `isUltraDistrict` is `render.streetDetail === true` — which almost every belt
   *  also sets — so neither can tell a fine belt from an ordinary one. Leaving it to the
   *  id suffix is how `tung-chung-belt-v2` silently landed on the Ultra shelf while being
   *  an ordinary 5.6 m/cell belt. Set this flag deliberately or the planet is a belt. */
  ultraDetail?: boolean;
  /**
   * A COMPUTED game world — a planet generated from a seed rather than baked from map
   * data, because the places it evokes have no licensable geometry (see ADR-0272 and
   * `scripts/lib/worldgen/`). Presence of this field is what makes a planet a game
   * world; it is not inferred from the id, for the reason `ultraDetail` exists.
   *
   * A game planet carries NO `region`: it is not in any city, and tagging it with one
   * would put it in that city's picker and credit its geometry to a place it never came
   * from. Its world is `games`, derived from this field.
   */
  game?: GamePlanetSpec;
  /** A planet whose SUBJECT is not the present-day city — a place reconstructed
   *  from the record rather than surveyed from what stands today. Files under the
   *  picker's "Special" heading instead of its scale group, because the scale
   *  ladder answers "how close am I?" and cannot answer "is this real?". It also
   *  keeps the scale headings honest: a synthesised planet under "Cube-belt · 6-face
   *  real HK" would make that label false. See ADR-0243. */
  special?: boolean;
  /** Non-HK city this district belongs to (global-city rollout, ADR-0236 research).
   *  Absent = Hong Kong. Region districts are HIDDEN from the HK /planet picker and
   *  surfaced on their own routes (/tokyo, /nyc); the HK-iso blend kit (DISTRICT_GEO,
   *  dseek DZI hand-off, RainNowcast) does not apply to them. */
  region?: RegionId;
  /** The REAL place this planet is in, when its world is not a city. A sport belt lives in
   *  the `sport` world but stands in London or Sydney, and the HUD attribution must say so:
   *  "Olympic Park, London — geometry © OpenStreetMap", never "…, Sport". Cities leave this
   *  unset and fall back to their region's place name. */
  place?: string;
}

/** Non-HK planet regions — each gets its own route (like /planet is for HK). */
export type RegionId = 'tokyo' | 'nyc' | 'paris' | 'amsterdam' | 'rotterdam' | 'berlin' | 'sanfrancisco' | 'vienna' | 'barcelona' | 'copenhagen' | 'toronto' | 'melbourne' | 'edinburgh' | 'seoul' | 'losangeles' | 'dubai' | 'bangkok' | 'capetown' | 'rio' | 'istanbul' | 'shenzhen' | 'venice' | 'taipei' | 'miami' | 'fukuoka' | 'nagoya' | 'yokohama' | 'osaka' | 'athens' | 'lisbon' | 'helsinki' | 'zurich' | 'prague' | 'kyoto' | 'vatican' | 'monaco' | 'macau' | 'geneva' | 'singapore' | 'rome' | 'london' | 'jakarta' | 'mexicocity' | 'sanjose' | 'telaviv' | 'chicago' | 'boston' | 'mumbai' | 'moscow' | 'nanjing' | 'beijing' | 'shanghai' | 'sydney' | 'kualalumpur' | 'washingtondc' | 'xian' | 'zhengzhou' | 'luoyang' | 'sport';
export interface RegionSpec {
  /** Route path + world-index region tag. */
  id: RegionId;
  /** Place name for HUD labels + attribution ("Shinjuku, Tokyo — …"). */
  place: string;
  /** Picker heading. */
  label: string;
  /** Bare-route landing district. */
  defaultDistrict: string;
  /** The region's WHOLE-CITY planet — what "All <city>" lands on, mirroring how
   *  All HK lands on hksar-cube. Every region ships one (the level-4 cubes). */
  wholeCity: string;
  /** The country keeps LEFT-hand traffic (Japan, the UK, Australia, Hong Kong's
   *  neighbours…). Absent = right. Ambient cars keep this side of the centreline;
   *  see data/driveSide.ts. */
  drivesOnLeft?: true;
  /**
   * Declared ONLY for a sovereign microstate whose entire territory is smaller than
   * the ten default belts every other region must ship (ADR-0298). `areaKm2` is the
   * state's real land area, and regions.test derives the planet floor from it —
   * Monaco (2.02 km²) owes three planets, Vatican City (0.49) owes two — so a city
   * that could field ten distinct districts can never use this to ship fewer.
   */
  microstate?: { areaKm2: number };
  /**
   * The whole-city cube ships without the ten-belt roster (regions.test lowers the
   * planet floor to 1). Used for Zhengzhou and Luoyang: the ask was two HD cubes, the
   * same atom as every other city cube, not a ten-belt roster. Each still carries ONE
   * walkable belt as its front door — the catalog, the sea orbit and every Enter link
   * land in a city on foot, never on the cube (worldsCatalog.test, orbitApproach.test).
   */
  cubeOnly?: boolean;
}
export const REGIONS: Readonly<Record<RegionId, RegionSpec>> = {
  // Bare /tokyo + /nyc land on the CUBE-BELT (the whole-ball planet), mirroring how
  // bare /planet lands on central-belt for HK.
  tokyo: { id: 'tokyo', place: 'Tokyo', label: 'Tokyo planets', defaultDistrict: 'shinjuku-belt', wholeCity: 'tokyo-cube', drivesOnLeft: true },
  nyc: { id: 'nyc', place: 'New York', label: 'New York planets', defaultDistrict: 'manhattan-belt', wholeCity: 'nyc-cube' },
  paris: { id: 'paris', place: 'Paris', label: 'Paris planets', defaultDistrict: 'ile-de-la-cite-belt', wholeCity: 'paris-cube' },
  amsterdam: { id: 'amsterdam', place: 'Amsterdam', label: 'Amsterdam planets', defaultDistrict: 'grachtengordel-belt', wholeCity: 'amsterdam-cube' },
  rotterdam: { id: 'rotterdam', place: 'Rotterdam', label: 'Rotterdam planets', defaultDistrict: 'centrum-belt', wholeCity: 'rotterdam-cube' },
  berlin: { id: 'berlin', place: 'Berlin', label: 'Berlin planets', defaultDistrict: 'mitte-belt', wholeCity: 'berlin-cube' },
  sanfrancisco: { id: 'sanfrancisco', place: 'San Francisco', label: 'San Francisco planets', defaultDistrict: 'golden-gate-belt', wholeCity: 'sanfrancisco-cube' },
  vienna: { id: 'vienna', place: 'Vienna', label: 'Vienna planets', defaultDistrict: 'innere-stadt-belt', wholeCity: 'vienna-cube' },
  barcelona: { id: 'barcelona', place: 'Barcelona', label: 'Barcelona planets', defaultDistrict: 'eixample-belt', wholeCity: 'barcelona-cube' },
  copenhagen: { id: 'copenhagen', place: 'Copenhagen', label: 'Copenhagen planets', defaultDistrict: 'indre-by-belt', wholeCity: 'copenhagen-cube' },
  toronto: { id: 'toronto', place: 'Toronto', label: 'Toronto planets', defaultDistrict: 'downtown-core-belt', wholeCity: 'toronto-cube' },
  melbourne: { id: 'melbourne', place: 'Melbourne', label: 'Melbourne planets', defaultDistrict: 'hoddle-grid-belt', wholeCity: 'melbourne-cube', drivesOnLeft: true },
  edinburgh: { id: 'edinburgh', place: 'Edinburgh', label: 'Edinburgh planets', defaultDistrict: 'castlehill-belt', wholeCity: 'edinburgh-cube', drivesOnLeft: true },
  seoul: { id: 'seoul', place: 'Seoul', label: 'Seoul planets', defaultDistrict: 'gwanghwamun-belt', wholeCity: 'seoul-cube' },
  losangeles: { id: 'losangeles', place: 'Los Angeles', label: 'Los Angeles planets', defaultDistrict: 'bunker-hill-belt', wholeCity: 'losangeles-cube' },
  dubai: { id: 'dubai', place: 'Dubai', label: 'Dubai planets', defaultDistrict: 'downtown-belt', wholeCity: 'dubai-cube' },
  bangkok: { id: 'bangkok', place: 'Bangkok', label: 'Bangkok planets', defaultDistrict: 'rattanakosin-belt', wholeCity: 'bangkok-cube', drivesOnLeft: true },
  capetown: { id: 'capetown', place: 'Cape Town', label: 'Cape Town planets', defaultDistrict: 'cape-town-cbd-belt', wholeCity: 'capetown-cube', drivesOnLeft: true },
  rio: { id: 'rio', place: 'Rio de Janeiro', label: 'Rio planets', defaultDistrict: 'copacabana-belt', wholeCity: 'rio-cube' },
  istanbul: { id: 'istanbul', place: 'Istanbul', label: 'Istanbul planets', defaultDistrict: 'sultanahmet-belt', wholeCity: 'istanbul-cube' },
  // Shenzhen ships 12 belts rather than the usual 15, and the three missing are a deliberate
  // omission: dameisha, yantian, guanlan, shajing and pingshan all measured BELOW the sparsest
  // belt on the roster (399 raw building ways, Los Angeles) even after the face was enlarged,
  // so they were dropped instead of padding the region with empty worlds. Its belts are also
  // the first to override belt face geometry — 2.66 km at size 512, the same 5.2 m/cell as
  // every other belt over 4x the ground, because Shenzhen's superblocks are 400-800 m a side.
  // See the roster note in scripts/bake-city-fleet.mjs.
  // Venice ships 11 belts, not the usual 15: sant'elena 285, burano 293 and marghera 169
  // all measured below the shipped floor of 399 raw building ways, so they were dropped
  // rather than padded. Eleven belts plus the cube is 12 planets, clearing the >=10
  // per-region invariant on real data.
  taipei: { id: 'taipei', place: 'Taipei', label: 'Taipei planets', defaultDistrict: 'taipei-main-belt', wholeCity: 'taipei-cube' },
  miami: { id: 'miami', place: "Miami", label: "Miami planets", defaultDistrict: 'south-beach-belt', wholeCity: 'miami-cube' },
  fukuoka: { id: 'fukuoka', place: "Fukuoka", label: "Fukuoka planets", defaultDistrict: 'hakata-station-belt', wholeCity: 'fukuoka-cube', drivesOnLeft: true },
  nagoya: { id: 'nagoya', place: "Nagoya", label: "Nagoya planets", defaultDistrict: 'meieki-belt', wholeCity: 'nagoya-cube', drivesOnLeft: true },
  yokohama: { id: 'yokohama', place: "Yokohama", label: "Yokohama planets", defaultDistrict: 'minato-mirai-belt', wholeCity: 'yokohama-cube', drivesOnLeft: true },
  osaka: { id: 'osaka', place: "Osaka", label: "Osaka planets", defaultDistrict: 'umeda-belt', wholeCity: 'osaka-cube', drivesOnLeft: true },
  athens: { id: 'athens', place: "Athens", label: "Athens planets", defaultDistrict: 'plaka-belt', wholeCity: 'athens-cube' },
  lisbon: { id: 'lisbon', place: "Lisbon", label: "Lisbon planets", defaultDistrict: 'baixa-belt', wholeCity: 'lisbon-cube' },
  helsinki: { id: 'helsinki', place: "Helsinki", label: "Helsinki planets", defaultDistrict: 'keskusta-belt', wholeCity: 'helsinki-cube' },
  zurich: { id: 'zurich', place: "Zurich", label: "Zurich planets", defaultDistrict: 'altstadt-belt', wholeCity: 'zurich-cube' },
  prague: { id: 'prague', place: "Prague", label: "Prague planets", defaultDistrict: 'old-town-belt', wholeCity: 'prague-cube' },
  kyoto: { id: 'kyoto', place: "Kyoto", label: "Kyoto planets", defaultDistrict: 'gion-belt', wholeCity: 'kyoto-cube', drivesOnLeft: true },
  vatican: { id: 'vatican', place: "Vatican City", label: "Vatican City planets", defaultDistrict: 'vaticano-belt', wholeCity: 'vatican-cube', microstate: { areaKm2: 0.49 } },
  monaco: { id: 'monaco', place: "Monaco", label: "Monaco planets", defaultDistrict: 'fontvieille-belt', wholeCity: 'monaco-cube', microstate: { areaKm2: 2.02 } },
  macau: { id: 'macau', place: "Macau", label: "Macau planets", defaultDistrict: 'se-belt', wholeCity: 'macau-cube', drivesOnLeft: true },
  geneva: { id: 'geneva', place: "Geneva", label: "Geneva planets", defaultDistrict: 'cite-belt', wholeCity: 'geneva-cube' },
  // The SPORT collection world. Not a city: its belts are Olympic parks, stadium
  // districts and links from all over the planet, each carrying its own `place` for
  // attribution. There is no extra OSM cube — AllSportsBoard lists these belts as
  // the collection overview. `wholeCity` stays the landing belt (London Olympic Park).
  sport: { id: 'sport', place: 'Sport', label: 'Sport planets · Olympic parks, stadium districts, links', defaultDistrict: 'olympic-park-london-belt', wholeCity: 'olympic-park-london-belt' },
  singapore: { id: 'singapore', place: "Singapore", label: "Singapore planets", defaultDistrict: 'raffles-place-belt', wholeCity: 'singapore-cube', drivesOnLeft: true },
  rome: { id: 'rome', place: "Rome", label: "Rome planets", defaultDistrict: 'centro-storico-belt', wholeCity: 'rome-cube' },
  london: { id: 'london', place: "London", label: "London planets", defaultDistrict: 'paddington-london-belt', wholeCity: 'london-cube', drivesOnLeft: true },
  jakarta: { id: 'jakarta', place: "Jakarta", label: "Jakarta planets", defaultDistrict: 'kota-tua-belt', wholeCity: 'jakarta-cube', drivesOnLeft: true },
  mexicocity: { id: 'mexicocity', place: "Mexico City", label: "Mexico City planets", defaultDistrict: 'centro-historico-belt', wholeCity: 'mexicocity-cube' },
  sanjose: { id: 'sanjose', place: "San Jose", label: "San Jose planets", defaultDistrict: 'san-jose-downtown-belt', wholeCity: 'sanjose-cube' },
  telaviv: { id: 'telaviv', place: "Tel Aviv", label: "Tel Aviv planets", defaultDistrict: 'lev-hair-belt', wholeCity: 'telaviv-cube' },
  chicago: { id: 'chicago', place: "Chicago", label: "Chicago planets", defaultDistrict: 'the-loop-belt', wholeCity: 'chicago-cube' },
  boston: { id: 'boston', place: "Boston", label: "Boston planets", defaultDistrict: 'back-bay-belt', wholeCity: 'boston-cube' },
  mumbai: { id: 'mumbai', place: "Mumbai", label: "Mumbai planets", defaultDistrict: 'colaba-belt', wholeCity: 'mumbai-cube', drivesOnLeft: true },
  moscow: { id: 'moscow', place: "Moscow", label: "Moscow planets", defaultDistrict: 'zamoskvorechye-belt', wholeCity: 'moscow-cube' },
  nanjing: { id: 'nanjing', place: "Nanjing", label: "Nanjing planets", defaultDistrict: 'fuzimiao-belt', wholeCity: 'nanjing-cube' },
  beijing: { id: 'beijing', place: "Beijing", label: "Beijing planets", defaultDistrict: 'qianmen-dashilan-belt', wholeCity: 'beijing-cube' },
  shanghai: { id: 'shanghai', place: "Shanghai", label: "Shanghai planets", defaultDistrict: 'the-bund-belt', wholeCity: 'shanghai-cube' },
  sydney: { id: 'sydney', place: "Sydney", label: "Sydney planets", defaultDistrict: 'circular-quay-belt', wholeCity: 'sydney-cube', drivesOnLeft: true },
  kualalumpur: { id: 'kualalumpur', place: "Kuala Lumpur", label: "Kuala Lumpur planets", defaultDistrict: 'masjid-jamek-belt', wholeCity: 'kualalumpur-cube', drivesOnLeft: true },
  washingtondc: { id: 'washingtondc', place: "Washington", label: "Washington planets", defaultDistrict: 'capitol-hill-belt', wholeCity: 'washingtondc-cube' },
  xian: { id: 'xian', place: "Xi'an", label: "Xi'an planets", defaultDistrict: 'bell-tower-belt', wholeCity: 'xian-cube' },
  zhengzhou: { id: 'zhengzhou', place: 'Zhengzhou', label: 'Zhengzhou planets', defaultDistrict: 'erqi-belt', wholeCity: 'zhengzhou-cube', cubeOnly: true },
  luoyang: { id: 'luoyang', place: 'Luoyang', label: 'Luoyang planets', defaultDistrict: 'lijingmen-belt', wholeCity: 'luoyang-cube', cubeOnly: true },
  venice: { id: 'venice', place: 'Venice', label: 'Venice planets', defaultDistrict: 'rialto-belt', wholeCity: 'venice-cube' },
  shenzhen: { id: 'shenzhen', place: 'Shenzhen', label: 'Shenzhen planets', defaultDistrict: 'futian-cbd-belt', wholeCity: 'shenzhen-cube' },
};
/**
 * The ride you start on, per region — a `VEHICLE_MODELS` id.
 *
 * Without this every city handed you Hong Kong's "HK red taxi", because the scene
 * hard-codes VEHICLE_MODELS[0]. A taxi trundling down a Paris quay or an Amsterdam
 * gracht is the single cheapest thing that breaks place-feel, and the roster already
 * had suitable bodies — no new art. `V` and the garage still offer the whole roster
 * everywhere, including the city bike. The bike is the start ride only in Amsterdam
 * and Copenhagen. Hong Kong is absent on purpose so it falls through to the taxi.
 */
export const DEFAULT_VEHICLE: Readonly<Record<RegionId, string>> = {
  tokyo: 'kei', // kei truck — the small-street workhorse
  nyc: 'kart', // quickest road body; stands in for a cab on the avenues
  paris: 'moped', // the city-moped read
  amsterdam: 'bike', // the gracht city rides a bicycle before anything else
  rotterdam: 'kei',
  berlin: 'moped', // the city that rides everything — a moped over a tram on the Ringbahn streets
  sanfrancisco: 'kart', // the lowest, quickest body — the only one that reads right over 20% crests
  vienna: 'tram', // the ring line loops through the landing belt — the one city where the tram IS the street
  barcelona: 'moped', // the scooter city — the grid's chamfered corners were made for it
  copenhagen: 'bike', // the bicycle city
  toronto: 'kart',
  melbourne: 'tram', // the grid is threaded by trams end to end
  edinburgh: 'kart', // the lowest body — the only one that reads right on the crag streets
  seoul: 'moped',
  losangeles: 'kart',
  dubai: 'kart',
  bangkok: 'moped',
  // Cape Town takes the San Francisco / Edinburgh call — the lowest, quickest body is
  // the only one that reads right on streets running straight up a mountain foot.
  capetown: 'kart',
  rio: 'moped',
  istanbul: 'moped',
  taipei: 'moped', // the scooter city — the street's real traffic by a wide margin
  venice: 'moped', // there is no car on the islands at all; the moped is the closest body the roster has
  shenzhen: 'moped', // superblocks like Seoul's, and the delivery scooter is the street's real traffic
  miami: 'moped',
  fukuoka: 'kei',
  nagoya: 'kei',
  yokohama: 'kei',
  osaka: 'kei',
  athens: 'moped',
  lisbon: 'tram',
  helsinki: 'tram',
  zurich: 'tram',
  prague: 'tram',
  kyoto: 'kei',
  vatican: 'moped',
  monaco: 'kart',
  macau: 'minibus',
  geneva: 'tram',
  sport: 'kart', // the lowest, quickest body — right for a lap of an athletics track
  singapore: 'moped',
  rome: 'moped',
  london: 'kart',
  jakarta: 'moped',
  mexicocity: 'moped',
  sanjose: 'kart',
  telaviv: 'moped',
  chicago: 'kart',
  boston: 'kart',
  mumbai: 'moped',
  moscow: 'kart',
  nanjing: 'moped',
  beijing: 'moped',
  shanghai: 'moped',
  sydney: 'kart',
  kualalumpur: 'moped',
  washingtondc: 'kart',
  xian: 'moped',
  zhengzhou: 'moped',
  luoyang: 'moped',
};

/** Registry-ordered districts of one region (the region routes' picker list). */
export function districtsForRegion(region: RegionId): HdDistrict[] {
  return HD_DISTRICTS.filter((d) => d.region === region);
}

/**
 * A HONG KONG planet — what the HK picker, the HK daily pool and the HK-iso blend kit
 * (DISTRICT_GEO, the dseek DZI hand-off, baked tunnels) all actually mean.
 *
 * BOTH exclusions are load-bearing and neither implies the other: a `region` district is
 * a real place in ANOTHER city, a `game` district is no place at all. Spelling this as
 * `!d.region` alone was correct only while every non-HK planet was region-tagged, and it
 * is exactly how the computed game worlds (ADR-0272) — which carry no region ON PURPOSE —
 * silently landed back in the Hong Kong roster. Ask through here, never re-derive it.
 */
export function isHkDistrict(d: HdDistrict): boolean {
  return !d.region && !d.game;
}

export const HD_DISTRICTS: ReadonlyArray<HdDistrict> = [
  { id: "yuen-long-belt", name: "Yuen Long · Cube-Belt", blurb: "The whole planet is real HK — Yuen Long on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "fanling-belt", name: "Fanling & Sheung Shui · Cube-Belt", blurb: "The whole planet is real HK — Fanling & Sheung Shui on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "sai-kung-belt", name: "Sai Kung · Cube-Belt", blurb: "The whole planet is real HK — Sai Kung on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "cheung-chau-belt", name: "Cheung Chau · Cube-Belt", blurb: "The whole planet is real HK — Cheung Chau on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "tsing-ma-belt", name: "Tsing Ma Bridge · Cube-Belt", blurb: "The whole planet is real HK — Tsing Ma Bridge on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "airport-belt", name: "Airport · Cube-Belt", blurb: "The whole planet is real HK — Airport on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "wan-chai-belt", name: "Wan Chai · Cube-Belt", blurb: "The whole planet is real HK — Wan Chai on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  // ── Cube-belt rollout planets (ultra 10m, 6 real HK faces each) ──
{ id: "causeway-belt", name: "Causeway Bay · Cube-Belt", blurb: "The whole planet is real HK — Causeway Bay on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "peak-belt", name: "The Peak · Cube-Belt", blurb: "The whole planet is real HK — The Peak on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "tst-belt", name: "Tsim Sha Tsui · Cube-Belt", blurb: "The whole planet is real HK — Tsim Sha Tsui on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "mong-kok-belt", name: "Mong Kok · Cube-Belt", blurb: "The whole planet is real HK — Mong Kok on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "kowloon-tong-belt", name: "Kowloon Tong · Cube-Belt", blurb: "The whole planet is real HK — Kowloon Tong on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "kai-tak-belt", name: "Kai Tak · Cube-Belt", blurb: "The whole planet is real HK — Kai Tak on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  // The Walled City as a walkable CUBE: the ghost block fills the front face, the real
  // Kowloon City that outlived it wraps the other five. Same synthesised massing +
  // provenance as the 4m hero tile (ADR-0243); footMinU keeps the ~60 m² party-wall
  // lots at their real size so the block's grain survives to the screen.
  { id: "kowloon-walled-city-belt", special: true, name: "Kowloon Walled City · Cube-Belt", blurb: "The vanished city on the front face — 350 party-walled towers computed from the 1987 survey — with the living Kowloon City wrapping every other face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, footMinU: 0.1, dialect: 'kwc', outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "sham-shui-po-belt", name: "Sham Shui Po · Cube-Belt", blurb: "The whole planet is real HK — Sham Shui Po on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "aberdeen-belt", name: "Aberdeen · Cube-Belt", blurb: "The whole planet is real HK — Aberdeen on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "stanley-belt", name: "Stanley · Cube-Belt", blurb: "The whole planet is real HK — Stanley on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "shatin-belt", name: "Sha Tin · Cube-Belt", blurb: "The whole planet is real HK — Sha Tin on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  { id: "tuen-mun-belt", name: "Tuen Mun · Cube-Belt", blurb: "The whole planet is real HK — Tuen Mun on the front, its neighbourhood wrapping every face.", has: { cloud: true, roads: true, terrain: true }, level: 1, render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 } },
  // ── Central cube-belt (6-face real-HK pilot) ──
  {
    // CUBE-BELT planet: all 6 cube faces carry a REAL HK tile — Central on the +Z
    // front, the Victoria Harbour belt running E-W (Sheung Wan · Admiralty · North
    // Point), the harbour crossing to Kowloon over the north pole, the climb to The
    // Peak under the south. Assembled from 6 face tiles (CUBE_BELT_PLANETS) at load;
    // the whole ball is real HK, welded at the shared cube edges (no procedural fill).
    id: 'central-belt',
    name: 'Central · Cube-Belt',
    blurb:
      'The whole planet is real Hong Kong — Central on the front, the Victoria Harbour belt running east-west, the harbour crossing to Kowloon over the north pole and the climb to The Peak under the south',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: {
      vexag: 1.15,
      hknee: 2.4,
      maxU: 8.0,
      footMaxU: 2.6,
      outlineA: 0.07,
      streetDetail: true,
      // worldScale 4 big ball → divide crowd/props back out (1.9→0.7, 0.35→0.09) so
      // people/trees stay human-scale + normalized with the district planets on the grand
      // sphere instead of kaiju/monster-forest.
      crowdHeight: 0.7,
      overviewDefault: true,
      propScale: 0.09,
      // Terrain sculpt. Central was MISSED when the belts were tuned — the other 98
      // districts carry a single-line render tune and this one is hand-formatted, so a
      // bulk edit skipped it silently and the DEFAULT /planet landing kept flat hills.
      // Its own relief is modest (baked peak 1.04–2.54 vs the Peak's clamped 7.5), so a
      // touch more exaggeration than the fleet earns the climb up to the south face
      // without turning the harbour front into a ridge.
      terrainExag: 2.8,
      seaLevelLift: 0.1,
      // Denser canopy on the DEFAULT landing planet: keep 1-in-5 vegetation cells
      // (fleet default 1-in-14). The belt builder's adaptive dither dilutes this
      // against the device tree cap, so it can never re-trigger the cap-bias bald
      // patches — Central's own veg count is modest, the Peak face earns the green.
      treeEvery: 5,
    },
  },
  // ── Kennedy Town & HKU cube-belt — the 5.5m/cell + real-terrain flagship ──
  {
    // The finest cube-belt: baked at faceSize 256 (~5.5 m/cell, the -core-ultra grade,
    // vs the 10m fleet) with real Copernicus DEM elevation, on a COASTLINE-FAVOURING crop of the
    // western-tip peninsula — Green Island + Victoria Harbour + the Pok Fu Lam / HKU
    // hills are actual climbable terrain, and the sea fills the north cap + west face.
    id: 'kennedy-town-belt',
    name: 'Kennedy Town & HKU · Cube-Belt',
    blurb:
      'The whole planet is real Hong Kong at 5.5 m per cell — Kennedy Town on the western-tip waterfront, Green Island and Mount Davis to the west, Victoria Harbour over the north, and the real climb to HKU and Pok Fu Lam under the south',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: {
      vexag: 1.15,
      hknee: 2.4,
      maxU: 8.0,
      footMaxU: 2.6,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
    },
  },
  // ── Central flagship ──
  {
    // ULTRA tile: a ~1.2×1.4 km waterfront crop at 128 cells (~10 m/cell — vs the
    // ~140 m/cell districts) with painted road centre-lines and eased building
    // caps so the 1,464 real CSDI footprints keep their true height/size VARIETY
    // (default caps flatten a tight crop into uniform blocks). IFC / HSBC / BOC
    // render as crafted landmark meshes (registry silhouettes, boxes suppressed).
    id: 'central-ultra',
    name: 'Central Waterfront · Ultra',
    blurb:
      'The ultra-detail crop — Star Ferry piers to the tower canyons at 10m per cell: painted streets, true building masses, the harbourfront up close',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with HK Island + Kowloon buildings around the Central
      // crop so it reads FULL like the metas (reach-limited so the antipode stays tidy)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    // 5 m/CELL evaluation tile: the SAME Central financial core, baked at faceSize 160
    // over a tight ~800 m crop → ~5 m per cell (2× finer than the 10 m ultra). Uniform
    // faceSize (the asymmetric front-face LOD engine is a later phase), so the crop is
    // tightened rather than the ball inflated. Reuses central-ultra's building cloud
    // (realBuildings: 'central-ultra' in the baked JSON) + the same surround, so it
    // reads full — only the ground/street grid is twice as fine.
    id: 'central-core-ultra',
    name: 'Central Core · 5m',
    blurb:
      'The tightest crop yet — the Central tower canyons at 5m per cell: twice the street-grid detail of the 10m ultra, real building masses, harbour just north',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // 5.5m/cell tile: a large M2U already renders real heights tall, so ease the
      // vertical drama + lower the cap so the skyline reads as a believable canyon,
      // not a single monolithic landmark spike (maxU 8 → 6; landmark = maxU×1.15).
      vexag: 1.0,
      hknee: 2.4,
      maxU: 6.0,
      footMaxU: 2.4,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
      // ASYMMETRIC front-face LOD: baked at --size 256 over the FULL Central extent so
      // the +Z front carries ~5.5m/cell over ~1.4km (2.5x the old 880m tile) while the
      // 5 sea/surround shell faces stay coarse (48) — front 256²+5·48² < uniform-160.
      shellFaceSize: 48,
      // BIG-BALL 5.5m: render this 5.5m Central cap on a much larger sphere (worldScale 5
      // vs the ~1 a small district clamps to) with a smaller cap angle, so the ball reads
      // BIG while the ground you land on stays real 5.5m/cell. Probe for the per-face-LOD
      // roam-around cube (Option A) — validates the big-ball + 5.5m visual cheaply.
      worldScale: 5,
      capDeg: 40,
    },
  },
  {
    // 5.5m HERO tile (the central-core-ultra recipe): the TST waterfront at 5.5m/cell
    // — Star Ferry, the promenade, the harbour edge with Central across, at twice the
    // 10m ultra's street-grid detail. Reuses the tst-ultra building cloud.
    id: 'tst-core-ultra',
    name: 'TST Waterfront · 5m',
    blurb:
      'The Tsim Sha Tsui promenade at 5m per cell — Star Ferry, the harbour edge and the Central skyline across the water, street markings and crowds up close',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.0,
      hknee: 2.4,
      maxU: 6.0,
      footMaxU: 2.4,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
      // asymmetric front-face LOD (see central-core-ultra): 256 front over the full
      // ~1.4km extent at 5.5m/cell; coarse 48 sea/surround shell.
      shellFaceSize: 48,
      // BIG-BALL 5.5m (see central-core-ultra): render this fine cap on a large sphere
      // with a small cap angle → a grand planet you land on at real 5.5m/cell.
      worldScale: 5,
      capDeg: 40,
    },
  },
  {
    // Second ULTRA crop (same recipe): the Kowloon-side waterfront — promenade,
    // Star Ferry piers, museum row, the start of Nathan Road.
    id: 'tst-ultra',
    name: 'TST Waterfront · Ultra',
    blurb:
      'Kowloon’s harbour edge at 10m per cell — the promenade, ferry piers and museum row across the water from Central',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.15,
      hknee: 2.4,
      maxU: 8.0, // pass 6: street-level skyline (see central-ultra note)
      footMaxU: 2.6,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    // 5.5m HERO tile (the central-core-ultra recipe): Mong Kok's market grid at
    // 5.5m/cell — Nathan Road, the stall streets and tenement canyons at twice the
    // 10m ultra's detail. Reuses the mong-kok-ultra cloud. Inland dense core (no harbour).
    id: 'mong-kok-core-ultra',
    name: 'Mong Kok · 5m',
    blurb:
      'The market heart of Kowloon at 5m per cell — stall streets, tenement canyons and the neon grid, awning to awning at twice the street detail',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.0,
      hknee: 2.4,
      maxU: 6.0,
      footMaxU: 2.4,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
      // asymmetric front-face LOD (see central-core-ultra): 256 front over the full
      // ~1.4km extent at 5.5m/cell; coarse 48 sea/surround shell.
      shellFaceSize: 48,
      // BIG-BALL 5.5m (see central-core-ultra): render this fine cap on a large sphere
      // with a small cap angle → a grand planet you land on at real 5.5m/cell.
      worldScale: 5,
      capDeg: 40,
    },
  },
  {
    // Third ULTRA crop: Earth's densest retail grid — Nathan Road, the market
    // streets, the tenement canyons. The market-zone bake (shop density) gives
    // its streets real awnings.
    id: 'mong-kok-ultra',
    name: 'Mong Kok · Ultra',
    blurb:
      'The market heart of Kowloon at 10m per cell — stall streets, tenement canyons and the neon grid, awning to awning',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.15,
      hknee: 2.4,
      maxU: 8.0, // pass 6: street-level skyline (see central-ultra note)
      footMaxU: 2.6,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    // 5.5m HERO tile (the central-core-ultra recipe): the Causeway Bay retail crush at
    // 5.5m/cell — Times Square, the mall canyons and tram streets at twice the 10m
    // ultra's detail. Reuses the causeway-bay-ultra cloud.
    id: 'causeway-core-ultra',
    name: 'Causeway Bay · 5m',
    blurb:
      'The shopping crucible at 5m per cell — Times Square, the mall canyons and tram streets, the retail maze up close at twice the detail',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.0,
      hknee: 2.4,
      maxU: 6.0,
      footMaxU: 2.4,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
      // asymmetric front-face LOD (see central-core-ultra): 256 front over the full
      // ~1.4km extent at 5.5m/cell; coarse 48 sea/surround shell.
      shellFaceSize: 48,
      // BIG-BALL 5.5m (see central-core-ultra): render this fine cap on a large sphere
      // with a small cap angle → a grand planet you land on at real 5.5m/cell.
      worldScale: 5,
      capDeg: 40,
    },
  },
  {
    // 4 m/CELL — the finest ground in the repo, and the only planet whose subject no
    // longer exists. Kowloon Walled City was 210 × 120 m, 2.6 ha, ~350 buildings of
    // 10-14 storeys, 33,000 residents, demolished 1993-94. No vector footprints for it
    // survive anywhere, so the cloud's massing is COMPUTED from the published 1987
    // survey statistics (ADR-0243) and stands inside the real present-day Kowloon City.
    //
    // 4 m rather than the hero tier's 5.5 m because the subject is 50× smaller than a
    // belt: the crop is 1024 m at faceSize 256. Three shipped belt faces already cover
    // this ground at 5.63 m, so anything coarser would have been a regression.
    id: 'kowloon-walled-city',
    special: true,
    name: 'Kowloon Walled City · 4m',
    blurb:
      'The densest place that ever existed, rebuilt at 4m per cell — 350 party-walled towers packed into 2.6 hectares, capped at fourteen storeys by the flight path, with the park that replaced it all around',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      vexag: 1.0,
      hknee: 2.4,
      maxU: 6.0,
      footMaxU: 2.4,
      // The default 0.3 footprint floor renders every ~60 m² party-wall lot as the
      // same ~10 m square — the block's whole grain erased before rasterisation.
      // 0.1 ≈ 3.3 m keeps slivers alive while the 353 ghost towers keep their real
      // (small) plots, so the lanes between lot groups survive to the screen.
      footMinU: 0.1,
      // Caged balconies on every walk-up — the Walled City's window language.
      dialect: 'kwc',
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 1.9,
      overviewDefault: true,
      propScale: 0.35,
      // Kowloon only — the site is 4 km inland, so the island set would spend its
      // stride budget on buildings behind the camera.
      surround: { sets: ['kowloon'], reachKm: 5, stride: 3 },
      shellFaceSize: 48,
      // BIG-BALL (see central-core-ultra): worldScale × capDeg / tileMetres holds the
      // apparent ground scale, so a 1024 m tile at capDeg 40 wants ~3.6 where the
      // 1400 m hero tiles want 5.
      worldScale: 3.6,
      capDeg: 40,
      capCenter: { lon: 114.190224, lat: 22.332173 },
    },
  },
  {
    id: 'causeway-bay-ultra',
    name: 'Causeway Bay · Ultra',
    blurb:
      'The shopping crucible at 10m per cell — Victoria Park to Times Square: tram streets, mall canyons, the whole retail maze walkable',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'wan-chai-ultra',
    name: 'Wan Chai · Ultra',
    blurb:
      'Old-meets-new at 10m per cell — the Convention Centre waterfront down to the wet markets and shophouse lanes of the south side',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'sheung-wan-ultra',
    name: 'Sheung Wan · Ultra',
    blurb:
      'Dried-seafood streets and gallery slopes at 10m per cell — the old trading quarter climbing from the ferry piers',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'sham-shui-po-ultra',
    name: 'Sham Shui Po · Ultra',
    blurb:
      'The gadget-and-fabric grid at 10m per cell — street stalls, walk-ups and workshops in the most old-school corner of Kowloon',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'yau-ma-tei-ultra',
    name: 'Yau Ma Tei · Ultra',
    blurb:
      'Night-market Kowloon at 10m per cell — temple squares, fruit stalls and tenement canyons down to the harbourfront',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'north-point-ultra',
    name: 'North Point · Ultra',
    blurb:
      'The tram-through-market quarter at 10m per cell — wet-market lanes running straight down to the eastern harbour',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'kennedy-town-ultra',
    name: 'Kennedy Town · Ultra',
    blurb:
      'Where the island city ends at 10m per cell — waterfront praya, university slopes and the western harbour light',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'aberdeen-ultra',
    name: 'Aberdeen · Ultra',
    blurb:
      'The fishing harbour at 10m per cell — sampans between two shores, seafood quays and the island town across the channel',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'stanley-ultra',
    name: 'Stanley · Ultra',
    blurb:
      'The seaside market town at 10m per cell — bay-front lanes, the old fort peninsula and beaches on both sides',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'hung-hom-ultra',
    name: 'Hung Hom · Ultra',
    blurb:
      'The dockyard-turned-neighbourhood at 10m per cell — waterfront estates around the old shipyard basin',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'central-3m',
    name: 'Central · 3m',
    blurb: 'Central at 2.8 m per cell — banking towers, escalator lanes and the ferry piers at a scale where kerbs read',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 114.158, lat: 22.282 }, surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'tst-3m',
    name: 'Tsim Sha Tsui · 3m',
    blurb: 'The Kowloon waterfront at 2.8 m per cell — the promenade, the clock tower and the blocks behind Nathan Road',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 114.1722, lat: 22.2975 }, surround: { sets: ['kowloon', 'hk-island'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'mong-kok-3m',
    name: 'Mong Kok · 3m',
    blurb: 'The densest street grid on earth at 2.8 m per cell — market lanes and tong lau stacked over the pavement',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 114.1694, lat: 22.3193 }, surround: { sets: ['kowloon'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'causeway-3m',
    name: 'Causeway Bay · 3m',
    blurb: 'Causeway Bay at 2.8 m per cell — the typhoon shelter, the tram crossings and the department-store blocks',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 114.1852, lat: 22.2803 }, surround: { sets: ['hk-island', 'kowloon'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'ngong-ping-3m',
    name: 'Ngong Ping · 3m',
    blurb: 'The monastery plateau at 2.8 m per cell — Ngong Ping village and the terraces below the Big Buddha',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 113.906, lat: 22.2561 }, surround: { sets: ['tian-tan-buddha', 'tung-chung'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'kwun-tong-3m',
    name: 'Kwun Tong · 3m',
    blurb: 'East Kowloon at 2.8 m per cell — the converted industrial blocks and the promenade on the old runway channel',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 6.0, footMaxU: 2.4, footMinU: 0.12, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, worldScale: 2.5, capDeg: 40, capCenter: { lon: 114.2255, lat: 22.313 }, surround: { sets: ['kowloon'], reachKm: 5, stride: 3 }, shellFaceSize: 48,},
  },
  {
    id: 'kwun-tong-ultra',
    name: 'Kwun Tong · Ultra',
    blurb:
      'Kowloon East reinvented at 10m per cell — factory blocks turned studios along the typhoon-shelter waterfront',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['kowloon'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'sai-kung-ultra',
    name: 'Sai Kung · Ultra',
    blurb:
      'The seafood town at 10m per cell — pier-front promenade, old village lanes and the island-dotted harbour',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['sai-kung'], reachKm: 6, stride: 2 },
    },
  },
  {
    id: 'the-peak-ultra',
    name: 'The Peak · Ultra',
    blurb:
      'The summit viewpoint at 10m per cell — the Peak Tower, the tram terminus and the forest walks looking down on the whole harbour',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hk-island'], reachKm: 5, stride: 3 },
    },
  },
  {
    id: 'cheung-chau-ultra',
    name: 'Cheung Chau · Ultra',
    blurb:
      'The dumbbell island at 10m per cell — the village packed on the sandbar isthmus, fishing harbour on one side, beach on the other',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
    },
  },
  {
    id: 'tuen-mun-ultra',
    name: 'Tuen Mun · Ultra',
    blurb:
      'The western new town at 10m per cell — the town-centre malls and the light-rail lines along the river channel to the sea',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['tuen-mun'], reachKm: 6, stride: 2 },
    },
  },
  {
    id: 'hkust-bay-ultra',
    name: 'HKUST Bay · Ultra',
    blurb:
      'The seaside campus at 10m per cell — the terraced halls dropping to Port Shelter, the clock tower and the bay full of junks',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      // Ease vertical drama (real heights already vary 3–378 m in-crop) + raise
      // the caps so towers differentiate instead of all hitting the ceiling.
      vexag: 1.15,
      hknee: 2.4,
      // Pass 6: towers UP to 8 wu — the ultra tier's fiction is street-level
      // exploration, so the skyline out-scales even the 6.2 wu giant here
      // (deliberate ultra-only departure from the ADR-0209 giant-towers read;
      // regular districts keep it). Landmark ceiling rises in tandem (maxU×1.15).
      maxU: 8.0,
      footMaxU: 2.6, // tight-crop M2U is large — cap malls before they eat streets
      outlineA: 0.07, // hundreds of small masses — thin the ink or it crusts black
      streetDetail: true,
      crowdHeight: 1.9, // people-sized villagers (match the pedestrian courier)
      overviewDefault: true, // land on the model-city diorama; walk in from there
      propScale: 0.35, // fixed-wu trees read ~7-10× too big beside a metre-true 10m/cell city
      // fill the little planet with the surrounding neighbourhood’s buildings so it
      // reads FULL like the metas (reach-limited → tidy antipode; see central-ultra)
      surround: { sets: ['hkust', 'sai-kung'], reachKm: 6, stride: 2 },
    },
  },
  {
    id: 'hk-island-north-ultra',
    name: 'HK Island North · Ultra',
    blurb:
      'The north-shore wall of towers as one ULTRA planet — Kennedy Town to Shau Kei Wan at 50m per cell, every skyline landmark crowned and glowing',
    has: { cloud: true, roads: true, terrain: true },
    // Big-planet ULTRA: streetDetail marks it ultra + overview boot; NO tight-crop
    // tune (maxU/propScale/crowdHeight are wrong at km/cell — autoPropScale + the
    // crowd auto-divide + default caps handle scale). Landmark highlights ride along.
    render: { streetDetail: true, overviewDefault: true },
  },
  {
    id: 'hk-island-ultra',
    name: 'Hong Kong Island · Ultra',
    blurb:
      'The whole island, ultra — the tower-wall north shore, the Peak ridge and the south-side bays, with the flagship towers lit like beacons',
    has: { cloud: true, roads: true, terrain: true },
    // Big-planet ULTRA: streetDetail marks it ultra + overview boot; NO tight-crop
    // tune (maxU/propScale/crowdHeight are wrong at km/cell — autoPropScale + the
    // crowd auto-divide + default caps handle scale). Landmark highlights ride along.
    render: { streetDetail: true, overviewDefault: true },
  },
  {
    id: 'hksar-ultra',
    name: 'Hong Kong · Ultra',
    blurb:
      'The entire territory as one ultra planet — Lantau to Sai Kung, harbour to hills, every crowned landmark a glowing pin on the biggest world',
    has: { cloud: true, roads: true, terrain: true },
    // Big-planet ULTRA: streetDetail marks it ultra + overview boot; NO tight-crop
    // tune (maxU/propScale/crowdHeight are wrong at km/cell — autoPropScale + the
    // crowd auto-divide + default caps handle scale). Landmark highlights ride along.
    render: { streetDetail: true, overviewDefault: true },
  },
  {
    id: 'hk-island-meta',
    name: 'Hong Kong Island · Meta',
    blurb:
      'The whole island stitched from its ULTRA crops on one giant sphere — walk street-level Central, across the harbour-side wall to Causeway Bay, up to the Peak, down to Stanley, every city core at 10m per cell',
    has: { cloud: true, roads: true, terrain: true },
    // Meta cloud EXAGGERATION: the island's shared projection has a small M2U, so a
    // real 400m tower renders short. On a standalone ultra crop the towers rise
    // because the crop fills its own sphere (big M2U). Replicate that ultra skyline
    // on the shared island sphere with a big vertical exaggeration + high cap.
    render: {
      streetDetail: true,
      overviewDefault: true,
      vexag: 4.0,
      hknee: 2.0,
      maxU: 9,
      footMaxU: 3.2,
      outlineA: 0.08,
      propScale: 0.15,
    },
  },
  {
    id: 'hksar-meta',
    name: 'Hong Kong · Meta',
    blurb:
      'The WHOLE HKSAR on one giant sphere — Island, Kowloon, the New Territory towns, Lantau and the outlying islands, every district stitched at its true place. Overview and explore the territory as one planet',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      streetDetail: true,
      overviewDefault: true,
      vexag: 4.2,
      hknee: 2.0,
      maxU: 9,
      footMaxU: 3.0,
      outlineA: 0.07,
    },
  },
  {
    // The "globe" whole-HKSAR meta — the same territory rendered with a large conformal
    // azimuthal cap for TRUE HK shape + zero pole distortion + no seam (the equirect
    // sibling above trades those for a fully-filled ball). The back cap is open sea.
    id: 'hksar-globe-meta',
    name: 'Hong Kong · Globe',
    blurb:
      'The whole HKSAR as one seamless globe — every district at its true GIS shape (no stretch, no pole pinch), Island to Lantau wrapping a round world down to open sea. The zero-distortion overview',
    has: { cloud: true, roads: true, terrain: true },
    render: {
      streetDetail: true,
      overviewDefault: true,
      vexag: 4.2,
      hknee: 2.0,
      maxU: 9,
      footMaxU: 3.0,
      outlineA: 0.07,
    },
  },
  {
    id: 'central-region',
    name: 'Central · Whole Planet',
    level: 2,
    blurb:
      'The little-planet showcase — Central’s towers crown the top while real harbour, streets & Mid-Levels hills wrap the whole 10km sphere down to open sea',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'central-core',
    name: 'Central Core',
    blurb: 'The dense CBD core crowns the cap — a packed toy-block forest over real streets + Mid-Levels relief',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'central-harbour',
    name: 'Central & Harbour',
    blurb: 'The waterfront crowns the cap — CBD towers + piers meeting Victoria Harbour, The Peak rising behind',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── HK Island ──
  {
    id: 'the-peak',
    name: 'The Peak',
    blurb: 'Victoria Peak — HK’s summit in real Copernicus DEM relief, winding hill roads + the toy-block cloud',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'wan-chai',
    name: 'Wan Chai',
    blurb: 'HK Island’s buzzing heart — dense towers, old-shop streets, the convention harbourfront',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'causeway',
    name: 'Causeway Bay',
    blurb: 'Dense HK Island shopping core — neon towers, Victoria Park greenery, the harbour edge',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'north-point',
    name: 'North Point',
    blurb: 'Old eastern HK Island — packed walk-ups, the tram terminus, the wet-market bustle',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'kennedy-town-hku',
    name: 'Kennedy Town',
    blurb: 'The western tip — harbour praya + old streets climbing into the green of Lung Fu Shan',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'aberdeen',
    name: 'Aberdeen',
    blurb: 'South-side fishing harbour — sampans + the typhoon shelter between the hills and Ap Lei Chau',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'ocean-park',
    name: 'Ocean Park',
    blurb: 'A south-side headland of green hills + coastline — the seaside park between the bays',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'repulse-bay',
    name: 'Repulse Bay',
    blurb: 'The golden south-side beach in its bay, framed by green hills and seaside towers',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'stanley',
    name: 'Stanley',
    blurb: 'A breezy green peninsula town — Stanley Market + the promenade among the south-side hills',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── Kowloon ──
  {
    id: 'tsim-sha-tsui',
    name: 'Tsim Sha Tsui',
    blurb: 'Dense Kowloon waterfront towers across the harbour — real cloud, streets + relief',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'mong-kok',
    name: 'Mong Kok',
    blurb: 'One of Earth’s densest cores — a packed forest of Kowloon towers, markets + Nathan Road',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'sham-shui-po',
    name: 'Sham Shui Po',
    blurb: 'Gritty old Kowloon — wall-to-wall tenements, street markets, the densest grid of all',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'kowloon-tong',
    name: 'Kowloon Tong',
    blurb: 'The low-rise garden belt between the Kowloon ridges — campuses + quiet crescents',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'kai-tak',
    name: 'Kai Tak',
    blurb: 'The old-airport runway reborn — a Kowloon East peninsula jutting into the harbour',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'wong-tai-sin',
    name: 'Wong Tai Sin',
    blurb: 'Kowloon temple town — dense estates below the green ridge of Lion Rock',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── New Territories ──
  {
    id: 'sha-tin',
    name: 'Sha Tin',
    blurb: 'New Territories valley town on the Shing Mun River, ringed by green country-park hills',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tsuen-wan',
    name: 'Tsuen Wan',
    blurb: 'The first new town — dense towers under Tai Mo Shan, facing the Rambler Channel',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tsing-ma-bridge',
    name: 'Tsing Ma Bridge',
    blurb: 'Tsing Yi ↔ Ma Wan — the great suspension span and its green island landfalls',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tuen-mun',
    name: 'Tuen Mun',
    blurb: 'West-NT river-channel town between Castle Peak and the sea',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'yuen-long',
    name: 'Yuen Long',
    blurb: 'The northwest-plain market town turned new town, wetlands to the north',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tin-shui-wai',
    name: 'Tin Shui Wai',
    blurb: 'The grid-planned town beside the wetland park — towers meeting fishponds',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tai-po',
    name: 'Tai Po',
    blurb: 'Tolo Harbour market town — the old market + river meeting the tidal inlet',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'fanling-sheung-shui',
    name: 'Fanling & Sheung Shui',
    blurb: 'The northern twin towns on the border plain, last stop before the hills',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tseung-kwan-o',
    name: 'Tseung Kwan O',
    blurb: 'The Junk Bay new town — waterfront promenades ringed by sharp green ridges',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'sai-kung',
    name: 'Sai Kung',
    blurb: 'The east-NT seafood town + harbour, gateway to the green country-park hills',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── Lantau & islands ──
  {
    id: 'tung-chung',
    name: 'Tung Chung',
    blurb: 'The Lantau gateway town under the cable car, beside the airport channel',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'hk-airport',
    name: 'HK Airport',
    blurb: 'Chek Lap Kok — the reclaimed-island airport, terminals + aprons ringed by sea',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'hk-disneyland',
    name: 'HK Disneyland',
    blurb: 'Penny’s Bay on Lantau — the resort on reclaimed land at the water’s edge',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'tian-tan-buddha',
    name: 'Tian Tan Buddha',
    blurb: 'Ngong Ping plateau on Lantau — the Big Buddha + monastery amid forested mountain peaks',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'cheung-chau',
    name: 'Cheung Chau',
    blurb: 'A dumbbell outlying island — the car-free village waterfront between two green headlands',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── Campuses ──
  {
    id: 'hku',
    name: 'HKU Campus',
    blurb: 'The century-old hillside campus — courtyards and stairways over the western harbour',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'cuhk',
    name: 'CUHK Hillside',
    blurb: 'A university built down a mountainside — terraced campus over Tolo Harbour',
    has: { cloud: true, roads: true, terrain: true },
  },
  {
    id: 'hkust',
    name: 'HKUST Bay',
    blurb: 'The campus amphitheatre cascading to Port Shelter — sea views from every step',
    has: { cloud: true, roads: true, terrain: true },
  },
  // ── Level-2 areas — one iso strip map = several connected districts ──
  {
    id: 'hk-island-north',
    name: 'HK Island North',
    blurb: 'The island\u2019s north shore in one sweep \u2014 Sheung Wan to Causeway along the harbour',
    has: { cloud: true, roads: true, terrain: true },
    level: 2,
  },
  {
    id: 'victoria-harbour',
    name: 'Victoria Harbour',
    blurb:
      'The cross-harbour core as one planet — Central, Wan Chai and Tsim Sha Tsui facing each other over the water, ferries in between',
    has: { cloud: true, roads: true, terrain: true },
    level: 2,
    render: { overviewDefault: true }, // a territory reads as a diorama first
  },
  {
    id: 'hk-island',
    name: 'Hong Kong Island',
    blurb:
      'The whole island as one world — the north-shore wall of towers, the Peak ridge, and the beach towns of the south side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 }, // big cube-belt
  },
  {
    id: 'kowloon',
    name: 'Kowloon',
    blurb:
      'The whole peninsula — TST to Lion Rock, the densest urban fabric on earth read as a single tiny planet',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 }, // big cube-belt
  },
  {
    // VICTORIA HARBOUR CORE — the flagship harbour planet: Hong Kong Island +
    // Victoria Harbour + Kowloon as one 6-face cube with the WATER DEAD CENTRE
    // on the face you land on, baked half again as sharp as hksar-cube
    // (faceSize 768 → 15.8 m/cell vs 23.8), on 34,264 real CSDI footprints with
    // coast/MTR/ferry/rail/pitch overlays. hksar-cube stays the territory-wide
    // "All HK" step above this; this is the harbour city itself at max detail.
    id: 'victoria-cube',
    name: 'Victoria Harbour · Cube',
    blurb:
      'The harbour at full stretch — Hong Kong Island along one shore, Kowloon along the other, and the water that made the city in between; the whole core at the sharpest whole-harbour bake',
    has: { cloud: true, roads: true, terrain: true },
    // level 4 → the 'territories' picker group, beside hksar-cube and the
    // Island/Kowloon globes: this planet IS a territory-scale planet, and the
    // 'downtown tiles' group would have filed the flagship under small tiles.
    level: 4,
    // Tuned to CENTRAL-BELT, not to the whole-city cubes: at 9.9 m/cell this
    // planet resolves an actual street grid, so it takes the belt's street
    // detail (which also gates the road-network detail pass, the mesh-persona
    // plaza and the belt traffic layer), its heavier ink, and its crowd height.
    render: {
      vexag: 1.15,
      hknee: 2.4,
      maxU: 8.0,
      footMaxU: 2.6,
      outlineA: 0.07,
      streetDetail: true,
      crowdHeight: 0.7,
      overviewDefault: true,
      propScale: 0.09,
    },
  },
  {
    // The whole-HKSAR "All HK" cube — faceSize-512, ~24 m/cell, 6 faces of ground/coast/
    // terrain + building clouds + the full overlay set; true 5.5 m is on drill-down.
    //
    // RE-GEOMETRIED (scripts/bake-city-cube.mjs): the first bake spanned a wider territory
    // with dLon = dLat and no cos(lat) correction, which pushed the BACK face into open sea
    // east of Sai Kung — one OSM way in the whole face, so every cell kept the default
    // ground material and it rendered as a blank grey plate with no building cloud at all.
    // The south cap was 95% water. The cube is now centred on the harbour at a diameter
    // where all six faces carry real Hong Kong: harbour + Kowloon in front, Hong Kong
    // Island on the south cap, Tai Po/Fanling north, Sai Kung east, Tsuen Wan/Lantau west,
    // and the Geopark islands on the back. Covers less territory than the first attempt —
    // the cross reaches four face-widths east of centre and Hong Kong ends before that, so
    // wider necessarily means emptier.
    id: 'hksar-cube',
    name: 'Hong Kong SAR · Cube',
    blurb: 'The whole territory as one cubed planet — the harbour front and centre, Hong Kong Island below, the New Territories behind and the Sai Kung islands on the far side; drill into any region for 5.5m detail',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    // worldScale 4 (see CUBE_BELT_PLANETS) renders the whole territory as a GRAND big
    // ball; props/crowd are divided ~back out (0.6→0.24, 1.4→0.56) so trees/people stay
    // human-scale on the big sphere instead of a monster forest / kaiju crowd.
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  // ── Territory tiles — big planets (downtown golden block + the airport gateway) ──
  {
    id: 'tile-b2',
    name: 'Airport Gateway',
    level: 3,
    blurb: 'The reclaimed-island airport at full tile scale — runways, terminals, Tung Chung and the western channel',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-b5',
    level: 3,
    name: 'Tsuen Wan Gateway',
    blurb: 'The western gateway — Tsuen Wan + Tsing Yi wrap a container-port shore into one big planet',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-b6',
    level: 3,
    name: 'Kowloon Heart',
    blurb: 'The Kowloon core as one planet — Mong Kok + Sham Shui Po density from harbour to hills',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-b7',
    level: 3,
    name: 'Kowloon East',
    blurb: 'Kai Tak, Kwun Tong + the Sha Tin valley — east Kowloon\'s runway-to-river sweep',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-c5',
    level: 3,
    name: 'Western Harbour',
    blurb: 'The harbour\'s western gate — Kennedy Town + HKU slopes facing the open channel',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-c6',
    level: 3,
    name: 'Island Core',
    blurb: 'The HK-Island planet — Central, TST across the water, Causeway, The Peak, Aberdeen + Stanley on one world',
    has: { cloud: true, roads: true, terrain: true },
    // Big cube-belt (CUBE_BELT_PLANETS): coarser grid than the 5.5 m districts, so props
    // scale up a touch; the real island relief gets a little extra vexag.
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  {
    id: 'tile-c7',
    level: 3,
    name: 'Island East',
    blurb: 'The Island\'s eastern reach — Chai Wan + the narrows where the harbour opens to sea',
    has: { cloud: true, roads: true, terrain: true },
    render: { vexag: 1.2, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.06, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.45 },
  },
  // ── Global-city planets (ADR-0236 rollout) ──
  // Every city planet is a 6-FACE CUBE-BELT: the whole ball is that city, exactly like
  // the HK belts. Baked by scripts/bake-city-fleet.mjs (cos(lat)-correct faces); rivers
  // arrive as natural=water/waterway relations, coastlines via --coastline on bay-side
  // and island belts. `region` hides them from the HK picker — they lead /tokyo + /nyc.
  {
    id: 'shinjuku-belt',
    name: 'Shinjuku · Cube-Belt',
    blurb: 'The skyscraper heart — west-side towers, the busiest station on Earth, Kabukicho neon',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'shibuya-belt',
    name: 'Shibuya · Cube-Belt',
    blurb: 'The scramble and its valley — dense low blocks under the station towers',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, nearStreet: true },
  },
  {
    id: 'shibuya-line-belt',
    name: 'Shibuya · Line',
    blurb: 'The scramble at 2.8 m per cell in colour plus ink. Buildings, the walker, cars and the sky shelves keep their lines. The mood colour stays.',
    has: { cloud: true, roads: true, terrain: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, footMinU: 0.12, outlineA: 0.1, streetDetail: true, crowdHeight: 1.9, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, lineArt: true },
  },
  {
    id: 'ginza-belt',
    name: 'Ginza · Cube-Belt',
    blurb: 'The department-store grid and the expressway canal cutting through it',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'asakusa-belt',
    name: 'Asakusa · Cube-Belt',
    blurb: 'The old low city on the Sumida — temple lanes, riverbank, the wide water bend',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ueno-belt',
    name: 'Ueno · Cube-Belt',
    blurb: 'The park plateau and the market lanes below it, tracks fanning north',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ikebukuro-belt',
    name: 'Ikebukuro · Cube-Belt',
    blurb: 'North-west terminus — big-box towers over a tight shopping warren',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'akihabara-belt',
    name: 'Akihabara · Cube-Belt',
    blurb: 'Electric Town — the Kanda river, elevated tracks, arcade blocks',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'roppongi-belt',
    name: 'Roppongi · Cube-Belt',
    blurb: 'Embassy hills and tower complexes above a night-time grid',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'odaiba-belt',
    name: 'Odaiba · Cube-Belt',
    blurb: 'The bay-fill island — waterfront promenade, the harbour opening south',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'shinagawa-belt',
    name: 'Shinagawa · Cube-Belt',
    blurb: 'Bay-side rail gateway — canals, docks, the shinkansen corridor',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'manhattan-belt',
    name: 'Lower Manhattan · Cube-Belt',
    blurb: 'The island tip — FiDi canyons, Battery Park, the harbour edge where the grid began',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'midtown-belt',
    name: 'Midtown · Cube-Belt',
    blurb: 'The tall middle — the avenues at their densest, river to river',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'central-park-belt',
    name: 'Central Park · Cube-Belt',
    blurb: 'The green rectangle and the walls of buildings framing it',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'harlem-belt',
    name: 'Harlem · Cube-Belt',
    blurb: 'Uptown grid on the Harlem river bend, brownstone blocks and wide avenues',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'upper-west-belt',
    name: 'Upper West Side · Cube-Belt',
    blurb: 'Hudson-side blocks between the park and the river',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'brooklyn-heights-belt',
    name: 'Brooklyn Heights · Cube-Belt',
    blurb: 'The promenade bluff facing the harbour across the East River',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'dumbo-belt',
    name: 'Dumbo · Cube-Belt',
    blurb: 'Under the bridge approaches — warehouse blocks on the waterfront',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'williamsburg-belt',
    name: 'Williamsburg · Cube-Belt',
    blurb: 'East River waterfront, low warehouse grid and the bridge landing',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'lic-belt',
    name: 'Long Island City · Cube-Belt',
    blurb: 'Queens waterfront towers facing Midtown across the river',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'red-hook-belt',
    name: 'Red Hook · Cube-Belt',
    blurb: 'Working waterfront on the harbour — piers, basins, low brick',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  // ── European cluster (Paris beachhead → NL). Rivers/canals arrive via the
  // relation-flattening water path; no --coastline (that floods from the tile edge,
  // for OPEN SEA only — on an inland river city it would drown the tile).
  // Paris:
  {
    id: 'ile-de-la-cite-belt',
    name: 'Île de la Cité · Cube-Belt',
    blurb: 'The island where the city began — the Seine splitting around cathedral stone',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'louvre-belt',
    name: 'Louvre · Cube-Belt',
    blurb: 'Palace wings and the Tuileries, the river running along the south edge',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'marais-belt',
    name: 'Le Marais · Cube-Belt',
    blurb: 'The old quarter that escaped Haussmann — narrow lanes, courtyard hôtels',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'latin-quarter-belt',
    name: 'Latin Quarter · Cube-Belt',
    blurb: 'Left-bank slopes of lecture halls and bookshops climbing away from the river',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'eiffel-belt',
    name: 'Champ de Mars · Cube-Belt',
    blurb: 'The great lawn, the river bend, and the iron silhouette over it',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'montmartre-belt',
    name: 'Montmartre · Cube-Belt',
    blurb: 'The butte above the city — stairs, vineyard, the white dome on top',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'opera-belt',
    name: 'Opéra · Cube-Belt',
    blurb: 'Haussmann at full volume — boulevards, department stores, uniform stone',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'bastille-belt',
    name: 'Bastille · Cube-Belt',
    blurb: 'Where the boulevards converge on the old fortress square and the canal',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'saint-germain-belt',
    name: 'Saint-Germain · Cube-Belt',
    blurb: 'Left-bank cafés and gallery streets behind the quais',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'la-defense-belt',
    name: 'La Défense · Cube-Belt',
    blurb: 'The tower cluster west of the axis — the one skyline Paris allows',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  // Amsterdam:
  {
    id: 'grachtengordel-belt',
    name: 'Grachtengordel · Cube-Belt',
    blurb: 'The canal ring itself — concentric water, gabled houses, a thousand bridges',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'jordaan-belt',
    name: 'Jordaan · Cube-Belt',
    blurb: 'Tight canal blocks and courtyards west of the ring',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'centraal-belt',
    name: 'Centraal · Cube-Belt',
    blurb: 'The station island and the open IJ water behind it',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'de-pijp-belt',
    name: 'De Pijp · Cube-Belt',
    blurb: 'Dense brick blocks and the market street running through them',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'oud-west-belt',
    name: 'Oud-West · Cube-Belt',
    blurb: 'Nineteenth-century grid between the ring and the park',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'oostelijk-belt',
    name: 'Oostelijk Havengebied · Cube-Belt',
    blurb: 'Rebuilt eastern docklands — water everywhere, new blocks on old piers',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'museumplein-belt',
    name: 'Museumplein · Cube-Belt',
    blurb: 'The museum lawn and the grand houses around it',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'nieuwmarkt-belt',
    name: 'Nieuwmarkt · Cube-Belt',
    blurb: 'Old harbour edge — canals, the weigh house, Chinatown lanes',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'westerpark-belt',
    name: 'Westerpark · Cube-Belt',
    blurb: 'Park, gasworks and the canal running west',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'noord-belt',
    name: 'Amsterdam-Noord · Cube-Belt',
    blurb: 'Across the IJ — former shipyards turned waterfront',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  // Rotterdam:
  {
    id: 'kop-van-zuid-belt',
    name: 'Kop van Zuid · Cube-Belt',
    blurb: 'The south bank towers facing the river across the bridge',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'centrum-belt',
    name: 'Centrum · Cube-Belt',
    blurb: 'The rebuilt heart — postwar blocks and modern towers, no old grid',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'erasmusbrug-belt',
    name: 'Erasmusbrug · Cube-Belt',
    blurb: 'The white bridge and the wide Maas either side of it',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'delfshaven-belt',
    name: 'Delfshaven · Cube-Belt',
    blurb: 'The surviving old harbour — the one piece the war left standing',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'kralingen-belt',
    name: 'Kralingen · Cube-Belt',
    blurb: 'The lake and the wooded east side of the city',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'noordereiland-belt',
    name: 'Noordereiland · Cube-Belt',
    blurb: 'A whole island in the middle of the river',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'katendrecht-belt',
    name: 'Katendrecht · Cube-Belt',
    blurb: 'The peninsula between the basins — docks turned neighbourhood',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'blijdorp-belt',
    name: 'Blijdorp · Cube-Belt',
    blurb: 'Northern blocks and the zoo, away from the water',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'feijenoord-belt',
    name: 'Feijenoord · Cube-Belt',
    blurb: 'South-bank harbour blocks and the stadium beyond',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'europoort-belt',
    name: 'Waalhaven · Cube-Belt',
    blurb: 'The working port — basins, cranes, the scale of the thing',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'tai-po-belt',
    name: 'Tai Po · Cube-Belt',
    blurb: 'Market-town riverfront at the head of Tolo Harbour, hills rising behind',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'tseung-kwan-o-belt',
    name: 'Tseung Kwan O · Cube-Belt',
    blurb: 'New-town towers ringing a reclaimed bay in the eastern New Territories',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'tsuen-wan-belt',
    name: 'Tsuen Wan · Cube-Belt',
    blurb: 'A New Territories town centre where the MTR meets the container coast, hills climbing close behind',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'kwai-chung-belt',
    name: 'Kwai Chung & Tsing Yi · Cube-Belt',
    blurb: 'Container terminals and stacked estates along the working edge of the harbour',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ma-on-shan-belt',
    name: 'Ma On Shan · Cube-Belt',
    blurb: 'Waterfront new-town towers under the ridge at the head of Tolo Harbour',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'chai-wan-belt',
    name: 'Chai Wan & Shau Kei Wan · Cube-Belt',
    blurb: 'The eastern end of the Island — typhoon shelter, tenements and hillside estates',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'tin-shui-wai-belt',
    name: 'Tin Shui Wai · Cube-Belt',
    blurb: 'Planned new-town blocks set among the northwest wetlands and fish ponds',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'cuhk-belt',
    name: 'CUHK & Ma Liu Shui · Cube-Belt',
    blurb: 'A university on the water at Ma Liu Shui — campus terraces climbing from the Tolo shoreline',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'hkust-belt',
    name: 'HKUST & Clear Water Bay · Cube-Belt',
    blurb: 'The Clear Water Bay headland — a hillside campus above the bays of the eastern peninsula',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'disneyland-belt',
    name: 'Hong Kong Disneyland · Cube-Belt',
    blurb: 'The resort at Penny’s Bay — parks, hotels and the pier on the northeast Lantau shore',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'repulse-bay-belt',
    name: 'Repulse Bay & Deep Water Bay · Cube-Belt',
    blurb: 'The southside beaches — apartment terraces stepping down to the sand',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ocean-park-belt',
    name: 'Ocean Park & Wong Chuk Hang · Cube-Belt',
    blurb: 'The headland park above Wong Chuk Hang, where the old industrial blocks meet the ridge',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'kwun-tong-fine',
    name: 'Kwun Tong · Ultra-Belt',
    blurb: 'East Kowloon at 2.8 m per cell — converted industrial blocks and the promenade on the old runway channel',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'central-fine',
    name: 'Central · Ultra-Belt',
    blurb: 'Central at 2.8 m per cell — banking towers, escalator lanes and the ferry piers at a scale where kerbs read',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1, smoothSurface: true },
  },
  {
    id: 'tst-fine',
    name: 'Tsim Sha Tsui · Ultra-Belt',
    blurb: 'The Kowloon waterfront at 2.8 m per cell — the promenade, the clock tower and the blocks behind Nathan Road',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'mong-kok-fine',
    name: 'Mong Kok · Ultra-Belt',
    blurb: 'The densest street grid on earth at 2.8 m per cell — market lanes and tong lau stacked over the pavement',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'causeway-fine',
    name: 'Causeway Bay · Ultra-Belt',
    blurb: 'Causeway Bay at 2.8 m per cell — the typhoon shelter, the tram crossings and the department-store blocks',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ngong-ping-fine',
    name: 'Ngong Ping · Ultra-Belt',
    blurb: 'The monastery plateau at 2.8 m per cell — Po Lin, the Big Buddha and the cable-car village, with Lantau Peak standing over them',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    ultraDetail: true,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'quarry-bay-belt',
    name: 'Quarry Bay · Cube-Belt',
    blurb: 'Eastern Island offices and the harbour edge below Braemar Hill',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'koenji-belt',
    name: 'Koenji · Cube-Belt',
    blurb: 'Low-rise west-side lanes, record shops and covered arcades',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'kinshicho-belt',
    name: 'Kinshicho · Cube-Belt',
    blurb: 'East-bank towers over a flat sprawl of small blocks',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'toyosu-belt',
    name: 'Toyosu · Cube-Belt',
    blurb: 'Reclaimed bay island with wide roads, tower clusters and open water',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'kichijoji-belt',
    name: 'Kichijoji · Cube-Belt',
    blurb: 'Suburban hub beside the park, dense little streets around the station',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'astoria-belt',
    name: 'Astoria · Cube-Belt',
    blurb: 'Queens low-rise grid running down to the East River',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'park-slope-belt',
    name: 'Park Slope · Cube-Belt',
    blurb: 'Brownstone rows on the slope beside the park',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'pere-lachaise-belt',
    name: 'Pere-Lachaise · Cube-Belt',
    blurb: 'The cemetery hill and the dense blocks wrapped around it',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'butte-aux-cailles-belt',
    name: 'Butte-aux-Cailles · Cube-Belt',
    blurb: 'A low village of small houses tucked inside the 13th',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'batignolles-belt',
    name: 'Batignolles · Cube-Belt',
    blurb: 'North-west blocks around the park and the old rail cutting',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'bercy-belt',
    name: 'Bercy · Cube-Belt',
    blurb: 'Riverside warehouses turned park, the Seine along the whole south edge',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'oud-zuid-belt',
    name: 'Oud-Zuid · Cube-Belt',
    blurb: 'Brick mansion blocks south of the museums',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'jiyugaoka-belt',
    name: 'Jiyugaoka · Cube-Belt',
    blurb: 'Leafy south-west streets of small shops and low houses',
    has: { cloud: true, roads: true },
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'east-village-belt',
    name: 'East Village · Cube-Belt',
    blurb: 'Tenement blocks and small parks east of the avenues',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'greenwich-village-belt',
    name: 'Greenwich Village · Cube-Belt',
    blurb: 'The one place the grid breaks, angled streets and low townhouses',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'inwood-belt',
    name: 'Inwood · Cube-Belt',
    blurb: 'The northern tip of the island, hills above the Harlem river bend',
    has: { cloud: true, roads: true },
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'belleville-belt',
    name: 'Belleville · Cube-Belt',
    blurb: 'The hill in the east, steep streets and a long view back over the city',
    has: { cloud: true, roads: true },
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'watergraafsmeer-belt',
    name: 'Watergraafsmeer · Cube-Belt',
    blurb: 'A polder below sea level, laid out on its own grid',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'zuidas-belt',
    name: 'Zuidas · Cube-Belt',
    blurb: 'The business strip, the one cluster of towers in the city',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'bos-en-lommer-belt',
    name: 'Bos en Lommer · Cube-Belt',
    blurb: 'Post-war west blocks along the canal',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'hillegersberg-belt',
    name: 'Hillegersberg · Cube-Belt',
    blurb: 'Villa streets around the lake in the north',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'charlois-belt',
    name: 'Charlois · Cube-Belt',
    blurb: 'South-bank neighbourhood between the harbour basins',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'overschie-belt',
    name: 'Overschie · Cube-Belt',
    blurb: 'Old village on the Schie, motorways threading past',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'prins-alexander-belt',
    name: 'Prins Alexander · Cube-Belt',
    blurb: 'Eastern new-town blocks on the deep polder',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ommoord-belt',
    name: 'Ommoord · Cube-Belt',
    blurb: 'Ring-planned north-east estate among green strips',
    has: { cloud: true, roads: true },
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'tung-chung-belt',
    name: 'Tung Chung · Cube-Belt',
    blurb: 'Lantau new town under the peaks, the airport channel to the north',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'wong-tai-sin-belt',
    name: 'Wong Tai Sin · Cube-Belt',
    blurb: 'Estate blocks packed under the Kowloon foothills',
    has: { cloud: true, roads: true, terrain: true },
    level: 1,
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  {
    id: 'ijburg-belt',
    name: 'IJburg · Cube-Belt',
    blurb: 'Islands built into the IJmeer, water on every side',
    has: { cloud: true, roads: true },
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 2.4, seaLevelLift: 0.1 },
  },
  // ── Whole-city cubes (scripts/bake-city-cube.mjs) ──
  // One per global city: the same 6-face cubed sphere as the district belts, but sized
  // to the WHOLE city (10-14 km/face at faceSize 512 → ~20-28 m/cell) — the hksar-cube
  // pattern, which is the only whole-city model that transplants off Hong Kong (the -meta
  // planets need ultra crops + dense member coverage only HK has).
  //
  // level 4 ⇒ planetKindOf 'territories': an OVERVIEW planet, deliberately not a Siege
  // stage — you read the city here and drill into one of its 15 district belts to play.
  // The render tune follows hksar-cube rather than the belts: at worldScale 4.0 the sphere
  // is grand, so props and crowd are divided back out (0.35 → 0.09, 1.9 → 0.56) to keep
  // trees and people human-scale instead of a monster forest on a giant ball.
  {
    id: 'tokyo-cube',
    name: 'Tokyo · Whole-City Cube',
    blurb:
      'The whole Yamanote city as one planet — the west-side towers, the imperial green, the bay reclaimed east and the Tama hills behind',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'nyc-cube',
    name: 'New York · Whole-City Cube',
    blurb:
      'The harbour city as one planet — the Manhattan spine river to river, the bridges east, the Upper Bay opening south',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'nyc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'paris-cube',
    name: 'Paris · Whole-City Cube',
    blurb:
      'The whole of intramuros Paris as one planet — the Seine through the middle, Montmartre on the rise, the boulevards wheeling out to the ring',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    // Polder city: no DTM baked (the whole region sits within a few metres of NAP), so
    // `terrain` is false — an honest flat world, not a missing asset.
    id: 'rotterdam-cube',
    name: 'Rotterdam · Whole-City Cube',
    blurb:
      'The port city as one planet — the Maas cutting it in two, the towers of Kop van Zuid, the harbours running west to the sea',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'rotterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'amsterdam-cube',
    name: 'Amsterdam · Whole-City Cube',
    blurb:
      'The canal city as one planet — the grachten in their arcs, the IJ behind the station, the polder grid running out to the ring',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'amsterdam',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'berlin-cube',
    name: 'Berlin · Whole-City Cube',
    blurb:
      'The whole inner city as one planet — the Spree threading its old glacial valley, the Tiergarten green in the middle, Charlottenburg west and the Ringbahn wheeling around it all',
    // `terrain` where the other flat cities have none: Berlin's cube is the one baked
    // with a DTM, because the Urstromtal valley between the Barnim and Teltow plateaux
    // is ~80 m of real relief, not polder.
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'sanfrancisco-cube',
    name: 'San Francisco · Whole-City Cube',
    blurb:
      'The whole peninsula as one planet — the grid draped straight over the hills, the great span across the strait, ocean along one side and the bay down the other',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'vienna-cube',
    name: 'Vienna · Whole-City Cube',
    blurb:
      'The whole inner city as one planet — the boulevard ring drawing its oval round the old core, the canal cutting behind it, the great river braided across the top and the wooded hills climbing away west',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'barcelona-cube',
    name: 'Barcelona · Whole-City Cube',
    blurb:
      'The whole plain as one planet — the cut-corner grid filling the middle, the old town tangled down at the shore, the sea along the front and the wooded ridge standing behind it all',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'copenhagen-cube',
    name: 'Copenhagen · Whole-City Cube',
    blurb:
      'The whole inner city as one planet — the harbour cut straight down the middle between its made islands and dockyards, the old core and its chain of lakes west of the water, the flat reclaimed ground south and the open strait running away east',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'toronto-cube',
    name: 'Toronto · Whole-City Cube',
    blurb:
      'The whole lakefront city as one planet — the tower cluster and its one great needle above the shore, the ravine system cutting green fingers inland, the island sandbar lying offshore',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'melbourne-cube',
    name: 'Melbourne · Whole-City Cube',
    blurb:
      'The whole delta city as one planet — the ruled central grid with its laneways cut through, the river bending past it, the bay opening south and the ranges standing far behind',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'edinburgh-cube',
    name: 'Edinburgh · Whole-City Cube',
    blurb:
      'The whole old city as one planet — a volcanic crag with its fortress on the crown, the ridge street running down from it, the planned grid squared off to the north and a bare hill standing over it all',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'seoul-cube',
    name: 'Seoul · Whole-City Cube',
    blurb:
      'The whole basin as one planet — the great river across the middle with its island in midstream, a wooded cone standing alone in the dense grid, the old walled core under the northern hill and granite mountains closing the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'losangeles-cube',
    name: 'Los Angeles · Whole-City Cube',
    blurb:
      'The whole basin as one planet — the tower cluster standing alone in a plain that runs to the horizon, the river ruled through it as a concrete trough, the mountains cutting straight across the built grid',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'dubai-cube',
    name: 'Dubai · Whole-City Cube',
    blurb:
      'The whole coast as one planet — the creek cutting inland past the old quarter, the boulevard of towers running beside it, the dredged marina and the made islands laid out on the gulf',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'bangkok-cube',
    name: 'Bangkok · Whole-City Cube',
    blurb:
      'The whole delta as one planet — the great river looping through the middle, the old walled quarter inside its bend, canals threading the flats and the towers standing over them',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'capetown-cube',
    name: 'Cape Town · Whole-City Cube',
    blurb:
      'The whole cape as one planet — the flat-topped mountain filling half the front with its summit dams, the city bowl caught in the amphitheatre below it, the working harbour basins along the top and the Atlantic down the whole western edge',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'rio-cube',
    name: 'Rio de Janeiro · Whole-City Cube',
    blurb:
      'The whole bay as one planet — the forested massif walling the west side, bare granite domes standing out of the water at the mouth, the ocean beaches running along the bottom and the old centre and its port up at the top',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'istanbul-cube',
    name: 'Istanbul · Whole-City Cube',
    blurb:
      'The whole strait as one planet — salt water running right down the middle with dense city pressed against both banks, a second inlet branching west past the old walled peninsula, and the great bridges springing clean across the channel',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    // The basin city. `terrain: true` — the massif ringing it reads 1,082 m on the baker's own
    // 48x48 grid against dtmRef 1150, i.e. 7.0 of the 7.5 cap, so the bowl is real and
    // unclamped. Its ARM, not its skyline, decided the geometry: see bake-city-cube.mjs.
    id: 'taipei-cube',
    name: 'Taipei · Whole-City Cube',
    blurb:
      'The whole basin as one planet — a flat grid of superblocks filling the middle with one lone supertall over it, two rivers meeting behind their levees at the far corner, and a volcanic massif standing above the hot-spring valley on the skyline',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'miami-cube',
    name: "Miami · Whole-City Cube",
    blurb:
      'The whole bay as one planet — pastel hotels on the beach, a wall of towers on the downtown shore, and the river cutting the grid before it meets the water',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'fukuoka-cube',
    name: "Fukuoka · Whole-City Cube",
    blurb:
      'Two downtowns on either side of a river — a great station hall to the east, covered arcades and a canal of boats to the west, a seaside tower on reclaimed sand, and a bay of islands to the north',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'nagoya-cube',
    name: "Nagoya · Whole-City Cube",
    blurb:
      'A wide station of twin towers, a TV mast over a park of fountains, a reconstructed castle keep in a moat of pines, and a shrine forest to the south of a grid of low blocks',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'yokohama-cube',
    name: "Yokohama · Whole-City Cube",
    blurb:
      'A harbour of piers and red-brick warehouses, a cluster of glass towers on reclaimed land, a chinatown of gates and a bluff of wooden houses above the water, and a long bay opening east',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'osaka-cube',
    name: "Osaka · Whole-City Cube",
    blurb:
      'A dense grid of low blocks filling a river delta — two towers standing over the station at one end, a green-roofed castle keep in a park of moats to the east, a lattice tower and a shopping street to the south, and a ferris wheel on the bay',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'athens-cube',
    name: "Athens · Whole-City Cube",
    blurb:
      'A white sea of low apartment blocks filling a basin between bare mountains — a rock with a marble temple standing in the middle of it, a pine-covered hill with a chapel beside it, a port of ships and cranes at the south-western edge, and the sea opening beyond',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'lisbon-cube',
    name: "Lisbon · Whole-City Cube",
    blurb:
      'Seven hills of pale tiled houses tumbling to a wide river mouth — a grid of straight streets on the flat between two castle-topped ridges, yellow trams climbing the slopes, a long bridge over the water, monasteries and a tower on the western shore, and a forested hill park behind',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'helsinki-cube',
    name: "Helsinki · Whole-City Cube",
    blurb:
      'A capital on a fist of peninsulas — a white cathedral on a square above the harbour, ferries and a market by the water, granite islands with a star fort scattered south, stone blocks and rail yards inland, and the sea reaching in on every side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'zurich-cube',
    name: "Zurich · Whole-City Cube",
    blurb:
      'A long lake narrowing into a river between two old-town banks, church towers on both sides, tram lines fanning out to leafy hills, a wooded ridge standing over the west, and the Alps as a white line beyond the far end of the water',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'prague-cube',
    name: "Prague · Whole-City Cube",
    blurb:
      'A river bending through a bowl of hills — a castle and cathedral on the crest above one bank, bridges with statues on their parapets, red roofs and spires packed along the other, and rail yards and housing blocks climbing the valley sides beyond',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'kyoto-cube',
    name: "Kyoto · Whole-City Cube",
    blurb:
      'The old capital as one planet — a grid of low wooden streets filling a basin, temple roofs and a pagoda standing over the eastern hills, a long river with wide gravel banks, a wall of forested mountain on three sides, and the modern towers gathered around one station',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'vatican-cube',
    name: "Vatican City · Whole-City Cube",
    blurb:
      'The smallest state as a whole planet — a great domed church and its colonnaded square, walled gardens behind, the long galleries beside them, a round fortress on the river, and the roofs of the old city crowding right up to the walls',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'vatican',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'monaco-cube',
    name: "Monaco · Whole-City Cube",
    blurb:
      'A cliff city stacked in terraces between a mountain wall and the sea — a harbour of white boats, a rock with a palace on it, a casino square, cranes and towers climbing every slope, and the coast road running off east and west along the water',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'macau-cube',
    name: "Macau · Whole-City Cube",
    blurb:
      'A peninsula of tight old streets and tower blocks joined by long bridges to two islands — a strip of enormous hotel blocks in the middle, a green hill island with fishing villages and a black-sand beach at the far end, and the wide brown estuary opening on every side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'geneva-cube',
    name: "Geneva · Whole-City Cube",
    blurb:
      'A lake city seen whole — the old town on its hill above a river mouth, the long jet in the harbour, embassies and glass along the north shore, vineyard slopes and a limestone wall of mountain standing over the south, and the far shore of the lake fading into the Alps',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'singapore-cube',
    name: "Singapore · Whole-City Cube",
    blurb:
      'The whole island as one planet — a knot of dark bank towers standing at a river mouth with a wide made bay beside it and three hotel slabs carrying a boat across their roofs, low green ridges of rainforest running behind, ranks of pale housing slabs filling every quarter out to the runways, and a strait of anchored ships wrapping two whole sides',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'rome-cube',
    name: "Rome · Whole-City Cube",
    blurb:
      'The whole basin as one planet — a green river winding in tight bends through a low ochre city of terracotta roofs, one great dome alone on the west bank and a broken ring of arches on the east, straight avenues cut through fields of ruins, and bare limestone ridges climbing in steps along one whole side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'london-cube',
    name: "London · Whole-City Cube",
    blurb:
      'The whole river bend as one planet — a broad brown river looping twice through an unbroken carpet of low brick terraces, two separate knots of glass towers standing out of that carpet with one lead dome between them, great green rectangles of park cut into the brick, and a wooded ridge along one edge with bare chalk downs climbing the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'jakarta-cube',
    name: "Jakarta · Whole-City Cube",
    blurb:
      'The whole delta as one planet — a single obelisk alone in the middle of a huge grass square, one straight tower avenue running south from it, canals ruled dead straight through a carpet of small roofs out to a sea wall along the top edge, and the ground climbing away to green hills on the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'mexicocity-cube',
    name: "Mexico City · Whole-City Cube",
    blurb:
      'The whole basin as one planet — a flat lakebed grid filling the middle with a vast bare paved square at its heart, one dead-straight tree-lined boulevard cutting diagonally across that grid to a great wooded park, and pine-dark sierra climbing along two whole sides',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'sanjose-cube',
    name: "San Jose · Whole-City Cube",
    blurb:
      'The whole valley as one planet — a low grid running unbroken from one range to the other with a short cluster of flat-topped towers alone in the middle of it, two runways laid right beside them, a river threading north to the salt marsh at the tip of the bay, and bare golden hills climbing to a white observatory crest on the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'telaviv-cube',
    name: "Tel Aviv · Whole-City Cube",
    blurb:
      'The whole coastal plain as one planet — a straight sand beach running the full length of one side with a flat white grid of low apartment blocks behind it, a stone hill and a small harbour at the south end of the shore, glass tower clusters standing in threes and fours out of the flatness, and bare ridges climbing in steps on the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'chicago-cube',
    name: "Chicago · Whole-City Cube",
    blurb:
      'The whole lake plain as one planet — a dead-straight grid of streets running to the horizon in every direction, a river forking three ways through a knot of dark towers at the middle, a made green edge ruled along the water, and open lake filling one whole side',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'boston-cube',
    name: "Boston · Whole-City Cube",
    blurb:
      'The whole harbour as one planet — a filled peninsula of brick rowhouse grids pushing out into a ragged bay of drowned hills and islands, a wide basin of still water held back behind a dam with university quarters along its far bank, and a forested massif standing over the southern towns',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'mumbai-cube',
    name: "Mumbai · Whole-City Cube",
    blurb:
      'The whole peninsula as one planet — a curved sea wall round a reclaimed bay with arcaded stone blocks behind it, mill chimneys and chawl roofs filling the middle, a forested ridge standing over the northern suburbs, and open ocean along one whole side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'moscow-cube',
    name: "Moscow · Whole-City Cube",
    blurb:
      'The whole ring city as one planet — a brick-walled triangle of gold-domed churches at the centre with a long paved square beside it, boulevards closing round it in three concentric rings, a river looping twice through the middle, and stepped towers with spires standing at intervals out on the ring',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'nanjing-cube',
    name: "Nanjing · Whole-City Cube",
    blurb:
      'The whole river city as one planet — a Ming rampart still tracing its circuit round the old town, a lake with islands and causeways inside the north-east corner, a forested massif standing over the eastern wall and the Yangtze swinging past the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'beijing-cube',
    name: "Beijing · Whole-City Cube",
    blurb:
      'The whole capital as one planet — a walled palace compound of yellow roofs dead centre on a straight north-south axis, a great paved square below it, concentric ring roads wrapping the whole thing and a cluster of towers standing off the eastern ring',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'shanghai-cube',
    name: "Shanghai · Whole-City Cube",
    blurb:
      'The whole delta city as one planet — a curved stone waterfront of domed banks facing a wide brown river, three supertowers and a ball on splayed legs standing together on the far bank, and a grid of tight lane houses running back behind both sides',
    has: { cloud: true, roads: true, terrain: false },
    level: 4,
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'sydney-cube',
    name: "Sydney · Whole-City Cube",
    blurb:
      'The whole harbour as one planet — a drowned valley of headlands and bays cutting right through the middle, a steel arch over the narrows with a cluster of towers beside it and a shell-roofed point below them, and the open ocean along the far edge',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'kualalumpur-cube',
    name: "Kuala Lumpur · Whole-City Cube",
    blurb:
      'The whole valley as one planet — two rivers meeting at a domed mosque in the middle, a pair of tapering silver towers and a taller spire standing over the low shophouse grid around them, and a forested range closing the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'washingtondc-cube',
    name: "Washington · Whole-City Cube",
    blurb:
      'The whole plan as one planet — a ruled green rectangle straight through the middle with a stone obelisk standing on it and a domed block on a terrace at one end, diagonal avenues cutting across a gridiron of rowhouse streets, and two rivers meeting at the corner',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'xian-cube',
    name: "Xi'an · Whole-City Cube",
    blurb:
      'The whole plain as one planet \u2014 a walled rectangle with its moat sitting square in the middle, a tiled tower at the crossing of its four straight streets, two brick pagodas standing down the axis running south, and loess tableland climbing in steps beyond the rivers on the far side',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    // The water planet, and the roster's FINEST whole-city cube at 8.6 m/cell — fine enough
    // that a 5-15 m rio is 1-2 cells and actually cuts the raster, which is the entire point
    // of the city. NO `terrain`: the whole lagoon sits within about a metre of sea level, so
    // the config carries no dtm at all. Its regression assertions live in
    // apps/web/src/planet/data/water-network.test.ts.
    id: 'venice-cube',
    name: 'Venice · Whole-City Cube',
    blurb:
      '  },',
    has: { cloud: true, roads: true },
    level: 4,
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    // The wave-5 city, and the only one of its four candidates whose data supported a cube.
    // `terrain: true` because the eastern massif reads to 902 m on the baker's own 48x48 grid
    // against dtmRef 950 — 7.1 of the 7.5 cap, so the relief is real and unclamped.
    // Same render block as seoul-cube: identical face class (12.2 km / 23.7 m per cell) and
    // the same flat-basin-with-mountains-on-the-rim shape.
    id: 'shenzhen-cube',
    name: 'Shenzhen · Whole-City Cube',
    blurb:
      'The border city as one planet — a wall of towers standing over the central park, the boundary river running right across the middle with the crossings on it, reclaimed bay curling in from the west and a granite massif closing the eastern sky',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'mitte-belt',
    name: 'Mitte · Cube-Belt',
    blurb: 'The Spree splitting around the museum island under the TV tower',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'reichstag-belt',
    name: 'Regierungsviertel · Cube-Belt',
    blurb: 'The gate, the glass dome and the memorial grid on the Tiergarten edge',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kreuzberg-belt',
    name: 'Kreuzberg · Cube-Belt',
    blurb: 'The twin-towered river bridge, the painted wall along the bank',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'prenzlauer-berg-belt',
    name: 'Prenzlauer Berg · Cube-Belt',
    blurb: 'Unbroken five-storey blocks around their courtyards and the water tower knoll',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'charlottenburg-belt',
    name: 'Charlottenburg · Cube-Belt',
    blurb: 'The palace and its formal garden west of the ring',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'city-west-belt',
    name: 'City West · Cube-Belt',
    blurb: 'The broken spire, the department store and the boulevard running east',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tempelhof-belt',
    name: 'Tempelhofer Feld · Cube-Belt',
    blurb: 'A whole airfield kept as open grass, runways and all',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'neukolln-belt',
    name: 'Neukölln · Cube-Belt',
    blurb: 'Dense tenement south with the canal along its edge',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'friedrichshain-belt',
    name: 'Friedrichshain · Cube-Belt',
    blurb: 'Square-planned blocks and the yards behind the old rail works',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'moabit-belt',
    name: 'Moabit · Cube-Belt',
    blurb: 'An island quarter ringed by water, the great station beside it',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wedding-belt',
    name: 'Wedding · Cube-Belt',
    blurb: 'Working north, wide streets and the park on the old airfield road',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'schoneberg-belt',
    name: 'Schöneberg · Cube-Belt',
    blurb: 'Town-hall streets and quiet garden squares',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'treptow-belt',
    name: 'Treptower Park · Cube-Belt',
    blurb: 'Riverside park and the long memorial axis east of the Spree',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'olympiastadion-belt',
    name: 'Olympiastadion · Cube-Belt',
    blurb: 'The great stone bowl with the forest running up behind it',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tegel-belt',
    name: 'Tegeler See · Cube-Belt',
    blurb: 'Lake, islands and the woods along the north-west shore',
    has: { cloud: true, roads: true },
    region: 'berlin',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'golden-gate-belt',
    name: 'Golden Gate · Cube-Belt',
    blurb: 'Two tall towers carrying a slung cable across the strait, a round masonry fort at the south footing, forested bluffs behind',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'marina-belt',
    name: 'Marina · Cube-Belt',
    blurb: 'A yacht basin behind its breakwater, a domed rotunda beside a curved lagoon, flat pastel blocks right at the waterline',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'aquatic-park-belt',
    name: 'Aquatic Park · Cube-Belt',
    blurb: 'A curving pier wrapped around a crescent beach, finger piers stepping out east, a cable turntable at the head of the hill street',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'telegraph-hill-belt',
    name: 'Telegraph Hill · Cube-Belt',
    blurb: 'A steep cone cut away on its bay side, a fluted cylinder standing on the crown, tight small blocks and a green square below',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nob-hill-belt',
    name: 'Nob Hill · Cube-Belt',
    blurb: 'The crown where two cable lines cross, a stone cathedral and heavy hotel blocks around a small square, streets falling away four ways',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'financial-district-belt',
    name: 'Financial District · Cube-Belt',
    blurb: 'The one true tower cluster, a four-sided pyramid and a taller tapering shaft among flat slabs, on a grid cut by one wide diagonal',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'civic-center-belt',
    name: 'Civic Center · Cube-Belt',
    blurb: 'A domed hall facing a huge formal plaza, colonnaded blocks squared around it, a broad avenue running off on the slant',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rincon-belt',
    name: 'Rincon Hill · Cube-Belt',
    blurb: 'The western span of a double-deck bridge leaping off the shore on its towers, slim round-cornered towers crowded at the landing',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mission-bay-belt',
    name: 'Mission Bay · Cube-Belt',
    blurb: 'A ballpark diamond hard against the water with the outfield wall on the quay, a rectangular boat basin behind, new blocks on a fresh grid',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dogpatch-belt',
    name: 'Dogpatch · Cube-Belt',
    blurb: 'A working yard of floating dry docks and rail-mounted gantries, long shed roofs, small wooden houses stepping up the rise behind',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bernal-heights-belt',
    name: 'Bernal Heights · Cube-Belt',
    blurb: 'A bare round grassy dome with a road spiralling it and nothing built on top, tight rows of small houses packed against every slope',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mission-belt',
    name: 'The Mission · Cube-Belt',
    blurb: 'The flattest and most regular grid in the city, low painted blocks and narrow alleys, a long green slope stepping up the west edge',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'twin-peaks-belt',
    name: 'Twin Peaks · Cube-Belt',
    blurb: 'Two bare summits with a figure-of-eight road looped around them, a three-legged lattice mast on the ridge, the grid falling away east',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'golden-gate-park-belt',
    name: 'Golden Gate Park · Cube-Belt',
    blurb: 'Deep planted forest, a lake with a wooded island and a spiral path, a white glass house and a sunken concourse of formal gardens',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ocean-beach-belt',
    name: 'Ocean Beach · Cube-Belt',
    blurb: 'A dead-straight surf line the full height of the face, a highway and low dunes beside it, then block after block of identical two-storey houses',
    has: { cloud: true, roads: true },
    region: 'sanfrancisco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'innere-stadt-belt',
    name: 'Innere Stadt · Cube-Belt',
    blurb: 'A warren of narrow lanes and courtyard blocks under one enormous patterned roof and its single tall spire',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rathausplatz-belt',
    name: 'Rathausplatz · Cube-Belt',
    blurb: 'The boulevard curving past four monumental ranges, each set back in its own square or park',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'karlsplatz-belt',
    name: 'Karlsplatz · Cube-Belt',
    blurb: 'A green-domed church between two carved columns facing an open square across the water basins',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mariahilf-belt',
    name: 'Mariahilfer Straße · Cube-Belt',
    blurb: 'A dead-straight pedestrian shopping street ruled two kilometres through uniform six-storey blocks',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'belvedere-belt',
    name: 'Belvedere · Cube-Belt',
    blurb: 'Two long low ranges at either end of a formal garden stepping down the slope in terraces',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'schonbrunn-belt',
    name: 'Schönbrunn · Cube-Belt',
    blurb: 'A vast parterre climbing from a long palace range to a colonnaded folly on the crest, the animal park in radial wedges beside it',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'prater-belt',
    name: 'Prater · Cube-Belt',
    blurb: 'A giant wheel at the head of a dead-straight avenue driven kilometres through flat woodland',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'augarten-belt',
    name: 'Augarten · Cube-Belt',
    blurb: 'A walled baroque garden with two colossal windowless concrete towers standing in it',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'donaukanal-belt',
    name: 'Donaukanal · Cube-Belt',
    blurb: 'The walled channel at its sharpest bend, low bridges and a battlemented brick barracks along the bank',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'donauinsel-belt',
    name: 'Donauinsel · Cube-Belt',
    blurb: 'The river at its widest, split the long way by a thin man-made island and strode across by one great bridge',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'donaucity-belt',
    name: 'Donau City · Cube-Belt',
    blurb: 'The one cluster of glass towers, standing on flat ground above the north bank',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'alte-donau-belt',
    name: 'Alte Donau · Cube-Belt',
    blurb: 'A horseshoe oxbow lake ringed with boat sheds, bathing jetties and allotment plots',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'heiligenstadt-belt',
    name: 'Heiligenstadt · Cube-Belt',
    blurb: 'A single unbroken kilometre-long housing block pierced by giant arched gateways, the rail cutting alongside',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'simmering-belt',
    name: 'Simmering · Cube-Belt',
    blurb: 'Four huge brick drums in a row, hollowed out and lived in, beside the rail yards',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'grinzing-belt',
    name: 'Grinzing · Cube-Belt',
    blurb: 'Vine terraces running down off the wooded ridge into a village of low courtyard houses on winding lanes',
    has: { cloud: true, roads: true },
    region: 'vienna',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'eixample-belt',
    name: 'Eixample · Cube-Belt',
    blurb: 'Octagon blocks with their corners cut away, one filled edge to edge by a spired church',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gotic-belt',
    name: 'Barri Gòtic · Cube-Belt',
    blurb: 'Medieval tangle where no street runs straight, tiny courtyards and a cathedral square',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'raval-belt',
    name: 'El Raval · Cube-Belt',
    blurb: 'Dense old quarter with one broad palm-lined cut driven straight through it',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gracia-belt',
    name: 'Gràcia · Cube-Belt',
    blurb: 'Village grain rotated off the big grid, a chain of small paved squares',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'park-guell-belt',
    name: 'El Carmel · Cube-Belt',
    blurb: 'Terraced hillside park of serpentine walls, houses climbing the slope in curved rows',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'barceloneta-belt',
    name: 'La Barceloneta · Cube-Belt',
    blurb: 'A triangle of long narrow blocks, sand down one side and boat basins the other',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ciutadella-belt',
    name: 'Ciutadella · Cube-Belt',
    blurb: 'Formal park with a lake, glasshouses and animal enclosures, a brick arch on its axis',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vila-olimpica-belt',
    name: 'Vila Olímpica · Cube-Belt',
    blurb: 'Two slab towers alone above a marina, low even blocks behind, groyned sand running east',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'poblenou-belt',
    name: 'El Poblenou · Cube-Belt',
    blurb: 'Saw-tooth factory roofs and brick chimneys wedged between new glass boxes',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'diagonal-mar-belt',
    name: 'Diagonal Mar · Cube-Belt',
    blurb: 'A huge tilted plate raised on columns at the water, a bathing basin cut into the shore',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'montjuic-belt',
    name: 'Montjuïc · Cube-Belt',
    blurb: 'Terraced gardens on the hill, a stair-and-fountain axis to a domed hall, a star-fort above',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sants-belt',
    name: 'Sants · Cube-Belt',
    blurb: 'A long rail trench roofed over as a linear garden, an old high street cutting across',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'les-corts-belt',
    name: 'Les Corts · Cube-Belt',
    blurb: 'A great oval bowl with a smaller arena beside it, ringed by parking aprons',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'collserola-belt',
    name: 'Collserola · Cube-Belt',
    blurb: 'Forested crest with a lattice mast on a concrete shaft, the road hairpinning up to a terrace',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sant-andreu-belt',
    name: 'Sant Andreu · Cube-Belt',
    blurb: 'Low village core around its church square, a vast rail cutting scored past it',
    has: { cloud: true, roads: true },
    region: 'barcelona',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'indre-by-belt',
    name: 'Indre By · Cube-Belt',
    blurb: 'A knot of crooked lanes and courtyard blocks with one dead-straight pedestrian line ruled clean through it, and a squat round tower with a spiral ramp inside',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nyhavn-belt',
    name: 'Nyhavn · Cube-Belt',
    blurb: 'A short straight cut of water lined both sides with narrow gabled fronts and moored masts, opening onto an octagonal square of four matching ranges under a great green dome',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'christianshavn-belt',
    name: 'Christianshavn · Cube-Belt',
    blurb: 'A canal ruled down the middle of a made island, a stair spiralling up the outside of one spire, and grass-topped star points along the far bank',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'holmen-belt',
    name: 'Holmen · Cube-Belt',
    blurb: 'Parallel dockyard sheds and dry-dock slots on made islands, a black mast crane standing at the quay, and beyond them a vast plant with a run down its sloping roof',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nordhavn-belt',
    name: 'Nordhavn · Cube-Belt',
    blurb: 'Rectangular harbour basins set like the teeth of a comb, container stacks in colour rows under gantries, one tall concrete cylinder standing alone on the fill',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'faelledparken-belt',
    name: 'Fælledparken · Cube-Belt',
    blurb: 'A great flat sports common with an oval stadium bowl at one edge, ringed by unbroken five-storey blocks around their green courtyards',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bispebjerg-belt',
    name: 'Bispebjerg · Cube-Belt',
    blurb: 'A colossal brick west front stepped up like a rank of organ pipes, facing a square of identical yellow-brick houses, a terraced burial ground climbing beside it',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'norrebro-belt',
    name: 'Nørrebro · Cube-Belt',
    blurb: 'A walled avenue of old trees cut straight through dense tenement blocks, and a black paved wedge striped with white curving lines beside it',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'soerne-belt',
    name: 'Søerne · Cube-Belt',
    blurb: 'Three long straight-sided lakes chained end to end with a causeway across each, a formal garden and a domed glasshouse on the town side',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'radhuspladsen-belt',
    name: 'Rådhuspladsen · Cube-Belt',
    blurb: 'A wide open square under a stepped tower, a walled pleasure garden of timber-frame rides packed against it, and a long train shed with its fan of platforms behind',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vesterbro-belt',
    name: 'Vesterbro · Cube-Belt',
    blurb: 'Rows of low white flat-roofed market halls behind a spreading rail fan, with narrow tenement streets stacked tight to the north of them',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'humleby-belt',
    name: 'Humleby · Cube-Belt',
    blurb: 'Brick brewery halls and tall chimneys with a gate carried on four carved elephants, cylindrical silos and new towers threaded between, a grid of small yellow terraces below',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'frederiksberg-belt',
    name: 'Frederiksberg Have · Cube-Belt',
    blurb: 'A landscape garden threaded by narrow canals under a long low palace on the only rise, an animal park beside it with a slender lattice tower',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'orestad-belt',
    name: 'Ørestad · Cube-Belt',
    blurb: 'An elevated railway on columns running dead straight down a canal boulevard, a few enormous isolated blocks, and flat open common on both sides',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'amager-strand-belt',
    name: 'Amager Strand · Cube-Belt',
    blurb: 'A made sand island lying offshore with a long lagoon behind it and bridges across, dunes down its length and even low blocks on the mainland shore',
    has: { cloud: true, roads: true },
    region: 'copenhagen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'downtown-core-belt',
    name: 'Downtown Core · Cube-Belt',
    blurb: 'Half a dozen dark slab towers packed into four blocks, one stepped back at the crown, the streets narrow canyons between them',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'railway-lands-belt',
    name: 'Railway Lands · Cube-Belt',
    blurb: 'A hair-thin concrete shaft with a doughnut pod near its top beside a round stadium whose roof slides open, a fan of rail under both',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'harbourfront-belt',
    name: 'Harbourfront · Cube-Belt',
    blurb: 'Finger slips cut into the made ground, a low ferry terminal at the head of one, glass slabs standing right on the water edge',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'toronto-islands-belt',
    name: 'Toronto Islands · Cube-Belt',
    blurb: 'A low sandbar chain threaded with lagoons and no roads at all, one runway strip along the west end and ferry docks facing the towers',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'st-lawrence-belt',
    name: 'St Lawrence · Cube-Belt',
    blurb: 'A long twin-gabled market hall with a clerestory ridge, a wedge-shaped block filling a triangular corner, low brick warehouse rows around them',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'distillery-belt',
    name: 'Distillery District · Cube-Belt',
    blurb: 'A solid block of Victorian red brick with round chimneys and overhead walkways, cobbled lanes running through it and no cars at all',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kensington-belt',
    name: 'Kensington Market · Cube-Belt',
    blurb: 'A knot of one-way lanes lined with two-storey shopfronts under awnings, painted fronts and stalls spilling onto the pavement',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'queens-park-belt',
    name: 'Queens Park · Cube-Belt',
    blurb: 'A domed sandstone range standing alone in an oval park with a divided crescent looping round it, stone quadrangles and a round library west',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'annex-belt',
    name: 'The Annex · Cube-Belt',
    blurb: 'Unbroken rows of three-storey brick semis with round corner turrets and deep porches, wide streets under a full canopy of trees',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'davenport-belt',
    name: 'Davenport Hill · Cube-Belt',
    blurb: 'A long east-west bank with a public stair of a hundred steps climbing it and a castellated stone house with a tower on the crest',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'don-valley-belt',
    name: 'Don Valley · Cube-Belt',
    blurb: 'A deep wooded ravine with a river meandering on its floor, a parkway and a rail line threaded beside it, a steel arch viaduct striding across high above',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'high-park-belt',
    name: 'High Park · Cube-Belt',
    blurb: 'Deep oak woodland around a long narrow pond, savannah clearings, a ring road through it and a streetcar loop at the gate',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'humber-bay-belt',
    name: 'Humber Bay · Cube-Belt',
    blurb: 'A river mouth crossed by a white curving arch footbridge, marina basins behind a breakwater and a tight cluster of point towers on made ground',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beaches-belt',
    name: 'The Beaches · Cube-Belt',
    blurb: 'A long sand crescent with a timber boardwalk running its whole length, groynes stepping into the water, low houses on a narrow grid behind',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bluffs-belt',
    name: 'Scarborough Bluffs · Cube-Belt',
    blurb: 'A raw pale cliff sixty metres high dropping straight into the water, deep gullies cut into its face, a green man-made spit and boat basins at its foot',
    has: { cloud: true, roads: true },
    region: 'toronto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hoddle-grid-belt',
    name: 'Hoddle Grid · Cube-Belt',
    blurb: 'A dead-regular rectangle of wide streets with narrow half-width lanes ruled between every pair, tall blocks filling each square and rails down the middle of each street',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'southbank-belt',
    name: 'Southbank · Cube-Belt',
    blurb: 'A stepped promenade along the south bank with the tallest slender towers standing right at the water and one entertainment slab a quarter-kilometre long behind them',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'docklands-belt',
    name: 'Docklands · Cube-Belt',
    blurb: 'A square harbour basin cut into the land with finger berths of moored boats, a roofed stadium drum beside it and new glass slabs stood in rows on flat wharf ground',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'carlton-belt',
    name: 'Carlton · Cube-Belt',
    blurb: 'Radiating gravel paths and two fountain basins around a domed hall on a cross plan, university quadrangles west of it and a straight awninged eating strip',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fitzroy-belt',
    name: 'Fitzroy · Cube-Belt',
    blurb: 'Unbroken rows of narrow two-storey terraces with cast-iron verandahs over the footpath, a service lane behind every row and one long shopfront strip cutting north',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'abbotsford-belt',
    name: 'Abbotsford · Cube-Belt',
    blurb: 'The river doubling back on itself in a tight hairpin around a walled complex of long brick ranges and its own paddocks, rowing sheds on the bank and a block of tanks and stacks',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yarra-park-belt',
    name: 'Yarra Park · Cube-Belt',
    blurb: 'A colossal grass oval inside one continuous ring of stands under six light towers, three rectangular roofed arenas in a row beside it and open parkland all around',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cremorne-belt',
    name: 'Cremorne · Cube-Belt',
    blurb: 'A tight grid of saw-tooth factory roofs and brick chimneys turned over to offices, with a rail viaduct on brick arches running through it on the diagonal',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'botanic-gardens-belt',
    name: 'Botanic Gardens · Cube-Belt',
    blurb: 'A lake with a long sinuous shoreline and wooded islands under dense canopy, gravel paths curving between, and a stepped stone monument on a mound at the head of a straight avenue',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'albert-park-belt',
    name: 'Albert Park Lake · Cube-Belt',
    blurb: 'A wide shallow lake with a closed road circuit ruled in one continuous loop around it, grandstand rows along the main straight and playing ovals filling the rest',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'st-kilda-belt',
    name: 'St Kilda · Cube-Belt',
    blurb: 'A curved bay beach with a long straight pier reaching out to a hooked breakwater, a foreshore road behind it and a walled amusement ground with a timber coaster inside',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'port-melbourne-belt',
    name: 'Port Melbourne · Cube-Belt',
    blurb: 'Two enormous parallel finger piers standing out into the bay for ocean ships, a dead-straight light-rail causeway on an old rail formation and low new rows on cleared wharf land',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'williamstown-belt',
    name: 'Williamstown · Cube-Belt',
    blurb: 'A low peninsula tip with a stone light tower at the point, a walled dockyard of slipways and dry docks, a yacht basin packed with masts and single-storey cottages on a small grid',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'footscray-belt',
    name: 'Footscray · Cube-Belt',
    blurb: 'A wide market street of open-fronted stalls under continuous awnings, a broad brown river swinging past in a horseshoe and a triangle of rail sidings fanning out beside it',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'brunswick-belt',
    name: 'Brunswick · Cube-Belt',
    blurb: 'One dead-straight shopping street ruled three kilometres north with rails down the middle, and behind it brick factory shells, tall round kiln chimneys and long low sheds around their yards',
    has: { cloud: true, roads: true },
    region: 'melbourne',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'castlehill-belt',
    name: 'Castlehill · Cube-Belt',
    blurb: 'A sheer volcanic crag walled all round its flat top, a wide parade ground at the head and one long ridge street running down the tail',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'calton-hill-belt',
    name: 'Calton Hill · Cube-Belt',
    blurb: 'A steep little hill carrying an unfinished row of giant columns, a round tower and a domed observatory inside a walled enclosure',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'holyrood-park-belt',
    name: 'Holyrood Park · Cube-Belt',
    blurb: 'A bare grass summit with a mile of curved cliff along its flank, a loch under it and one road ringing the whole hill',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'new-town-belt',
    name: 'New Town · Cube-Belt',
    blurb: 'Three dead-straight parallel streets a kilometre long, a square closing each end and oval private gardens ruled between them',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dean-village-belt',
    name: 'Dean Village · Cube-Belt',
    blurb: 'A wooded gorge with a river and its mills in the bottom, crossed high above the roofs by a tall four-arch stone bridge',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'inverleith-belt',
    name: 'Inverleith · Cube-Belt',
    blurb: 'A botanic garden of stepped glasshouses and a terraced rock bank, beside a huge flat playing field with a pond in one corner',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'leith-shore-belt',
    name: 'Leith Shore · Cube-Belt',
    blurb: 'The last bend of a river walled in stone quays, tall gabled warehouses standing right on the water and a swing bridge across it',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'granton-belt',
    name: 'Granton · Cube-Belt',
    blurb: 'A great circular steel lattice drum standing alone on flat ground, a harbour of three long stone arms reaching out beside it',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'portobello-belt',
    name: 'Portobello · Cube-Belt',
    blurb: 'Two kilometres of dead-straight sand behind a promenade wall, low terraces packed in a tight grid right up to the back of it',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'craigmillar-belt',
    name: 'Craigmillar · Cube-Belt',
    blurb: 'A ruined keep inside a double curtain wall with round corner turrets, standing alone on a green rise above new housing grids',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'blackford-hill-belt',
    name: 'Blackford Hill · Cube-Belt',
    blurb: 'A bare rounded whinstone hill with two silver domes and a square tower on the crown, a small round pond at the foot',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'meadows-belt',
    name: 'The Meadows · Cube-Belt',
    blurb: 'A flat green rectangle ruled across by two straight tree avenues, unbroken four-storey tenement rows packed around every side',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'murrayfield-belt',
    name: 'Murrayfield · Cube-Belt',
    blurb: 'A great oval bowl of a stadium with a smaller ground beside it, a river bend and a railway curving past the end',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'corstorphine-hill-belt',
    name: 'Corstorphine Hill · Cube-Belt',
    blurb: 'A long wooded ridge with a square battlemented tower on the crest, terraced animal enclosures stepping up the south slope',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cramond-belt',
    name: 'Cramond · Cube-Belt',
    blurb: 'A river mouth with a small stone harbour and a straight causeway on pylons striding out to an island, white terraces stepping up from the water',
    has: { cloud: true, roads: true },
    region: 'edinburgh',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gwanghwamun-belt',
    name: 'Gwanghwamun · Cube-Belt',
    blurb: 'A walled rectangle of courts stepping back against the northern hill, a ceremonial avenue a hundred metres wide driven south from its gate',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bukchon-belt',
    name: 'Bukchon · Cube-Belt',
    blurb: 'A hillside carpet of tiny courtyard houses under deeply curved tiled roofs, threaded by stepped lanes barely wider than a cart',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'namsan-belt',
    name: 'Namsan · Cube-Belt',
    blurb: 'One wooded cone standing alone inside the dense grid, an old rampart over its crest and a cable line up one flank to the mast on the summit',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dongdaemun-belt',
    name: 'Dongdaemun · Cube-Belt',
    blurb: 'A vast seamless metal-skinned hall with not one straight edge, an excavated rampart trace and a sunken watercourse beside it',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'namdaemun-belt',
    name: 'Namdaemun · Cube-Belt',
    blurb: 'A two-tier tiled gate pavilion marooned on an island of traffic, covered market alleys behind it, a rail throat fanning out under a curving planted deck',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yeouido-belt',
    name: 'Yeouido · Cube-Belt',
    blurb: 'A long island in a kilometre-wide river, a tight knot of tall towers at one end and a low domed hall at the other, open ground between',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yongsan-belt',
    name: 'Yongsan · Cube-Belt',
    blurb: 'An enormous walled rectangle emptied of city, barrack rows and parade grounds inside, dense blocks pressed hard against every side',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gangnam-belt',
    name: 'Gangnam · Cube-Belt',
    blurb: 'Superblocks five hundred metres across ruled by two colossal perpendicular arterials, each interior packed with a capillary lattice of small buildings',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jamsil-belt',
    name: 'Jamsil · Cube-Belt',
    blurb: 'One tapering supertall standing far above a low plain beside a pair of round lakes, a walled amusement enclosure wrapped round the nearer one',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mokdong-belt',
    name: 'Mok-dong · Cube-Belt',
    blurb: 'Numbered ranks of identical slab towers laid in strict parallel rows, a linear park spine running the whole length of them',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'seonyudo-belt',
    name: 'Seonyudo · Cube-Belt',
    blurb: 'A small river island of roofless concrete basins turned into walled garden rooms, reached by one long arched footbridge',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sangam-belt',
    name: 'Sangam · Cube-Belt',
    blurb: 'Two enormous grassed cones of capped ground rising off dead-flat river meadows, reed beds and a wetland pond between them',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bukhansan-belt',
    name: 'Bukhansan · Cube-Belt',
    blurb: 'Three bare granite domes shouldered together above forested ravines, a stone rampart running the saddle between them to a gate on the col',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'naksan-belt',
    name: 'Naksan · Cube-Belt',
    blurb: 'A stone rampart climbing a bare rock spine, tiny houses stacked in stepped terraces hard against both of its faces',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'seongsu-belt',
    name: 'Seongsu · Cube-Belt',
    blurb: 'A wooded park filling a river bend where a tributary joins, a curving footbridge vaulting the levee expressway, brick workshop blocks and slim towers behind',
    has: { cloud: true, roads: true },
    region: 'seoul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bunker-hill-belt',
    name: 'Bunker Hill · Cube-Belt',
    blurb: 'A dozen flat-topped slabs and one crown-lit tapering shaft packed onto a low rise, a crumpled steel-shelled hall with curved sail roofs at its corner, stepped plazas and a short funicular track dropping to the streets below',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'arts-district-belt',
    name: 'Arts District · Cube-Belt',
    blurb: 'The river reduced to a ruled concrete trough with a thin wet ribbon down its centre, arched and truss viaducts striding across it, brick warehouse blocks and loading docks packed hard against both banks',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'el-pueblo-belt',
    name: 'El Pueblo · Cube-Belt',
    blurb: 'A long low arcaded terminus with a square clock tower and a fan of open platforms behind it, a walled courtyard of tiled-roof ranges opposite, rail throats converging on it from three directions',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'elysian-park-belt',
    name: 'Elysian Park · Cube-Belt',
    blurb: 'A terraced ballpark bowl set down in a hollow of bare hills, concentric car parks stepping away from it on every slope, wooded ridges and a fenced hilltop reservoir above',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'koreatown-belt',
    name: 'Koreatown · Cube-Belt',
    blurb: 'Unbroken commercial strips under stacked signage boards two and three storeys tall, deco corner towers at the big intersections and dense courtyard apartment blocks filling everything behind',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'melrose-belt',
    name: 'Melrose · Cube-Belt',
    blurb: 'A walled lot of blank windowless stage boxes with a false street of plaster fronts ruled between them, an ornamental arched gate, a painted sky wall and a water tank up on legs',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hollywood-boulevard-belt',
    name: 'Hollywood Boulevard · Cube-Belt',
    blurb: 'A boulevard whose pavement is inlaid end to end with brass stars, a walled forecourt theatre under an upturned tiled pagoda roof, and a terraced plaza of stacked arches and elephant columns cut into the block behind',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cahuenga-pass-belt',
    name: 'Cahuenga Pass · Cube-Belt',
    blurb: 'A bandshell of concentric arcs standing in a natural ravine with benched terraces climbing the slope above it, and a freeway threading the pass right past its mouth',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'griffith-park-belt',
    name: 'Griffith Park · Cube-Belt',
    blurb: 'Three copper domes on a white terraced belvedere at the head of a switchback road, a long lawn falling away below it and bare chaparral ridges standing behind',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mount-lee-belt',
    name: 'Mount Lee · Cube-Belt',
    blurb: 'A bare chaparral ridge carrying nine enormous white letters propped along its south face, a fenced transmitter mast on the summit and fire roads switchbacking up the folds',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'glendale-narrows-belt',
    name: 'Glendale Narrows · Cube-Belt',
    blurb: 'The one unpaved reach of the channel, willows and gravel bars grown up on its floor between sloped concrete banks, with stables and bridle trails along the top of the levee',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'century-city-belt',
    name: 'Century City · Cube-Belt',
    blurb: 'A tight knot of tall slabs and two identical triangular towers standing alone above an ocean of two-storey sprawl, on superblocks with no through streets at all',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'miracle-mile-belt',
    name: 'Miracle Mile · Cube-Belt',
    blurb: 'A fenced pond of black seeping asphalt with life-size mammoth figures standing in it, railed-off excavation pits across the lawns and a long ribbed gallery range raised on stilts beside a boulevard of deco fronts',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'boyle-heights-belt',
    name: 'Boyle Heights · Cube-Belt',
    blurb: 'Four freeways meeting in a stack of curving ramps four levels deep, a rail yard threading underneath and small clapboard houses on the bluff above, cut off on every side',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'watts-belt',
    name: 'Watts · Cube-Belt',
    blurb: 'Seventeen slender spires of steel rod encrusted with tile and bottle glass rising out of a tiny triangular yard, a rail line beside them and a single-storey bungalow grid running flat to every horizon',
    has: { cloud: true, roads: true },
    region: 'losangeles',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'downtown-belt',
    name: 'Downtown · Cube-Belt',
    blurb: 'One tapering supertall standing far above everything around it, a shaped lake with fountain jets at its foot and a mall slab covering several blocks beside it, ringed by tall towers on a wide new grid',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'business-bay-belt',
    name: 'Business Bay · Cube-Belt',
    blurb: 'A canyon of towers packed hard against both banks of a straight sea cut, low arched bridges across it and a basin of moored boats at one bend',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'trade-centre-belt',
    name: 'Trade Centre · Cube-Belt',
    blurb: 'A wall of tall towers ranked along one twelve-lane arterial with a rail viaduct on columns down the middle of it, a pair of dark triangular-topped shafts among them and a great roundabout closing the end',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'al-safa-belt',
    name: 'Al Safa · Cube-Belt',
    blurb: 'A sea-water canal cut dead straight through a settled villa quarter and its park, faced in stone the whole way, with high arched footbridges over it and low blocks pressed to both banks',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jumeira-belt',
    name: 'Jumeira · Cube-Belt',
    blurb: 'A long straight beach behind a promenade wall with low white villas on a loose grid inland, and a small curled island of made ground lying offshore on one thin causeway',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'al-mina-belt',
    name: 'Al Mina · Cube-Belt',
    blurb: 'Rectangular port basins walled in straight stone quays, a retired ocean liner moored permanently against one of them, long low cruise sheds along another and a made peninsula of dry docks and slipways beyond',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'al-shindagha-belt',
    name: 'Al Shindagha · Cube-Belt',
    blurb: 'The point where the tidal creek meets the open sea, a corniche curving round it and a walled compound of low courtyard houses under square wind towers, with tunnel portals dropping under the water',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'al-fahidi-belt',
    name: 'Al Fahidi · Cube-Belt',
    blurb: 'A warren of coral-stone lanes barely wider than a cart under square wind towers, covered market arcades behind it, and wooden trading boats moored three deep along the far quay',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'al-jaddaf-belt',
    name: 'Al Jaddaf · Cube-Belt',
    blurb: 'Open boatyards along the creek bank where wooden hulls stand upright on timber props, slipways and dry docks between them, and new glass towers rising straight off the same bank',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ras-al-khor-belt',
    name: 'Ras Al Khor · Cube-Belt',
    blurb: 'Shallow salt lagoons and mudflats at the head of the creek with wading birds standing in them, low viewing hides along the bank and a hard industrial fringe pressed right up to the water',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nad-al-sheba-belt',
    name: 'Nad Al Sheba · Cube-Belt',
    blurb: 'A grandstand roof a kilometre long curving beside a wide turf oval with a dirt track inside it, a floodlit sand training circle and long rows of stable blocks behind',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'umm-suqeim-belt',
    name: 'Umm Suqeim · Cube-Belt',
    blurb: 'A single sail-shaped tower standing alone on its own small square island at the end of a curved causeway, a long wave-roofed range on the beach beside it and low villas on a quiet grid behind',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'palm-jumeirah-belt',
    name: 'Palm Jumeirah · Cube-Belt',
    blurb: 'A made island shaped like a tree: one straight trunk causeway with slender fronds branching off both sides, a single row of houses down each frond and water between every pair, wrapped in a broken crescent breakwater',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'marsa-dubai-belt',
    name: 'Marsa Dubai · Cube-Belt',
    blurb: 'A dredged canal curving in a long S through a forest of very slender towers packed right to the water on both sides, one of them twisting as it rises, with berths of moored boats the whole length',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bluewaters-belt',
    name: 'Bluewaters · Cube-Belt',
    blurb: 'A small oval island of made ground carrying one enormous observation wheel, a road bridge out to it, and on the shore opposite a wall of parallel slab blocks along a beach strip',
    has: { cloud: true, roads: true },
    region: 'dubai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rattanakosin-belt',
    name: 'Rattanakosin · Cube-Belt',
    blurb: 'A walled compound of stepped tiled roofs and slender gilded spires beside a huge bare grassed oval, with a moat canal ruling the whole quarter off from the mainland',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bangkok-noi-belt',
    name: 'Bangkok Noi · Cube-Belt',
    blurb: 'A wide canal mouth lined with timber houses standing on stilts out over the water, walled temple compounds behind them and a railway ending in one long shed on the bank',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kudi-chin-belt',
    name: 'Kudi Chin · Cube-Belt',
    blurb: 'A tall stepped tower crusted all over in porcelain standing right at the water, long low ranges of curved tile beside it and tight lanes of two-storey shophouses packed behind',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wongwian-yai-belt',
    name: 'Wongwian Yai · Cube-Belt',
    blurb: 'A circular traffic island with six streets radiating off it, a branch railway whose track runs down the middle of the roadway, and a wide canal curving past behind the shophouse rows',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yaowarat-belt',
    name: 'Yaowarat · Cube-Belt',
    blurb: 'One long sinuous street of unbroken shophouse rows with signboards hung right across the roadway, and a lattice of covered alleys barely a metre wide threaded behind them',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dusit-belt',
    name: 'Dusit · Cube-Belt',
    blurb: 'A dead-straight tree-lined avenue driven from the river to a domed marble hall, rectangular garden plots ruled either side of it and a walled garden of ponds and timber pavilions',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bang-rak-belt',
    name: 'Bang Rak · Cube-Belt',
    blurb: 'Timber pier decks stepping out into the river, tall slabs standing right on the bank behind them and one narrow lane of old shophouses running the whole length of the face',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'siam-belt',
    name: 'Siam · Cube-Belt',
    blurb: 'Two elevated railways crossing at right angles above a junction, a raised walkway spine strung between vast deep retail blocks, and a straight boat canal along the north',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lumphini-belt',
    name: 'Lumphini · Cube-Belt',
    blurb: 'A rectangular park of lakes and looping paths dropped into the middle of the grid, jetties along the water and unbroken tower walls pressed against all four sides',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chatuchak-belt',
    name: 'Chatuchak · Cube-Belt',
    blurb: 'A dense grid of tiny numbered stall blocks under one continuous roof, a park with a long lake beside it and a rail interchange fanning out to the north',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'asok-belt',
    name: 'Asok · Cube-Belt',
    blurb: 'An elevated railway ruled dead straight down a wide avenue and crossed at right angles by a second line, with short dead-end lanes combed off both sides into dense low blocks',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'khlong-toei-belt',
    name: 'Khlong Toei · Cube-Belt',
    blurb: 'Rectangular port basins with container stacks in colour rows under gantries, a vast market of continuous roofs beside them and tight lanes of low tin-roofed housing behind',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bang-krachao-belt',
    name: 'Bang Krachao · Cube-Belt',
    blurb: 'A river bend wrapping a peninsula of dense orchard plots threaded by raised concrete walkways barely wide enough for two, with almost no road on it at all',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'huai-khwang-belt',
    name: 'Huai Khwang · Cube-Belt',
    blurb: 'A wide straight avenue with superblock towers set back off it, a night market of parallel stall rows filling one side and a straight canal running the whole length behind',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'phra-khanong-belt',
    name: 'Phra Khanong · Cube-Belt',
    blurb: 'A canal meeting the river in a sharp bend, a market spilling down both banks to the water and an elevated railway ending in a depot of parallel sidings',
    has: { cloud: true, roads: true },
    region: 'bangkok',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sea-point-belt',
    name: 'Sea Point · Cube-Belt',
    blurb: 'An unbroken wall of white apartment slabs four rows deep standing behind a seawall promenade, a rectangular tidal pool cut into the black rocks below it and one long straight shopping street of awnings running the whole length behind',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'clifton-belt',
    name: 'Clifton · Cube-Belt',
    blurb: 'Four small crescent beaches divided by piles of pale granite boulders, flat-roofed houses stacked in tiers up the cliff above them reached by stepped footpaths rather than streets, and one contour road threaded along the top',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'camps-bay-belt',
    name: 'Camps Bay · Cube-Belt',
    blurb: 'A wide white beach with a single line of palms down its road, white villas terraced in stepped rows up the slope behind, and a wall of grey rock buttresses standing over the whole quarter',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'green-point-belt',
    name: 'Green Point · Cube-Belt',
    blurb: 'An oval stadium bowl wrapped in white lattice standing in mown parkland, a golf course and running track beside it, a red and white banded tower out on the point and low flat-roofed blocks on a tight grid behind',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'table-bay-harbour-belt',
    name: 'Table Bay Harbour · Cube-Belt',
    blurb: 'Rectangular dredged basins walled in straight stone quays, container stacks in colour rows under gantry cranes, a long breakwater arm closing the whole thing off, and older cut-stone basins with a swing bridge at the inner end',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'paarden-eiland-belt',
    name: 'Paarden Eiland · Cube-Belt',
    blurb: 'Flat grey warehouse roofs the size of whole blocks laid in rows, rail sidings fanning out between them, a canalised river running down to a shallow lagoon and a low bridge onto a sandy island at its mouth',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bo-kaap-belt',
    name: 'Bo-Kaap · Cube-Belt',
    blurb: 'Rows of tiny flat-roofed houses painted in solid blocks of colour stepping up a steep grid of cobbled streets, a slender minaret rising out of the terraces and bare grassy slope closing the top of the quarter',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cape-town-cbd-belt',
    name: 'Cape Town CBD · Cube-Belt',
    blurb: 'A tight grid of office slabs with one pedestrian street cut through it, a bare rectangular parade ground beside a five-pointed stone star fort, and an elevated motorway that stops dead in mid air over the reclaimed edge',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gardens-belt',
    name: 'Gardens · Cube-Belt',
    blurb: 'A dead straight oak avenue running from a domed museum to a colonnaded assembly hall, walled botanical plots ruled either side of it, and stepped apartment blocks climbing the slope behind on hairpin streets',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'woodstock-belt',
    name: 'Woodstock · Cube-Belt',
    blurb: 'Long terraces of narrow cottages under corrugated roofs with cast-iron verandas along their fronts, sawtooth factory sheds converted among them, and a railway running dead straight through the whole quarter',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'salt-river-belt',
    name: 'Salt River · Cube-Belt',
    blurb: 'A railway triangle where three lines meet and fan out into ranks of sidings, sawtooth workshop roofs packed inside the junction, and a big traffic circle with tight cottage rows radiating away from it',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'observatory-belt',
    name: 'Observatory · Cube-Belt',
    blurb: 'A grid of narrow semi-detached cottages with painted parapets, a small river winding through willow flats at the edge of them, a white domed instrument house on the rise beyond and a hospital of long parallel wings above',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rondebosch-belt',
    name: 'Rondebosch · Cube-Belt',
    blurb: 'Terraced stone halls stepping up a mountain foot behind a broad flight of steps and a columned portico, playing fields and oak avenues below them, and a flat open common crossed by one main road',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'newlands-belt',
    name: 'Newlands · Cube-Belt',
    blurb: 'Two stadium bowls side by side under a wooded mountain flank, one an oval of grass and one rectangular, brewery vats and long sheds beside them, and oak-lined streets running up into forest',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kirstenbosch-belt',
    name: 'Kirstenbosch · Cube-Belt',
    blurb: 'Terraced lawns and formal beds laid out across a mountain foot, a curving raised walkway looping out through the tree canopy, a long clipped hedge of old planting and a forested ravine climbing to bare cliffs above',
    has: { cloud: true, roads: true },
    region: 'capetown',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rocinha-belt',
    name: 'Rocinha · Cube-Belt',
    blurb: 'A whole hillside packed with small flat-roofed brick boxes stacked four and five deep up a ravine, one hairpin road switchbacking through them and an elevated motorway curving across the slope at their foot',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ipanema-belt',
    name: 'Ipanema · Cube-Belt',
    blurb: 'A low rock headland with a paved viewing deck splitting two long beaches, a regular grid of ten-storey blocks running back off the sand and a lagoon edge of sports pitches closing the far side',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'leblon-belt',
    name: 'Leblon · Cube-Belt',
    blurb: 'A tight grid of pale blocks pinned between a lagoon shore of running tracks and a straight beach, with a short walled canal cutting from lagoon to surf and a bare mountain flank closing the far end',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tijuca-belt',
    name: 'Tijuca · Cube-Belt',
    blurb: 'A flat dense grid of mid-rise blocks whose tree-lined streets run dead into a wall of forested mountain, a broad square with a covered market at the centre and twin tunnel mouths driven straight into the rock',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gamboa-belt',
    name: 'Gamboa · Cube-Belt',
    blurb: 'Long parallel warehouse sheds ruled along a line of finger piers, a steep hill of small stacked houses rising directly behind them and rail sidings threading between the sheds to a wide new boulevard',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'copacabana-belt',
    name: 'Copacabana · Cube-Belt',
    blurb: 'A long crescent of sand edged by a promenade paved in black-and-white waves, an unbroken wall of tall thin blocks behind it and a grid squeezed only three streets deep between the beach and bare rock',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sao-cristovao-belt',
    name: 'São Cristóvão · Cube-Belt',
    blurb: 'A great landscaped park of lakes and looping carriage drives with a low rectangular palace on the rise above it, a huge shallow ellipse of a roofed hall nearby and rail sidings fanning along one edge',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'maracana-belt',
    name: 'Maracanã · Cube-Belt',
    blurb: 'A vast elliptical bowl with a ring of white roof over an empty green oval, a smaller round arena beside it and a straightened concrete river channel running past beneath a raised rail line',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'centro-belt',
    name: 'Centro · Cube-Belt',
    blurb: 'A knot of narrow colonial lanes wedged between two dead-straight avenues of glass slabs, with a wide paved square opening onto the water and a hollow ribbed cone standing clear of everything around it',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jardim-botanico-belt',
    name: 'Jardim Botânico · Cube-Belt',
    blurb: 'A walled garden with a dead-straight avenue of single-trunk palms ruled through it, glasshouse rectangles and a lily pond off to one side and a broad grass racing oval just beyond the wall',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'urca-belt',
    name: 'Urca · Cube-Belt',
    blurb: 'Two bare granite domes rising straight out of the water with a cable strung between them, a low grid of garden villas on the spit at their foot and a small harbour of moored boats behind a sea wall',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'botafogo-belt',
    name: 'Botafogo · Cube-Belt',
    blurb: 'A deep cove ringed by towers with a curving shopping street behind it, a marina of masts filling the water and a steep hill of stacked painted houses served by a cable-hauled track up its spine',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'flamengo-belt',
    name: 'Flamengo · Cube-Belt',
    blurb: 'A wide reclaimed strip of curving paths and open lawns laid between a six-lane pair and a crescent of sand, with a marina of pontoons at one end and an unbroken wall of pale apartment slabs behind',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'santa-teresa-belt',
    name: 'Santa Teresa · Cube-Belt',
    blurb: 'Hairpin lanes climbing a ridge with tram rails set into the roadway, villas under curved tile roofs in wooded gardens and a row of tall white arches carrying a single track across the valley below',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cosme-velho-belt',
    name: 'Cosme Velho · Cube-Belt',
    blurb: 'A single-track rack railway climbing out of a small shed station straight into dense forest, villas stepped along a stream valley beneath it and a tight knot of tiled roofs around a tiny cobbled square',
    has: { cloud: true, roads: true },
    region: 'rio',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'eyup-belt',
    name: 'Eyüp · Cube-Belt',
    blurb: 'The head of a narrowing inlet where it thins away to a stream, a domed courtyard mosque with two slim minarets standing right on the bank, and a terraced hillside of close-packed upright stones under dark cypresses climbing behind it',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fenerbahce-kalamis-belt',
    name: 'Fenerbahçe & Kalamış · Cube-Belt',
    blurb: 'A blunt wooded headland with a lawned tip and a promenade ringing it, a bay behind packed with moored masts in ranks, and even rows of six-storey blocks running back off the shore',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fener-balat-belt',
    name: 'Fener & Balat · Cube-Belt',
    blurb: 'Steep narrow lanes of tall thin painted row houses stepping straight up from a quay wall, a huge red-brick block with a corner turret riding the ridge above them, and small walled churchyards let in between',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sisli-nisantasi-belt',
    name: 'Şişli & Nişantaşı · Cube-Belt',
    blurb: 'Unbroken courtyard blocks packed along tree-lined boulevards on a shallow ridge, a large walled burial ground of ranked stones and dark conifers cut into the middle of them, and a line of office slabs closing the view north',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ortakoy-bogazici-belt',
    name: 'Ortaköy · Cube-Belt',
    blurb: 'A small square domed mosque standing out on its own quay at the water, an enormous suspension bridge deck springing off the shore straight overhead, and a tight knot of tiny stall roofs packed behind',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kuzguncuk-beylerbeyi-belt',
    name: 'Kuzguncuk & Beylerbeyi · Cube-Belt',
    blurb: 'One narrow street of small coloured timber houses running up a green valley to open ground, a long white waterfront palace with a pavilion at each end of its quay, and the far foot of a great bridge',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kadikoy-belt',
    name: 'Kadıköy · Cube-Belt',
    blurb: 'Ferry piers combed out into the water with boat wakes fanning off them, and behind them a tight triangle of narrow pedestrian streets under continuous awnings, radiating off one small circular junction',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'suleymaniye-belt',
    name: 'Süleymaniye · Cube-Belt',
    blurb: 'A vast cascading dome with four thin minarets on the crown of a hill inside a walled court, tight lanes of leaning timber houses falling away from it, and a double-tier arched aqueduct striding across the valley',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kasimpasa-halic-belt',
    name: 'Kasımpaşa · Cube-Belt',
    blurb: 'Dry docks cut square into the bank with long slipway sheds over them inside a walled yard, steep streets of small flat-roofed blocks climbing straight behind, and one stadium bowl notched into the hillside',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'besiktas-dolmabahce-belt',
    name: 'Beşiktaş & Dolmabahçe · Cube-Belt',
    blurb: 'A very long low white palace ruled straight along the waterfront behind its own quay wall, a small ornate clock tower at its gate, and a dense grid of market awnings and ferry berths crowding the shore just north',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beyoglu-taksim-belt',
    name: 'Beyoğlu & Taksim · Cube-Belt',
    blurb: 'One huge bare paved square at the head of a long pedestrian street that curves away south with a single tram track laid down its middle, tall ornate blocks and glass-roofed arcades pressed either side',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'karakoy-galata-belt',
    name: 'Karaköy & Galata · Cube-Belt',
    blurb: 'A fat round stone tower with a conical cap standing alone on the hilltop with tight lanes radiating off it, and below, a double-deck bridge crossing the inlet mouth to ranks of ferry berths',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'haydarpasa-harem-belt',
    name: 'Haydarpaşa & Harem · Cube-Belt',
    blurb: 'A monumental stone station block standing right on the water at the end of a fan of platform tracks, a breakwater harbour of cranes beside it, and a huge hollow square barracks with a tower at each corner above',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sultanahmet-belt',
    name: 'Sultanahmet · Cube-Belt',
    blurb: 'A huge buttressed dome and a six-minaret mosque facing each other across a garden, a long narrow oval of an old racetrack with standing stones down its spine, and a walled palace of low pavilions out on the wooded point',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'uskudar-belt',
    name: 'Üsküdar · Cube-Belt',
    blurb: 'Two domed mosques standing right at the quay either side of a waterfront square with ferry piers combing off it, a small round tower alone on a rock offshore, and streets climbing steeply into a cypress hillside',
    has: { cloud: true, roads: true },
    region: 'istanbul',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'futian-cbd-belt',
    name: 'Futian CBD · Cube-Belt',
    blurb: 'A ring of glass towers standing around a rectangular central park with one of them far taller than the rest, on a grid so wide that the cross streets sit most of a kilometre apart',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'huaqiangbei-belt',
    name: 'Huaqiangbei · Cube-Belt',
    blurb: 'A pedestrianised spine with vast deep halls opening straight off it, residential slabs stacked tight behind them and narrow service lanes combed off both sides',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'luohu-belt',
    name: 'Luohu · Cube-Belt',
    blurb: 'The old eastern quarter pressed up against a border river, a terminus with its platform sheds and stepped towers standing over dense blocks of market streets',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'houhai-belt',
    name: 'Houhai · Cube-Belt',
    blurb: 'A tight knot of tall offices on dead-flat reclaimed ground with the bay curling in along one edge and a park strip following the waterline the whole way',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nanshan-belt',
    name: 'Nanshan · Cube-Belt',
    blurb: 'Low campus blocks in loose quadrangles between a wooded ridge and the shore, tower clusters rising where the grid runs down to meet the water',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shekou-belt',
    name: 'Shekou · Cube-Belt',
    blurb: 'A working headland of rectangular quays and container stacks under gantries, low ridges behind them and older walk-up blocks in tight rows between',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'huaqiaocheng-belt',
    name: 'Huaqiaocheng · Cube-Belt',
    blurb: 'Artificial lakes and looping wooded lanes dropped into the middle of the grid, with tower estates pressed hard against every side of the green',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'baoan-belt',
    name: 'Baoan · Cube-Belt',
    blurb: 'A civic quarter of wide plazas and set-back slabs on flat reclaimed ground, the coast running as one straight ruled edge along the western side',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xixiang-belt',
    name: 'Xixiang · Cube-Belt',
    blurb: 'Dense low workshops and market lanes packed into irregular blocks, thinning out into fish ponds and straight drainage channels towards the shore',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'longhua-belt',
    name: 'Longhua · Cube-Belt',
    blurb: 'Long parallel rows of new-town residential slabs ruled across a valley floor, with wooded hills closing off the top of the face',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bantian-belt',
    name: 'Bantian · Cube-Belt',
    blurb: 'Estate towers and factory campuses stacked up hillside terraces, the roads bending around the contours instead of running straight',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'buji-belt',
    name: 'Buji · Cube-Belt',
    blurb: 'A packed river-valley suburb of narrow blocks crowded along one winding road, with a ridge rising steeply on the eastern side',
    has: { cloud: true, roads: true },
    region: 'shenzhen',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'santa-croce-belt',
    name: 'Santa Croce · Cube-Belt',
    blurb: 'A wide waterway curving past a bus terminus and a rail causeway landing, with tight blocks behind cut by rii and one broad basin opening off the bend',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cannaregio-belt',
    name: 'Cannaregio · Cube-Belt',
    blurb: 'Long straight parallel canals with unbroken quays either side, dense blocks ruled between them, and a walled island quarter reached by two bridges',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rialto-belt',
    name: 'Rialto · Cube-Belt',
    blurb: 'The great reversed-S waterway at its narrowest, one stone arch carrying shops right across it, and market halls packed along the bank behind',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'san-marco-belt',
    name: 'San Marco · Cube-Belt',
    blurb: 'A vast paved rectangle opening straight onto the water, a free-standing brick campanile at its corner, a domed basilica and an arcaded palace along the basin front',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'castello-belt',
    name: 'Castello · Cube-Belt',
    blurb: 'A walled naval yard of rectangular fitting-out basins behind a gated water entrance, long rope-walk sheds beside it and a wooded public garden east',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dorsoduro-belt',
    name: 'Dorsoduro · Cube-Belt',
    blurb: 'A broad canal mouth with a domed octagonal church on the point, a long southern quay facing open water and gallery blocks along the north bank',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'giudecca-belt',
    name: 'Giudecca · Cube-Belt',
    blurb: 'A long thin island across a wide channel, mills and warehouse ranges along its north quay, gardens and boatyards behind',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sacca-fisola-belt',
    name: 'Sacca Fisola · Cube-Belt',
    blurb: 'A reclaimed rectangular islet of post-war blocks and sports grounds, joined by one bridge, with working boatyards on the channel',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'murano-belt',
    name: 'Murano · Cube-Belt',
    blurb: 'A smaller island town split by its own broad canal, furnace sheds with tall stacks along the quays and a low arcaded church east',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lido-belt',
    name: 'Lido · Cube-Belt',
    blurb: 'A straight barrier island with the open sea along one edge and the lagoon along the other, a grid of villas between and a long bathing frontage',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mestre-belt',
    name: 'Mestre · Cube-Belt',
    blurb: 'The mainland town on solid ground — an ordinary continental grid of blocks and boulevards, a rail yard fanning out and no canal in sight',
    has: { cloud: true, roads: true },
    region: 'venice',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'taipei-main-belt',
    name: 'Taipei Main · Cube-Belt',
    blurb: 'A vast hall over a buried interchange with bus decks stacked beside it, a ruled grid of wide avenues around, and a green rectangle of old walled ground east',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zhongshan-belt',
    name: 'Zhongshan · Cube-Belt',
    blurb: 'A long straight boulevard with a planted median running north, department blocks along it and a lattice of narrow arcaded lanes threaded behind',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'datong-belt',
    name: 'Datong · Cube-Belt',
    blurb: 'The oldest quarter by the river — two-storey shophouse rows with arcades cut through their fronts, a tight lane grid, and a wholesale market shed by the levee',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ximending-belt',
    name: 'Ximending · Cube-Belt',
    blurb: 'A pedestrianised knot of streets radiating off one crossing, low signboarded blocks packed tight, and a red octagonal market hall at the centre',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wanhua-belt',
    name: 'Wanhua · Cube-Belt',
    blurb: 'A riverside district behind a high levee, a walled temple compound with a curved tiled roof, herbalist lanes and a long park strip on the embankment',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'guting-belt',
    name: 'Guting · Cube-Belt',
    blurb: 'University quadrangles behind long colonnaded ranges, a broad tree-lined avenue down one side and dense low housing lanes packed either side',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'daan-belt',
    name: 'Daan · Cube-Belt',
    blurb: 'A big rectangular park of lakes and looping paths dropped into the grid, tower walls pressed against all four sides and a night-market lane north',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xinyi-belt',
    name: 'Xinyi · Cube-Belt',
    blurb: 'The planned quarter — one tapering supertall of stacked sections standing alone above wide plazas, a raised walkway spine and deep retail blocks around it',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'songshan-belt',
    name: 'Songshan · Cube-Belt',
    blurb: 'A city airfield with one runway ruled along the top edge, a long straight market street south of it and a canal-side park strip between',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nangang-belt',
    name: 'Nangang · Cube-Belt',
    blurb: 'An eastern rail and freight quarter, exhibition sheds with saw-tooth roofs, a software campus in blocks and the river bending past the north',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'neihu-belt',
    name: 'Neihu · Cube-Belt',
    blurb: 'A basin of low science-park blocks around a small lake, hills closing three sides and elevated roads climbing out over the river',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shilin-belt',
    name: 'Shilin · Cube-Belt',
    blurb: 'A night-market lattice of tiny stall lanes under one roofline, a walled garden compound of pavilions north and the river along the south edge',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beitou-belt',
    name: 'Beitou · Cube-Belt',
    blurb: 'A hot-spring valley of bathhouses stepping up a wooded slope, a steaming green pool at its head and a narrow park following the stream',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'banqiao-belt',
    name: 'Banqiao · Cube-Belt',
    blurb: 'A across-river new town of tower slabs around a station plaza, a walled classical garden of ponds and pavilions, and a stadium bowl',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'muzha-belt',
    name: 'Muzha · Cube-Belt',
    blurb: 'A southern hill valley with a gondola line strung up the ridge, terraced tea slopes above and a zoo laid out in enclosures along the valley floor',
    has: { cloud: true, roads: true },
    region: 'taipei',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bell-tower-belt',
    name: 'Bell Tower · Cube-Belt',
    blurb: 'A tiled tower on a brick platform marooned in a traffic circle, four straight streets running off it, and department blocks over a sunken concourse knotted round the crossing',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beiyuanmen-belt',
    name: 'Beiyuanmen · Cube-Belt',
    blurb: 'A drum tower arch opening into a lane of food stalls, courtyard houses packed tight behind it, and a mosque laid out as a walled garden of pavilions rather than domes',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yongningmen-belt',
    name: 'Yongningmen · Cube-Belt',
    blurb: 'A gate in three stacked halls with a barbican thrown out in front, a moat and planted rampart running either way, and a lane of ink and seal shops under the wall inside',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'changlemen-belt',
    name: 'Changlemen · Cube-Belt',
    blurb: 'The eastern gate and its barbican standing over the moat, a broad avenue of station-front hotels running north from it, and mid-rise blocks pressed hard against the rampart',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wulukou-belt',
    name: 'Wulukou · Cube-Belt',
    blurb: 'A station forecourt under the north-east rampart, five roads meeting in one knot beside it, and old commercial blocks with hanging signboards stacked round the junction',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'longshouyuan-belt',
    name: 'Longshouyuan · Cube-Belt',
    blurb: 'The straight axis running north out of the wall over a low loess rise, walled housing compounds ranked either side of it and market lanes cutting through between them',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tumen-belt',
    name: 'Tumen · Cube-Belt',
    blurb: 'The western works quarter — brick worker housing in long parallel rows, a wide dusty arterial down the middle and factory sheds standing behind the street wall',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hansenzhai-belt',
    name: 'Hansenzhai · Cube-Belt',
    blurb: 'Tight ranks of six-storey walk-ups from the works era, a wholesale market spilling onto the pavement and a wide road running east under plane trees',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xingqing-belt',
    name: 'Xingqing · Cube-Belt',
    blurb: 'A palace garden rebuilt round a lake with a university campus of gridded teaching blocks beside it, and student housing packed along the road between them',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xiaoyanta-belt',
    name: 'Xiaoyanta · Cube-Belt',
    blurb: 'A thirteen-storey brick pagoda with its top storeys shaken off, a temple courtyard and museum halls round its foot, and low blocks packed against the compound wall',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xiaozhai-belt',
    name: 'Xiaozhai · Cube-Belt',
    blurb: 'A crossing of two wide roads with shopping towers on all four corners, an underground concourse linking them and campus walls and student lanes running off behind',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dayanta-belt',
    name: 'Dayanta · Cube-Belt',
    blurb: 'A seven-storey brick pagoda on a temple platform, a stepped fountain plaza falling away north from it and hotel and shop blocks ringing the whole square',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dianzicheng-belt',
    name: 'Dianzicheng · Cube-Belt',
    blurb: 'An electronics works town of long grey slab housing on numbered lanes, with newer towers dropped into the gaps between the old compounds',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'qujiang-belt',
    name: 'Qujiang · Cube-Belt',
    blurb: 'A pedestrian avenue of timber-bracketed halls built new in an old style, a lake park at the end of it and apartment towers standing in ranks behind',
    has: { cloud: true, roads: true },
    region: 'xian',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'capitol-hill-belt',
    name: 'Capitol Hill · Cube-Belt',
    blurb: 'A white domed block on a terraced platform with a long rectangular reflecting basin below it, a columned marble hall squared off on each flank and a mile of open green running away west',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'farragut-square-belt',
    name: 'Farragut Square · Cube-Belt',
    blurb: 'Two small treed squares set into the grid with a low colonnaded house in a walled park between them, and office blocks ranked all around cut off dead flat at exactly the same height',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'georgetown-belt',
    name: 'Georgetown · Cube-Belt',
    blurb: 'A narrow canal with stepped stone lock chambers running one block back from the river, brick fronts standing straight on the towpath, and a steep flight of steps cut down between two buildings to the road below',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dupont-circle-belt',
    name: 'Dupont Circle · Cube-Belt',
    blurb: 'A wide traffic circle with a tiered marble fountain in the middle, six streets meeting it at odd angles, and long rows of bay-fronted brick houses on high stoops running out along every one',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'logan-circle-belt',
    name: 'Logan Circle · Cube-Belt',
    blurb: 'A green oval ringed by tall bay-fronted houses with iron railings and deep stoops, one broad shopping street ruled north from it, and narrow alley dwellings hidden inside the blocks behind',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shaw-belt',
    name: 'Shaw · Cube-Belt',
    blurb: 'A theatre street of narrow shopfronts under a tiled corner tower, church spires standing above unbroken rowhouse ranks behind it, and one wide avenue cutting the grid clean through on the diagonal',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'adams-morgan-belt',
    name: 'Adams Morgan · Cube-Belt',
    blurb: 'A single ridge street of three-storey painted shopfronts, apartment blocks stepping down a steep slope behind it, and a road bridge on tall arches striding over a wooded gorge at the end',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'columbia-heights-belt',
    name: 'Columbia Heights · Cube-Belt',
    blurb: 'A ridge-top grid of tall brick apartment blocks with flat-roofed retail slabs and a paved plaza at the main crossing, and unbroken rows of narrow porch-fronted houses running down every side street',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'petworth-belt',
    name: 'Petworth · Cube-Belt',
    blurb: 'Streets radiating from a small circular green in a spoke pattern, unbroken rows of porch-fronted brick houses filling each wedge between them, and one long avenue ruled through the whole thing on the diagonal',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tenleytown-belt',
    name: 'Tenleytown · Cube-Belt',
    blurb: 'The highest ground in the city carrying a castellated stone water tower and a fenced compound of lattice masts, one ridge avenue of low shopfronts and detached wooden houses on the slopes either side',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'brookland-belt',
    name: 'Brookland · Cube-Belt',
    blurb: 'A great domed basilica with a free-standing bell tower beside it, university quadrangles behind, and small detached wooden houses on deep lots running down to a rail cut',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'h-street-belt',
    name: 'H Street · Cube-Belt',
    blurb: 'One long commercial street with streetcar rails set into the roadway and a single wire strung above it, low flat-fronted shops down both sides, and a dense grid of two-storey rowhouses packed behind to a rail embankment',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'eastern-market-belt',
    name: 'Eastern Market · Cube-Belt',
    blurb: 'A long red brick market hall with open stalls under awnings along its flank, and unbroken rows of narrow bay-fronted houses with high front steps filling every block around it',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'navy-yard-belt',
    name: 'Navy Yard · Cube-Belt',
    blurb: 'A walled riverside yard of long brick ranges with old gun barrels stood in rows outside them, a boardwalk park on the bank beside it and new glass blocks packed hard along the whole waterfront behind',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'anacostia-belt',
    name: 'Anacostia · Cube-Belt',
    blurb: 'A white frame house with a long columned porch standing on a terraced hilltop lawn above the river, a small grid of two-storey shopfronts below it and elevated freeway ramps curling past the foot of the slope',
    has: { cloud: true, roads: true },
    region: 'washingtondc',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'masjid-jamek-belt',
    name: 'Masjid Jamek · Cube-Belt',
    blurb: 'Two brown rivers meet at a point under a mosque of striped onion domes and slim minarets, with a long arcaded range and its clock tower down one side of a flat green oval',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'petaling-street-belt',
    name: 'Petaling Street · Cube-Belt',
    blurb: 'A grid of two-storey shophouse rows with covered walkways, one street roofed end to end in green glazed tile, and a single enormous tower standing over a low concrete stadium bowl to the south',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bukit-bintang-belt',
    name: 'Bukit Bintang · Cube-Belt',
    blurb: 'Mall slabs packed shoulder to shoulder along one street with a monorail threading between them on single columns, and a narrow lane of awninged tables behind',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'klcc-belt',
    name: 'KLCC · Cube-Belt',
    blurb: 'Twin tapering towers joined by a bridge halfway up, standing over a lawn and a lake with a curved mall wrapped around their base',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kampung-baru-belt',
    name: 'Kampung Baru · Cube-Belt',
    blurb: 'Timber houses on stilts in their own fruit-tree lots on a village grid, with glass towers standing directly over the far side of the road',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chow-kit-belt',
    name: 'Chow Kit · Cube-Belt',
    blurb: 'Open-sided market halls of wet stalls under one continuous roof, and a straight street of narrow textile shophouses running past them',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pudu-belt',
    name: 'Pudu · Cube-Belt',
    blurb: 'A tight grid of old shop rows around a market block, with an elevated highway on columns cutting straight across the roofs',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'brickfields-belt',
    name: 'Brickfields · Cube-Belt',
    blurb: 'Tiered temple towers of carved figures on a street of small shopfronts, an arch across the road, and the fan of platforms under one long station roof at the top',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bangsar-belt',
    name: 'Bangsar · Cube-Belt',
    blurb: 'Low shop rows around a square of open-air tables, with bungalow lots on curving roads climbing the hill behind',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'titiwangsa-belt',
    name: 'Titiwangsa · Cube-Belt',
    blurb: 'A lake with long lobed arms and rowing jetties, a theatre under a stepped ceremonial roof at one end, and the whole tower skyline standing across the water',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sentul-belt',
    name: 'Sentul · Cube-Belt',
    blurb: 'Long-span railway workshop sheds with a fan of sidings running into them, and a walled garden of old trees pressed against one side',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ttdi-belt',
    name: 'Taman Tun Dr Ismail · Cube-Belt',
    blurb: 'Unbroken rows of terraced houses on a garden-suburb grid running up to a wooded ridge with trails cut through it',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kepong-belt',
    name: 'Kepong · Cube-Belt',
    blurb: 'Sheets of terraced housing packed to the edge of flooded mining pools, with one long lake and a path all the way round it',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pandan-indah-belt',
    name: 'Pandan Indah · Cube-Belt',
    blurb: 'A dense lattice of shop rows and low walk-up flats filling every block, with a concrete-channelled river ruled straight through',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cheras-belt',
    name: 'Cheras · Cube-Belt',
    blurb: 'Parallel terrace rows stepping up the slope in long ranks, with one street of stalls running two kilometres straight down the middle',
    has: { cloud: true, roads: true },
    region: 'kualalumpur',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'circular-quay-belt',
    name: 'Circular Quay · Cube-Belt',
    blurb: 'Ferry wharves in a row along a curved cove, a shell-roofed hall standing on the point beside them, and the arch of a great steel bridge springing out of the stone lanes behind.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sydney-cbd-belt',
    name: 'Sydney CBD · Cube-Belt',
    blurb: 'A canyon of towers packed onto a narrow ridge between two harbours, one gold-crowned needle on a slender shaft above them, and a sandstone market hall under a row of domes.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'barangaroo-belt',
    name: 'Barangaroo · Cube-Belt',
    blurb: 'A curved-crown tower standing alone at the water over a stepped waterfront, timber finger wharves ranked along the shore north of it, and a made headland faced in stacked sandstone blocks.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pyrmont-belt',
    name: 'Pyrmont · Cube-Belt',
    blurb: 'A cable-stayed bridge on two tall concrete pylons over a working bay, a fish market on the wharf below it, and a squat casino tower behind a peninsula of converted wool stores.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'haymarket-belt',
    name: 'Haymarket · Cube-Belt',
    blurb: 'A sandstone terminus under a square clock tower with a fan of platforms behind it, a covered market shed beside a lantern-hung pedestrian street, and a chequerboard-fronted tower over both.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'surry-hills-belt',
    name: 'Surry Hills · Cube-Belt',
    blurb: 'Unbroken rows of two-storey terraces with iron-lace verandahs on a tight grid of narrow one-way streets, brick warehouse blocks with saw-tooth roofs pushed in among them.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kings-cross-belt',
    name: 'Kings Cross · Cube-Belt',
    blurb: 'A spherical fountain at the fork of two streets under a rotating rooftop sign, walk-up flats stacked tight on the ridge behind, and a quarter-kilometre timber wharf lying in the bay below.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'paddington-belt',
    name: 'Paddington · Cube-Belt',
    blurb: 'Terrace rows in stepped ranks down a ridge, each behind a cast-iron balcony, and a long walled parade ground of Georgian sandstone ranges cut into the middle of them.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'redfern-belt',
    name: 'Redfern · Cube-Belt',
    blurb: 'A row of enormous brick locomotive workshops with clerestory ridges beside a wide fan of rail, and a grid of single-fronted terraces running right up to the tracks.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'newtown-belt',
    name: 'Newtown · Cube-Belt',
    blurb: 'One dead-straight high street of continuous two-storey shopfronts painted end to end, a rail line running alongside it, and a walled burial ground behind a spired church.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'milsons-point-belt',
    name: 'Milsons Point · Cube-Belt',
    blurb: 'A steel arch landing between two stone pylons, a laughing-face gateway and a Ferris wheel on the foreshore beside it, and a fifty-metre pool set right at the edge of the water',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'north-sydney-belt',
    name: 'North Sydney · Cube-Belt',
    blurb: 'A second cluster of office towers standing on a ridge, split down one side by a fifteen-lane freeway trench with footbridges stepping across it.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bondi-beach-belt',
    name: 'Bondi Beach · Cube-Belt',
    blurb: 'A kilometre of sand curved between two sandstone headlands, a rectangular pool cut into the rock at one end, and low flat-roofed blocks stacked up the slope behind.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'manly-belt',
    name: 'Manly · Cube-Belt',
    blurb: 'A narrow neck of land with a surf beach under a row of tall pines on one side and a ferry wharf on a sheltered cove on the other, one straight pedestrian street ruled between them.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'parramatta-belt',
    name: 'Parramatta · Cube-Belt',
    blurb: 'A tower cluster at the head of a river where a stone weir marks the tidal limit, a single-arch sandstone bridge below it, and a Georgian colonial house alone in a walled park.',
    has: { cloud: true, roads: true },
    region: 'sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'the-bund-belt',
    name: 'The Bund · Cube-Belt',
    blurb: 'A curved stone waterfront of domed and columned banks facing a wide brown river, a raised promenade the whole length of it and a grid of tight old blocks running back behind',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lujiazui-belt',
    name: 'Lujiazui · Cube-Belt',
    blurb: 'Three supertowers and a ball on splayed legs standing together on the inside of a river bend, with wide avenues fanning back east into long ranks of matching housing slabs',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'renmin-square-belt',
    name: 'Renmin Square · Cube-Belt',
    blurb: 'A big open square with museums and a park on it, a pedestrianised shopping street running east off one corner and dense blocks of offices and old brick terraces packed all around',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yuyuan-belt',
    name: 'Yuyuan · Cube-Belt',
    blurb: 'The oval street plan of a walled old town with a tiled garden quarter at its middle, low roofs packed tight inside the ring and the river swinging past along one side',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xintiandi-belt',
    name: 'Xintiandi · Cube-Belt',
    blurb: 'Restored grey-brick lane houses with carved stone door frames opening onto a shopping boulevard, and a warren of two-storey alleys hung with wires a few blocks south',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wukang-belt',
    name: 'Wukang · Cube-Belt',
    blurb: 'Plane trees closing over narrow streets of walled villas and art-deco flats, with one wedge-shaped block standing where two of the roads meet at an angle',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jingan-belt',
    name: 'Jingan · Cube-Belt',
    blurb: 'A gilded temple roof wedged between office towers on a broad shopping street, with plane-tree lanes of brick terraces running off it into tight courtyard alleys',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zhongshan-park-belt',
    name: 'Zhongshan Park · Cube-Belt',
    blurb: 'A wooded park with a lake in it hemmed by mall towers, an elevated railway running past on stilts and plane-tree lanes of six-storey blocks filling in behind',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xujiahui-belt',
    name: 'Xujiahui · Cube-Belt',
    blurb: 'A knot of ramps and footbridges under clustered mall towers, a twin-spired brick church on one corner and a walled campus of low old halls behind it',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shanghai-station-belt',
    name: 'Shanghai Station · Cube-Belt',
    blurb: 'A wide fan of railway tracks under a slab terminus with a square in front of it, tight walk-up housing packed right up to the fences on both sides',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'moganshan-belt',
    name: 'Moganshan · Cube-Belt',
    blurb: 'A narrow creek looping back on itself between brick warehouses painted wall to wall, slab housing stacked close along both banks and low bridges every few hundred metres',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'north-bund-belt',
    name: 'North Bund · Cube-Belt',
    blurb: 'Old wharf sheds and a cruise berth along a bend of the river, brick tenements in tight parallel rows climbing away from the water and a steel truss bridge at the creek mouth',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'century-avenue-belt',
    name: 'Century Avenue · Cube-Belt',
    blurb: 'A boulevard wide enough for eight lanes and a tree strip running dead straight between office towers, six-storey worker housing in long parallel rows either side of it',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wujiaochang-belt',
    name: 'Wujiaochang · Cube-Belt',
    blurb: 'A five-way roundabout under a floating elliptical canopy lit from beneath, malls around it and long ranks of red-tiled walk-up housing on the grid behind',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hongqiao-gubei-belt',
    name: 'Hongqiao Gubei · Cube-Belt',
    blurb: 'Ranks of residential towers standing in landscaped courtyards behind their own gates, wide signed roads between them and rows of small shophouses along the older cross streets',
    has: { cloud: true, roads: true },
    region: 'shanghai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'qianmen-dashilan-belt',
    name: 'Qianmen & Dashilan · Cube-Belt',
    blurb: 'A vast paved rectangle with a gate tower standing alone in it, and immediately south a mat of one-storey grey courtyards cut by lanes barely wide enough for a cart',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wangfujing-dongdan-belt',
    name: 'Wangfujing & Dongdan · Cube-Belt',
    blurb: 'A pedestrianised shopping street of department blocks with courtyard lanes pressed right up behind it, and a long red wall closing the western edge',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xisi-baitasi-belt',
    name: 'Xisi & Baitasi · Cube-Belt',
    blurb: 'Wall-to-wall grey courtyard roofs combed into east-west lanes, a white stupa standing over them, and four straight avenues cutting the mat into quarters',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shichahai-gulou-belt',
    name: 'Shichahai & Gulou · Cube-Belt',
    blurb: 'Three linked lakes with willow banks and low grey roofs crowding right down to the water, a drum tower and a bell tower standing in line north of them',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beijing-station-belt',
    name: 'Beijing Station & Jianguomen · Cube-Belt',
    blurb: 'A twin-towered terminus over a wide track apron, a stub of old city wall and its watchtower beside it, and glass office slabs marching east along a ten-lane avenue',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'guomao-cbd-belt',
    name: 'Guomao CBD · Cube-Belt',
    blurb: 'A tight cluster of glass towers around a looped one and a tapered one, standing over a ring-road interchange on blocks so wide the cross streets sit half a kilometre apart',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sanlitun-gongti-belt',
    name: 'Sanlitun & Gongti · Cube-Belt',
    blurb: 'A low open-air mall of coloured glass boxes, a bowl stadium beside it, and walled compounds on tree-lined streets packed in behind',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yonghegong-dongzhimen-belt',
    name: 'Yonghegong & Dongzhimen · Cube-Belt',
    blurb: 'Yellow-tiled temple halls in a walled sequence, grey courtyard lanes packed hard against them, and a knot of office towers over an interchange at the eastern end',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hepingli-anzhen-belt',
    name: 'Hepingli & Anzhen · Cube-Belt',
    blurb: 'Rows of six-storey walk-up housing in gridded compounds, a ring road on stilts cutting straight across, and taller newer slabs stacked up behind it',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wangjing-belt',
    name: 'Wangjing · Cube-Belt',
    blurb: 'Curved glass hills of a three-part tower standing over rank after rank of tall residential slabs, each block walled and gated off its own service loop',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xizhimen-beijing-north-belt',
    name: 'Xizhimen & Beijing North · Cube-Belt',
    blurb: 'Three linked towers over a station throat where the rail lines fan out, an elevated loop line curving away west, and dense walk-up blocks filling everything else',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zhongguancun-belt',
    name: 'Zhongguancun · Cube-Belt',
    blurb: 'Electronics malls and glass office towers on a wide crossroads, walled campus quadrangles with old trees behind them, and a ring road running past on a viaduct',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yuetan-muxidi-belt',
    name: 'Yuetan & Muxidi · Cube-Belt',
    blurb: 'Long grey ministry slabs set back behind hedges on a ten-lane avenue, a square walled altar park mid-block, and brick walk-ups filling everything between',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beijing-west-station-belt',
    name: 'Beijing West Station · Cube-Belt',
    blurb: 'A colossal gatehouse terminus with a tiled roof bridging its own tracks, a fan of rail lines running west out of it, and blocks of housing wrapping the yards',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fangzhuang-belt',
    name: 'Fangzhuang · Cube-Belt',
    blurb: 'Four numbered estates of identical tall slabs, each ring-fenced with its own gate and internal loop road, laid out on half-kilometre blocks around a ring-road junction',
    has: { cloud: true, roads: true },
    region: 'beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fuzimiao-belt',
    name: 'Fuzimiao · Cube-Belt',
    blurb: 'Lantern-hung lanes packed behind the temple hall, the Qinhuai running through them under stone bridges',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xinjiekou-belt',
    name: 'Xinjiekou · Cube-Belt',
    blurb: 'Four department-store towers on one crossroads, walled ministry courtyards a block behind them',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gulou-belt',
    name: 'Gulou · Cube-Belt',
    blurb: 'The drum tower on its mound with a 450 m spire behind it and university walls closing three sides',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shanxi-road-belt',
    name: 'Shanxi Road · Cube-Belt',
    blurb: 'A round plaza feeding five streets of shopfronts, plane trees meeting over the road',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'xiaguan-belt',
    name: 'Xiaguan · Cube-Belt',
    blurb: 'Worker housing and warehouse sheds along the embankment, bridge piers stepping out over the water',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yueyahu-belt',
    name: 'Yueyahu · Cube-Belt',
    blurb: 'A crescent of water inside the rampart, brick wall on one bank and six-storey blocks on the other',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'guanghuamen-belt',
    name: 'Guanghuamen · Cube-Belt',
    blurb: 'A gate reduced to a junction, glass office slabs on one side and walk-up courtyards on the other',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zhonghuamen-belt',
    name: 'Zhonghuamen · Cube-Belt',
    blurb: 'A four-chamber gatehouse straddling the wall, tiled roofs and canal branches spilling south of it',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nanhu-belt',
    name: 'Nanhu · Cube-Belt',
    blurb: 'The new village at full size: dozens of matched walk-ups, ground-floor shops and a park lake in the middle',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chaotiangong-belt',
    name: 'Chaotiangong · Cube-Belt',
    blurb: 'Yellow temple walls on a terrace above the street, a lake and a city gate a few blocks west',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'longjiang-belt',
    name: 'Longjiang · Cube-Belt',
    blurb: 'Matched tower blocks along a wide boulevard, the old wall and a wooded ridge closing the east end',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hexi-belt',
    name: 'Hexi · Cube-Belt',
    blurb: 'Wide new avenues between gated tower estates, walk-up ranks behind them and the river levee at the west end',
    has: { cloud: true, roads: true },
    region: 'nanjing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zamoskvorechye-belt',
    name: 'Zamoskvorechye · Cube-Belt',
    blurb: 'Low merchant streets behind a straight drainage canal, a small church with green and gold onion domes every few blocks, and a red brick gallery with a carved gable facing a lane',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'arbat-belt',
    name: 'Arbat · Cube-Belt',
    blurb: 'A pedestrian lane of souvenir stalls and portrait painters, art-nouveau apartment blocks with tiled fronts in the streets behind it, and a stepped tower with a spire closing the far end',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chistye-prudy-belt',
    name: 'Chistye Prudy · Cube-Belt',
    blurb: 'A long rectangular pond set in the middle of a planted boulevard, tram rails curving round the end of it, and five-storey blocks with shops cut into their ground floors',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sretenka-belt',
    name: 'Sretenka · Cube-Belt',
    blurb: 'One narrow straight street with two dozen side lanes branching off it at right angles, tenement blocks built hard to the pavement and a courtyard behind every arch',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ostozhenka-belt',
    name: 'Ostozhenka · Cube-Belt',
    blurb: 'A white cathedral under one huge gold dome standing on the embankment, a footbridge striding across the river beside it, and streets of pale rendered mansion blocks behind',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'patriarshiye-belt',
    name: 'Patriarshiye · Cube-Belt',
    blurb: 'A square pond with a railed walk round it in a pocket of quiet lanes, low pre-revolutionary apartment houses either side and embassy villas behind walled front gardens',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'taganka-belt',
    name: 'Taganka · Cube-Belt',
    blurb: 'A wide square on a hilltop where five roads meet, heavy stone apartment blocks facing onto it and the streets dropping steeply away behind',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tverskaya-belt',
    name: 'Tverskaya · Cube-Belt',
    blurb: 'A wide radial avenue of set-back stone facades, a bronze statue on the plaza where the boulevard crosses it, and deep shopfront arcades cut into the ground floors',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'baumanskaya-belt',
    name: 'Baumanskaya · Cube-Belt',
    blurb: 'A five-domed cathedral in blue and white standing over a road junction, a small river cutting through the quarter behind it, and brick workshops and merchant houses down the lanes',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'paveletskaya-belt',
    name: 'Paveletskaya · Cube-Belt',
    blurb: 'A terminus train shed with a paved square in front of it, glass office blocks pushed up against the platform ends and a wedge of low warehouses behind',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'semyonovskaya-belt',
    name: 'Semyonovskaya · Cube-Belt',
    blurb: 'Long brick works buildings standing behind their own gates, ranks of panel blocks stacked in a grid beside them and a wide road running east through the middle',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sokol-belt',
    name: 'Sokol · Cube-Belt',
    blurb: 'A pocket of small pitched-roof cottages on curving lanes named after painters, pressed hard against a many-laned avenue of columned apartment blocks',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tulskaya-belt',
    name: 'Tulskaya · Cube-Belt',
    blurb: 'A single slab block four hundred metres long standing over the junction, a round market hall beside it and a white-walled monastery with a bell-tower gate across the road',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'khamovniki-belt',
    name: 'Khamovniki · Cube-Belt',
    blurb: 'A straight run of embankment under heavy 1950s blocks with columned porches, barrack ranges and their parade ground set back behind, and the river swinging away south',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kitay-gorod-belt',
    name: 'Kitay-gorod · Cube-Belt',
    blurb: 'A cathedral of clustered painted spiral domes at the end of a long paved square, a glass-roofed shopping arcade facing it and narrow lanes of small white churches and trading rows behind',
    has: { cloud: true, roads: true },
    region: 'moscow',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'colaba-belt',
    name: 'Colaba · Cube-Belt',
    blurb: 'A long sea wall curving round the point with arcaded stone blocks behind it, a domed arch on the water and naval sheds along the harbour side',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fort-mumbai-belt',
    name: 'Fort · Cube-Belt',
    blurb: 'Arcaded Victorian blocks in dark basalt on a tight grid, a vaulted railway terminus with carved stone spires, and banyan-shaded lanes of book stalls',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'marine-lines-belt',
    name: 'Marine Lines · Cube-Belt',
    blurb: 'A two-mile promenade curving round the bay behind a low parapet, art-deco flats facing it in one continuous line and a cricket maidan behind them',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'girgaon-belt',
    name: 'Girgaon · Cube-Belt',
    blurb: 'Wooden-balconied chawls packed along narrow wadis, a beach at the end of the main road and small painted temples between the tenements',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'byculla-belt',
    name: 'Byculla · Cube-Belt',
    blurb: 'Mill chimneys and long weaving sheds behind high walls, chawl blocks ranked opposite them and a walled garden with a domed museum at one corner',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'worli-belt',
    name: 'Worli · Cube-Belt',
    blurb: 'A fishing village of low painted houses on a rocky point, tower blocks standing over it and a cable-stayed bridge striding out across the bay',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dadar-belt',
    name: 'Dadar · Cube-Belt',
    blurb: 'A flower market spilling over the pavements outside a junction station, ranks of low co-operative housing behind and a beach at the western end',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'matunga-belt',
    name: 'Matunga · Cube-Belt',
    blurb: 'Gridded garden-suburb blocks with deep verandahs, a tank and stepped temple tower at the middle, and coffee houses under the arcades by the station',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mahim-belt',
    name: 'Mahim · Cube-Belt',
    blurb: 'A creek mouth with fishing boats drawn up on the mud, a domed shrine and a small fort behind the beach, and a causeway running north over the water',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bandra-west-belt',
    name: 'Bandra West · Cube-Belt',
    blurb: 'Tile-roofed villas on lanes behind a rocky sea front, a stone church on the hill, and a promenade looking back at the bridge across the bay',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vile-parle-belt',
    name: 'Vile Parle · Cube-Belt',
    blurb: 'Low suburban blocks on a tight grid either side of the railway, college compounds behind them and the airport fence closing the eastern edge',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'andheri-west-belt',
    name: 'Andheri West · Cube-Belt',
    blurb: 'Dense mid-rise blocks packed round a station bridge, film studio compounds behind walls and a market street running the length of it',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ghatkopar-belt',
    name: 'Ghatkopar · Cube-Belt',
    blurb: 'Ranks of tower blocks stepping up a low hill, an elevated metro on concrete legs running through, and older chawl rows filling the ground between',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chembur-belt',
    name: 'Chembur · Cube-Belt',
    blurb: 'Refinery flare stacks and tank farms behind the tree line, garden-suburb bungalows on curving lanes, and a golf course cut into the middle',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'borivali-belt',
    name: 'Borivali · Cube-Belt',
    blurb: 'Suburban tower blocks running right up to a forest edge, a lake and rock-cut caves inside the tree line, and a wide highway down the eastern side',
    has: { cloud: true, roads: true },
    region: 'mumbai',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'back-bay-belt',
    name: 'Back Bay · Cube-Belt',
    blurb: 'A ruled grid of five long parallel streets on filled ground, unbroken rows of bow-fronted brownstone on high stoops facing a tree-lined central mall, and one plain mirrored slab standing over a heavy stone church tower',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'beacon-hill-belt',
    name: 'Beacon Hill · Cube-Belt',
    blurb: 'Narrow brick lanes climbing a steep rise between flat-fronted rowhouses with black shutters, cobbles and gas lamps underfoot, a gilded dome on the crest and a railed private square of plane trees below it',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'north-end-belt',
    name: 'North End · Cube-Belt',
    blurb: 'A tangle of lanes barely wide enough for one car between three-storey brick tenements with fire escapes down their fronts, a tall white steeple standing over them and a working wharf edge closing the quarter off',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'downtown-crossing-belt',
    name: 'Downtown Crossing · Cube-Belt',
    blurb: 'Crooked colonial streets meeting at odd angles with no grid at all, a pedestrianised shopping street cut through them, walled burying grounds of thin slate stones and three long granite market halls set end to end',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chinatown-boston-belt',
    name: 'Chinatown · Cube-Belt',
    blurb: 'A stone gateway of three arches under a tiled roof at the head of the main street, dense low blocks of shopfronts with signboards stacked up their faces, and a ribbon of new parkland where an elevated road used to run',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'south-end-belt',
    name: 'South End · Cube-Belt',
    blurb: 'Street after street of bow-fronted brick rowhouses on high stoops, small oval railed gardens dropped into the pattern at intervals, and long avenues running dead level across filled ground',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fenway-belt',
    name: 'Fenway · Cube-Belt',
    blurb: 'An irregular green ballfield wedged into the street grid with one very high wall closing its short side, a reedy marsh park of winding water and garden plots beside it, and brick apartment blocks ranked around both',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'charlestown-belt',
    name: 'Charlestown · Cube-Belt',
    blurb: 'A granite obelisk standing alone on a hilltop above steep streets of clapboard and brick rowhouses, and below them a walled navy yard of stone dry docks with a three-masted ship moored at the head of one',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'east-boston-belt',
    name: 'East Boston · Cube-Belt',
    blurb: 'Ranks of three-decker houses with stacked porches climbing two rounded hills, a working waterfront of piers facing the city across open water, and flat made ground of runways spread out behind',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'south-boston-belt',
    name: 'South Boston · Cube-Belt',
    blurb: 'A tight grid of three-decker houses running out along a peninsula, a curving beach with a domed bathhouse and a causeway to a hilltop fort, and grain elevators and pier sheds along the harbour side',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dorchester-belt',
    name: 'Dorchester · Cube-Belt',
    blurb: 'Street after street of three-decker houses with porches stacked three high, a long shopping avenue of two-storey brick blocks cutting through them and a rapid-transit line running in an open cut alongside',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jamaica-plain-belt',
    name: 'Jamaica Plain · Cube-Belt',
    blurb: 'A deep kettle pond ringed by a gravel path and wooded bank, a hillside of specimen trees laid out in curving drives beside it, and streets of three-deckers and turreted timber houses climbing away from both',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'harvard-square-belt',
    name: 'Harvard Square · Cube-Belt',
    blurb: 'A walled yard of red brick halls set at angles around elm grass with a white steeple over one end, a triangular paved square outside the gates where five streets meet, and a domed white hall on the far side',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kendall-belt',
    name: 'Kendall · Cube-Belt',
    blurb: 'A low grey dome on a columned front facing a wide basin, one continuous limestone facade running a quarter mile behind it, and a quarter of glass laboratory blocks beyond with a windowless brick cylinder in a moat',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'davis-square-belt',
    name: 'Davis Square · Cube-Belt',
    blurb: 'Five streets meeting at one small paved junction of two-storey brick blocks, a paved path running straight through on an old rail alignment, and dense three-deckers packed on narrow lots all around',
    has: { cloud: true, roads: true },
    region: 'boston',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'the-loop-belt',
    name: 'The Loop · Cube-Belt',
    blurb: 'An elevated railway ruled in a closed rectangle above the street, dark towers packed tight inside it, and a river swinging past the north side under a row of lifting bridges',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'river-north-belt',
    name: 'River North · Cube-Belt',
    blurb: 'A river forking three ways under a dozen trunnion bridges, twin round corn-cob towers standing on stilts above the water, and a colossal brick block filling one whole bank',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gold-coast-belt',
    name: 'Gold Coast · Cube-Belt',
    blurb: 'A wall of tall slabs shoulder to shoulder along a curved lakefront drive, a pale turreted water tower behind them, and streets of stone mansions under old trees',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lincoln-park-belt',
    name: 'Lincoln Park · Cube-Belt',
    blurb: 'A long lakeside park of lagoons and winding paths with a small zoo and a domed glasshouse in it, a boat harbour behind a breakwater, and dense low blocks to the west',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wrigleyville-belt',
    name: 'Wrigleyville · Cube-Belt',
    blurb: 'A green steel ballpark wedged into an ordinary residential block with a hand-turned scoreboard on the far wall, rooftop bleachers facing in, and an elevated line running past',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'uptown-belt',
    name: 'Uptown · Cube-Belt',
    blurb: 'Enormous terracotta theatre and ballroom fronts standing over low shopfronts, a broad lakefront park with a curving harbour behind it, and courtyard apartment blocks in long rows',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'logan-square-belt',
    name: 'Logan Square · Cube-Belt',
    blurb: 'A wide planted boulevard running to a great circle with a single tall stone column in the middle of it, greystone three-flats lining the way, and an elevated line on an embankment',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wicker-park-belt',
    name: 'Wicker Park · Cube-Belt',
    blurb: 'Six streets meeting at one skewed intersection under a clock tower on a thin wedge block, ornate greystone fronts with high stoops around a small square park',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'west-loop-belt',
    name: 'West Loop · Cube-Belt',
    blurb: 'Brick meatpacking sheds with loading docks and canopies turned over to offices, a vast station concourse under a barrel roof, and a sunken expressway trench along one edge',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'garfield-park-belt',
    name: 'Garfield Park · Cube-Belt',
    blurb: 'A gigantic glasshouse of sloping haystack roofs set in a boulevard park with lagoons, a golden-domed pavilion beside it, and a straight planted parkway running out of it',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pilsen-belt',
    name: 'Pilsen · Cube-Belt',
    blurb: 'Two-flat brick rows with raised front stairs and painted murals across whole walls, twin-spired churches standing over them, and a rail embankment cutting the grid on the diagonal',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chinatown-belt',
    name: 'Chinatown · Cube-Belt',
    blurb: 'A tiled ceremonial gate across the main street, a square of curved-eave pavilions around a plaza, low shophouse rows behind, and a river bending past a wall of rail bridges',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bridgeport-belt',
    name: 'Bridgeport · Cube-Belt',
    blurb: 'Tight rows of one-storey workers cottages on narrow lots, a straight canal with lift bridges along one side, and grain elevators and rail yards on the far bank',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bronzeville-belt',
    name: 'Bronzeville · Cube-Belt',
    blurb: 'Long ranks of greystone and brick three-flats along a wide planted boulevard, a striding bronze figure on a tall plinth, and a campus of low steel-and-glass pavilions',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hyde-park-belt',
    name: 'Hyde Park · Cube-Belt',
    blurb: 'Gothic limestone quadrangles with pinnacled towers around cloistered courts, a mile-long sunken green strip ruled straight beside them, and a domed classical hall across a lagoon park',
    has: { cloud: true, roads: true },
    region: 'chicago',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lev-hair-belt',
    name: 'Lev HaIr · Cube-Belt',
    blurb: 'Rounded white balconies stacked on block after block of low flat-roofed apartments, a tree-lined boulevard with a raised walking median cutting a diagonal through the grid',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'florentin-belt',
    name: 'Florentin · Cube-Belt',
    blurb: 'A tight grid of two-storey workshops and print shops with roller shutters along every ground floor, paint covering the end walls, furniture yards spilling onto the pavement',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'old-jaffa-belt',
    name: 'Old Jaffa · Cube-Belt',
    blurb: 'A stone hill of vaulted alleys above a small working harbour, a square clock tower at the gate and a flea market of open stalls spreading down the slope behind it',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ajami-belt',
    name: 'Ajami · Cube-Belt',
    blurb: 'Low sandstone houses with arched windows stepping down a slope to the sea, a broad grassed park built over rubble mounds along the shore, a long straight breakwater beyond',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'neve-tzedek-belt',
    name: 'Neve Tzedek · Cube-Belt',
    blurb: 'Narrow lanes of one and two-storey houses under red tile roofs, a restored railway terminus of stone sheds and platforms at the bottom edge, one thin tower standing over all of it',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dizengoff-belt',
    name: 'Dizengoff · Cube-Belt',
    blurb: 'A raised circular plaza with a coloured fountain at its centre and traffic passing underneath, white curved-balcony blocks radiating from it along converging streets',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'old-north-belt',
    name: 'Old North · Cube-Belt',
    blurb: 'A large open paved square with a low stepped monument at one end and a long slab of a city hall down one side, straight avenues of white apartment blocks running north from it',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tel-aviv-port-belt',
    name: 'Tel Aviv Port · Cube-Belt',
    blurb: 'A wave-shaped timber deck laid along the waterfront with converted cargo hangars behind it, a river mouth spanned by a footbridge, a pair of tall power station chimneys at the point',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sarona-belt',
    name: 'Sarona · Cube-Belt',
    blurb: 'Two dozen restored red-roofed colony farmhouses set in lawns directly beneath a cluster of towers, one round and one triangular and one square, a walled compound alongside',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'neve-shaanan-belt',
    name: 'Neve Shaanan · Cube-Belt',
    blurb: 'A vast derelict concrete bus terminal of stacked decks filling most of the frame, a spice market street of open sacks running off it, tenement blocks packed tight around',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yad-eliyahu-belt',
    name: 'Yad Eliyahu · Cube-Belt',
    blurb: 'A round white arena ringed with floodlights, ranks of pale housing blocks on open lawns around it, a sunken motorway and rail corridor running past one edge',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ramat-gan-bursa-belt',
    name: 'Ramat Gan Bursa · Cube-Belt',
    blurb: 'A knot of glass office towers standing abruptly out of low suburb, joined at height by enclosed bridges, the tallest tapering to a mast above the rest',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'givatayim-belt',
    name: 'Givatayim · Cube-Belt',
    blurb: 'Streets curving in terraces round a low hill with a domed observatory on the crest, small pale apartment houses on every terrace, one tall tower breaking the line at the edge',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bnei-brak-belt',
    name: 'Bnei Brak · Cube-Belt',
    blurb: 'Apartment blocks packed at the tightest spacing in frame with balconies enclosed floor to floor, courtyard synagogues between them, a wide market street cutting through the middle',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bat-yam-belt',
    name: 'Bat Yam · Cube-Belt',
    blurb: 'A curving beach promenade with a row of white towers standing directly behind it, a rectangular grid of older housing blocks inland, a long sea wall reaching out at one end',
    has: { cloud: true, roads: true },
    region: 'telaviv',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'san-jose-downtown-belt',
    name: 'Downtown San Jose · Cube-Belt',
    blurb: 'A short cluster of flat-topped towers all held to one height by the flight path, a free-standing glass rotunda beside the council block, a fountain plaza and a twin-spired church on the square, and surface parking eating whole blocks between them',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sofa-district-belt',
    name: 'SoFA · Cube-Belt',
    blurb: 'A straight low street of brick shopfronts with tall vertical theatre signs standing off them, tram rails laid down the middle, and a long windowless convention shed closing one end',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'japantown-san-jose-belt',
    name: 'Japantown · Cube-Belt',
    blurb: 'Two blocks of low shopfronts with a tiled temple roof set back among them, a timber gate at the corner, and small gabled bungalows on narrow lots running out in every direction',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'midtown-san-jose-belt',
    name: 'Midtown & Diridon · Cube-Belt',
    blurb: 'A wide silver drum of an arena alone in its parking apron, a low pale depot across the road with a fan of rail lines and long platforms behind it, and a willow-lined river channel cutting past',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'naglee-park-belt',
    name: 'Naglee Park · Cube-Belt',
    blurb: 'Deep-porched wooden houses on straight tree-lined streets with long back lots, and the hard edge of a campus of brick blocks and lawns closing the western side',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'spartan-keyes-belt',
    name: 'Spartan Keyes · Cube-Belt',
    blurb: 'Small square bungalows packed onto a tight grid, a freight line on an embankment slicing across it diagonally, and low warehouse sheds filling the wedges left over',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'willow-glen-belt',
    name: 'Willow Glen · Cube-Belt',
    blurb: 'A short village high street of awninged shops under big street trees with angled parking at both kerbs, and block after block of low bungalows with deep front lawns behind it',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'rose-garden-belt',
    name: 'Rose Garden · Cube-Belt',
    blurb: 'A formal garden of concentric rose beds around a sunken lawn and an arched stone screen, with wide curving streets of tile-roofed houses and mature palms laid out around it',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'burbank-belt',
    name: 'Burbank · Cube-Belt',
    blurb: 'The tightest lot pattern in the valley, tiny hip-roofed houses shoulder to shoulder on short blocks, and a long strip of motels and used-car lots running the length of one edge',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'santana-row-belt',
    name: 'Santana Row · Cube-Belt',
    blurb: 'One invented boulevard of six-storey blocks with a planted promenade down its middle, and straight across the road an enormous windowless mall box ringed by multi-storey parking decks',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'santa-clara-downtown-belt',
    name: 'Santa Clara · Cube-Belt',
    blurb: 'A white adobe mission church with a bell arch and cross at the head of a long palm-lined garden walk, quadrangles of low arcaded college buildings around it, and a plain street grid beyond',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'little-portugal-belt',
    name: 'Little Portugal · Cube-Belt',
    blurb: 'A long low avenue of stucco storefronts and bakeries with a domed white hall among them, and flat-roofed single-storey houses on wide straight streets running off both sides',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'little-saigon-belt',
    name: 'Little Saigon · Cube-Belt',
    blurb: 'Two big single-storey shopping plazas with curved tile roofs facing each other across a six-lane road, their parking lots bigger than the buildings, and ranch houses on curved streets behind',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'alum-rock-belt',
    name: 'Alum Rock · Cube-Belt',
    blurb: 'The last straight streets of the grid running up against bare rounded hills, a creek in a wooded gulch cutting down out of them, and a road climbing away into the folds above',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'berryessa-belt',
    name: 'Berryessa · Cube-Belt',
    blurb: 'A long glassy transit hall with a multi-deck car park beside it standing at the end of the line, a great gravelled market ground alongside, and ranch-house tracts on looping streets beyond',
    has: { cloud: true, roads: true },
    region: 'sanjose',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'centro-historico-belt',
    name: 'Centro Historico · Cube-Belt',
    blurb: 'A vast bare paved square with a single flagpole dead centre, twin stone bell towers leaning visibly off vertical on one side of it, an excavated stepped platform of raw volcanic stone open to the sky on another, and a grid of courtyard blocks with one slender white tower standing over them',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tepito-belt',
    name: 'Tepito · Cube-Belt',
    blurb: 'Street after street roofed over end to end in stall tarpaulins until the roadway disappears under them, tight blocks of two-storey tenements built round narrow shared courtyards behind, and a wide rail-side avenue closing the quarter off',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tlatelolco-belt',
    name: 'Tlatelolco · Cube-Belt',
    blurb: 'Ranks of identical long housing slabs standing on open lawns at an angle to the street grid, a square at the centre holding low excavated stone platforms with a small stone church set right on their edge, and a raised motorway ruling the whole estate off down one side',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'buenavista-belt',
    name: 'Buenavista · Cube-Belt',
    blurb: 'A rail terminus fanning out into parallel sidings under one long glazed shed, an octagonal iron pavilion with a domed lattice roof standing in a formal square nearby, and streets of tall narrow stucco townhouses with iron balconies packed round both',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'la-villa-belt',
    name: 'La Villa · Cube-Belt',
    blurb: 'A huge paved atrium with a low round green-roofed hall along one side and an older twin-towered stone church tilting visibly beside it, a stepped path of small chapels climbing the hill behind, and streets of stalls funnelling in from every direction',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'reforma-belt',
    name: 'Paseo de la Reforma · Cube-Belt',
    blurb: 'A dead-straight tree-lined boulevard cutting diagonally clean across the street grid, roundabouts strung along it at intervals with a tall column carrying a winged gilt figure in one, glass towers set well back behind service roads, and the edge of a great wooded park closing the far end',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'polanco-belt',
    name: 'Polanco · Cube-Belt',
    blurb: 'A museum courtyard roofed along one side by a single vast concrete canopy hung off one carved pillar, the lake and wooded slopes of a park running away behind it, and streets of low white flat-roofed blocks under close-planted trees on a diagonal grid',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'doctores-belt',
    name: 'Doctores · Cube-Belt',
    blurb: 'A tight grid of two and three storey blocks with open-fronted workshops filling every ground floor, a windowless concrete drum under a shallow domed roof standing among them, and a hospital campus of long parallel ward slabs taking up several blocks at once',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'roma-norte-belt',
    name: 'Roma Norte · Cube-Belt',
    blurb: 'Broad tree-lined avenues of tall stucco mansions with iron balconies and steep mansard roofs, two small square plazas with fountains set into the grid a few blocks apart, and a covered market hall under one long barrel roof filling a whole block',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'condesa-belt',
    name: 'Condesa · Cube-Belt',
    blurb: 'An oval ring street tracing the outline of a vanished racecourse with a long narrow park inside it, curved apartment blocks following the bend on rounded corners with banded balconies, and a second smaller park a few streets away',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'narvarte-belt',
    name: 'Narvarte · Cube-Belt',
    blurb: 'An unbroken grid of low flat-roofed houses in long identical rows, one wide avenue cutting across it on a diagonal, small square parks dropped in at regular intervals, and a stadium bowl with a retail slab wrapped round it at one edge',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'del-valle-belt',
    name: 'Del Valle · Cube-Belt',
    blurb: 'A leafy grid of walled single-storey houses with gardens hidden behind them, a sunken park set a full storey below street level with ramped paths down into it, and a wide multi-lane avenue running dead straight through the whole quarter',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'san-angel-belt',
    name: 'San Angel · Cube-Belt',
    blurb: 'Narrow cobbled lanes running between high garden walls with tiled roofs showing over the top, a monastery of three tiled domes and a walled orchard in the middle, and a small stone-paved square that fills edge to edge with stalls one day a week',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'coyoacan-belt',
    name: 'Coyoacan · Cube-Belt',
    blurb: 'Two linked plazas with a bandstand kiosk in one and a stone church under a single squat tower on the other, cobbled streets of flat-fronted houses painted in solid blocks of colour, and a covered market of narrow aisles filling a block behind',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ciudad-universitaria-belt',
    name: 'Ciudad Universitaria · Cube-Belt',
    blurb: 'A windowless rectangular tower faced on all four sides in stone mosaic standing over open lawns, low slabs raised on pilotis scattered round it, a stadium built as an earth bowl with a relief band round its rim, and black lava rock breaking through the scrub between them',
    has: { cloud: true, roads: true },
    region: 'mexicocity',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kota-tua-belt',
    name: 'Kota Tua · Cube-Belt',
    blurb: 'A cobbled square ringed by white shuttered ranges, a narrow stone-walled canal running north to the old harbour, low warehouse rows and a lifting bridge across the water',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'glodok-belt',
    name: 'Glodok · Cube-Belt',
    blurb: 'A dense grid of two-storey shophouses with awnings out over the pavement, temple courtyards buried inside the blocks and market lanes too narrow for cars',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tambora-belt',
    name: 'Tambora · Cube-Belt',
    blurb: 'A carpet of small tin roofs packed edge to edge, alleys barely a metre wide winding between them, a walled canal cut straight through and rail sidings along one side',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pluit-belt',
    name: 'Pluit · Cube-Belt',
    blurb: 'Flat reclaimed ground behind a concrete sea wall with the water sitting higher than the streets, a long rectangular boat basin, dense low blocks and pump houses at the drain outfalls',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tanjung-priok-belt',
    name: 'Tanjung Priok · Cube-Belt',
    blurb: 'Container stacks in colour rows under rail-mounted gantries, straight quays enclosing parallel basins, tank farms and long shed roofs packed behind the fence line',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kelapa-gading-belt',
    name: 'Kelapa Gading · Cube-Belt',
    blurb: 'A new-town grid of wide straight boulevards and gated blocks laid over drained fish ponds, long low mall slabs with their parking decks, a canal ruled dead straight down one side',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'grogol-belt',
    name: 'Grogol · Cube-Belt',
    blurb: 'A many-level flyover knot where two arterials cross, university quadrangles and hospital slabs around it, and a brown canal running behind the shophouse rows',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gambir-belt',
    name: 'Gambir · Cube-Belt',
    blurb: 'One tall obelisk standing alone in the middle of a huge grass square, a walled palace garden and colonnaded ministry ranges facing it on every side, a station train shed at the corner',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'senen-belt',
    name: 'Senen · Cube-Belt',
    blurb: 'A multi-storey market block with buses wedged around its base, a rail station and flyover ramps crossing above the street, tight kampung roofs filling in behind',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tanah-abang-belt',
    name: 'Tanah Abang · Cube-Belt',
    blurb: 'Stacked market halls the size of city blocks with goods carts jamming every approach, a rail line and platforms along one edge, a concrete-walled canal running past',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'menteng-belt',
    name: 'Menteng · Cube-Belt',
    blurb: 'Wide tree-lined avenues meeting at circles and triangular islands, deep garden plots with single villas under full canopy, a small planted park at every junction',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sudirman-belt',
    name: 'Sudirman · Cube-Belt',
    blurb: 'A dead-straight eight-lane avenue with a raised rail line down the middle, glass towers standing in a continuous wall either side, a fountain roundabout closing one end',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jatinegara-belt',
    name: 'Jatinegara · Cube-Belt',
    blurb: 'An old market town swallowed by the city, arcaded two-storey shopfronts along a bent high street, a river bend and a rail junction with sidings fanning out beside it',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tebet-belt',
    name: 'Tebet · Cube-Belt',
    blurb: 'A tight grid of low houses behind a rail embankment, shophouse strips along every through road, two green squares linked by a footbridge over the tracks',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'blok-m-belt',
    name: 'Blok M · Cube-Belt',
    blurb: 'A planned garden suburb of wide curving avenues and roundabouts, a bus terminal under a shopping deck at the centre, low walled houses in deep plots all around',
    has: { cloud: true, roads: true },
    region: 'jakarta',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'paddington-london-belt',
    name: 'Paddington · Cube-Belt',
    blurb: 'A great iron and glass arched shed over the platforms, a canal basin of narrowboats behind it, long white stucco terraces running west, and a raised motorway curving over the rooftops',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'south-kensington-belt',
    name: 'South Kensington · Cube-Belt',
    blurb: 'A terracotta museum carved with beasts and spires facing a domed oval concert hall across a straight avenue, red brick mansion blocks stacked behind them, and a gilded memorial canopy at the park edge',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'marylebone-belt',
    name: 'Marylebone · Cube-Belt',
    blurb: 'Flat fronted brick terraces in ruled unbroken rows, iron railings and sunken basement areas along every street, one curving high street cutting across the grain, and cream stucco palace fronts facing a great park at the top edge',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'camden-town-belt',
    name: 'Camden Town · Cube-Belt',
    blurb: 'A canal stepping down through timber lock gates into a warren of brick market halls, railway viaducts crossing above the water, and a round brick drum of an old engine house standing just north of it',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'soho-belt',
    name: 'Soho · Cube-Belt',
    blurb: 'The tightest street grid in the city, four and five storey brick fronts packed shoulder to shoulder over lit shopfronts, painted gates spanning one narrow lane, and a single railed garden square in the middle of it',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'westminster-belt',
    name: 'Westminster · Cube-Belt',
    blurb: 'A long pinnacled riverside palace with a clock tower at one end and a square tower at the other, a twin towered abbey behind it, a wide avenue of grey stone government blocks running north, and a lake in a park alongside',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bloomsbury-belt',
    name: 'Bloomsbury · Cube-Belt',
    blurb: 'Railed garden squares laid one after another through a grid of brick terraces, a colonnaded museum with a glass roofed round court at its heart, and a white stepped tower standing over the university quarter',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kings-cross-london-belt',
    name: 'Kings Cross · Cube-Belt',
    blurb: 'Two great train sheds side by side, one plain twin arched brick and one hidden behind a red gothic hotel of spires, circular iron gasholder frames standing beside them, and a canal basin with wide steps down to the water',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'waterloo-belt',
    name: 'Waterloo · Cube-Belt',
    blurb: 'A giant white wheel turning on the riverbank, slab sided concrete arts halls on raised walkways behind it, a curved colonnaded hall facing the water, and a wide fan of railway tracks running into a long station roof',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'clerkenwell-belt',
    name: 'Clerkenwell · Cube-Belt',
    blurb: 'A long cast iron market hall with arcaded ends and painted ironwork, tall brick warehouses converted around it, narrow lanes of workshops between them, and a green railed square with a medieval stone gatehouse nearby',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'city-of-london-belt',
    name: 'City of London · Cube-Belt',
    blurb: 'A great lead dome on a colonnaded drum standing alone at one end, a knot of oddly shaped glass towers at the other, medieval lanes threading between them, and three concrete residential towers over a walled estate to the north',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'borough-belt',
    name: 'Borough · Cube-Belt',
    blurb: 'A tapering glass spire far taller than anything near it, a Victorian iron market packed under brick railway arches at its foot, a small stone cathedral beside the river, and viaducts fanning out south',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shoreditch-belt',
    name: 'Shoreditch · Cube-Belt',
    blurb: 'Brick warehouses with painted walls and iron shutters along every street, a white stone church with a tall pointed steeple over a glass roofed market hall, and rows of tall weaver windows on the older houses',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'canary-wharf-belt',
    name: 'Canary Wharf · Cube-Belt',
    blurb: 'A tight cluster of flat glass towers with one pyramid capped shaft in the middle, long rectangles of still dock water cut between them, a light railway threading through on concrete legs, and a timber lattice roof over the quay',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'greenwich-belt',
    name: 'Greenwich · Cube-Belt',
    blurb: 'Twin baroque domes facing the river across a lawn with a white villa framed between them, a clipper ship raised in a dry dock under glass at the waterside, and a small domed observatory on a green hill looking back over it all',
    has: { cloud: true, roads: true },
    region: 'london',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'centro-storico-belt',
    name: 'Centro Storico · Cube-Belt',
    blurb: 'A domed round temple with a hole open to the sky at the centre of a knot of ochre lanes, a long oval piazza laid out on the outline of a buried racetrack, and a white marble monument of tiered steps and columns closing the far end',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'monti-belt',
    name: 'Monti · Cube-Belt',
    blurb: 'A vast broken ring of tiered arches at the foot of the slope, a cleared field of fallen columns and roofless brick halls beside it, and steep narrow lanes of ochre tenements with washing strung above the doorways',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'trastevere-belt',
    name: 'Trastevere · Cube-Belt',
    blurb: 'Cobbled lanes of peeling ochre and rose houses on the far bank, a square with a gold mosaic church front and a stepped fountain in the middle, and a pine ridge above with a terrace looking back over every roof',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pigneto-belt',
    name: 'Pigneto · Cube-Belt',
    blurb: 'Low two-storey houses with roof terraces and vines along one pedestrian street of bars, a railway embankment cutting the quarter clean in half, and long ranks of postwar apartment slabs behind',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'esquilino-belt',
    name: 'Esquilino · Cube-Belt',
    blurb: 'A huge arcaded square wrapped round a walled garden of palms and a ruined brick fountain, unbroken ranks of grey nineteenth-century blocks, and a long low station shed with a wave-shaped concrete canopy over the platform ends',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'aventino-belt',
    name: 'Aventino · Cube-Belt',
    blurb: 'A long flat oval of grass sunk between two hills where a racetrack used to run, walled convent gardens of orange trees and low brick basilicas on the slope above it, and a small round temple of white columns standing alone by the river gate',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'garbatella-belt',
    name: 'Garbatella · Cube-Belt',
    blurb: 'Small pastel courtyard blocks with outside staircases and shared gardens stepping up a low hill, arched gateways and pergolas between them, and a pale arcaded square of 1930s workshops at the top',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'borgo-belt',
    name: 'Borgo · Cube-Belt',
    blurb: 'A great dome standing over an oval piazza ringed four deep in columns, one straight avenue running from it to a drum-shaped brick fortress on the riverbank, and walled gardens and courtyards filling the enclave behind',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'balduina-belt',
    name: 'Balduina · Cube-Belt',
    blurb: 'Postwar apartment towers stacked in terraces up the steepest hill in the city, hairpin roads and long stair-streets threading between them, a lattice mast on the wooded crest, and the flat city spread out far below',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'san-lorenzo-belt',
    name: 'San Lorenzo · Cube-Belt',
    blurb: 'A tight grid of low painted tenements covered in murals and shutters, university faculties in pale rationalist blocks along one edge, and a vast walled cemetery of cypress avenues and marble chapels beside the railway',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'monteverde-belt',
    name: 'Monteverde · Cube-Belt',
    blurb: 'A great walled park of umbrella pines with a lake and a formal villa at its centre, ranks of 1930s apartment terraces stepping down the ridge beside it, and quiet streets of pale villas under stone pines',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'san-giovanni-belt',
    name: 'San Giovanni · Cube-Belt',
    blurb: 'A long white basilica front carrying a row of colossal statues along its roofline, the tallest obelisk in the city standing on the open square before it, and a brick city wall with a twin-towered gate where the avenues meet',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'testaccio-belt',
    name: 'Testaccio · Cube-Belt',
    blurb: 'A regular grid of low brick blocks inside a river bend, long iron sheds of a converted slaughterhouse along one side, a grassy mound built entirely of broken pottery, and a white stone pyramid set into the old city wall',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'parioli-belt',
    name: 'Parioli · Cube-Belt',
    blurb: 'Curving avenues of pale rendered villas and mansion blocks laid over a low hill, walled private gardens and pine crowns behind every railing, and playing fields on the flat green floor of the valley below',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'prati-belt',
    name: 'Prati · Cube-Belt',
    blurb: 'A dead-straight grid of tall cream apartment blocks with balconies on every floor, wide shopping streets crossing it at right angles, and radiating avenues meeting at a star-shaped square planted with pines',
    has: { cloud: true, roads: true },
    region: 'rome',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'raffles-place-belt',
    name: 'Raffles Place · Cube-Belt',
    blurb: 'A small railed lawn with a sunken station entrance in the middle of it, dark bank towers standing shoulder to shoulder on all four sides, and a row of two-storey shophouses left along the river bank at their feet',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chinatown-singapore-belt',
    name: 'Chinatown · Cube-Belt',
    blurb: 'Rows of narrow two and three-storey shophouses painted in different colours with shuttered upper windows, a red five-tier temple roof at one corner and a carved tower of figures over a gate opposite, and market awnings filling the side lanes',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tanjong-pagar-belt',
    name: 'Tanjong Pagar · Cube-Belt',
    blurb: 'One tapering tower standing well above everything near it, long unbroken terraces of restored shophouses running away from its foot, a disused station of stone with four carved figures over the entrance, and gantry cranes ranked along the water behind',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'clarke-quay-belt',
    name: 'Clarke Quay · Cube-Belt',
    blurb: 'A bend of narrow river with restored warehouse rows painted bright along both banks under stretched canopies, bumboats moored at the steps, a low green hill of old trees rising just behind, and a flying-saucer disc set on a courthouse roof beyond',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bugis-belt',
    name: 'Bugis & Kampong Glam · Cube-Belt',
    blurb: 'A mosque with a great gold onion dome and two minarets closing the end of a straight shophouse street, textile rolls stacked out onto the pavement, painted walls down a lane one shop wide, and a covered market of stalls packed under one roof nearby',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'little-india-belt',
    name: 'Little India · Cube-Belt',
    blurb: 'Shophouses painted in vivid greens and yellows with cast-iron balconies, a temple gate stacked with painted figures rising over the roofline, garland and produce stalls spilling out of a market hall, and five-foot ways crowded end to end',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'orchard-belt',
    name: 'Orchard · Cube-Belt',
    blurb: 'One long tree-lined shopping street with mall after mall pressed straight up against the pavement, one of them faceted in glass like a cut stone, walkways bridging over the road, and hotel towers rising directly out of their roofs',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'novena-belt',
    name: 'Novena · Cube-Belt',
    blurb: 'A cluster of hospital slabs with linked walkways between them, condominium towers on podium car parks stepping up the slope behind, and a church with a low tiled roof and an open forecourt on the corner',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'geylang-belt',
    name: 'Geylang · Cube-Belt',
    blurb: 'Numbered side lanes running off both sides of one straight main road, two-storey shophouses with tiled fronts and open-air eating houses on every corner, red lanterns strung across the five-foot ways, and a mosque minaret above the roofline',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'katong-belt',
    name: 'Katong & Joo Chiat · Cube-Belt',
    blurb: 'Terraces of two-storey houses with pastel plaster fronts, glazed tiles set in panels under every window and carved timber swing doors, low eaves running unbroken down both sides of the street, and long straight residential roads behind',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bedok-belt',
    name: 'Bedok · Cube-Belt',
    blurb: 'Long housing slabs a dozen storeys high standing in parallel rows on open grass, open ground floors on columns beneath them, a covered hawker centre and bus interchange at the middle, and a flat sheet of reservoir water behind',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'holland-village-belt',
    name: 'Holland Village · Cube-Belt',
    blurb: 'A short strip of two-storey shops with awnings and outdoor tables along one street, low terraces of pale flats behind it, black-and-white timbered bungalows under big rain trees on the roads around, and condominium blocks closing the view',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'clementi-belt',
    name: 'Clementi · Cube-Belt',
    blurb: 'A mall wrapped around a raised railway station with bus bays underneath it, tall pale housing blocks with coloured panels ranked on all sides, a straight concrete canal cutting through, and green corridors of trees between the rows',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bishan-belt',
    name: 'Bishan · Cube-Belt',
    blurb: 'A wide park with a river let loose to wander in a shallow stony bed across mown grass, housing slabs standing back from both banks, a mall and station at one end, and a rail depot of parallel tracks along the far side',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'serangoon-belt',
    name: 'Serangoon · Cube-Belt',
    blurb: 'A large mall slab joined straight onto a station and bus interchange, dense housing blocks stepping away from it in rows, older two-storey terrace houses on narrow lanes beyond, and a broad road cutting the frame in two',
    has: { cloud: true, roads: true },
    region: 'singapore',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'olympic-park-london-belt',
    name: 'Olympic Park · Cube-Belt',
    blurb: 'A great arched stadium bowl on an island between two river channels, a twisting red lattice tower beside it, a wave-roofed swimming hall and a velodrome curving over the parkland north',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'London',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wimbledon-belt',
    name: 'Wimbledon · Cube-Belt',
    blurb: 'Nineteen fenced grass courts laid in ruled rows behind a roofed show court, ivy-walled stands around it, and a golf course and common woodland closing the western side',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'London',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'st-andrews-links-belt',
    name: 'The Links · Cube-Belt',
    blurb: 'Open coastal turf running to the sea with pale bunker scars pocked across it, a stone road bridge over a burn on the closing hole, and a grey stone town gable-on to the fairways',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'St Andrews',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sydney-olympic-park-belt',
    name: 'Sydney Olympic Park · Cube-Belt',
    blurb: 'A vast white-arched main stadium with a domed arena beside it, an aquatic hall along the plaza, ranks of steel light towers, and mangrove flats along the river behind',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Sydney',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'olympic-green-belt',
    name: 'Olympic Green · Cube-Belt',
    blurb: 'A lattice-shell stadium of woven steel facing a bubble-walled swimming box across a wide axis, a long central lawn running north, and a dragon-shaped lake curling behind them',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Beijing',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'jingu-gaien-belt',
    name: 'Jingu Gaien · Cube-Belt',
    blurb: 'A baseball ground with a lattice of floodlights beside a rugby stadium, a ginkgo avenue ruled straight between them, and batting cages and tennis courts packed into the blocks around',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Tokyo',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'olympiapark-munich-belt',
    name: 'Olympiapark · Cube-Belt',
    blurb: 'A sweeping tent roof of glass panels slung on cables over a stadium and a swimming hall, a grassed rubble hill rising behind it, and a slim tower standing over a boating lake',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Munich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'olympic-park-montreal-belt',
    name: 'Parc Olympique · Cube-Belt',
    blurb: 'A leaning concrete mast curving over an oval stadium on cables, a pyramid of stepped terraces beside it, and a domed glass biosphere across the parkland',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Montreal',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'oaka-belt',
    name: 'Olympic Complex · Cube-Belt',
    blurb: 'Two great steel arches carrying a curved roof over a stadium, a velodrome under a second white shell, a wave-walled plaza of steel arches between them, and pines on the ridge behind',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'speedway-belt',
    name: 'The Speedway · Cube-Belt',
    blurb: 'A two-and-a-half mile rectangle of banked track with a golf course laid inside its infield, grandstands running the whole front straight, and a grid of small houses pressed against the outer wall',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Indianapolis',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'barra-olympic-park-belt',
    name: 'Barra Olympic Park · Cube-Belt',
    blurb: 'Four boxy arenas ranked along a paved spine on a spit between two lagoons, a velodrome and a tennis centre behind them, and low green hills across the water',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Rio de Janeiro',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'saint-denis-belt',
    name: 'Saint-Denis · Cube-Belt',
    blurb: 'An elliptical stadium under a floating oval roof ringed by ramps, a canal and rail yards along one side, and dense low blocks running back to a basilica spire',
    has: { cloud: true, roads: true },
    region: 'sport',
    place: 'Paris',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, stadiumStands: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  // ── Computed GAME WORLDS (ADR-0272) ────────────────────────────────────────
  // Generated from a seed by `scripts/lib/worldgen/`, not baked from map data — the
  // places these evoke have no licensable geometry, so the geometry is ours. Each is
  // reproducible: `bake-game-planet --archetype <a> --seed <s> --check` regenerates and
  // byte-compares. They carry no `region`: a computed world is in no city.
  {
    id: 'grounds-complex',
    name: 'The Six Grounds',
    blurb:
      'Six walled competitive grounds on one small world — a desert quarter of two yards and three lanes, a river vale, a canal town on a single bridge, a drop island, an open canyon between two bases, and a field of quantised terraces',
    has: { cloud: true, roads: true, terrain: true },
    game: { archetype: 'grounds-complex', seed: 3, source: 'computed', form: 'cube', modes: ['survival', 'courier', 'rally'] },
    render: { vexag: 1.0, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 1.4, treeEvery: 8 },
  },
  {
    id: 'province-snow-peaks',
    name: 'Frostpeak Province',
    blurb:
      'A province of ridged snow peaks over forested valleys and frozen tarns, a walled town on the valley floor and a road that climbs to the pass',
    has: { cloud: true, roads: true, terrain: true },
    game: { archetype: 'province-snow-peaks', seed: 11, source: 'computed', form: 'cube', modes: ['survival', 'rally', 'courier'] },
    render: { vexag: 1.0, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 1.4, treeEvery: 8 },
  },
  {
    id: 'arena-two-site-desert',
    name: 'Khamsin Quarter',
    blurb:
      'A walled desert quarter of flat roofs and shaded arcades, two open objective yards at opposite ends and three lanes running between them',
    has: { cloud: true, roads: false, terrain: true },
    game: { archetype: 'arena-two-site-desert', seed: 7, source: 'computed', form: 'tile', modes: ['survival', 'courier'] },
    render: { vexag: 1.0, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35, terrainExag: 1.4, treeEvery: 8 },
  },
  {
    id: 'cite-belt',
    name: 'Cité · Vieille-Ville · Cube-Belt',
    blurb: 'A hill of tight old streets under a plain twin-towered cathedral, a river splitting around a mid-stream island below it, quays and a lake mouth opening to the north',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'paquis-belt',
    name: 'Pâquis · Nations · Cube-Belt',
    blurb: 'A lakefront of stone hotels and a long pier, a tall jet of water standing off the far shore, and a boulevard climbing north past embassy lawns to a great glass-and-marble hall',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'eaux-vives-belt',
    name: 'Eaux-Vives · Cube-Belt',
    blurb: 'The south shore of the lake with a long park and a marina, six-storey apartment streets running uphill behind it, and the water opening wide to the north',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'plainpalais-belt',
    name: 'Plainpalais · Jonction · Cube-Belt',
    blurb: 'A vast diamond-shaped plaza of packed gravel, faculty buildings and tram lines around its edges, and two rivers of different colours meeting at a point just beyond',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'champel-belt',
    name: 'Champel · Cube-Belt',
    blurb: 'Leafy avenues of tall apartment houses on a plateau above a river gorge, a hospital campus filling one side, and a footbridge crossing to a wooded bank',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'servette-belt',
    name: 'Servette · Petit-Saconnex · Cube-Belt',
    blurb: 'Long tram avenues lined with courtyard blocks, a football ground in a bowl of housing, and the ground rising gently toward the airport plateau',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'charmilles-belt',
    name: 'Charmilles · Saint-Jean · Cube-Belt',
    blurb: 'Rail yards and a river bend on one side, tight streets of pale housing blocks on the other, and a ridge of workshops turned into studios between them',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'carouge-belt',
    name: 'Carouge · Cube-Belt',
    blurb: 'A low grid of ochre two-storey houses around a square of plane trees, a river along its northern edge, and an old town laid out like a piece of Italy',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lancy-belt',
    name: 'Lancy · Cube-Belt',
    blurb: 'Long housing slabs stepped along a slope, a rail line in a cutting, and playing fields filling the flat ground toward the river',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vernier-belt',
    name: 'Vernier · Cube-Belt',
    blurb: 'Tower estates set in open lawns beside a river loop, a wooded gorge to the south, and the main road running out toward the airport',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'grand-saconnex-belt',
    name: 'Grand-Saconnex · Cointrin · Cube-Belt',
    blurb: 'A runway and terminal roofs on flat ground, a motorway cutting past, and the low pale blocks of an international quarter between the airport and the lake',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'meyrin-belt',
    name: 'Meyrin · CERN · Cube-Belt',
    blurb: 'A planned satellite town of long housing bars on a plain, a ring of laboratory halls and a great domed building at the edge of the fields, and the mountains standing behind',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chene-belt',
    name: 'Chêne-Bougeries · Thônex · Cube-Belt',
    blurb: 'Villa streets under big trees, a tram line running out to a border, and the first vineyards on the slopes beyond the last houses',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cologny-belt',
    name: 'Cologny · Cube-Belt',
    blurb: 'A hillside of walled gardens and villas looking down on the whole lake, one long lawn open to the water, and the city spread on the far shore',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'versoix-belt',
    name: 'Versoix · Cube-Belt',
    blurb: 'A small lakeside town with a harbour of masts, a rail line pinned along the shore, and orchards climbing toward the wooded hills',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'onex-belt',
    name: 'Onex · Bernex · Cube-Belt',
    blurb: 'Housing estates on a plateau edge, a river valley curving below, and market gardens on the flats where the town runs out',
    has: { cloud: true, roads: true },
    region: 'geneva',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'se-belt',
    name: 'Sé · Senado · Cube-Belt',
    blurb: 'A wave-patterned paved square between pastel colonial facades, a cathedral on the rise above it, and narrow shop streets packed tight in every direction',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'santo-antonio-belt',
    name: 'Santo António · Ruínas · Cube-Belt',
    blurb: 'A stone church facade standing alone at the top of a great flight of steps, a fortress on the hill behind it, and old streets falling away below',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sao-lazaro-belt',
    name: 'São Lázaro · Guia · Cube-Belt',
    blurb: 'A wooded hill with a lighthouse and a chapel on its crown, a cable car climbing it, and dense apartment blocks pressed around its foot',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mong-ha-belt',
    name: 'Mong-Há · Toi San · Cube-Belt',
    blurb: 'A small fortified hill in a sea of tightly packed housing, a racecourse to the east, and market streets shoulder to shoulder',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fatima-belt',
    name: 'Fátima · Areia Preta · Cube-Belt',
    blurb: 'The densest quarter of all, tower blocks wall to wall on reclaimed land, a reservoir on one side and the ferry water on the other',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ilha-verde-belt',
    name: 'Ilha Verde · Portas do Cerco · Cube-Belt',
    blurb: 'A gate and a broad plaza where the peninsula meets the mainland, a canal along the border, and housing rising on a hill that was once an island',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'barra-belt',
    name: 'Barra · São Lourenço · Cube-Belt',
    blurb: 'A temple at the foot of a hill by the water, a maritime museum on the point, and a lane of old houses climbing to a fort on the ridge',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nam-van-belt',
    name: 'Nam Van · Praia Grande · Cube-Belt',
    blurb: 'A curved lakefront promenade backed by government buildings and a tower with a glass ring, and two artificial lakes divided by a causeway',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nape-belt',
    name: 'NAPE · Zape · Cube-Belt',
    blurb: 'A gridded reclamation of hotels and casinos in a row, a golden statue on the waterfront, and the long bridges leaving for the islands',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'taipa-belt',
    name: 'Taipa Velha · Cube-Belt',
    blurb: 'A village of narrow lanes and mint-green houses under a hill, five old houses along a former shoreline, and the casino skyline standing up beyond the marsh',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'cotai-belt',
    name: 'Cotai · Cube-Belt',
    blurb: 'A strip of enormous hotel blocks on reclaimed flatland between two islands, a canal and a half-scale tower among them, and the hills of both islands rising at either end',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'coloane-belt',
    name: 'Coloane · Cube-Belt',
    blurb: 'A green island of forested hills and a fishing village of coloured houses on a square, a chapel by the water, and a black-sand beach on the far shore',
    has: { cloud: true, roads: true },
    region: 'macau',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fontvieille-belt',
    name: 'Fontvieille · Cube-Belt',
    blurb: 'A harbour quarter built out into the sea below a cliff, a stadium roof, a rose garden, and a heliport on the reclaimed edge',
    has: { cloud: true, roads: true },
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'monaco-ville-belt',
    name: 'Monaco-Ville · Le Rocher · Cube-Belt',
    blurb: 'A walled old town on a rock above the sea, a palace square at one end, a cathedral and an aquarium on the cliff, and both harbours spread below',
    has: { cloud: true, roads: true },
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'condamine-belt',
    name: 'La Condamine · Port Hercule · Cube-Belt',
    blurb: 'A harbour of white yachts in a deep bay, a market street behind it, and terraces of apartment blocks climbing the slope in every direction',
    has: { cloud: true, roads: true },
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'monte-carlo-belt',
    name: 'Monte-Carlo · Cube-Belt',
    blurb: 'A casino square with gardens above the harbour, hotels stacked along the hill, and a coast road threading under the towers',
    has: { cloud: true, roads: true },
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'larvotto-belt',
    name: 'Larvotto · Saint-Roman · Cube-Belt',
    blurb: 'A beach of imported sand under a wall of high towers, a promenade of cafes, and the cliffs of the next country beginning just past the last block',
    has: { cloud: true, roads: true },
    region: 'monaco',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vaticano-belt',
    name: 'Città del Vaticano · Giardini · Cube-Belt',
    blurb: 'Walled gardens on a hill, the long galleries of a museum along one edge, a great dome rising above them, and a colonnaded square opening east to the city',
    has: { cloud: true, roads: true },
    region: 'vatican',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gion-belt',
    name: 'Gion · Higashiyama · Cube-Belt',
    blurb: 'Lantern-lit lanes of dark wooden teahouses under a hillside of temple roofs, a canal lined with willows, and a great red gate at the foot of the slope',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: {
      vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07,
      streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35,
      roofStyle: {
        common: ['#ad775b', '#bd8a69', '#cc9c75'],
        accents: ['#c45a4a', '#e2b15a'],
        accentRate: 0.10,
      },
      // Authored examples on three existing masses beside a verified ground street.
      // Geographic joins survive bake ordering; this is not surveyed machiya identity.
      machiyaStudy: [
        { lon: 135.777398, lat: 35.001319, quarterTurn: -1, canopyDepthM: 1.6 },
        { lon: 135.777393, lat: 35.001232, quarterTurn: -1, canopyDepthM: 1.6 },
        { lon: 135.777408, lat: 35.001117, quarterTurn: 0, canopyDepthM: 1.6 },
      ],
    },
  },
  {
    id: 'kawaramachi-belt',
    name: 'Kawaramachi · Cube-Belt',
    blurb: 'A wide shallow river with stepping stones and a promenade of restaurant terraces, covered shopping arcades running west, and the downtown grid packed behind',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'karasuma-belt',
    name: 'Shijō-Karasuma · Cube-Belt',
    blurb: 'The business crossing of the old grid — bank blocks and department stores on a straight avenue, machiya townhouses surviving in the side streets, and a temple compound a block away',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kyoto-station-belt',
    name: 'Kyoto Station · Cube-Belt',
    blurb: 'A vast glass-and-steel hall with a tower beside it, a five-storey pagoda rising to the south-west, bus loops and rail yards, and low streets running north into the grid',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'toji-belt',
    name: 'Tōji · Cube-Belt',
    blurb: 'A walled temple with the tallest wooden pagoda in the country standing over rail lines and a quiet residential grid, markets on the compound days',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nijo-belt',
    name: 'Nijō · Cube-Belt',
    blurb: 'A moated castle with white walls and pine gardens filling one block, a subway avenue beside it, and low townhouse streets on every side',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gosho-belt',
    name: 'Gosho · Imperial Park · Cube-Belt',
    blurb: 'A huge walled park of gravel paths and pines with a palace enclosure inside it, university buildings on one edge, and the grid resuming beyond the moat',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nishijin-belt',
    name: 'Nishijin · Cube-Belt',
    blurb: 'The densest weave of wooden townhouses in the city, a textile quarter of narrow lattice-fronted houses, small shrines in every block, and a low hill with a golden temple to the north',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'demachiyanagi-belt',
    name: 'Demachiyanagi · Cube-Belt',
    blurb: 'Two rivers meeting at a wedge of gravel and stepping stones, a shrine in a forest of tall trees on the point between them, and student streets on both banks',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'heian-belt',
    name: 'Heian · Okazaki · Cube-Belt',
    blurb: 'A giant vermilion gate over an avenue, a shrine with sweeping green-tiled roofs, museums and a zoo around a canal, and the eastern hills rising behind',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ginkakuji-belt',
    name: 'Ginkaku-ji · Tetsugaku-no-michi · Cube-Belt',
    blurb: 'A silver pavilion and raked sand garden at the foot of the hills, a cherry-lined canal path running south, and villas climbing the slope',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kiyomizu-belt',
    name: 'Kiyomizu · Cube-Belt',
    blurb: 'A temple on a great wooden stage over a forested ravine, steep stone lanes of shops climbing to it, pagodas on the ridge, and the city spread below',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tofukuji-belt',
    name: 'Tōfuku-ji · Cube-Belt',
    blurb: 'A Zen compound of dark halls and a covered bridge over a maple valley, rail lines on one side, and a hillside of small houses on the other',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fushimi-inari-belt',
    name: 'Fushimi Inari · Cube-Belt',
    blurb: 'Thousands of vermilion gates in tunnels up a wooded mountain, a shrine of fox statues at the foot, and a station town of shops along the tracks',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kinkakuji-belt',
    name: 'Kinkaku-ji · Cube-Belt',
    blurb: 'A gold-leafed pavilion mirrored in a pond of pine islands, a mountain rising behind it, and a quiet residential grid with a rock garden temple nearby',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'arashiyama-belt',
    name: 'Arashiyama · Cube-Belt',
    blurb: 'A wide river gorge with a long wooden bridge, a forest of bamboo on the slope, temple gardens, and boats on the water under a wooded mountain',
    has: { cloud: true, roads: true },
    region: 'kyoto',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'old-town-belt',
    name: 'Staré Město · Cube-Belt',
    blurb: 'A square of gabled facades with an astronomical clock on a tower, twin black spires behind, lanes of arcades and courtyards, and a stone bridge of statues across the river',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'mala-strana-belt',
    name: 'Malá Strana · Cube-Belt',
    blurb: 'Baroque palaces and gardens climbing from the river to a castle wall, a green-domed church over the square, and an island with a mill wheel below the bridge',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hradcany-belt',
    name: 'Hradčany · Cube-Belt',
    blurb: 'A castle on a ridge with a cathedral rising out of its courtyards, a lane of tiny coloured houses along the wall, and monastery gardens looking down on the whole city',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nove-mesto-belt',
    name: 'Nové Město · Cube-Belt',
    blurb: 'A long sloping boulevard ending in a museum on a rise, hotel and department-store facades along it, and a river embankment of tall pastel apartment houses',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vinohrady-belt',
    name: 'Vinohrady · Cube-Belt',
    blurb: 'Broad streets of ornate five-storey apartment houses, a park with a cathedral of glazed brick, and a hilltop water tower over leafy squares',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'zizkov-belt',
    name: 'Žižkov · Cube-Belt',
    blurb: 'A television tower on stilts over a working quarter of tenements, a hill with an equestrian monument and a memorial hall, and pubs on every corner',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'karlin-belt',
    name: 'Karlín · Cube-Belt',
    blurb: 'A flat riverside grid of restored warehouses and new glass offices, a church square in the middle, and a hill of gardens rising at the southern edge',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'holesovice-belt',
    name: 'Holešovice · Cube-Belt',
    blurb: 'A river loop of rail yards, exhibition halls and a market, a big park with a planetarium, and blocks of converted industry along the water',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'letna-belt',
    name: 'Letná · Cube-Belt',
    blurb: 'A plateau park with a giant metronome overlooking the river bends, a stadium and a museum at its edges, and streets of tall apartment houses behind',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'smichov-belt',
    name: 'Smíchov · Cube-Belt',
    blurb: 'A riverside district of brewery yards turned into shops and offices, a hill of villas rising west, and a rail bridge crossing to the old town',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vysehrad-belt',
    name: 'Vyšehrad · Cube-Belt',
    blurb: 'A fortress on a rock over the river with a twin-spired church and a cemetery of writers inside its walls, cubist villas below, and a rail bridge at its foot',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'dejvice-belt',
    name: 'Dejvice · Cube-Belt',
    blurb: 'A star of avenues meeting at a round square, university blocks and embassies in gardens, and the airport road heading out past a hilltop hotel',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nusle-belt',
    name: 'Nusle · Cube-Belt',
    blurb: 'A valley of tenements under a soaring road-and-metro bridge, a brewery on the slope, and terraces of housing climbing both sides',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vrsovice-belt',
    name: 'Vršovice · Cube-Belt',
    blurb: 'Two football grounds in a district of ornate apartment houses, a church in a park on the hill, and tram lines threading the slopes',
    has: { cloud: true, roads: true },
    region: 'prague',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'altstadt-belt',
    name: 'Altstadt · Niederdorf · Cube-Belt',
    blurb: 'A river leaving a lake between two old banks — twin church towers on one, a slender spire and a clock face on the other, guild houses along the quays, and lanes of bars behind',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'bahnhofstrasse-belt',
    name: 'Bahnhofstrasse · Cube-Belt',
    blurb: 'A tree-lined avenue of banks and watch shops running from a great station hall to the lake, tram after tram along its whole length',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'seefeld-belt',
    name: 'Seefeld · Cube-Belt',
    blurb: 'A lakeside promenade of lawns and bathing decks, an opera house on the quay, and streets of tall apartment houses running back to the hill',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'enge-belt',
    name: 'Enge · Cube-Belt',
    blurb: 'A lakeshore park with a Chinese garden and a lido, villas and insurance towers on the slope above, and a rail line in a cutting behind',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kreis-4-belt',
    name: 'Langstrasse · Kreis 4 · Cube-Belt',
    blurb: 'A long street of bars, kebab shops and clubs under a rail viaduct, dense five-storey blocks on both sides, and the station yards to the north',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kreis-5-belt',
    name: 'Zürich-West · Kreis 5 · Cube-Belt',
    blurb: 'A former industrial quarter of a converted shipyard hall, a tower on a factory site, arches of a rail viaduct filled with shops, and a river with a lido',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wiedikon-belt',
    name: 'Wiedikon · Cube-Belt',
    blurb: 'A district of courtyard housing blocks climbing toward a wooded ridge, a market street of small shops, and tram lines converging on a square',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hottingen-belt',
    name: 'Hottingen · Cube-Belt',
    blurb: 'University buildings and a polytechnic terrace over the city, villa streets climbing toward the zoo, and a funicular running up the slope',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'unterstrass-belt',
    name: 'Unterstrass · Cube-Belt',
    blurb: 'A quarter of apartment houses on a hillside above the station yards, a park with a botanical garden, and the main road running north',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'fluntern-belt',
    name: 'Fluntern · Cube-Belt',
    blurb: 'A hilltop of a zoo, a cemetery and a sports ground among villas, a tram line ending at the forest edge, and the city and lake spread below',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wollishofen-belt',
    name: 'Wollishofen · Cube-Belt',
    blurb: 'A lakeside of boatyards and bathing lawns under a rail line, housing estates on the slope, and a ferry pier on the point',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'altstetten-belt',
    name: 'Altstetten · Cube-Belt',
    blurb: 'Rail yards and a market-hall district turning into offices, tower housing on the flats, and vineyards on the slope of the ridge to the south',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hoengg-belt',
    name: 'Höngg · Cube-Belt',
    blurb: 'A village core of a church and old farmhouses on a hill, vineyards falling to the river, and a university campus on the plateau behind',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'oerlikon-belt',
    name: 'Oerlikon · Cube-Belt',
    blurb: 'A rail junction with a new quarter of towers and parks on old factory land, a market square, and the tunnel portal into the hill',
    has: { cloud: true, roads: true },
    region: 'zurich',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'keskusta-belt',
    name: 'Keskusta · Cube-Belt',
    blurb: 'A station with a stone-and-copper tower, a granite square of museums, department stores along the main street, and a park running to the harbour',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kamppi-belt',
    name: 'Kamppi · Cube-Belt',
    blurb: 'A bus terminal and shopping block, a chapel shaped like a wooden bowl on the square, and a church cut into solid rock a few streets north',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kruununhaka-belt',
    name: 'Kruununhaka · Senate Square · Cube-Belt',
    blurb: 'A white cathedral on a great flight of steps over a square of yellow neoclassical buildings, the market by the harbour below, and ferries leaving for the islands',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'punavuori-belt',
    name: 'Punavuori · Eira · Cube-Belt',
    blurb: 'Design shops and cafes in art nouveau streets sloping to the water, a park along the shore, and embassy villas on the southern point',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'toolo-belt',
    name: 'Töölö · Cube-Belt',
    blurb: 'A bay with a marble concert hall and an opera house on its shore, a white granite church, an Olympic stadium tower, and streets of pale apartment houses',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kallio-belt',
    name: 'Kallio · Cube-Belt',
    blurb: 'A working quarter of granite tenements on a hill topped by a grey church, bars along the main street, and a bay of moored boats below',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sornainen-belt',
    name: 'Sörnäinen · Cube-Belt',
    blurb: 'A former harbour of gas holders turned into a culture quarter, new blocks rising on the reclaimed shore, and a bridge to the island suburbs',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'vallila-belt',
    name: 'Vallila · Cube-Belt',
    blurb: 'Wooden villa streets beside a tram depot, brick factory halls converted to offices, and allotment gardens along the rail line',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pasila-belt',
    name: 'Pasila · Cube-Belt',
    blurb: 'A rail hub with a tower over the tracks, exhibition halls, and blocks of concrete housing on the plateau to the east',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'lauttasaari-belt',
    name: 'Lauttasaari · Cube-Belt',
    blurb: 'An island suburb of low apartment houses and pine shore paths, a water tower on its hill, and a bridge carrying the road to the city',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'arabianranta-belt',
    name: 'Arabianranta · Cube-Belt',
    blurb: 'A bay of new waterfront housing around an old ceramics factory, a design school, and reed shores facing the river mouth',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'munkkiniemi-belt',
    name: 'Munkkiniemi · Cube-Belt',
    blurb: 'A tram terminus by a beach, a manor in a park, villas on the shore, and a strait of boats between two green headlands',
    has: { cloud: true, roads: true },
    region: 'helsinki',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'baixa-belt',
    name: 'Baixa · Cube-Belt',
    blurb: 'A grid of straight pale streets rebuilt on a river-mouth flat between two ridges — a great arcaded square open to the water, an iron lift tower over the rooftops, and trams and ferries at the quay',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'alfama-belt',
    name: 'Alfama · Cube-Belt',
    blurb: 'A tangle of stepped lanes and tiled facades climbing from the river to a castle on the ridge, viewpoints of terraces over the rooftops, and a cathedral with two towers at the foot',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'chiado-belt',
    name: 'Chiado · Bairro Alto · Cube-Belt',
    blurb: 'Elegant shopping streets on a slope, a ruined church open to the sky, a funicular up the hill, and a grid of narrow bar streets on the ridge above',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'principe-real-belt',
    name: 'Príncipe Real · Cube-Belt',
    blurb: 'A garden square with a giant spreading tree, pastel mansions around it, a botanical garden falling down the slope, and a viewpoint terrace over the city',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'avenida-belt',
    name: 'Avenida · Marquês · Cube-Belt',
    blurb: 'A broad tree-lined avenue climbing from the old town to a roundabout with a tall column, a park sloping up behind it, and hotel and office blocks along both sides',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'graca-belt',
    name: 'Graça · Cube-Belt',
    blurb: 'A hilltop quarter of a convent and a church on the highest ridge, small squares with viewpoints over the castle and the river, and tram tracks winding up the slope',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'intendente-belt',
    name: 'Intendente · Anjos · Cube-Belt',
    blurb: 'Tiled facades along a long avenue on the flat behind the hills, a square that was reborn as a cafe terrace, and side streets of workshops and markets',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'estrela-belt',
    name: 'Estrela · Lapa · Cube-Belt',
    blurb: 'A white-domed basilica over a garden, embassy mansions on the slope below, and quiet streets of walled villas running down toward the river',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'campo-de-ourique-belt',
    name: 'Campo de Ourique · Cube-Belt',
    blurb: 'A calm grid of low apartment houses on a plateau, a covered market, a cemetery of cypresses at one edge, and the aqueduct arches striding past to the north',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'alcantara-belt',
    name: 'Alcântara · Cube-Belt',
    blurb: 'Docks under the pier of a great red suspension bridge, converted warehouses on the waterfront, a valley of the aqueduct behind, and the river opening west',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'belem-belt',
    name: 'Belém · Cube-Belt',
    blurb: 'A riverfront of lawns and monuments — a fortified tower in the water, a monastery of carved stone, a monument of navigators on the quay, and a modern cultural block beside them',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'saldanha-belt',
    name: 'Saldanha · Cube-Belt',
    blurb: 'An office district around a crossing of avenues, a metro hub, mid-century blocks and towers, and a park with a small lake nearby',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'alvalade-belt',
    name: 'Alvalade · Cube-Belt',
    blurb: 'A planned quarter of tree-lined avenues and low apartment blocks, a stadium at its edge, and a long airport-bound axis running north',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'benfica-belt',
    name: 'Benfica · Cube-Belt',
    blurb: 'A district of housing estates and a big stadium on the northern slopes, a forested park hill rising to the south, and the ring road threading between',
    has: { cloud: true, roads: true },
    region: 'lisbon',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'plaka-belt',
    name: 'Plaka · Acropolis · Cube-Belt',
    blurb: 'A rock with a marble temple standing over lanes of low neoclassical houses, an ancient market of columns at its foot, and cafe steps climbing toward the cliff',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'syntagma-belt',
    name: 'Syntagma · Cube-Belt',
    blurb: 'A square before a former palace with guards in front of it, a national garden of palms and paths behind, and hotel and ministry blocks along the avenues',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'psyrri-belt',
    name: 'Psyrri · Monastiraki · Cube-Belt',
    blurb: 'A flea-market square below the rock, a mosque and a library of ancient columns, narrow streets of workshops turned into bars, and the metro line crossing underneath',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kolonaki-belt',
    name: 'Kolonaki · Cube-Belt',
    blurb: 'Steep boutique streets climbing a pine-covered hill with a chapel at the top, a funicular up the slope, and apartment blocks with roof terraces looking over the basin',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'exarcheia-belt',
    name: 'Exarcheia · Cube-Belt',
    blurb: 'A student quarter of tall painted apartment blocks around a square, a museum of ancient marble at its edge, and a park hill of pines rising behind',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'pangrati-belt',
    name: 'Pangrati · Cube-Belt',
    blurb: 'A marble stadium in a horseshoe of hills, a wooded hill of ancient tombs beside it, and blocks of balconied apartments spreading east',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'koukaki-belt',
    name: 'Koukaki · Cube-Belt',
    blurb: 'Quiet residential streets under the southern slope of the rock, a pedestrian promenade past ancient theatres, a museum of glass and concrete, and a hill of pines with a monument on top',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'petralona-belt',
    name: 'Petralona · Cube-Belt',
    blurb: 'Low houses and tavernas on the lanes below a pine-covered hill, a railway line at the edge, and the rock and its temple in view up every street',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'gazi-belt',
    name: 'Gazi · Kerameikos · Cube-Belt',
    blurb: 'Old gasworks towers turned into a culture park, an ancient cemetery of marble stones beside them, and streets of nightlife blocks around a metro square',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kypseli-belt',
    name: 'Kypseli · Cube-Belt',
    blurb: 'The densest streets of the city — tall balconied apartment blocks along a long pedestrian avenue of trees, a covered market, and small squares between',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ampelokipoi-belt',
    name: 'Ampelokipoi · Cube-Belt',
    blurb: 'A crossing of wide avenues under a tower, hospitals and a stadium among apartment blocks, and a park with a pond at the foot of the hill',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'neos-kosmos-belt',
    name: 'Neos Kosmos · Cube-Belt',
    blurb: 'A tight quarter of apartment blocks on the flat south of the rock, a long avenue toward the sea, a tram line, and a cultural centre with a hanging roof to the south',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'piraeus-belt',
    name: 'Piraeus · Cube-Belt',
    blurb: 'A great port of ferries and cranes around a round harbour, a peninsula of yacht marinas and a stone stadium, and streets of shipping offices climbing the hill',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'glyfada-belt',
    name: 'Glyfada · Cube-Belt',
    blurb: 'A seaside suburb of marinas and beaches, a coastal tram line, shopping streets of low blocks, and a golf course under the mountain',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kifisia-belt',
    name: 'Kifisia · Cube-Belt',
    blurb: 'A leafy northern suburb of villas and gardens, a square of cafes by a small train station, and the mountain rising close behind',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'marousi-belt',
    name: 'Marousi · Cube-Belt',
    blurb: 'An Olympic stadium with white steel arches, office parks along the motorway, and streets of houses with gardens on the plain',
    has: { cloud: true, roads: true },
    region: 'athens',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'umeda-belt',
    name: 'Umeda · Cube-Belt',
    blurb: 'A knot of stations under a cluster of towers, a twin-tower sky garden to the west, bus loops and department-store blocks, and elevated tracks running south into the grid',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nakanoshima-belt',
    name: 'Nakanoshima · Cube-Belt',
    blurb: 'A long island of civic halls and libraries between two rivers, stone quays and weeping willows, and office towers on both banks',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'osaka-castle-belt',
    name: 'Osaka Castle · Cube-Belt',
    blurb: 'A green-roofed keep on a stone mound inside a wide moat of pines, park lawns and a hall on the water, and the downtown towers standing beyond the walls',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'honmachi-belt',
    name: 'Honmachi · Cube-Belt',
    blurb: 'The old merchant grid of straight avenues and mid-rise blocks, a covered arcade, and the castle park opening to the east',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shinsaibashi-belt',
    name: 'Shinsaibashi · Cube-Belt',
    blurb: 'A long covered shopping arcade of low blocks, a canal of neon and restaurant terraces one street south, and department stores on the crossing',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'namba-belt',
    name: 'Namba · Cube-Belt',
    blurb: 'A station of many lines under a maze of covered streets, a canal of signs and a running man on a billboard, and theatres packed along the south edge',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shinsekai-belt',
    name: 'Shinsekai · Cube-Belt',
    blurb: 'A lattice observation tower over a grid of small restaurants and a park of spa buildings, rail lines on one side, and low wooden streets on the other',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tennoji-belt',
    name: 'Tennoji · Abeno · Cube-Belt',
    blurb: 'A park of a zoo and a temple beside a station, and a 300-metre tower standing over a department-store podium to the south',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tempozan-belt',
    name: 'Tempozan · Cube-Belt',
    blurb: 'A harbour-mouth of a giant ferris wheel, an aquarium of a whale-shark hall, and a park of lawns on reclaimed land facing the bay',
    has: { cloud: true, roads: true },
    region: 'osaka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'minato-mirai-belt',
    name: 'Minato Mirai · Cube-Belt',
    blurb: 'Reclaimed harbour land of glass towers and a giant wheel, a sail-shaped hotel, and a strip of piers and parks along the water',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yokohama-station-belt',
    name: 'Yokohama Station · Cube-Belt',
    blurb: 'A vast station of department stores and bus decks, elevated tracks, and streets of mid-rise blocks running down toward the harbour',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kannai-belt',
    name: 'Kannai · Cube-Belt',
    blurb: 'The old port quarter of brick halls and a park, government blocks on a grid, and a chinatown of gates a few streets east',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sakuragicho-belt',
    name: 'Sakuragicho · Cube-Belt',
    blurb: 'A station on the water between the old town and the new towers, a park of a brick warehouse, and a promenade of ships',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'osanbashi-belt',
    name: 'Osanbashi · Cube-Belt',
    blurb: 'A long passenger pier of a waving wooden roof, the harbour opening on three sides, and the skyline of towers across the water',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'motomachi-belt',
    name: 'Motomachi · Chinatown · Cube-Belt',
    blurb: 'A shopping street of low houses under a bluff, a chinatown of painted gates and lanterns, and a harbour park of brick warehouses to the north',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yamate-belt',
    name: 'Yamate · Cube-Belt',
    blurb: 'A wooded bluff of wooden houses and churches above the harbour, a park looking over the bay, and steep lanes dropping to the old town',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'shin-yokohama-belt',
    name: 'Shin-Yokohama · Cube-Belt',
    blurb: 'A shinkansen station on the north plain, a ramen museum block, stadiums and office parks, and housing estates spreading out',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'honmoku-belt',
    name: 'Honmoku · Cube-Belt',
    blurb: 'A garden of a western-style house on a headland, container cranes along the shore, and a bay of ships opening south',
    has: { cloud: true, roads: true },
    region: 'yokohama',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'meieki-belt',
    name: 'Meieki · Cube-Belt',
    blurb: 'A wide station of twin towers and a glass office spire, bus decks and department stores, and a grid of mid-rise blocks spreading east',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nagoya-fushimi-belt',
    name: 'Fushimi · Cube-Belt',
    blurb: 'Office streets between the station and the park of the TV mast, brick warehouses turned galleries, and a canal of willows',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'sakae-belt',
    name: 'Sakae · Cube-Belt',
    blurb: 'A park of fountains and a lattice TV tower, a glass oasis of a hanging roof, and department-store blocks on every side',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nagoya-castle-belt',
    name: 'Nagoya Castle · Cube-Belt',
    blurb: 'A reconstructed keep with golden dolphins on a wide stone base, a palace of painted screens, and a moat of pines wrapping the park',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'osu-belt',
    name: 'Osu · Cube-Belt',
    blurb: 'Covered shopping streets of small shops and temples, a great hall with a lantern, and a grid of low houses around it',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'kanayama-belt',
    name: 'Kanayama · Cube-Belt',
    blurb: 'A south-side station of many lines, a park with a hall, and streets of small factories and houses filling the blocks',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tsurumai-belt',
    name: 'Tsurumai · Cube-Belt',
    blurb: 'A long park of a stadium and rows of cherry trees, a university campus, and a subway avenue of apartment blocks',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'atsuta-belt',
    name: 'Atsuta · Cube-Belt',
    blurb: 'A shrine forest of gravel paths and great trees, a treasure hall, and a station town of low houses along the tracks',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nagoya-marunouchi-belt',
    name: 'Marunouchi · Cube-Belt',
    blurb: 'Government halls and law-court blocks on a grid north of the station, a park of a castle wall in view, and wide avenues of offices',
    has: { cloud: true, roads: true },
    region: 'nagoya',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hakata-station-belt',
    name: 'Hakata Station · Cube-Belt',
    blurb: 'A great hall of shinkansen and local lines, a department-store podium, bus loops, and streets of hotels spreading west toward the river',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'tenjin-belt',
    name: 'Tenjin · Cube-Belt',
    blurb: 'Covered arcades of shops and a bus terminal under office towers, a river of stone quays to the east, and a park of a hall to the west',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'nakasu-belt',
    name: 'Nakasu · Cube-Belt',
    blurb: 'A sandbar of restaurants between two rivers, neon along the water, and bridges packing both downtowns together',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'canal-city-belt',
    name: 'Canal City · Cube-Belt',
    blurb: 'A canal of a shopping canyon through a block of hotels and theatres, and the station streets on one side with the river on the other',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'ohori-belt',
    name: 'Ohori · Cube-Belt',
    blurb: 'A great pond of a former castle moat, a park of pines and a Japanese garden, and quiet streets of houses around the water',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'momochi-belt',
    name: 'Momochi · Cube-Belt',
    blurb: 'Reclaimed sand of a seaside tower, a dome stadium, a beach and a park of lawns, and the bay opening north',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hakata-port-belt',
    name: 'Hakata Port · Cube-Belt',
    blurb: 'Ferry terminals and a park on the harbour, an island of a shrine in the bay, and warehouses along the water',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'yakuin-belt',
    name: 'Yakuin · Cube-Belt',
    blurb: 'A quiet residential grid south of Tenjin, a temple and a shopping street, and tram tracks running toward the river',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'hakata-gion-belt',
    name: 'Hakata Gion · Cube-Belt',
    blurb: 'A shrine of a festival float hall, a temple of a great wooden Buddha, and streets of small houses between the station and the river',
    has: { cloud: true, roads: true },
    region: 'fukuoka',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'erqi-belt',
    name: 'Erqi · HD Cube-Belt',
    blurb:
      'Erqi core as the HD cube-belt — the memorial tower and the station on a 2.56 km front face, about 5 m per cell',
    has: { cloud: true, roads: true, terrain: true },
    region: 'zhengzhou',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'zhengzhou-cube',
    name: 'Zhengzhou · Whole-City Cube',
    blurb:
      'The whole plain city as one planet — a memorial tower at the crossing in Erqi, the railway station just north of it, and a grid of blocks across the middle, about 13.9 m per cell',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'zhengzhou',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'luoyang-cube',
    name: 'Luoyang · Whole-City Cube',
    blurb:
      'The whole basin city as one planet — the old town grid on the front face, the Luo river crossing it, and the Longmen cliffs standing on the south face',
    has: { cloud: true, roads: true, terrain: true },
    level: 4,
    region: 'luoyang',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.05, streetDetail: false, crowdHeight: 0.56, overviewDefault: true, propScale: 0.09 },
  },
  {
    id: 'lijingmen-belt',
    name: 'Lijingmen · Cube-Belt',
    blurb: 'A rebuilt gate tower over the west end of the old town, a straight street of shopfronts running east to a drum tower, and courtyard lanes packed either side of it',
    has: { cloud: true, roads: true },
    region: 'luoyang',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'south-beach-belt',
    name: 'South Beach · Cube-Belt',
    blurb: 'A straight ocean drive of low pastel hotels, a beach of palms on one side and a grid of art-deco fronts on the other',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'miami-beach-belt',
    name: 'Miami Beach · Cube-Belt',
    blurb: 'Mid-beach hotels and a golf course behind a long public sand, the ocean on the east and the bay on the west',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'north-beach-belt',
    name: 'North Beach · Cube-Belt',
    blurb: 'Lower hotels and houses along a quieter beach, a pier, and the bay opening behind the island',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'downtown-miami-belt',
    name: 'Downtown · Cube-Belt',
    blurb: 'A tight cluster of towers on the bay, a cruise port, and the river mouth cutting the grid',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'brickell-belt',
    name: 'Brickell · Cube-Belt',
    blurb: 'A wall of residential towers along the bay, a boulevard of offices, and the river on the north edge',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'wynwood-belt',
    name: 'Wynwood · Cube-Belt',
    blurb: 'Low warehouse blocks painted in big flat colours, a grid of murals, and the expressway on the south',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'design-district-belt',
    name: 'Design District · Cube-Belt',
    blurb: 'Low showroom blocks and a few towers, palm rows, and a straight avenue running north',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'little-havana-belt',
    name: 'Little Havana · Cube-Belt',
    blurb: 'A long commercial street of low shopfronts, courtyard houses, and a river of trees one block south',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'coral-gables-belt',
    name: 'Coral Gables · Cube-Belt',
    blurb: 'A planned grid of Mediterranean houses, a city hall with a tower, and canals cut through the lawns',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'coconut-grove-belt',
    name: 'Coconut Grove · Cube-Belt',
    blurb: 'A village of low shops under large trees, a bay of marinas, and houses climbing a low ridge',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'overtown-belt',
    name: 'Overtown · Cube-Belt',
    blurb: 'A grid of small houses and mid-rise blocks under elevated expressways, with the downtown towers standing to the east',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'edgewater-belt',
    name: 'Edgewater · Cube-Belt',
    blurb: 'A line of bayfront towers and a park on the water, with a grid of low blocks inland',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'little-haiti-belt',
    name: 'Little Haiti · Cube-Belt',
    blurb: 'Low painted houses and a market street, a few churches, and the design district blocks to the south',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'key-biscayne-belt',
    name: 'Key Biscayne · Cube-Belt',
    blurb: 'A causeway over the bay onto a thin island of parks, a beach, and a few low towers',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
  {
    id: 'miami-river-belt',
    name: 'Miami River · Cube-Belt',
    blurb: 'A working river of boatyards and low bridges, warehouses on both banks, and the downtown towers at the mouth',
    has: { cloud: true, roads: true },
    region: 'miami',
    render: { vexag: 1.15, hknee: 2.4, maxU: 8.0, footMaxU: 2.6, outlineA: 0.07, streetDetail: true, crowdHeight: 1.9, overviewDefault: true, propScale: 0.35 },
  },
]; // ── end HD_DISTRICTS

export const DEFAULT_DISTRICT_ID = 'central-belt'; // Central · Cube-Belt — the assembled-belt showcase (default landing for bare /planet)

/** Look up a district by id (null if not in the registry). */
/** Ultra tiles: the street-level-detail crops (10 m/cell + the detail render
 *  passes). Grouped as their own mode in every picker. */
export function isUltraDistrict(d: HdDistrict): boolean {
  return d.render?.streetDetail === true;
}
export const ULTRA_DISTRICTS: ReadonlyArray<HdDistrict> = HD_DISTRICTS.filter(isUltraDistrict);

/** Cube-belt planet: a 6-face real-HK cubed-sphere (id ends `-belt`). */
export function isBeltDistrict(d: HdDistrict): boolean {
  return d.id.endsWith('-belt');
}
/** Meta globe: a whole-territory / whole-island ball (hksar-globe-meta, hksar-meta, hk-island-meta). */
export function isMetaDistrict(d: HdDistrict): boolean {
  return d.id.includes('meta');
}

/** Curated planet-picker groups. Every browsable planet falls into exactly one; a
 *  plain level-1 single-district (no cube-belt / ultra / meta) returns `null` and is
 *  HIDDEN from the pickers (still reachable by direct `?district=` URL). Order = display
 *  order. `belt` first tier so the 6-face cubed spheres lead. */
export type PickerGroupKey =
  | 'globes'
  | 'special'
  | 'belt'
  | 'ultra'
  | 'areas'
  | 'downtown'
  | 'territories'
  | 'games';
/** Superseded 10 m ultras — each place ALSO ships a 5.5 m `-core-ultra` AND a
 *  cube-belt, so listing all three made the picker read as clutter (Central
 *  appeared four times). RETIRED from the picker + the siege stage list only:
 *  the ids stay fully ROUTABLE (direct links, dseek WorldIndex, players'
 *  persisted records) and their bakes stay on disk (hk-island-meta stitches
 *  them as members). Un-retire by removing from this set. */
export const RETIRED_PICKER_IDS: ReadonlySet<string> = new Set([
  // ── 2026-08 fine-BELT pass ────────────────────────────────────────────────
  // Each of these is a single 0.51 km2 azimuthal tile, superseded by a 6-face
  // cube-belt at the SAME 2.80 m/cell covering ~3 km2 — same detail, six times the
  // ground, and you can walk around it instead of off it. Assets stay deep-linkable.
  'kwun-tong-3m',
  'central-3m',
  'tst-3m',
  'mong-kok-3m',
  'causeway-3m',
  // ── 2026-08 fine-tile pass ────────────────────────────────────────────────
  // Both were the last planets on the roster COARSER than the belts above them
  // (11.25 vs 5.6 m/cell), sitting under a heading that promises street detail.
  // Each is now superseded at finer resolution, so the Ultra shelf can finally mean
  // what it says:
  'kwun-tong-ultra', // → kwun-tong-3m at 2.80 m/cell (4x), same ground
  'hkust-bay-ultra', // → hkust-belt at 5.62 m/cell on six faces, same headland
  'central-ultra',
  'mong-kok-ultra',
  'tst-ultra',
  'causeway-bay-ultra',

  // ── 2026-08 HK curation ───────────────────────────────────────────────────
  // Hong Kong listed 64 planets against 16 for every other city, and most of the
  // excess was the SAME GROUND at a worse resolution. Measured by bbox overlap and
  // centre distance on the baked payloads, then hidden here — assets stay committed,
  // index.json stays at 100 HK entries, and every id below stays deep-linkable.
  // Takes the HK picker to 30.

  // Legacy 10 m ultras, each fully enclosed by a cube-belt that is 2x sharper
  // (5.63 vs 11.27 m/cell) and carries 2-10x the buildings. The belt also
  // synthesises courier parcels at load, so the ultra's 3 authored deliveries
  // are not a loss.
  'wan-chai-ultra',
  'sheung-wan-ultra',
  'sham-shui-po-ultra',
  'yau-ma-tei-ultra',
  'north-point-ultra',
  'kennedy-town-ultra',
  'aberdeen-ultra',
  'stanley-ultra',
  'hung-hom-ultra',
  'sai-kung-ultra',
  'the-peak-ultra',
  'cheung-chau-ultra',
  'tuen-mun-ultra',

  // The four flat 5.5 m "core" tiles. Same resolution as their belt but ONE flat
  // face instead of a 6-face ball, a quarter of the buildings, and maxElevation 0.
  'central-core-ultra',
  'tst-core-ultra',
  'mong-kok-core-ultra',
  'causeway-core-ultra',

  // Mislabelled as street-detail: 49.9 / 109.5 / 453.5 m per cell, in a group
  // headed "street-level detail". hk-island-north-ultra is additionally a
  // BYTE-DUPLICATE of hk-island-north (the payloads differ only by the id string).
  'hk-island-north-ultra',
  'hk-island-ultra',
  'hksar-ultra',

  // The whole level-2 "areas" tier, superseded by victoria-cube (10.09 m/cell,
  // 63,669 buildings). central-region at 168.3 m/cell was the coarsest HK planet;
  // victoria-harbour sits 100% inside it with no coast asset and no deliveries.
  'hk-island-north',
  'central-region',
  'victoria-harbour',

  // Same planet twice: identical base and identical 15-member list to hksar-meta,
  // differing only in projection — and the 120-degree cap leaves ~25% of the ball
  // as filler sea where the equirect wrap is 100% real HK.
  'hksar-globe-meta',

  // Downtown tiles enclosed by a FINER cube (12.07 vs victoria-cube's 10.09 m/cell).
  // tile-b2 stays: it is 0% inside any territory cube and is the only Lantau planet
  // left once the two thin Lantau belts below go.
  'tile-b5',
  'tile-b6',
  'tile-b7',
  'tile-c5',
  'tile-c6',
  'tile-c7',

  // Belts too thin to stand on. Fleet median is 5,091 buildings across six faces.
  'airport-belt', // 428 buildings total; face n has 3
  // 'tung-chung-belt' UN-RETIRED 2026-08: retired at 578 buildings (face b had 0). Its
  // building clouds were re-baked (bake-belt-gap) and it now carries 1,285 across six
  // faces with a 528-building landing face — thin, but the only Lantau new-town planet
  // and 2x sharper than tile-b2, the only other thing covering that ground.
  'tsing-ma-belt', // landing face 95.2% water with 6 buildings — you spawn on open sea
  'wong-tai-sin-belt', // no coastline asset; face b has 1 building, 67.3% pinned at the cap
]);

/** What KIND of planet this is, independent of which picker lists it. Region
 *  districts (Tokyo/NYC) classify normally here — a city cube-belt is the same
 *  6-face game stage as an HK one, so Siege/Survival must recognise it. Use this
 *  for capability questions; use pickerGroupOf for "does the HK picker list it". */
export function planetKindOf(d: HdDistrict): PickerGroupKey | null {
  if (RETIRED_PICKER_IDS.has(d.id)) return null; // superseded — hidden, still routable
  // BEFORE everything else. A game planet sets `streetDetail: true` like most belts, so
  // `isUltraDistrict` would happily claim it and file a computed arena under "Ultra ·
  // 10 m street tiles" — a label that would be false twice over.
  if (d.game) return 'games';
  if (isMetaDistrict(d)) return 'globes';
  // Before the scale ladder: a reconstructed place is sorted by WHAT IT IS, not by
  // how many metres a cell covers. This also keeps the scale labels truthful —
  // moving the two Walled City planets out is what lets "Cube-belt · 6-face real HK"
  // and "Ultra · 10 m street tiles" stay accurate for everything left in them.
  if (d.special === true) return 'special';
  // Level 2-4 first, so a big cube-belt (a downtown tile / territory rendered as a
  // 6-face cube) stays in its own scale group instead of the level-1 'belt' group.
  if (d.level === 4) return 'territories';
  if (d.level === 3) return 'downtown';
  if (d.level === 2) return 'areas';
  // Before the belt check: a fine belt IS a cube-belt, so isBeltDistrict would claim it.
  // Resolution is what shelves it, not face count.
  if (d.ultraDetail === true) return 'ultra';
  if (isBeltDistrict(d)) return 'belt'; // level-1 district cube-belts
  if (isUltraDistrict(d)) return 'ultra'; // level-1 ultra 5 m tiles
  return null; // plain single-district → hidden
}

/** Picker group for the HK `/planet` picker. Non-HK region districts are excluded —
 *  they are listed by their own route's picker (districtsForRegion). */
export function pickerGroupOf(d: HdDistrict): PickerGroupKey | null {
  if (d.region || d.game) return null; // listed by their own world's picker
  return planetKindOf(d);
}
export const PICKER_GROUPS: ReadonlyArray<{ key: PickerGroupKey; label: string; icon: string; accent: boolean }> = [
  { key: 'globes', label: 'Hong Kong Globes · whole territory & island', icon: '🌐', accent: true },
  { key: 'special', label: 'Special · places that no longer exist', icon: '🕯', accent: true },
  { key: 'belt', label: 'Cube-belt · 6-face real HK', icon: '⬢', accent: true },
  // The 5.5 m tiles were all retired in the 2026-08 curation (their cube-belts are the
  // same resolution on a 6-face ball). What is left in this group is the two 11.26 m
  // street tiles covering ground no belt reaches, so the label says 10 m — claiming
  // "5 m" for an 11.26 m tile is the next honesty bug waiting to happen.
  { key: 'ultra', label: 'Ultra · 10 m street tiles', icon: '⚡', accent: true },
  { key: 'areas', label: 'Areas · multi-district', icon: '', accent: false },
  { key: 'downtown', label: 'Downtown tiles · big planets', icon: '', accent: false },
  { key: 'territories', label: 'Territories · whole islands · Kowloon', icon: '🏝', accent: false },
  { key: 'games', label: 'Game worlds · computed from a seed', icon: '🎮', accent: true },
];
/** Districts in a given picker group, in registry order. */
export function pickerDistricts(key: PickerGroupKey): HdDistrict[] {
  return HD_DISTRICTS.filter((d) => pickerGroupOf(d) === key);
}

// ── DISPLAY TIERS — the three shelves the HK picker actually browses ──────────
//
// Seven scale groups is a taxonomy for the people who BAKE planets; a player
// browsing /planet is asking a much simpler question — "the whole city, a district
// I can walk, or something unusual?". These three tiers answer that, and the group
// keys above stay exactly as they are underneath.
//
// Deliberately a SEPARATE layer, not a narrowing of PickerGroupKey. That union is
// load-bearing for GAMEPLAY, not just display: PlanetRoute gates Siege/Survival on
// `planetKindOf(d) === 'belt' | 'ultra' | 'special'`, and worlds.ts sorts the 23
// city pickers on `planetKindOf(d) === 'territories'`. Collapsing the keys would
// silently hand Siege to every whole-territory cube (the exact bug regions.test.ts
// guards) or strip it from the Walled City. Tiers group the DISPLAY; kinds keep
// answering "what kind of planet is this".
export type PickerTier = 'cube' | 'belt' | 'special';

/** A labelled shelf INSIDE a tier. Three tiers is the right answer to "what am I
 *  choosing between?", but two of them hold genuinely different things and saying so
 *  costs one line of markup: Cube mixes the territory cubes with the whole-SAR globes,
 *  and Special mixes street-detail ultras (a closer look at ground a belt already
 *  covers) with places that exist nowhere else (the Walled City). A section labelled
 *  `null` renders flat — that is the Belt tier, which is one homogeneous thing. */
export interface TierSection {
  readonly label: string | null;
  readonly kinds: ReadonlyArray<PickerGroupKey>;
}

/** Sections per tier, in display order. The four whole-HK cubes lead Cube (they are what
 *  "Hong Kong" means at a glance); inside Special, ultras come first because they answer
 *  "show me more of the district I am standing in", which is the commoner errand. */
const TIER_SECTIONS: Readonly<Record<PickerTier, ReadonlyArray<TierSection>>> = {
  cube: [
    { label: '🏝 Territories · whole islands · Kowloon', kinds: ['territories'] },
    { label: 'Globes · the whole SAR on one ball', kinds: ['globes'] },
    { label: 'Big tiles', kinds: ['downtown', 'areas'] },
  ],
  belt: [{ label: null, kinds: ['belt'] }],
  special: [
    { label: '⚡ Ultra · street detail inside an HD district', kinds: ['ultra'] },
    { label: '🕯 Lost & one-off places', kinds: ['special'] },
  ],
};

/** Kinds on a tier, flattened in section order. */
const TIER_KINDS: Readonly<Record<PickerTier, ReadonlyArray<PickerGroupKey>>> = {
  cube: TIER_SECTIONS.cube.flatMap((s) => s.kinds),
  belt: TIER_SECTIONS.belt.flatMap((s) => s.kinds),
  special: TIER_SECTIONS.special.flatMap((s) => s.kinds),
};

/** Move the landing planet to the front of a shelf. The other members stay in
 *  registry order. HD_DISTRICTS itself is not touched — bake scripts and the
 *  world index read that array positionally. */
function leadWithLanding(members: HdDistrict[]): HdDistrict[] {
  const index = members.findIndex((d) => d.id === DEFAULT_DISTRICT_ID);
  if (index <= 0) return members;
  const next = members.slice();
  const [landing] = next.splice(index, 1);
  next.unshift(landing!);
  return next;
}

function membersForKinds(kinds: ReadonlyArray<PickerGroupKey>, leadLanding: boolean): HdDistrict[] {
  const members = kinds.flatMap((k) => pickerDistricts(k));
  return leadLanding ? leadWithLanding(members) : members;
}

/** A tier's sections with their members resolved; empty sections are dropped. */
export function tierSections(key: PickerTier): Array<{ label: string | null; members: HdDistrict[] }> {
  return TIER_SECTIONS[key]
    .map((s) => ({ label: s.label, members: membersForKinds(s.kinds, key === 'belt') }))
    .filter((s) => s.members.length > 0);
}

/** Player order for the Hong Kong picker and the top-left dropdown.
 *  The walkable HD districts come first (Central is the landing planet and the
 *  first card), then the ultra / lost-place shelf, then the whole-city cubes. */
export const PICKER_TIERS: ReadonlyArray<{
  key: PickerTier;
  label: string;
  icon: string;
  accent: boolean;
}> = [
  { key: 'belt', label: 'Cube-belt · HD districts', icon: '⬢', accent: true },
  { key: 'special', label: 'Special · ultra detail & lost places', icon: '🕯', accent: true },
  { key: 'cube', label: 'Cube · all Hong Kong', icon: '🌐', accent: true },
];

/**
 * One flat ground sheet instead of a per-cell atlas tile.
 *
 * Every registered planet asks for this — cube-belts, whole-territory balls,
 * other cities, and the specials. The mesh builders honour it on each ground
 * path. A missing id stays on the atlas.
 */
export function wantsSmoothSurface(d: HdDistrict | null | undefined): boolean {
  return d != null;
}

/** Display tier for a district, or null when the picker hides it. */
export function pickerTierOf(d: HdDistrict): PickerTier | null {
  const kind = pickerGroupOf(d);
  if (!kind) return null;
  return (Object.keys(TIER_KINDS) as PickerTier[]).find((t) => TIER_KINDS[t].includes(kind)) ?? null;
}

/** Districts on a tier's shelf — kind by kind in TIER_KINDS order, registry order
 *  within each kind. Concatenating per kind (rather than one filter over the whole
 *  registry) is what puts the territory cubes above the metas without reordering
 *  HD_DISTRICTS, which the bake scripts and the world index both read positionally.
 *  The belt shelf then leads with the landing planet (central-belt). */
export function tierDistricts(key: PickerTier): HdDistrict[] {
  return membersForKinds(TIER_KINDS[key], key === 'belt');
}

/** Meta-planets: a base district planet with ultra crops stitched onto its sphere
 *  at their true geo positions (engine/planetMesh buildMetaPatches). Assembled at
 *  load from these member JSONs — no bake. `base` supplies the connective ground +
 *  building cloud; `members` are the high-detail city cores. */
export interface MetaPlanetSpec {
  /** Base district planet (connective ground + shared projection + coastline). */
  readonly base: string;
  /** Member ids whose data is stitched in (ultra crops OR any baked district). */
  readonly members: readonly string[];
  /** Render each member's OWN building cloud (territory mode) instead of the base's
   *  single cloud (island mode). Default false. */
  readonly memberClouds?: boolean;
  /** Overlay fine 10m/cell ground patches per member (street mode). Default true —
   *  set false for the overview-only territory meta (patches are invisible from
   *  far + heavy at 15+ members). */
  readonly groundPatches?: boolean;
  /** Load each member's fine roads (street mode). Default true; false for territory. */
  readonly memberRoads?: boolean;
  /** Sphere size override (default META_WORLD_SCALE 4.6). */
  readonly worldScale?: number;
  /** BIPOLAR: cover the WHOLE ball — south half on the front hemisphere, north
   *  half on the back (flipped), meeting at the equator. For the whole territory. */
  /** EQUIRECTANGULAR: cover the WHOLE ball — HK's longitude wraps 360° and its
   *  latitude runs pole-to-pole, so every point is real HK (no azimuthal ocean ring).
   *  The whole ball is real HK. */
  readonly equirect?: boolean;
  /** Azimuthal-cap override (degrees) for a GLOBE meta: a LARGE conformal
   *  stereographic cap (~110–125°) shows the whole territory with CORRECT GIS shape
   *  (zero E-W stretch), no pole pinch (the only singularity is the antipode, which is
   *  sea) and no seam — the reference "seamless, zero-pole" ball. The back cap fills
   *  with tidy sea. Only read on non-equirect metas; overrides the base
   *  `_meta.capDeg`. */
  readonly capDeg?: number;
  /** Recentre the globe cap on this (lon,lat) instead of the base focusBbox centroid
   *  (which for whole-HK sits over open water west of Kowloon). Point it at the urban
   *  core / Victoria Harbour so the Island+Kowloon skylines crown the ball. Keeps the
   *  focus EXTENT (scale), only shifts the centre. */
  readonly capCenter?: { readonly lon: number; readonly lat: number };
}

/** Cube-belt planets: all 6 cube faces carry a real HK tile (gnomonic cube-face
 *  windows with abutting bboxes → the whole ball is continuous real HK). `base` is
 *  the +Z FRONT tile (supplies courierStart / nodes / the planet name); `faces` binds
 *  the other 5 tiles to their cube face. Assembled at load (PlanetRoute) — no bake. */
export interface CubeBeltSpec {
  /** The +Z FRONT (face 4) tile district id — the focal district. */
  readonly base: string;
  /** The other 5 faces: 0=+X east · 1=−X west · 2=+Y north cap · 3=−Y south cap · 5=−Z back. */
  readonly faces: readonly { readonly face: number; readonly id: string }[];
  /** Render-time Y-rotation (radians) so the harbour belt frames E-W. */
  readonly bearing?: number;
  /** Grand world scale for a whole-territory cube (e.g. hksar-cube) — the planet renders
   *  big and the player shrinks ÷worldScale into a tiny figure on a vast world. Omit for a
   *  normal district-scale belt (falls back to the GIS planetScaleFor). */
  readonly worldScale?: number;
}

export const CUBE_BELT_PLANETS: Readonly<Record<string, CubeBeltSpec>> = {
  "tsuen-wan-belt": { base: "tsuen-wan-belt-z", faces: [{ face: 0, id: "tsuen-wan-belt-e" }, { face: 1, id: "tsuen-wan-belt-w" }, { face: 2, id: "tsuen-wan-belt-n" }, { face: 3, id: "tsuen-wan-belt-s" }, { face: 5, id: "tsuen-wan-belt-b" }] },
  "kwai-chung-belt": { base: "kwai-chung-belt-z", faces: [{ face: 0, id: "kwai-chung-belt-e" }, { face: 1, id: "kwai-chung-belt-w" }, { face: 2, id: "kwai-chung-belt-n" }, { face: 3, id: "kwai-chung-belt-s" }, { face: 5, id: "kwai-chung-belt-b" }] },
  "ma-on-shan-belt": { base: "ma-on-shan-belt-z", faces: [{ face: 0, id: "ma-on-shan-belt-e" }, { face: 1, id: "ma-on-shan-belt-w" }, { face: 2, id: "ma-on-shan-belt-n" }, { face: 3, id: "ma-on-shan-belt-s" }, { face: 5, id: "ma-on-shan-belt-b" }] },
  "chai-wan-belt": { base: "chai-wan-belt-z", faces: [{ face: 0, id: "chai-wan-belt-e" }, { face: 1, id: "chai-wan-belt-w" }, { face: 2, id: "chai-wan-belt-n" }, { face: 3, id: "chai-wan-belt-s" }, { face: 5, id: "chai-wan-belt-b" }] },
  "tin-shui-wai-belt": { base: "tin-shui-wai-belt-z", faces: [{ face: 0, id: "tin-shui-wai-belt-e" }, { face: 1, id: "tin-shui-wai-belt-w" }, { face: 2, id: "tin-shui-wai-belt-n" }, { face: 3, id: "tin-shui-wai-belt-s" }, { face: 5, id: "tin-shui-wai-belt-b" }] },
  "cuhk-belt": { base: "cuhk-belt-z", faces: [{ face: 0, id: "cuhk-belt-e" }, { face: 1, id: "cuhk-belt-w" }, { face: 2, id: "cuhk-belt-n" }, { face: 3, id: "cuhk-belt-s" }, { face: 5, id: "cuhk-belt-b" }] },
  "hkust-belt": { base: "hkust-belt-z", faces: [{ face: 0, id: "hkust-belt-e" }, { face: 1, id: "hkust-belt-w" }, { face: 2, id: "hkust-belt-n" }, { face: 3, id: "hkust-belt-s" }, { face: 5, id: "hkust-belt-b" }] },
  "disneyland-belt": { base: "disneyland-belt-z", faces: [{ face: 0, id: "disneyland-belt-e" }, { face: 1, id: "disneyland-belt-w" }, { face: 2, id: "disneyland-belt-n" }, { face: 3, id: "disneyland-belt-s" }, { face: 5, id: "disneyland-belt-b" }] },
  "repulse-bay-belt": { base: "repulse-bay-belt-z", faces: [{ face: 0, id: "repulse-bay-belt-e" }, { face: 1, id: "repulse-bay-belt-w" }, { face: 2, id: "repulse-bay-belt-n" }, { face: 3, id: "repulse-bay-belt-s" }, { face: 5, id: "repulse-bay-belt-b" }] },
  "ocean-park-belt": { base: "ocean-park-belt-z", faces: [{ face: 0, id: "ocean-park-belt-e" }, { face: 1, id: "ocean-park-belt-w" }, { face: 2, id: "ocean-park-belt-n" }, { face: 3, id: "ocean-park-belt-s" }, { face: 5, id: "ocean-park-belt-b" }] },
  "kwun-tong-fine": { base: "kwun-tong-fine-z", faces: [{ face: 0, id: "kwun-tong-fine-e" }, { face: 1, id: "kwun-tong-fine-w" }, { face: 2, id: "kwun-tong-fine-n" }, { face: 3, id: "kwun-tong-fine-s" }, { face: 5, id: "kwun-tong-fine-b" }] },
  "central-fine": { base: "central-fine-z", faces: [{ face: 0, id: "central-fine-e" }, { face: 1, id: "central-fine-w" }, { face: 2, id: "central-fine-n" }, { face: 3, id: "central-fine-s" }, { face: 5, id: "central-fine-b" }] },
  "tst-fine": { base: "tst-fine-z", faces: [{ face: 0, id: "tst-fine-e" }, { face: 1, id: "tst-fine-w" }, { face: 2, id: "tst-fine-n" }, { face: 3, id: "tst-fine-s" }, { face: 5, id: "tst-fine-b" }] },
  "mong-kok-fine": { base: "mong-kok-fine-z", faces: [{ face: 0, id: "mong-kok-fine-e" }, { face: 1, id: "mong-kok-fine-w" }, { face: 2, id: "mong-kok-fine-n" }, { face: 3, id: "mong-kok-fine-s" }, { face: 5, id: "mong-kok-fine-b" }] },
  "causeway-fine": { base: "causeway-fine-z", faces: [{ face: 0, id: "causeway-fine-e" }, { face: 1, id: "causeway-fine-w" }, { face: 2, id: "causeway-fine-n" }, { face: 3, id: "causeway-fine-s" }, { face: 5, id: "causeway-fine-b" }] },
  "ngong-ping-fine": { base: "ngong-ping-fine-z", faces: [{ face: 0, id: "ngong-ping-fine-e" }, { face: 1, id: "ngong-ping-fine-w" }, { face: 2, id: "ngong-ping-fine-n" }, { face: 3, id: "ngong-ping-fine-s" }, { face: 5, id: "ngong-ping-fine-b" }] },
  "yuen-long-belt": { base: "yuen-long-belt-z", faces: [{ face: 0, id: "yuen-long-belt-e" }, { face: 1, id: "yuen-long-belt-w" }, { face: 2, id: "yuen-long-belt-n" }, { face: 3, id: "yuen-long-belt-s" }, { face: 5, id: "yuen-long-belt-b" }] },
  "fanling-belt": { base: "fanling-belt-z", faces: [{ face: 0, id: "fanling-belt-e" }, { face: 1, id: "fanling-belt-w" }, { face: 2, id: "fanling-belt-n" }, { face: 3, id: "fanling-belt-s" }, { face: 5, id: "fanling-belt-b" }] },
  "sai-kung-belt": { base: "sai-kung-belt-z", faces: [{ face: 0, id: "sai-kung-belt-e" }, { face: 1, id: "sai-kung-belt-w" }, { face: 2, id: "sai-kung-belt-n" }, { face: 3, id: "sai-kung-belt-s" }, { face: 5, id: "sai-kung-belt-b" }] },
  "cheung-chau-belt": { base: "cheung-chau-belt-z", faces: [{ face: 0, id: "cheung-chau-belt-e" }, { face: 1, id: "cheung-chau-belt-w" }, { face: 2, id: "cheung-chau-belt-n" }, { face: 3, id: "cheung-chau-belt-s" }, { face: 5, id: "cheung-chau-belt-b" }] },
  "tsing-ma-belt": { base: "tsing-ma-belt-z", faces: [{ face: 0, id: "tsing-ma-belt-e" }, { face: 1, id: "tsing-ma-belt-w" }, { face: 2, id: "tsing-ma-belt-n" }, { face: 3, id: "tsing-ma-belt-s" }, { face: 5, id: "tsing-ma-belt-b" }] },
  "airport-belt": { base: "airport-belt-z", faces: [{ face: 0, id: "airport-belt-e" }, { face: 1, id: "airport-belt-w" }, { face: 2, id: "airport-belt-n" }, { face: 3, id: "airport-belt-s" }, { face: 5, id: "airport-belt-b" }] },
  "wan-chai-belt": { base: "wan-chai-belt-z", faces: [{ face: 0, id: "wan-chai-belt-e" }, { face: 1, id: "wan-chai-belt-w" }, { face: 2, id: "wan-chai-belt-n" }, { face: 3, id: "wan-chai-belt-s" }, { face: 5, id: "wan-chai-belt-b" }] },
  // ── 18-district rollout (baked cube-belt planets, central-belt standard) ──
"causeway-belt": { base: "causeway-belt-z", faces: [{ face: 0, id: "causeway-belt-e" }, { face: 1, id: "causeway-belt-w" }, { face: 2, id: "causeway-belt-n" }, { face: 3, id: "causeway-belt-s" }, { face: 5, id: "causeway-belt-b" }] },
  "peak-belt": { base: "peak-belt-z", faces: [{ face: 0, id: "peak-belt-e" }, { face: 1, id: "peak-belt-w" }, { face: 2, id: "peak-belt-n" }, { face: 3, id: "peak-belt-s" }, { face: 5, id: "peak-belt-b" }] },
  "tst-belt": { base: "tst-belt-z", faces: [{ face: 0, id: "tst-belt-e" }, { face: 1, id: "tst-belt-w" }, { face: 2, id: "tst-belt-n" }, { face: 3, id: "tst-belt-s" }, { face: 5, id: "tst-belt-b" }] },
  "mong-kok-belt": { base: "mong-kok-belt-z", faces: [{ face: 0, id: "mong-kok-belt-e" }, { face: 1, id: "mong-kok-belt-w" }, { face: 2, id: "mong-kok-belt-n" }, { face: 3, id: "mong-kok-belt-s" }, { face: 5, id: "mong-kok-belt-b" }] },
  "kowloon-tong-belt": { base: "kowloon-tong-belt-z", faces: [{ face: 0, id: "kowloon-tong-belt-e" }, { face: 1, id: "kowloon-tong-belt-w" }, { face: 2, id: "kowloon-tong-belt-n" }, { face: 3, id: "kowloon-tong-belt-s" }, { face: 5, id: "kowloon-tong-belt-b" }] },
  "kai-tak-belt": { base: "kai-tak-belt-z", faces: [{ face: 0, id: "kai-tak-belt-e" }, { face: 1, id: "kai-tak-belt-w" }, { face: 2, id: "kai-tak-belt-n" }, { face: 3, id: "kai-tak-belt-s" }, { face: 5, id: "kai-tak-belt-b" }] },
  "kowloon-walled-city-belt": { base: "kowloon-walled-city-belt-z", faces: [{ face: 0, id: "kowloon-walled-city-belt-e" }, { face: 1, id: "kowloon-walled-city-belt-w" }, { face: 2, id: "kowloon-walled-city-belt-n" }, { face: 3, id: "kowloon-walled-city-belt-s" }, { face: 5, id: "kowloon-walled-city-belt-b" }] },
  "sham-shui-po-belt": { base: "sham-shui-po-belt-z", faces: [{ face: 0, id: "sham-shui-po-belt-e" }, { face: 1, id: "sham-shui-po-belt-w" }, { face: 2, id: "sham-shui-po-belt-n" }, { face: 3, id: "sham-shui-po-belt-s" }, { face: 5, id: "sham-shui-po-belt-b" }] },
  "aberdeen-belt": { base: "aberdeen-belt-z", faces: [{ face: 0, id: "aberdeen-belt-e" }, { face: 1, id: "aberdeen-belt-w" }, { face: 2, id: "aberdeen-belt-n" }, { face: 3, id: "aberdeen-belt-s" }, { face: 5, id: "aberdeen-belt-b" }] },
  "stanley-belt": { base: "stanley-belt-z", faces: [{ face: 0, id: "stanley-belt-e" }, { face: 1, id: "stanley-belt-w" }, { face: 2, id: "stanley-belt-n" }, { face: 3, id: "stanley-belt-s" }, { face: 5, id: "stanley-belt-b" }] },
  "shatin-belt": { base: "shatin-belt-z", faces: [{ face: 0, id: "shatin-belt-e" }, { face: 1, id: "shatin-belt-w" }, { face: 2, id: "shatin-belt-n" }, { face: 3, id: "shatin-belt-s" }, { face: 5, id: "shatin-belt-b" }] },
  "tuen-mun-belt": { base: "tuen-mun-belt-z", faces: [{ face: 0, id: "tuen-mun-belt-e" }, { face: 1, id: "tuen-mun-belt-w" }, { face: 2, id: "tuen-mun-belt-n" }, { face: 3, id: "tuen-mun-belt-s" }, { face: 5, id: "tuen-mun-belt-b" }] },
  // Central pilot: the Island north-shore harbourfront IS the equator. Front = Central;
  // the belt runs Sheung Wan → Central → Admiralty → North Point (E-W); the north cap
  // is Victoria Harbour → TST, the south cap climbs Mid-Levels → The Peak.
  'central-belt': {
    base: 'central-belt-z', // +Z front = Central
    // BIG roam-around 5.5m showcase: render this uniform-256 (5.5m/cell) 6-face cube on a
    // GRAND sphere so you roam the whole real-HK belt at 5.5m on a genuinely big planet
    // (no seam weld needed — uniform faceSize). worldScale > 2.5 triggers the overview
    // pull-back; props/crowd are divided back out in the render tune.
    worldScale: 4.0,
    faces: [
      { face: 0, id: 'central-belt-e' }, // +X east  = Admiralty / Wan Chai
      { face: 1, id: 'central-belt-w' }, // -X west  = Sheung Wan
      { face: 2, id: 'central-belt-n' }, // +Y ncap  = Victoria Harbour → TST
      { face: 3, id: 'central-belt-s' }, // -Y scap  = Mid-Levels → The Peak
      { face: 5, id: 'central-belt-b' }, // -Z back  = Causeway Bay / North Point
    ],
  },
  // Kennedy Town & HKU — 5.5m/cell + real terrain. Front = Kennedy Town waterfront;
  // E = HKU / Sai Ying Pun, W = Green Island / Mount Davis, +Y cap = Victoria Harbour,
  // -Y cap = Pok Fu Lam / HKU hills (climbable DTM), back = Sheung Wan.
  'kennedy-town-belt': {
    base: 'kennedy-town-belt-z', // +Z front = Kennedy Town + praya
    faces: [
      { face: 0, id: 'kennedy-town-belt-e' }, // +X east  = HKU / Sai Ying Pun
      { face: 1, id: 'kennedy-town-belt-w' }, // -X west  = Green Island / Mount Davis
      { face: 2, id: 'kennedy-town-belt-n' }, // +Y ncap  = Victoria Harbour
      { face: 3, id: 'kennedy-town-belt-s' }, // -Y scap  = Pok Fu Lam / HKU hills
      { face: 5, id: 'kennedy-town-belt-b' }, // -Z back  = Sheung Wan
    ],
  },
  // ── Big-planet cube-belts: Downtown tiles + Territories rendered as 6-face cubes.
  //    ids stay tile-*/hk-island/kowloon (NOT `-belt`) so pickerGroupOf keeps them in the
  //    Downtown/Territories scale groups. Bigger diameter per face (bake-belt-big.mjs) so
  //    all 6 faces hit real HK; faceSize 256 → ~12 m/cell (tiles) / ~16 m/cell (territory).
  'tile-c6': {
    base: 'tile-c6-z', // Island Core — HK Island
    faces: [
      { face: 0, id: 'tile-c6-e' },
      { face: 1, id: 'tile-c6-w' },
      { face: 2, id: 'tile-c6-n' },
      { face: 3, id: 'tile-c6-s' },
      { face: 5, id: 'tile-c6-b' },
    ],
  },
  'tile-b6': {
    base: 'tile-b6-z', // Kowloon Heart — Mong Kok / Yau Ma Tei
    faces: [
      { face: 0, id: 'tile-b6-e' },
      { face: 1, id: 'tile-b6-w' },
      { face: 2, id: 'tile-b6-n' },
      { face: 3, id: 'tile-b6-s' },
      { face: 5, id: 'tile-b6-b' },
    ],
  },
  // Remaining Downtown big-planet tiles + the 2 Territories (all 6-face cubes, urban-core-centred).
  'tile-b2': { base: 'tile-b2-z', faces: [{ face: 0, id: 'tile-b2-e' }, { face: 1, id: 'tile-b2-w' }, { face: 2, id: 'tile-b2-n' }, { face: 3, id: 'tile-b2-s' }, { face: 5, id: 'tile-b2-b' }] },
  'tile-b5': { base: 'tile-b5-z', faces: [{ face: 0, id: 'tile-b5-e' }, { face: 1, id: 'tile-b5-w' }, { face: 2, id: 'tile-b5-n' }, { face: 3, id: 'tile-b5-s' }, { face: 5, id: 'tile-b5-b' }] },
  'tile-b7': { base: 'tile-b7-z', faces: [{ face: 0, id: 'tile-b7-e' }, { face: 1, id: 'tile-b7-w' }, { face: 2, id: 'tile-b7-n' }, { face: 3, id: 'tile-b7-s' }, { face: 5, id: 'tile-b7-b' }] },
  'tile-c5': { base: 'tile-c5-z', faces: [{ face: 0, id: 'tile-c5-e' }, { face: 1, id: 'tile-c5-w' }, { face: 2, id: 'tile-c5-n' }, { face: 3, id: 'tile-c5-s' }, { face: 5, id: 'tile-c5-b' }] },
  'tile-c7': { base: 'tile-c7-z', faces: [{ face: 0, id: 'tile-c7-e' }, { face: 1, id: 'tile-c7-w' }, { face: 2, id: 'tile-c7-n' }, { face: 3, id: 'tile-c7-s' }, { face: 5, id: 'tile-c7-b' }] },
  'hk-island': { base: 'hk-island-z', faces: [{ face: 0, id: 'hk-island-e' }, { face: 1, id: 'hk-island-w' }, { face: 2, id: 'hk-island-n' }, { face: 3, id: 'hk-island-s' }, { face: 5, id: 'hk-island-b' }] },
  'kowloon': { base: 'kowloon-z', faces: [{ face: 0, id: 'kowloon-e' }, { face: 1, id: 'kowloon-w' }, { face: 2, id: 'kowloon-n' }, { face: 3, id: 'kowloon-s' }, { face: 5, id: 'kowloon-b' }] },
  // The whole-HKSAR "All HK" cube — 6 faces at faceSize 512, ~24 m/cell, covering harbour +
  // Kowloon + HK Island + inner NT + the Sai Kung islands, with ground/coast/terrain, building
  // clouds and the full overlay set. Uniform 5.5 m over the whole territory would be ~57M cells
  // (un-renderable), so true 5.5 m stays on drill-down into a region's own belt.
  // Re-geometried to kill a blank back face — see the HD_DISTRICTS entry for the why. Belt
  // geometry tiles a cross, not a square, so the far NW (Tuen Mun/Yuen Long) and far-west
  // Lantau are reached via the picker, not this cube.
  // worldScale 4.0 renders it as the same grand roam-around ball as the city cubes.
  'victoria-cube': { base: 'victoria-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'victoria-cube-e' }, { face: 1, id: 'victoria-cube-w' }, { face: 2, id: 'victoria-cube-n' }, { face: 3, id: 'victoria-cube-s' }, { face: 5, id: 'victoria-cube-b' }] },
  'hksar-cube': { base: 'hksar-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'hksar-cube-e' }, { face: 1, id: 'hksar-cube-w' }, { face: 2, id: 'hksar-cube-n' }, { face: 3, id: 'hksar-cube-s' }, { face: 5, id: 'hksar-cube-b' }] },

  // ── Global-city cube-belts (scripts/bake-city-fleet.mjs) ──
  // Identical 6-face contract to the HK belts — only the BAKE is city-generic
  // (dLon = dLat/cos(lat)). worldScale 4.0 = the same grand roam-around planet.
  // Tokyo:
  'shinjuku-belt': { base: 'shinjuku-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shinjuku-belt-e' }, { face: 1, id: 'shinjuku-belt-w' }, { face: 2, id: 'shinjuku-belt-n' }, { face: 3, id: 'shinjuku-belt-s' }, { face: 5, id: 'shinjuku-belt-b' }] },
  'shibuya-belt': { base: 'shibuya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shibuya-belt-e' }, { face: 1, id: 'shibuya-belt-w' }, { face: 2, id: 'shibuya-belt-n' }, { face: 3, id: 'shibuya-belt-s' }, { face: 5, id: 'shibuya-belt-b' }] },
  'shibuya-line-belt': { base: 'shibuya-line-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shibuya-line-belt-e' }, { face: 1, id: 'shibuya-line-belt-w' }, { face: 2, id: 'shibuya-line-belt-n' }, { face: 3, id: 'shibuya-line-belt-s' }, { face: 5, id: 'shibuya-line-belt-b' }] },
  'ginza-belt': { base: 'ginza-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ginza-belt-e' }, { face: 1, id: 'ginza-belt-w' }, { face: 2, id: 'ginza-belt-n' }, { face: 3, id: 'ginza-belt-s' }, { face: 5, id: 'ginza-belt-b' }] },
  'asakusa-belt': { base: 'asakusa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'asakusa-belt-e' }, { face: 1, id: 'asakusa-belt-w' }, { face: 2, id: 'asakusa-belt-n' }, { face: 3, id: 'asakusa-belt-s' }, { face: 5, id: 'asakusa-belt-b' }] },
  'ueno-belt': { base: 'ueno-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ueno-belt-e' }, { face: 1, id: 'ueno-belt-w' }, { face: 2, id: 'ueno-belt-n' }, { face: 3, id: 'ueno-belt-s' }, { face: 5, id: 'ueno-belt-b' }] },
  'ikebukuro-belt': { base: 'ikebukuro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ikebukuro-belt-e' }, { face: 1, id: 'ikebukuro-belt-w' }, { face: 2, id: 'ikebukuro-belt-n' }, { face: 3, id: 'ikebukuro-belt-s' }, { face: 5, id: 'ikebukuro-belt-b' }] },
  'akihabara-belt': { base: 'akihabara-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'akihabara-belt-e' }, { face: 1, id: 'akihabara-belt-w' }, { face: 2, id: 'akihabara-belt-n' }, { face: 3, id: 'akihabara-belt-s' }, { face: 5, id: 'akihabara-belt-b' }] },
  'roppongi-belt': { base: 'roppongi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'roppongi-belt-e' }, { face: 1, id: 'roppongi-belt-w' }, { face: 2, id: 'roppongi-belt-n' }, { face: 3, id: 'roppongi-belt-s' }, { face: 5, id: 'roppongi-belt-b' }] },
  'odaiba-belt': { base: 'odaiba-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'odaiba-belt-e' }, { face: 1, id: 'odaiba-belt-w' }, { face: 2, id: 'odaiba-belt-n' }, { face: 3, id: 'odaiba-belt-s' }, { face: 5, id: 'odaiba-belt-b' }] },
  'shinagawa-belt': { base: 'shinagawa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shinagawa-belt-e' }, { face: 1, id: 'shinagawa-belt-w' }, { face: 2, id: 'shinagawa-belt-n' }, { face: 3, id: 'shinagawa-belt-s' }, { face: 5, id: 'shinagawa-belt-b' }] },
  // New York:
  'manhattan-belt': { base: 'manhattan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'manhattan-belt-e' }, { face: 1, id: 'manhattan-belt-w' }, { face: 2, id: 'manhattan-belt-n' }, { face: 3, id: 'manhattan-belt-s' }, { face: 5, id: 'manhattan-belt-b' }] },
  'midtown-belt': { base: 'midtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'midtown-belt-e' }, { face: 1, id: 'midtown-belt-w' }, { face: 2, id: 'midtown-belt-n' }, { face: 3, id: 'midtown-belt-s' }, { face: 5, id: 'midtown-belt-b' }] },
  'central-park-belt': { base: 'central-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'central-park-belt-e' }, { face: 1, id: 'central-park-belt-w' }, { face: 2, id: 'central-park-belt-n' }, { face: 3, id: 'central-park-belt-s' }, { face: 5, id: 'central-park-belt-b' }] },
  'harlem-belt': { base: 'harlem-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'harlem-belt-e' }, { face: 1, id: 'harlem-belt-w' }, { face: 2, id: 'harlem-belt-n' }, { face: 3, id: 'harlem-belt-s' }, { face: 5, id: 'harlem-belt-b' }] },
  'upper-west-belt': { base: 'upper-west-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'upper-west-belt-e' }, { face: 1, id: 'upper-west-belt-w' }, { face: 2, id: 'upper-west-belt-n' }, { face: 3, id: 'upper-west-belt-s' }, { face: 5, id: 'upper-west-belt-b' }] },
  'brooklyn-heights-belt': { base: 'brooklyn-heights-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'brooklyn-heights-belt-e' }, { face: 1, id: 'brooklyn-heights-belt-w' }, { face: 2, id: 'brooklyn-heights-belt-n' }, { face: 3, id: 'brooklyn-heights-belt-s' }, { face: 5, id: 'brooklyn-heights-belt-b' }] },
  'dumbo-belt': { base: 'dumbo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dumbo-belt-e' }, { face: 1, id: 'dumbo-belt-w' }, { face: 2, id: 'dumbo-belt-n' }, { face: 3, id: 'dumbo-belt-s' }, { face: 5, id: 'dumbo-belt-b' }] },
  'williamsburg-belt': { base: 'williamsburg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'williamsburg-belt-e' }, { face: 1, id: 'williamsburg-belt-w' }, { face: 2, id: 'williamsburg-belt-n' }, { face: 3, id: 'williamsburg-belt-s' }, { face: 5, id: 'williamsburg-belt-b' }] },
  'lic-belt': { base: 'lic-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lic-belt-e' }, { face: 1, id: 'lic-belt-w' }, { face: 2, id: 'lic-belt-n' }, { face: 3, id: 'lic-belt-s' }, { face: 5, id: 'lic-belt-b' }] },
  'red-hook-belt': { base: 'red-hook-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'red-hook-belt-e' }, { face: 1, id: 'red-hook-belt-w' }, { face: 2, id: 'red-hook-belt-n' }, { face: 3, id: 'red-hook-belt-s' }, { face: 5, id: 'red-hook-belt-b' }] },

  // Paris:
  'ile-de-la-cite-belt': { base: 'ile-de-la-cite-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ile-de-la-cite-belt-e' }, { face: 1, id: 'ile-de-la-cite-belt-w' }, { face: 2, id: 'ile-de-la-cite-belt-n' }, { face: 3, id: 'ile-de-la-cite-belt-s' }, { face: 5, id: 'ile-de-la-cite-belt-b' }] },
  'louvre-belt': { base: 'louvre-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'louvre-belt-e' }, { face: 1, id: 'louvre-belt-w' }, { face: 2, id: 'louvre-belt-n' }, { face: 3, id: 'louvre-belt-s' }, { face: 5, id: 'louvre-belt-b' }] },
  'marais-belt': { base: 'marais-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marais-belt-e' }, { face: 1, id: 'marais-belt-w' }, { face: 2, id: 'marais-belt-n' }, { face: 3, id: 'marais-belt-s' }, { face: 5, id: 'marais-belt-b' }] },
  'latin-quarter-belt': { base: 'latin-quarter-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'latin-quarter-belt-e' }, { face: 1, id: 'latin-quarter-belt-w' }, { face: 2, id: 'latin-quarter-belt-n' }, { face: 3, id: 'latin-quarter-belt-s' }, { face: 5, id: 'latin-quarter-belt-b' }] },
  'eiffel-belt': { base: 'eiffel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'eiffel-belt-e' }, { face: 1, id: 'eiffel-belt-w' }, { face: 2, id: 'eiffel-belt-n' }, { face: 3, id: 'eiffel-belt-s' }, { face: 5, id: 'eiffel-belt-b' }] },
  'montmartre-belt': { base: 'montmartre-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'montmartre-belt-e' }, { face: 1, id: 'montmartre-belt-w' }, { face: 2, id: 'montmartre-belt-n' }, { face: 3, id: 'montmartre-belt-s' }, { face: 5, id: 'montmartre-belt-b' }] },
  'opera-belt': { base: 'opera-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'opera-belt-e' }, { face: 1, id: 'opera-belt-w' }, { face: 2, id: 'opera-belt-n' }, { face: 3, id: 'opera-belt-s' }, { face: 5, id: 'opera-belt-b' }] },
  'bastille-belt': { base: 'bastille-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bastille-belt-e' }, { face: 1, id: 'bastille-belt-w' }, { face: 2, id: 'bastille-belt-n' }, { face: 3, id: 'bastille-belt-s' }, { face: 5, id: 'bastille-belt-b' }] },
  'saint-germain-belt': { base: 'saint-germain-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'saint-germain-belt-e' }, { face: 1, id: 'saint-germain-belt-w' }, { face: 2, id: 'saint-germain-belt-n' }, { face: 3, id: 'saint-germain-belt-s' }, { face: 5, id: 'saint-germain-belt-b' }] },
  'la-defense-belt': { base: 'la-defense-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'la-defense-belt-e' }, { face: 1, id: 'la-defense-belt-w' }, { face: 2, id: 'la-defense-belt-n' }, { face: 3, id: 'la-defense-belt-s' }, { face: 5, id: 'la-defense-belt-b' }] },
  // Amsterdam:
  'grachtengordel-belt': { base: 'grachtengordel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'grachtengordel-belt-e' }, { face: 1, id: 'grachtengordel-belt-w' }, { face: 2, id: 'grachtengordel-belt-n' }, { face: 3, id: 'grachtengordel-belt-s' }, { face: 5, id: 'grachtengordel-belt-b' }] },
  'jordaan-belt': { base: 'jordaan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jordaan-belt-e' }, { face: 1, id: 'jordaan-belt-w' }, { face: 2, id: 'jordaan-belt-n' }, { face: 3, id: 'jordaan-belt-s' }, { face: 5, id: 'jordaan-belt-b' }] },
  'centraal-belt': { base: 'centraal-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'centraal-belt-e' }, { face: 1, id: 'centraal-belt-w' }, { face: 2, id: 'centraal-belt-n' }, { face: 3, id: 'centraal-belt-s' }, { face: 5, id: 'centraal-belt-b' }] },
  'de-pijp-belt': { base: 'de-pijp-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'de-pijp-belt-e' }, { face: 1, id: 'de-pijp-belt-w' }, { face: 2, id: 'de-pijp-belt-n' }, { face: 3, id: 'de-pijp-belt-s' }, { face: 5, id: 'de-pijp-belt-b' }] },
  'oud-west-belt': { base: 'oud-west-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'oud-west-belt-e' }, { face: 1, id: 'oud-west-belt-w' }, { face: 2, id: 'oud-west-belt-n' }, { face: 3, id: 'oud-west-belt-s' }, { face: 5, id: 'oud-west-belt-b' }] },
  'oostelijk-belt': { base: 'oostelijk-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'oostelijk-belt-e' }, { face: 1, id: 'oostelijk-belt-w' }, { face: 2, id: 'oostelijk-belt-n' }, { face: 3, id: 'oostelijk-belt-s' }, { face: 5, id: 'oostelijk-belt-b' }] },
  'museumplein-belt': { base: 'museumplein-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'museumplein-belt-e' }, { face: 1, id: 'museumplein-belt-w' }, { face: 2, id: 'museumplein-belt-n' }, { face: 3, id: 'museumplein-belt-s' }, { face: 5, id: 'museumplein-belt-b' }] },
  'nieuwmarkt-belt': { base: 'nieuwmarkt-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nieuwmarkt-belt-e' }, { face: 1, id: 'nieuwmarkt-belt-w' }, { face: 2, id: 'nieuwmarkt-belt-n' }, { face: 3, id: 'nieuwmarkt-belt-s' }, { face: 5, id: 'nieuwmarkt-belt-b' }] },
  'westerpark-belt': { base: 'westerpark-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'westerpark-belt-e' }, { face: 1, id: 'westerpark-belt-w' }, { face: 2, id: 'westerpark-belt-n' }, { face: 3, id: 'westerpark-belt-s' }, { face: 5, id: 'westerpark-belt-b' }] },
  'noord-belt': { base: 'noord-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'noord-belt-e' }, { face: 1, id: 'noord-belt-w' }, { face: 2, id: 'noord-belt-n' }, { face: 3, id: 'noord-belt-s' }, { face: 5, id: 'noord-belt-b' }] },
  // Rotterdam:
  'kop-van-zuid-belt': { base: 'kop-van-zuid-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kop-van-zuid-belt-e' }, { face: 1, id: 'kop-van-zuid-belt-w' }, { face: 2, id: 'kop-van-zuid-belt-n' }, { face: 3, id: 'kop-van-zuid-belt-s' }, { face: 5, id: 'kop-van-zuid-belt-b' }] },
  'centrum-belt': { base: 'centrum-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'centrum-belt-e' }, { face: 1, id: 'centrum-belt-w' }, { face: 2, id: 'centrum-belt-n' }, { face: 3, id: 'centrum-belt-s' }, { face: 5, id: 'centrum-belt-b' }] },
  'erasmusbrug-belt': { base: 'erasmusbrug-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'erasmusbrug-belt-e' }, { face: 1, id: 'erasmusbrug-belt-w' }, { face: 2, id: 'erasmusbrug-belt-n' }, { face: 3, id: 'erasmusbrug-belt-s' }, { face: 5, id: 'erasmusbrug-belt-b' }] },
  'delfshaven-belt': { base: 'delfshaven-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'delfshaven-belt-e' }, { face: 1, id: 'delfshaven-belt-w' }, { face: 2, id: 'delfshaven-belt-n' }, { face: 3, id: 'delfshaven-belt-s' }, { face: 5, id: 'delfshaven-belt-b' }] },
  'kralingen-belt': { base: 'kralingen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kralingen-belt-e' }, { face: 1, id: 'kralingen-belt-w' }, { face: 2, id: 'kralingen-belt-n' }, { face: 3, id: 'kralingen-belt-s' }, { face: 5, id: 'kralingen-belt-b' }] },
  'noordereiland-belt': { base: 'noordereiland-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'noordereiland-belt-e' }, { face: 1, id: 'noordereiland-belt-w' }, { face: 2, id: 'noordereiland-belt-n' }, { face: 3, id: 'noordereiland-belt-s' }, { face: 5, id: 'noordereiland-belt-b' }] },
  'katendrecht-belt': { base: 'katendrecht-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'katendrecht-belt-e' }, { face: 1, id: 'katendrecht-belt-w' }, { face: 2, id: 'katendrecht-belt-n' }, { face: 3, id: 'katendrecht-belt-s' }, { face: 5, id: 'katendrecht-belt-b' }] },
  'blijdorp-belt': { base: 'blijdorp-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'blijdorp-belt-e' }, { face: 1, id: 'blijdorp-belt-w' }, { face: 2, id: 'blijdorp-belt-n' }, { face: 3, id: 'blijdorp-belt-s' }, { face: 5, id: 'blijdorp-belt-b' }] },
  'feijenoord-belt': { base: 'feijenoord-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'feijenoord-belt-e' }, { face: 1, id: 'feijenoord-belt-w' }, { face: 2, id: 'feijenoord-belt-n' }, { face: 3, id: 'feijenoord-belt-s' }, { face: 5, id: 'feijenoord-belt-b' }] },
  'europoort-belt': { base: 'europoort-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'europoort-belt-e' }, { face: 1, id: 'europoort-belt-w' }, { face: 2, id: 'europoort-belt-n' }, { face: 3, id: 'europoort-belt-s' }, { face: 5, id: 'europoort-belt-b' }] },
  'tai-po-belt': { base: 'tai-po-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tai-po-belt-e' }, { face: 1, id: 'tai-po-belt-w' }, { face: 2, id: 'tai-po-belt-n' }, { face: 3, id: 'tai-po-belt-s' }, { face: 5, id: 'tai-po-belt-b' }] },
  'tseung-kwan-o-belt': { base: 'tseung-kwan-o-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tseung-kwan-o-belt-e' }, { face: 1, id: 'tseung-kwan-o-belt-w' }, { face: 2, id: 'tseung-kwan-o-belt-n' }, { face: 3, id: 'tseung-kwan-o-belt-s' }, { face: 5, id: 'tseung-kwan-o-belt-b' }] },
  'quarry-bay-belt': { base: 'quarry-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'quarry-bay-belt-e' }, { face: 1, id: 'quarry-bay-belt-w' }, { face: 2, id: 'quarry-bay-belt-n' }, { face: 3, id: 'quarry-bay-belt-s' }, { face: 5, id: 'quarry-bay-belt-b' }] },
  'koenji-belt': { base: 'koenji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'koenji-belt-e' }, { face: 1, id: 'koenji-belt-w' }, { face: 2, id: 'koenji-belt-n' }, { face: 3, id: 'koenji-belt-s' }, { face: 5, id: 'koenji-belt-b' }] },
  'kinshicho-belt': { base: 'kinshicho-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kinshicho-belt-e' }, { face: 1, id: 'kinshicho-belt-w' }, { face: 2, id: 'kinshicho-belt-n' }, { face: 3, id: 'kinshicho-belt-s' }, { face: 5, id: 'kinshicho-belt-b' }] },
  'toyosu-belt': { base: 'toyosu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'toyosu-belt-e' }, { face: 1, id: 'toyosu-belt-w' }, { face: 2, id: 'toyosu-belt-n' }, { face: 3, id: 'toyosu-belt-s' }, { face: 5, id: 'toyosu-belt-b' }] },
  'kichijoji-belt': { base: 'kichijoji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kichijoji-belt-e' }, { face: 1, id: 'kichijoji-belt-w' }, { face: 2, id: 'kichijoji-belt-n' }, { face: 3, id: 'kichijoji-belt-s' }, { face: 5, id: 'kichijoji-belt-b' }] },
  'astoria-belt': { base: 'astoria-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'astoria-belt-e' }, { face: 1, id: 'astoria-belt-w' }, { face: 2, id: 'astoria-belt-n' }, { face: 3, id: 'astoria-belt-s' }, { face: 5, id: 'astoria-belt-b' }] },
  'park-slope-belt': { base: 'park-slope-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'park-slope-belt-e' }, { face: 1, id: 'park-slope-belt-w' }, { face: 2, id: 'park-slope-belt-n' }, { face: 3, id: 'park-slope-belt-s' }, { face: 5, id: 'park-slope-belt-b' }] },
  'pere-lachaise-belt': { base: 'pere-lachaise-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pere-lachaise-belt-e' }, { face: 1, id: 'pere-lachaise-belt-w' }, { face: 2, id: 'pere-lachaise-belt-n' }, { face: 3, id: 'pere-lachaise-belt-s' }, { face: 5, id: 'pere-lachaise-belt-b' }] },
  'butte-aux-cailles-belt': { base: 'butte-aux-cailles-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'butte-aux-cailles-belt-e' }, { face: 1, id: 'butte-aux-cailles-belt-w' }, { face: 2, id: 'butte-aux-cailles-belt-n' }, { face: 3, id: 'butte-aux-cailles-belt-s' }, { face: 5, id: 'butte-aux-cailles-belt-b' }] },
  'batignolles-belt': { base: 'batignolles-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'batignolles-belt-e' }, { face: 1, id: 'batignolles-belt-w' }, { face: 2, id: 'batignolles-belt-n' }, { face: 3, id: 'batignolles-belt-s' }, { face: 5, id: 'batignolles-belt-b' }] },
  'bercy-belt': { base: 'bercy-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bercy-belt-e' }, { face: 1, id: 'bercy-belt-w' }, { face: 2, id: 'bercy-belt-n' }, { face: 3, id: 'bercy-belt-s' }, { face: 5, id: 'bercy-belt-b' }] },
  'oud-zuid-belt': { base: 'oud-zuid-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'oud-zuid-belt-e' }, { face: 1, id: 'oud-zuid-belt-w' }, { face: 2, id: 'oud-zuid-belt-n' }, { face: 3, id: 'oud-zuid-belt-s' }, { face: 5, id: 'oud-zuid-belt-b' }] },
  'jiyugaoka-belt': { base: 'jiyugaoka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jiyugaoka-belt-e' }, { face: 1, id: 'jiyugaoka-belt-w' }, { face: 2, id: 'jiyugaoka-belt-n' }, { face: 3, id: 'jiyugaoka-belt-s' }, { face: 5, id: 'jiyugaoka-belt-b' }] },
  'east-village-belt': { base: 'east-village-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'east-village-belt-e' }, { face: 1, id: 'east-village-belt-w' }, { face: 2, id: 'east-village-belt-n' }, { face: 3, id: 'east-village-belt-s' }, { face: 5, id: 'east-village-belt-b' }] },
  'greenwich-village-belt': { base: 'greenwich-village-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'greenwich-village-belt-e' }, { face: 1, id: 'greenwich-village-belt-w' }, { face: 2, id: 'greenwich-village-belt-n' }, { face: 3, id: 'greenwich-village-belt-s' }, { face: 5, id: 'greenwich-village-belt-b' }] },
  'inwood-belt': { base: 'inwood-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'inwood-belt-e' }, { face: 1, id: 'inwood-belt-w' }, { face: 2, id: 'inwood-belt-n' }, { face: 3, id: 'inwood-belt-s' }, { face: 5, id: 'inwood-belt-b' }] },
  'belleville-belt': { base: 'belleville-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'belleville-belt-e' }, { face: 1, id: 'belleville-belt-w' }, { face: 2, id: 'belleville-belt-n' }, { face: 3, id: 'belleville-belt-s' }, { face: 5, id: 'belleville-belt-b' }] },
  'watergraafsmeer-belt': { base: 'watergraafsmeer-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'watergraafsmeer-belt-e' }, { face: 1, id: 'watergraafsmeer-belt-w' }, { face: 2, id: 'watergraafsmeer-belt-n' }, { face: 3, id: 'watergraafsmeer-belt-s' }, { face: 5, id: 'watergraafsmeer-belt-b' }] },
  'zuidas-belt': { base: 'zuidas-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zuidas-belt-e' }, { face: 1, id: 'zuidas-belt-w' }, { face: 2, id: 'zuidas-belt-n' }, { face: 3, id: 'zuidas-belt-s' }, { face: 5, id: 'zuidas-belt-b' }] },
  'bos-en-lommer-belt': { base: 'bos-en-lommer-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bos-en-lommer-belt-e' }, { face: 1, id: 'bos-en-lommer-belt-w' }, { face: 2, id: 'bos-en-lommer-belt-n' }, { face: 3, id: 'bos-en-lommer-belt-s' }, { face: 5, id: 'bos-en-lommer-belt-b' }] },
  'hillegersberg-belt': { base: 'hillegersberg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hillegersberg-belt-e' }, { face: 1, id: 'hillegersberg-belt-w' }, { face: 2, id: 'hillegersberg-belt-n' }, { face: 3, id: 'hillegersberg-belt-s' }, { face: 5, id: 'hillegersberg-belt-b' }] },
  'charlois-belt': { base: 'charlois-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'charlois-belt-e' }, { face: 1, id: 'charlois-belt-w' }, { face: 2, id: 'charlois-belt-n' }, { face: 3, id: 'charlois-belt-s' }, { face: 5, id: 'charlois-belt-b' }] },
  'overschie-belt': { base: 'overschie-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'overschie-belt-e' }, { face: 1, id: 'overschie-belt-w' }, { face: 2, id: 'overschie-belt-n' }, { face: 3, id: 'overschie-belt-s' }, { face: 5, id: 'overschie-belt-b' }] },
  'prins-alexander-belt': { base: 'prins-alexander-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'prins-alexander-belt-e' }, { face: 1, id: 'prins-alexander-belt-w' }, { face: 2, id: 'prins-alexander-belt-n' }, { face: 3, id: 'prins-alexander-belt-s' }, { face: 5, id: 'prins-alexander-belt-b' }] },
  'ommoord-belt': { base: 'ommoord-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ommoord-belt-e' }, { face: 1, id: 'ommoord-belt-w' }, { face: 2, id: 'ommoord-belt-n' }, { face: 3, id: 'ommoord-belt-s' }, { face: 5, id: 'ommoord-belt-b' }] },
  'tung-chung-belt': { base: 'tung-chung-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tung-chung-belt-e' }, { face: 1, id: 'tung-chung-belt-w' }, { face: 2, id: 'tung-chung-belt-n' }, { face: 3, id: 'tung-chung-belt-s' }, { face: 5, id: 'tung-chung-belt-b' }] },
  'wong-tai-sin-belt': { base: 'wong-tai-sin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wong-tai-sin-belt-e' }, { face: 1, id: 'wong-tai-sin-belt-w' }, { face: 2, id: 'wong-tai-sin-belt-n' }, { face: 3, id: 'wong-tai-sin-belt-s' }, { face: 5, id: 'wong-tai-sin-belt-b' }] },
  'ijburg-belt': { base: 'ijburg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ijburg-belt-e' }, { face: 1, id: 'ijburg-belt-w' }, { face: 2, id: 'ijburg-belt-n' }, { face: 3, id: 'ijburg-belt-s' }, { face: 5, id: 'ijburg-belt-b' }] },

  // ── Whole-city cubes (scripts/bake-city-cube.mjs) ──
  // Identical 6-face contract to every belt above; what differs is only the BAKE — each
  // face spans 10-14 km instead of 1.3 km, so one cube is the entire city at ~20-28 m/cell.
  // worldScale 4.0 renders it as the same grand roam-around ball as hksar-cube.
  'tokyo-cube': { base: 'tokyo-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'tokyo-cube-e' }, { face: 1, id: 'tokyo-cube-w' }, { face: 2, id: 'tokyo-cube-n' }, { face: 3, id: 'tokyo-cube-s' }, { face: 5, id: 'tokyo-cube-b' }] },
  'nyc-cube': { base: 'nyc-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'nyc-cube-e' }, { face: 1, id: 'nyc-cube-w' }, { face: 2, id: 'nyc-cube-n' }, { face: 3, id: 'nyc-cube-s' }, { face: 5, id: 'nyc-cube-b' }] },
  'paris-cube': { base: 'paris-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'paris-cube-e' }, { face: 1, id: 'paris-cube-w' }, { face: 2, id: 'paris-cube-n' }, { face: 3, id: 'paris-cube-s' }, { face: 5, id: 'paris-cube-b' }] },
  'rotterdam-cube': { base: 'rotterdam-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'rotterdam-cube-e' }, { face: 1, id: 'rotterdam-cube-w' }, { face: 2, id: 'rotterdam-cube-n' }, { face: 3, id: 'rotterdam-cube-s' }, { face: 5, id: 'rotterdam-cube-b' }] },
  'amsterdam-cube': { base: 'amsterdam-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'amsterdam-cube-e' }, { face: 1, id: 'amsterdam-cube-w' }, { face: 2, id: 'amsterdam-cube-n' }, { face: 3, id: 'amsterdam-cube-s' }, { face: 5, id: 'amsterdam-cube-b' }] },
  'berlin-cube': { base: 'berlin-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'berlin-cube-e' }, { face: 1, id: 'berlin-cube-w' }, { face: 2, id: 'berlin-cube-n' }, { face: 3, id: 'berlin-cube-s' }, { face: 5, id: 'berlin-cube-b' }] },
  'sanfrancisco-cube': { base: 'sanfrancisco-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'sanfrancisco-cube-e' }, { face: 1, id: 'sanfrancisco-cube-w' }, { face: 2, id: 'sanfrancisco-cube-n' }, { face: 3, id: 'sanfrancisco-cube-s' }, { face: 5, id: 'sanfrancisco-cube-b' }] },
  'vienna-cube': { base: 'vienna-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'vienna-cube-e' }, { face: 1, id: 'vienna-cube-w' }, { face: 2, id: 'vienna-cube-n' }, { face: 3, id: 'vienna-cube-s' }, { face: 5, id: 'vienna-cube-b' }] },
  'barcelona-cube': { base: 'barcelona-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'barcelona-cube-e' }, { face: 1, id: 'barcelona-cube-w' }, { face: 2, id: 'barcelona-cube-n' }, { face: 3, id: 'barcelona-cube-s' }, { face: 5, id: 'barcelona-cube-b' }] },
  'copenhagen-cube': { base: 'copenhagen-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'copenhagen-cube-e' }, { face: 1, id: 'copenhagen-cube-w' }, { face: 2, id: 'copenhagen-cube-n' }, { face: 3, id: 'copenhagen-cube-s' }, { face: 5, id: 'copenhagen-cube-b' }] },
  'toronto-cube': { base: 'toronto-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'toronto-cube-e' }, { face: 1, id: 'toronto-cube-w' }, { face: 2, id: 'toronto-cube-n' }, { face: 3, id: 'toronto-cube-s' }, { face: 5, id: 'toronto-cube-b' }] },
  'melbourne-cube': { base: 'melbourne-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'melbourne-cube-e' }, { face: 1, id: 'melbourne-cube-w' }, { face: 2, id: 'melbourne-cube-n' }, { face: 3, id: 'melbourne-cube-s' }, { face: 5, id: 'melbourne-cube-b' }] },
  'edinburgh-cube': { base: 'edinburgh-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'edinburgh-cube-e' }, { face: 1, id: 'edinburgh-cube-w' }, { face: 2, id: 'edinburgh-cube-n' }, { face: 3, id: 'edinburgh-cube-s' }, { face: 5, id: 'edinburgh-cube-b' }] },
  'seoul-cube': { base: 'seoul-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'seoul-cube-e' }, { face: 1, id: 'seoul-cube-w' }, { face: 2, id: 'seoul-cube-n' }, { face: 3, id: 'seoul-cube-s' }, { face: 5, id: 'seoul-cube-b' }] },
  'losangeles-cube': { base: 'losangeles-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'losangeles-cube-e' }, { face: 1, id: 'losangeles-cube-w' }, { face: 2, id: 'losangeles-cube-n' }, { face: 3, id: 'losangeles-cube-s' }, { face: 5, id: 'losangeles-cube-b' }] },
  'dubai-cube': { base: 'dubai-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'dubai-cube-e' }, { face: 1, id: 'dubai-cube-w' }, { face: 2, id: 'dubai-cube-n' }, { face: 3, id: 'dubai-cube-s' }, { face: 5, id: 'dubai-cube-b' }] },
  'bangkok-cube': { base: 'bangkok-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'bangkok-cube-e' }, { face: 1, id: 'bangkok-cube-w' }, { face: 2, id: 'bangkok-cube-n' }, { face: 3, id: 'bangkok-cube-s' }, { face: 5, id: 'bangkok-cube-b' }] },
  'capetown-cube': { base: 'capetown-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'capetown-cube-e' }, { face: 1, id: 'capetown-cube-w' }, { face: 2, id: 'capetown-cube-n' }, { face: 3, id: 'capetown-cube-s' }, { face: 5, id: 'capetown-cube-b' }] },
  'taipei-cube': { base: 'taipei-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'taipei-cube-e' }, { face: 1, id: 'taipei-cube-w' }, { face: 2, id: 'taipei-cube-n' }, { face: 3, id: 'taipei-cube-s' }, { face: 5, id: 'taipei-cube-b' }] },
  'miami-cube': { base: 'miami-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'miami-cube-e' }, { face: 1, id: 'miami-cube-w' }, { face: 2, id: 'miami-cube-n' }, { face: 3, id: 'miami-cube-s' }, { face: 5, id: 'miami-cube-b' }] },
  'fukuoka-cube': { base: 'fukuoka-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'fukuoka-cube-e' }, { face: 1, id: 'fukuoka-cube-w' }, { face: 2, id: 'fukuoka-cube-n' }, { face: 3, id: 'fukuoka-cube-s' }, { face: 5, id: 'fukuoka-cube-b' }] },
  'nagoya-cube': { base: 'nagoya-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'nagoya-cube-e' }, { face: 1, id: 'nagoya-cube-w' }, { face: 2, id: 'nagoya-cube-n' }, { face: 3, id: 'nagoya-cube-s' }, { face: 5, id: 'nagoya-cube-b' }] },
  'yokohama-cube': { base: 'yokohama-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'yokohama-cube-e' }, { face: 1, id: 'yokohama-cube-w' }, { face: 2, id: 'yokohama-cube-n' }, { face: 3, id: 'yokohama-cube-s' }, { face: 5, id: 'yokohama-cube-b' }] },
  'osaka-cube': { base: 'osaka-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'osaka-cube-e' }, { face: 1, id: 'osaka-cube-w' }, { face: 2, id: 'osaka-cube-n' }, { face: 3, id: 'osaka-cube-s' }, { face: 5, id: 'osaka-cube-b' }] },
  'athens-cube': { base: 'athens-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'athens-cube-e' }, { face: 1, id: 'athens-cube-w' }, { face: 2, id: 'athens-cube-n' }, { face: 3, id: 'athens-cube-s' }, { face: 5, id: 'athens-cube-b' }] },
  'lisbon-cube': { base: 'lisbon-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'lisbon-cube-e' }, { face: 1, id: 'lisbon-cube-w' }, { face: 2, id: 'lisbon-cube-n' }, { face: 3, id: 'lisbon-cube-s' }, { face: 5, id: 'lisbon-cube-b' }] },
  'helsinki-cube': { base: 'helsinki-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'helsinki-cube-e' }, { face: 1, id: 'helsinki-cube-w' }, { face: 2, id: 'helsinki-cube-n' }, { face: 3, id: 'helsinki-cube-s' }, { face: 5, id: 'helsinki-cube-b' }] },
  'zurich-cube': { base: 'zurich-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'zurich-cube-e' }, { face: 1, id: 'zurich-cube-w' }, { face: 2, id: 'zurich-cube-n' }, { face: 3, id: 'zurich-cube-s' }, { face: 5, id: 'zurich-cube-b' }] },
  'prague-cube': { base: 'prague-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'prague-cube-e' }, { face: 1, id: 'prague-cube-w' }, { face: 2, id: 'prague-cube-n' }, { face: 3, id: 'prague-cube-s' }, { face: 5, id: 'prague-cube-b' }] },
  'kyoto-cube': { base: 'kyoto-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'kyoto-cube-e' }, { face: 1, id: 'kyoto-cube-w' }, { face: 2, id: 'kyoto-cube-n' }, { face: 3, id: 'kyoto-cube-s' }, { face: 5, id: 'kyoto-cube-b' }] },
  'vatican-cube': { base: 'vatican-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'vatican-cube-e' }, { face: 1, id: 'vatican-cube-w' }, { face: 2, id: 'vatican-cube-n' }, { face: 3, id: 'vatican-cube-s' }, { face: 5, id: 'vatican-cube-b' }] },
  'monaco-cube': { base: 'monaco-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'monaco-cube-e' }, { face: 1, id: 'monaco-cube-w' }, { face: 2, id: 'monaco-cube-n' }, { face: 3, id: 'monaco-cube-s' }, { face: 5, id: 'monaco-cube-b' }] },
  'macau-cube': { base: 'macau-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'macau-cube-e' }, { face: 1, id: 'macau-cube-w' }, { face: 2, id: 'macau-cube-n' }, { face: 3, id: 'macau-cube-s' }, { face: 5, id: 'macau-cube-b' }] },
  'geneva-cube': { base: 'geneva-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'geneva-cube-e' }, { face: 1, id: 'geneva-cube-w' }, { face: 2, id: 'geneva-cube-n' }, { face: 3, id: 'geneva-cube-s' }, { face: 5, id: 'geneva-cube-b' }] },
  'singapore-cube': { base: 'singapore-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'singapore-cube-e' }, { face: 1, id: 'singapore-cube-w' }, { face: 2, id: 'singapore-cube-n' }, { face: 3, id: 'singapore-cube-s' }, { face: 5, id: 'singapore-cube-b' }] },
  'rome-cube': { base: 'rome-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'rome-cube-e' }, { face: 1, id: 'rome-cube-w' }, { face: 2, id: 'rome-cube-n' }, { face: 3, id: 'rome-cube-s' }, { face: 5, id: 'rome-cube-b' }] },
  'london-cube': { base: 'london-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'london-cube-e' }, { face: 1, id: 'london-cube-w' }, { face: 2, id: 'london-cube-n' }, { face: 3, id: 'london-cube-s' }, { face: 5, id: 'london-cube-b' }] },
  'jakarta-cube': { base: 'jakarta-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'jakarta-cube-e' }, { face: 1, id: 'jakarta-cube-w' }, { face: 2, id: 'jakarta-cube-n' }, { face: 3, id: 'jakarta-cube-s' }, { face: 5, id: 'jakarta-cube-b' }] },
  'mexicocity-cube': { base: 'mexicocity-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'mexicocity-cube-e' }, { face: 1, id: 'mexicocity-cube-w' }, { face: 2, id: 'mexicocity-cube-n' }, { face: 3, id: 'mexicocity-cube-s' }, { face: 5, id: 'mexicocity-cube-b' }] },
  'sanjose-cube': { base: 'sanjose-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'sanjose-cube-e' }, { face: 1, id: 'sanjose-cube-w' }, { face: 2, id: 'sanjose-cube-n' }, { face: 3, id: 'sanjose-cube-s' }, { face: 5, id: 'sanjose-cube-b' }] },
  'telaviv-cube': { base: 'telaviv-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'telaviv-cube-e' }, { face: 1, id: 'telaviv-cube-w' }, { face: 2, id: 'telaviv-cube-n' }, { face: 3, id: 'telaviv-cube-s' }, { face: 5, id: 'telaviv-cube-b' }] },
  'chicago-cube': { base: 'chicago-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'chicago-cube-e' }, { face: 1, id: 'chicago-cube-w' }, { face: 2, id: 'chicago-cube-n' }, { face: 3, id: 'chicago-cube-s' }, { face: 5, id: 'chicago-cube-b' }] },
  'boston-cube': { base: 'boston-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'boston-cube-e' }, { face: 1, id: 'boston-cube-w' }, { face: 2, id: 'boston-cube-n' }, { face: 3, id: 'boston-cube-s' }, { face: 5, id: 'boston-cube-b' }] },
  'mumbai-cube': { base: 'mumbai-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'mumbai-cube-e' }, { face: 1, id: 'mumbai-cube-w' }, { face: 2, id: 'mumbai-cube-n' }, { face: 3, id: 'mumbai-cube-s' }, { face: 5, id: 'mumbai-cube-b' }] },
  'moscow-cube': { base: 'moscow-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'moscow-cube-e' }, { face: 1, id: 'moscow-cube-w' }, { face: 2, id: 'moscow-cube-n' }, { face: 3, id: 'moscow-cube-s' }, { face: 5, id: 'moscow-cube-b' }] },
  'nanjing-cube': { base: 'nanjing-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'nanjing-cube-e' }, { face: 1, id: 'nanjing-cube-w' }, { face: 2, id: 'nanjing-cube-n' }, { face: 3, id: 'nanjing-cube-s' }, { face: 5, id: 'nanjing-cube-b' }] },
  'beijing-cube': { base: 'beijing-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'beijing-cube-e' }, { face: 1, id: 'beijing-cube-w' }, { face: 2, id: 'beijing-cube-n' }, { face: 3, id: 'beijing-cube-s' }, { face: 5, id: 'beijing-cube-b' }] },
  'shanghai-cube': { base: 'shanghai-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'shanghai-cube-e' }, { face: 1, id: 'shanghai-cube-w' }, { face: 2, id: 'shanghai-cube-n' }, { face: 3, id: 'shanghai-cube-s' }, { face: 5, id: 'shanghai-cube-b' }] },
  'sydney-cube': { base: 'sydney-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'sydney-cube-e' }, { face: 1, id: 'sydney-cube-w' }, { face: 2, id: 'sydney-cube-n' }, { face: 3, id: 'sydney-cube-s' }, { face: 5, id: 'sydney-cube-b' }] },
  'kualalumpur-cube': { base: 'kualalumpur-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'kualalumpur-cube-e' }, { face: 1, id: 'kualalumpur-cube-w' }, { face: 2, id: 'kualalumpur-cube-n' }, { face: 3, id: 'kualalumpur-cube-s' }, { face: 5, id: 'kualalumpur-cube-b' }] },
  'washingtondc-cube': { base: 'washingtondc-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'washingtondc-cube-e' }, { face: 1, id: 'washingtondc-cube-w' }, { face: 2, id: 'washingtondc-cube-n' }, { face: 3, id: 'washingtondc-cube-s' }, { face: 5, id: 'washingtondc-cube-b' }] },
  'xian-cube': { base: 'xian-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'xian-cube-e' }, { face: 1, id: 'xian-cube-w' }, { face: 2, id: 'xian-cube-n' }, { face: 3, id: 'xian-cube-s' }, { face: 5, id: 'xian-cube-b' }] },
  'erqi-belt': { base: 'erqi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'erqi-belt-e' }, { face: 1, id: 'erqi-belt-w' }, { face: 2, id: 'erqi-belt-n' }, { face: 3, id: 'erqi-belt-s' }, { face: 5, id: 'erqi-belt-b' }] },
  'zhengzhou-cube': { base: 'zhengzhou-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'zhengzhou-cube-e' }, { face: 1, id: 'zhengzhou-cube-w' }, { face: 2, id: 'zhengzhou-cube-n' }, { face: 3, id: 'zhengzhou-cube-s' }, { face: 5, id: 'zhengzhou-cube-b' }] },
  'luoyang-cube': { base: 'luoyang-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'luoyang-cube-e' }, { face: 1, id: 'luoyang-cube-w' }, { face: 2, id: 'luoyang-cube-n' }, { face: 3, id: 'luoyang-cube-s' }, { face: 5, id: 'luoyang-cube-b' }] },
  'venice-cube': { base: 'venice-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'venice-cube-e' }, { face: 1, id: 'venice-cube-w' }, { face: 2, id: 'venice-cube-n' }, { face: 3, id: 'venice-cube-s' }, { face: 5, id: 'venice-cube-b' }] },
  'shenzhen-cube': { base: 'shenzhen-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'shenzhen-cube-e' }, { face: 1, id: 'shenzhen-cube-w' }, { face: 2, id: 'shenzhen-cube-n' }, { face: 3, id: 'shenzhen-cube-s' }, { face: 5, id: 'shenzhen-cube-b' }] },
  'mitte-belt': { base: 'mitte-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mitte-belt-e' }, { face: 1, id: 'mitte-belt-w' }, { face: 2, id: 'mitte-belt-n' }, { face: 3, id: 'mitte-belt-s' }, { face: 5, id: 'mitte-belt-b' }] },
  'reichstag-belt': { base: 'reichstag-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'reichstag-belt-e' }, { face: 1, id: 'reichstag-belt-w' }, { face: 2, id: 'reichstag-belt-n' }, { face: 3, id: 'reichstag-belt-s' }, { face: 5, id: 'reichstag-belt-b' }] },
  'kreuzberg-belt': { base: 'kreuzberg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kreuzberg-belt-e' }, { face: 1, id: 'kreuzberg-belt-w' }, { face: 2, id: 'kreuzberg-belt-n' }, { face: 3, id: 'kreuzberg-belt-s' }, { face: 5, id: 'kreuzberg-belt-b' }] },
  'prenzlauer-berg-belt': { base: 'prenzlauer-berg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'prenzlauer-berg-belt-e' }, { face: 1, id: 'prenzlauer-berg-belt-w' }, { face: 2, id: 'prenzlauer-berg-belt-n' }, { face: 3, id: 'prenzlauer-berg-belt-s' }, { face: 5, id: 'prenzlauer-berg-belt-b' }] },
  'charlottenburg-belt': { base: 'charlottenburg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'charlottenburg-belt-e' }, { face: 1, id: 'charlottenburg-belt-w' }, { face: 2, id: 'charlottenburg-belt-n' }, { face: 3, id: 'charlottenburg-belt-s' }, { face: 5, id: 'charlottenburg-belt-b' }] },
  'city-west-belt': { base: 'city-west-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'city-west-belt-e' }, { face: 1, id: 'city-west-belt-w' }, { face: 2, id: 'city-west-belt-n' }, { face: 3, id: 'city-west-belt-s' }, { face: 5, id: 'city-west-belt-b' }] },
  'tempelhof-belt': { base: 'tempelhof-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tempelhof-belt-e' }, { face: 1, id: 'tempelhof-belt-w' }, { face: 2, id: 'tempelhof-belt-n' }, { face: 3, id: 'tempelhof-belt-s' }, { face: 5, id: 'tempelhof-belt-b' }] },
  'neukolln-belt': { base: 'neukolln-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'neukolln-belt-e' }, { face: 1, id: 'neukolln-belt-w' }, { face: 2, id: 'neukolln-belt-n' }, { face: 3, id: 'neukolln-belt-s' }, { face: 5, id: 'neukolln-belt-b' }] },
  'friedrichshain-belt': { base: 'friedrichshain-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'friedrichshain-belt-e' }, { face: 1, id: 'friedrichshain-belt-w' }, { face: 2, id: 'friedrichshain-belt-n' }, { face: 3, id: 'friedrichshain-belt-s' }, { face: 5, id: 'friedrichshain-belt-b' }] },
  'moabit-belt': { base: 'moabit-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'moabit-belt-e' }, { face: 1, id: 'moabit-belt-w' }, { face: 2, id: 'moabit-belt-n' }, { face: 3, id: 'moabit-belt-s' }, { face: 5, id: 'moabit-belt-b' }] },
  'wedding-belt': { base: 'wedding-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wedding-belt-e' }, { face: 1, id: 'wedding-belt-w' }, { face: 2, id: 'wedding-belt-n' }, { face: 3, id: 'wedding-belt-s' }, { face: 5, id: 'wedding-belt-b' }] },
  'schoneberg-belt': { base: 'schoneberg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'schoneberg-belt-e' }, { face: 1, id: 'schoneberg-belt-w' }, { face: 2, id: 'schoneberg-belt-n' }, { face: 3, id: 'schoneberg-belt-s' }, { face: 5, id: 'schoneberg-belt-b' }] },
  'treptow-belt': { base: 'treptow-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'treptow-belt-e' }, { face: 1, id: 'treptow-belt-w' }, { face: 2, id: 'treptow-belt-n' }, { face: 3, id: 'treptow-belt-s' }, { face: 5, id: 'treptow-belt-b' }] },
  'olympiastadion-belt': { base: 'olympiastadion-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'olympiastadion-belt-e' }, { face: 1, id: 'olympiastadion-belt-w' }, { face: 2, id: 'olympiastadion-belt-n' }, { face: 3, id: 'olympiastadion-belt-s' }, { face: 5, id: 'olympiastadion-belt-b' }] },
  'tegel-belt': { base: 'tegel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tegel-belt-e' }, { face: 1, id: 'tegel-belt-w' }, { face: 2, id: 'tegel-belt-n' }, { face: 3, id: 'tegel-belt-s' }, { face: 5, id: 'tegel-belt-b' }] },
  'golden-gate-belt': { base: 'golden-gate-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'golden-gate-belt-e' }, { face: 1, id: 'golden-gate-belt-w' }, { face: 2, id: 'golden-gate-belt-n' }, { face: 3, id: 'golden-gate-belt-s' }, { face: 5, id: 'golden-gate-belt-b' }] },
  'marina-belt': { base: 'marina-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marina-belt-e' }, { face: 1, id: 'marina-belt-w' }, { face: 2, id: 'marina-belt-n' }, { face: 3, id: 'marina-belt-s' }, { face: 5, id: 'marina-belt-b' }] },
  'aquatic-park-belt': { base: 'aquatic-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'aquatic-park-belt-e' }, { face: 1, id: 'aquatic-park-belt-w' }, { face: 2, id: 'aquatic-park-belt-n' }, { face: 3, id: 'aquatic-park-belt-s' }, { face: 5, id: 'aquatic-park-belt-b' }] },
  'telegraph-hill-belt': { base: 'telegraph-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'telegraph-hill-belt-e' }, { face: 1, id: 'telegraph-hill-belt-w' }, { face: 2, id: 'telegraph-hill-belt-n' }, { face: 3, id: 'telegraph-hill-belt-s' }, { face: 5, id: 'telegraph-hill-belt-b' }] },
  'nob-hill-belt': { base: 'nob-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nob-hill-belt-e' }, { face: 1, id: 'nob-hill-belt-w' }, { face: 2, id: 'nob-hill-belt-n' }, { face: 3, id: 'nob-hill-belt-s' }, { face: 5, id: 'nob-hill-belt-b' }] },
  'financial-district-belt': { base: 'financial-district-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'financial-district-belt-e' }, { face: 1, id: 'financial-district-belt-w' }, { face: 2, id: 'financial-district-belt-n' }, { face: 3, id: 'financial-district-belt-s' }, { face: 5, id: 'financial-district-belt-b' }] },
  'civic-center-belt': { base: 'civic-center-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'civic-center-belt-e' }, { face: 1, id: 'civic-center-belt-w' }, { face: 2, id: 'civic-center-belt-n' }, { face: 3, id: 'civic-center-belt-s' }, { face: 5, id: 'civic-center-belt-b' }] },
  'rincon-belt': { base: 'rincon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rincon-belt-e' }, { face: 1, id: 'rincon-belt-w' }, { face: 2, id: 'rincon-belt-n' }, { face: 3, id: 'rincon-belt-s' }, { face: 5, id: 'rincon-belt-b' }] },
  'mission-bay-belt': { base: 'mission-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mission-bay-belt-e' }, { face: 1, id: 'mission-bay-belt-w' }, { face: 2, id: 'mission-bay-belt-n' }, { face: 3, id: 'mission-bay-belt-s' }, { face: 5, id: 'mission-bay-belt-b' }] },
  'dogpatch-belt': { base: 'dogpatch-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dogpatch-belt-e' }, { face: 1, id: 'dogpatch-belt-w' }, { face: 2, id: 'dogpatch-belt-n' }, { face: 3, id: 'dogpatch-belt-s' }, { face: 5, id: 'dogpatch-belt-b' }] },
  'bernal-heights-belt': { base: 'bernal-heights-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bernal-heights-belt-e' }, { face: 1, id: 'bernal-heights-belt-w' }, { face: 2, id: 'bernal-heights-belt-n' }, { face: 3, id: 'bernal-heights-belt-s' }, { face: 5, id: 'bernal-heights-belt-b' }] },
  'mission-belt': { base: 'mission-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mission-belt-e' }, { face: 1, id: 'mission-belt-w' }, { face: 2, id: 'mission-belt-n' }, { face: 3, id: 'mission-belt-s' }, { face: 5, id: 'mission-belt-b' }] },
  'twin-peaks-belt': { base: 'twin-peaks-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'twin-peaks-belt-e' }, { face: 1, id: 'twin-peaks-belt-w' }, { face: 2, id: 'twin-peaks-belt-n' }, { face: 3, id: 'twin-peaks-belt-s' }, { face: 5, id: 'twin-peaks-belt-b' }] },
  'golden-gate-park-belt': { base: 'golden-gate-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'golden-gate-park-belt-e' }, { face: 1, id: 'golden-gate-park-belt-w' }, { face: 2, id: 'golden-gate-park-belt-n' }, { face: 3, id: 'golden-gate-park-belt-s' }, { face: 5, id: 'golden-gate-park-belt-b' }] },
  'ocean-beach-belt': { base: 'ocean-beach-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ocean-beach-belt-e' }, { face: 1, id: 'ocean-beach-belt-w' }, { face: 2, id: 'ocean-beach-belt-n' }, { face: 3, id: 'ocean-beach-belt-s' }, { face: 5, id: 'ocean-beach-belt-b' }] },
  'innere-stadt-belt': { base: 'innere-stadt-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'innere-stadt-belt-e' }, { face: 1, id: 'innere-stadt-belt-w' }, { face: 2, id: 'innere-stadt-belt-n' }, { face: 3, id: 'innere-stadt-belt-s' }, { face: 5, id: 'innere-stadt-belt-b' }] },
  'rathausplatz-belt': { base: 'rathausplatz-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rathausplatz-belt-e' }, { face: 1, id: 'rathausplatz-belt-w' }, { face: 2, id: 'rathausplatz-belt-n' }, { face: 3, id: 'rathausplatz-belt-s' }, { face: 5, id: 'rathausplatz-belt-b' }] },
  'karlsplatz-belt': { base: 'karlsplatz-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'karlsplatz-belt-e' }, { face: 1, id: 'karlsplatz-belt-w' }, { face: 2, id: 'karlsplatz-belt-n' }, { face: 3, id: 'karlsplatz-belt-s' }, { face: 5, id: 'karlsplatz-belt-b' }] },
  'mariahilf-belt': { base: 'mariahilf-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mariahilf-belt-e' }, { face: 1, id: 'mariahilf-belt-w' }, { face: 2, id: 'mariahilf-belt-n' }, { face: 3, id: 'mariahilf-belt-s' }, { face: 5, id: 'mariahilf-belt-b' }] },
  'belvedere-belt': { base: 'belvedere-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'belvedere-belt-e' }, { face: 1, id: 'belvedere-belt-w' }, { face: 2, id: 'belvedere-belt-n' }, { face: 3, id: 'belvedere-belt-s' }, { face: 5, id: 'belvedere-belt-b' }] },
  'schonbrunn-belt': { base: 'schonbrunn-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'schonbrunn-belt-e' }, { face: 1, id: 'schonbrunn-belt-w' }, { face: 2, id: 'schonbrunn-belt-n' }, { face: 3, id: 'schonbrunn-belt-s' }, { face: 5, id: 'schonbrunn-belt-b' }] },
  'prater-belt': { base: 'prater-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'prater-belt-e' }, { face: 1, id: 'prater-belt-w' }, { face: 2, id: 'prater-belt-n' }, { face: 3, id: 'prater-belt-s' }, { face: 5, id: 'prater-belt-b' }] },
  'augarten-belt': { base: 'augarten-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'augarten-belt-e' }, { face: 1, id: 'augarten-belt-w' }, { face: 2, id: 'augarten-belt-n' }, { face: 3, id: 'augarten-belt-s' }, { face: 5, id: 'augarten-belt-b' }] },
  'donaukanal-belt': { base: 'donaukanal-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'donaukanal-belt-e' }, { face: 1, id: 'donaukanal-belt-w' }, { face: 2, id: 'donaukanal-belt-n' }, { face: 3, id: 'donaukanal-belt-s' }, { face: 5, id: 'donaukanal-belt-b' }] },
  'donauinsel-belt': { base: 'donauinsel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'donauinsel-belt-e' }, { face: 1, id: 'donauinsel-belt-w' }, { face: 2, id: 'donauinsel-belt-n' }, { face: 3, id: 'donauinsel-belt-s' }, { face: 5, id: 'donauinsel-belt-b' }] },
  'donaucity-belt': { base: 'donaucity-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'donaucity-belt-e' }, { face: 1, id: 'donaucity-belt-w' }, { face: 2, id: 'donaucity-belt-n' }, { face: 3, id: 'donaucity-belt-s' }, { face: 5, id: 'donaucity-belt-b' }] },
  'alte-donau-belt': { base: 'alte-donau-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'alte-donau-belt-e' }, { face: 1, id: 'alte-donau-belt-w' }, { face: 2, id: 'alte-donau-belt-n' }, { face: 3, id: 'alte-donau-belt-s' }, { face: 5, id: 'alte-donau-belt-b' }] },
  'heiligenstadt-belt': { base: 'heiligenstadt-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'heiligenstadt-belt-e' }, { face: 1, id: 'heiligenstadt-belt-w' }, { face: 2, id: 'heiligenstadt-belt-n' }, { face: 3, id: 'heiligenstadt-belt-s' }, { face: 5, id: 'heiligenstadt-belt-b' }] },
  'simmering-belt': { base: 'simmering-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'simmering-belt-e' }, { face: 1, id: 'simmering-belt-w' }, { face: 2, id: 'simmering-belt-n' }, { face: 3, id: 'simmering-belt-s' }, { face: 5, id: 'simmering-belt-b' }] },
  'grinzing-belt': { base: 'grinzing-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'grinzing-belt-e' }, { face: 1, id: 'grinzing-belt-w' }, { face: 2, id: 'grinzing-belt-n' }, { face: 3, id: 'grinzing-belt-s' }, { face: 5, id: 'grinzing-belt-b' }] },
  'eixample-belt': { base: 'eixample-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'eixample-belt-e' }, { face: 1, id: 'eixample-belt-w' }, { face: 2, id: 'eixample-belt-n' }, { face: 3, id: 'eixample-belt-s' }, { face: 5, id: 'eixample-belt-b' }] },
  'gotic-belt': { base: 'gotic-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gotic-belt-e' }, { face: 1, id: 'gotic-belt-w' }, { face: 2, id: 'gotic-belt-n' }, { face: 3, id: 'gotic-belt-s' }, { face: 5, id: 'gotic-belt-b' }] },
  'raval-belt': { base: 'raval-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'raval-belt-e' }, { face: 1, id: 'raval-belt-w' }, { face: 2, id: 'raval-belt-n' }, { face: 3, id: 'raval-belt-s' }, { face: 5, id: 'raval-belt-b' }] },
  'gracia-belt': { base: 'gracia-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gracia-belt-e' }, { face: 1, id: 'gracia-belt-w' }, { face: 2, id: 'gracia-belt-n' }, { face: 3, id: 'gracia-belt-s' }, { face: 5, id: 'gracia-belt-b' }] },
  'park-guell-belt': { base: 'park-guell-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'park-guell-belt-e' }, { face: 1, id: 'park-guell-belt-w' }, { face: 2, id: 'park-guell-belt-n' }, { face: 3, id: 'park-guell-belt-s' }, { face: 5, id: 'park-guell-belt-b' }] },
  'barceloneta-belt': { base: 'barceloneta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'barceloneta-belt-e' }, { face: 1, id: 'barceloneta-belt-w' }, { face: 2, id: 'barceloneta-belt-n' }, { face: 3, id: 'barceloneta-belt-s' }, { face: 5, id: 'barceloneta-belt-b' }] },
  'ciutadella-belt': { base: 'ciutadella-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ciutadella-belt-e' }, { face: 1, id: 'ciutadella-belt-w' }, { face: 2, id: 'ciutadella-belt-n' }, { face: 3, id: 'ciutadella-belt-s' }, { face: 5, id: 'ciutadella-belt-b' }] },
  'vila-olimpica-belt': { base: 'vila-olimpica-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vila-olimpica-belt-e' }, { face: 1, id: 'vila-olimpica-belt-w' }, { face: 2, id: 'vila-olimpica-belt-n' }, { face: 3, id: 'vila-olimpica-belt-s' }, { face: 5, id: 'vila-olimpica-belt-b' }] },
  'poblenou-belt': { base: 'poblenou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'poblenou-belt-e' }, { face: 1, id: 'poblenou-belt-w' }, { face: 2, id: 'poblenou-belt-n' }, { face: 3, id: 'poblenou-belt-s' }, { face: 5, id: 'poblenou-belt-b' }] },
  'diagonal-mar-belt': { base: 'diagonal-mar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'diagonal-mar-belt-e' }, { face: 1, id: 'diagonal-mar-belt-w' }, { face: 2, id: 'diagonal-mar-belt-n' }, { face: 3, id: 'diagonal-mar-belt-s' }, { face: 5, id: 'diagonal-mar-belt-b' }] },
  'montjuic-belt': { base: 'montjuic-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'montjuic-belt-e' }, { face: 1, id: 'montjuic-belt-w' }, { face: 2, id: 'montjuic-belt-n' }, { face: 3, id: 'montjuic-belt-s' }, { face: 5, id: 'montjuic-belt-b' }] },
  'sants-belt': { base: 'sants-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sants-belt-e' }, { face: 1, id: 'sants-belt-w' }, { face: 2, id: 'sants-belt-n' }, { face: 3, id: 'sants-belt-s' }, { face: 5, id: 'sants-belt-b' }] },
  'les-corts-belt': { base: 'les-corts-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'les-corts-belt-e' }, { face: 1, id: 'les-corts-belt-w' }, { face: 2, id: 'les-corts-belt-n' }, { face: 3, id: 'les-corts-belt-s' }, { face: 5, id: 'les-corts-belt-b' }] },
  'collserola-belt': { base: 'collserola-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'collserola-belt-e' }, { face: 1, id: 'collserola-belt-w' }, { face: 2, id: 'collserola-belt-n' }, { face: 3, id: 'collserola-belt-s' }, { face: 5, id: 'collserola-belt-b' }] },
  'sant-andreu-belt': { base: 'sant-andreu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sant-andreu-belt-e' }, { face: 1, id: 'sant-andreu-belt-w' }, { face: 2, id: 'sant-andreu-belt-n' }, { face: 3, id: 'sant-andreu-belt-s' }, { face: 5, id: 'sant-andreu-belt-b' }] },
  'indre-by-belt': { base: 'indre-by-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'indre-by-belt-e' }, { face: 1, id: 'indre-by-belt-w' }, { face: 2, id: 'indre-by-belt-n' }, { face: 3, id: 'indre-by-belt-s' }, { face: 5, id: 'indre-by-belt-b' }] },
  'nyhavn-belt': { base: 'nyhavn-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nyhavn-belt-e' }, { face: 1, id: 'nyhavn-belt-w' }, { face: 2, id: 'nyhavn-belt-n' }, { face: 3, id: 'nyhavn-belt-s' }, { face: 5, id: 'nyhavn-belt-b' }] },
  'christianshavn-belt': { base: 'christianshavn-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'christianshavn-belt-e' }, { face: 1, id: 'christianshavn-belt-w' }, { face: 2, id: 'christianshavn-belt-n' }, { face: 3, id: 'christianshavn-belt-s' }, { face: 5, id: 'christianshavn-belt-b' }] },
  'holmen-belt': { base: 'holmen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'holmen-belt-e' }, { face: 1, id: 'holmen-belt-w' }, { face: 2, id: 'holmen-belt-n' }, { face: 3, id: 'holmen-belt-s' }, { face: 5, id: 'holmen-belt-b' }] },
  'nordhavn-belt': { base: 'nordhavn-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nordhavn-belt-e' }, { face: 1, id: 'nordhavn-belt-w' }, { face: 2, id: 'nordhavn-belt-n' }, { face: 3, id: 'nordhavn-belt-s' }, { face: 5, id: 'nordhavn-belt-b' }] },
  'faelledparken-belt': { base: 'faelledparken-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'faelledparken-belt-e' }, { face: 1, id: 'faelledparken-belt-w' }, { face: 2, id: 'faelledparken-belt-n' }, { face: 3, id: 'faelledparken-belt-s' }, { face: 5, id: 'faelledparken-belt-b' }] },
  'bispebjerg-belt': { base: 'bispebjerg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bispebjerg-belt-e' }, { face: 1, id: 'bispebjerg-belt-w' }, { face: 2, id: 'bispebjerg-belt-n' }, { face: 3, id: 'bispebjerg-belt-s' }, { face: 5, id: 'bispebjerg-belt-b' }] },
  'norrebro-belt': { base: 'norrebro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'norrebro-belt-e' }, { face: 1, id: 'norrebro-belt-w' }, { face: 2, id: 'norrebro-belt-n' }, { face: 3, id: 'norrebro-belt-s' }, { face: 5, id: 'norrebro-belt-b' }] },
  'soerne-belt': { base: 'soerne-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'soerne-belt-e' }, { face: 1, id: 'soerne-belt-w' }, { face: 2, id: 'soerne-belt-n' }, { face: 3, id: 'soerne-belt-s' }, { face: 5, id: 'soerne-belt-b' }] },
  'radhuspladsen-belt': { base: 'radhuspladsen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'radhuspladsen-belt-e' }, { face: 1, id: 'radhuspladsen-belt-w' }, { face: 2, id: 'radhuspladsen-belt-n' }, { face: 3, id: 'radhuspladsen-belt-s' }, { face: 5, id: 'radhuspladsen-belt-b' }] },
  'vesterbro-belt': { base: 'vesterbro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vesterbro-belt-e' }, { face: 1, id: 'vesterbro-belt-w' }, { face: 2, id: 'vesterbro-belt-n' }, { face: 3, id: 'vesterbro-belt-s' }, { face: 5, id: 'vesterbro-belt-b' }] },
  'humleby-belt': { base: 'humleby-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'humleby-belt-e' }, { face: 1, id: 'humleby-belt-w' }, { face: 2, id: 'humleby-belt-n' }, { face: 3, id: 'humleby-belt-s' }, { face: 5, id: 'humleby-belt-b' }] },
  'frederiksberg-belt': { base: 'frederiksberg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'frederiksberg-belt-e' }, { face: 1, id: 'frederiksberg-belt-w' }, { face: 2, id: 'frederiksberg-belt-n' }, { face: 3, id: 'frederiksberg-belt-s' }, { face: 5, id: 'frederiksberg-belt-b' }] },
  'orestad-belt': { base: 'orestad-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'orestad-belt-e' }, { face: 1, id: 'orestad-belt-w' }, { face: 2, id: 'orestad-belt-n' }, { face: 3, id: 'orestad-belt-s' }, { face: 5, id: 'orestad-belt-b' }] },
  'amager-strand-belt': { base: 'amager-strand-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'amager-strand-belt-e' }, { face: 1, id: 'amager-strand-belt-w' }, { face: 2, id: 'amager-strand-belt-n' }, { face: 3, id: 'amager-strand-belt-s' }, { face: 5, id: 'amager-strand-belt-b' }] },
  'downtown-core-belt': { base: 'downtown-core-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'downtown-core-belt-e' }, { face: 1, id: 'downtown-core-belt-w' }, { face: 2, id: 'downtown-core-belt-n' }, { face: 3, id: 'downtown-core-belt-s' }, { face: 5, id: 'downtown-core-belt-b' }] },
  'railway-lands-belt': { base: 'railway-lands-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'railway-lands-belt-e' }, { face: 1, id: 'railway-lands-belt-w' }, { face: 2, id: 'railway-lands-belt-n' }, { face: 3, id: 'railway-lands-belt-s' }, { face: 5, id: 'railway-lands-belt-b' }] },
  'harbourfront-belt': { base: 'harbourfront-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'harbourfront-belt-e' }, { face: 1, id: 'harbourfront-belt-w' }, { face: 2, id: 'harbourfront-belt-n' }, { face: 3, id: 'harbourfront-belt-s' }, { face: 5, id: 'harbourfront-belt-b' }] },
  'toronto-islands-belt': { base: 'toronto-islands-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'toronto-islands-belt-e' }, { face: 1, id: 'toronto-islands-belt-w' }, { face: 2, id: 'toronto-islands-belt-n' }, { face: 3, id: 'toronto-islands-belt-s' }, { face: 5, id: 'toronto-islands-belt-b' }] },
  'st-lawrence-belt': { base: 'st-lawrence-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'st-lawrence-belt-e' }, { face: 1, id: 'st-lawrence-belt-w' }, { face: 2, id: 'st-lawrence-belt-n' }, { face: 3, id: 'st-lawrence-belt-s' }, { face: 5, id: 'st-lawrence-belt-b' }] },
  'distillery-belt': { base: 'distillery-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'distillery-belt-e' }, { face: 1, id: 'distillery-belt-w' }, { face: 2, id: 'distillery-belt-n' }, { face: 3, id: 'distillery-belt-s' }, { face: 5, id: 'distillery-belt-b' }] },
  'kensington-belt': { base: 'kensington-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kensington-belt-e' }, { face: 1, id: 'kensington-belt-w' }, { face: 2, id: 'kensington-belt-n' }, { face: 3, id: 'kensington-belt-s' }, { face: 5, id: 'kensington-belt-b' }] },
  'queens-park-belt': { base: 'queens-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'queens-park-belt-e' }, { face: 1, id: 'queens-park-belt-w' }, { face: 2, id: 'queens-park-belt-n' }, { face: 3, id: 'queens-park-belt-s' }, { face: 5, id: 'queens-park-belt-b' }] },
  'annex-belt': { base: 'annex-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'annex-belt-e' }, { face: 1, id: 'annex-belt-w' }, { face: 2, id: 'annex-belt-n' }, { face: 3, id: 'annex-belt-s' }, { face: 5, id: 'annex-belt-b' }] },
  'davenport-belt': { base: 'davenport-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'davenport-belt-e' }, { face: 1, id: 'davenport-belt-w' }, { face: 2, id: 'davenport-belt-n' }, { face: 3, id: 'davenport-belt-s' }, { face: 5, id: 'davenport-belt-b' }] },
  'don-valley-belt': { base: 'don-valley-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'don-valley-belt-e' }, { face: 1, id: 'don-valley-belt-w' }, { face: 2, id: 'don-valley-belt-n' }, { face: 3, id: 'don-valley-belt-s' }, { face: 5, id: 'don-valley-belt-b' }] },
  'high-park-belt': { base: 'high-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'high-park-belt-e' }, { face: 1, id: 'high-park-belt-w' }, { face: 2, id: 'high-park-belt-n' }, { face: 3, id: 'high-park-belt-s' }, { face: 5, id: 'high-park-belt-b' }] },
  'humber-bay-belt': { base: 'humber-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'humber-bay-belt-e' }, { face: 1, id: 'humber-bay-belt-w' }, { face: 2, id: 'humber-bay-belt-n' }, { face: 3, id: 'humber-bay-belt-s' }, { face: 5, id: 'humber-bay-belt-b' }] },
  'beaches-belt': { base: 'beaches-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beaches-belt-e' }, { face: 1, id: 'beaches-belt-w' }, { face: 2, id: 'beaches-belt-n' }, { face: 3, id: 'beaches-belt-s' }, { face: 5, id: 'beaches-belt-b' }] },
  'bluffs-belt': { base: 'bluffs-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bluffs-belt-e' }, { face: 1, id: 'bluffs-belt-w' }, { face: 2, id: 'bluffs-belt-n' }, { face: 3, id: 'bluffs-belt-s' }, { face: 5, id: 'bluffs-belt-b' }] },
  'hoddle-grid-belt': { base: 'hoddle-grid-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hoddle-grid-belt-e' }, { face: 1, id: 'hoddle-grid-belt-w' }, { face: 2, id: 'hoddle-grid-belt-n' }, { face: 3, id: 'hoddle-grid-belt-s' }, { face: 5, id: 'hoddle-grid-belt-b' }] },
  'southbank-belt': { base: 'southbank-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'southbank-belt-e' }, { face: 1, id: 'southbank-belt-w' }, { face: 2, id: 'southbank-belt-n' }, { face: 3, id: 'southbank-belt-s' }, { face: 5, id: 'southbank-belt-b' }] },
  'docklands-belt': { base: 'docklands-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'docklands-belt-e' }, { face: 1, id: 'docklands-belt-w' }, { face: 2, id: 'docklands-belt-n' }, { face: 3, id: 'docklands-belt-s' }, { face: 5, id: 'docklands-belt-b' }] },
  'carlton-belt': { base: 'carlton-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'carlton-belt-e' }, { face: 1, id: 'carlton-belt-w' }, { face: 2, id: 'carlton-belt-n' }, { face: 3, id: 'carlton-belt-s' }, { face: 5, id: 'carlton-belt-b' }] },
  'fitzroy-belt': { base: 'fitzroy-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fitzroy-belt-e' }, { face: 1, id: 'fitzroy-belt-w' }, { face: 2, id: 'fitzroy-belt-n' }, { face: 3, id: 'fitzroy-belt-s' }, { face: 5, id: 'fitzroy-belt-b' }] },
  'abbotsford-belt': { base: 'abbotsford-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'abbotsford-belt-e' }, { face: 1, id: 'abbotsford-belt-w' }, { face: 2, id: 'abbotsford-belt-n' }, { face: 3, id: 'abbotsford-belt-s' }, { face: 5, id: 'abbotsford-belt-b' }] },
  'yarra-park-belt': { base: 'yarra-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yarra-park-belt-e' }, { face: 1, id: 'yarra-park-belt-w' }, { face: 2, id: 'yarra-park-belt-n' }, { face: 3, id: 'yarra-park-belt-s' }, { face: 5, id: 'yarra-park-belt-b' }] },
  'cremorne-belt': { base: 'cremorne-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cremorne-belt-e' }, { face: 1, id: 'cremorne-belt-w' }, { face: 2, id: 'cremorne-belt-n' }, { face: 3, id: 'cremorne-belt-s' }, { face: 5, id: 'cremorne-belt-b' }] },
  'botanic-gardens-belt': { base: 'botanic-gardens-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'botanic-gardens-belt-e' }, { face: 1, id: 'botanic-gardens-belt-w' }, { face: 2, id: 'botanic-gardens-belt-n' }, { face: 3, id: 'botanic-gardens-belt-s' }, { face: 5, id: 'botanic-gardens-belt-b' }] },
  'albert-park-belt': { base: 'albert-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'albert-park-belt-e' }, { face: 1, id: 'albert-park-belt-w' }, { face: 2, id: 'albert-park-belt-n' }, { face: 3, id: 'albert-park-belt-s' }, { face: 5, id: 'albert-park-belt-b' }] },
  'st-kilda-belt': { base: 'st-kilda-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'st-kilda-belt-e' }, { face: 1, id: 'st-kilda-belt-w' }, { face: 2, id: 'st-kilda-belt-n' }, { face: 3, id: 'st-kilda-belt-s' }, { face: 5, id: 'st-kilda-belt-b' }] },
  'port-melbourne-belt': { base: 'port-melbourne-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'port-melbourne-belt-e' }, { face: 1, id: 'port-melbourne-belt-w' }, { face: 2, id: 'port-melbourne-belt-n' }, { face: 3, id: 'port-melbourne-belt-s' }, { face: 5, id: 'port-melbourne-belt-b' }] },
  'williamstown-belt': { base: 'williamstown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'williamstown-belt-e' }, { face: 1, id: 'williamstown-belt-w' }, { face: 2, id: 'williamstown-belt-n' }, { face: 3, id: 'williamstown-belt-s' }, { face: 5, id: 'williamstown-belt-b' }] },
  'footscray-belt': { base: 'footscray-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'footscray-belt-e' }, { face: 1, id: 'footscray-belt-w' }, { face: 2, id: 'footscray-belt-n' }, { face: 3, id: 'footscray-belt-s' }, { face: 5, id: 'footscray-belt-b' }] },
  'brunswick-belt': { base: 'brunswick-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'brunswick-belt-e' }, { face: 1, id: 'brunswick-belt-w' }, { face: 2, id: 'brunswick-belt-n' }, { face: 3, id: 'brunswick-belt-s' }, { face: 5, id: 'brunswick-belt-b' }] },
  'castlehill-belt': { base: 'castlehill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'castlehill-belt-e' }, { face: 1, id: 'castlehill-belt-w' }, { face: 2, id: 'castlehill-belt-n' }, { face: 3, id: 'castlehill-belt-s' }, { face: 5, id: 'castlehill-belt-b' }] },
  'calton-hill-belt': { base: 'calton-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'calton-hill-belt-e' }, { face: 1, id: 'calton-hill-belt-w' }, { face: 2, id: 'calton-hill-belt-n' }, { face: 3, id: 'calton-hill-belt-s' }, { face: 5, id: 'calton-hill-belt-b' }] },
  'holyrood-park-belt': { base: 'holyrood-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'holyrood-park-belt-e' }, { face: 1, id: 'holyrood-park-belt-w' }, { face: 2, id: 'holyrood-park-belt-n' }, { face: 3, id: 'holyrood-park-belt-s' }, { face: 5, id: 'holyrood-park-belt-b' }] },
  'new-town-belt': { base: 'new-town-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'new-town-belt-e' }, { face: 1, id: 'new-town-belt-w' }, { face: 2, id: 'new-town-belt-n' }, { face: 3, id: 'new-town-belt-s' }, { face: 5, id: 'new-town-belt-b' }] },
  'dean-village-belt': { base: 'dean-village-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dean-village-belt-e' }, { face: 1, id: 'dean-village-belt-w' }, { face: 2, id: 'dean-village-belt-n' }, { face: 3, id: 'dean-village-belt-s' }, { face: 5, id: 'dean-village-belt-b' }] },
  'inverleith-belt': { base: 'inverleith-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'inverleith-belt-e' }, { face: 1, id: 'inverleith-belt-w' }, { face: 2, id: 'inverleith-belt-n' }, { face: 3, id: 'inverleith-belt-s' }, { face: 5, id: 'inverleith-belt-b' }] },
  'leith-shore-belt': { base: 'leith-shore-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'leith-shore-belt-e' }, { face: 1, id: 'leith-shore-belt-w' }, { face: 2, id: 'leith-shore-belt-n' }, { face: 3, id: 'leith-shore-belt-s' }, { face: 5, id: 'leith-shore-belt-b' }] },
  'granton-belt': { base: 'granton-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'granton-belt-e' }, { face: 1, id: 'granton-belt-w' }, { face: 2, id: 'granton-belt-n' }, { face: 3, id: 'granton-belt-s' }, { face: 5, id: 'granton-belt-b' }] },
  'portobello-belt': { base: 'portobello-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'portobello-belt-e' }, { face: 1, id: 'portobello-belt-w' }, { face: 2, id: 'portobello-belt-n' }, { face: 3, id: 'portobello-belt-s' }, { face: 5, id: 'portobello-belt-b' }] },
  'craigmillar-belt': { base: 'craigmillar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'craigmillar-belt-e' }, { face: 1, id: 'craigmillar-belt-w' }, { face: 2, id: 'craigmillar-belt-n' }, { face: 3, id: 'craigmillar-belt-s' }, { face: 5, id: 'craigmillar-belt-b' }] },
  'blackford-hill-belt': { base: 'blackford-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'blackford-hill-belt-e' }, { face: 1, id: 'blackford-hill-belt-w' }, { face: 2, id: 'blackford-hill-belt-n' }, { face: 3, id: 'blackford-hill-belt-s' }, { face: 5, id: 'blackford-hill-belt-b' }] },
  'meadows-belt': { base: 'meadows-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'meadows-belt-e' }, { face: 1, id: 'meadows-belt-w' }, { face: 2, id: 'meadows-belt-n' }, { face: 3, id: 'meadows-belt-s' }, { face: 5, id: 'meadows-belt-b' }] },
  'murrayfield-belt': { base: 'murrayfield-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'murrayfield-belt-e' }, { face: 1, id: 'murrayfield-belt-w' }, { face: 2, id: 'murrayfield-belt-n' }, { face: 3, id: 'murrayfield-belt-s' }, { face: 5, id: 'murrayfield-belt-b' }] },
  'corstorphine-hill-belt': { base: 'corstorphine-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'corstorphine-hill-belt-e' }, { face: 1, id: 'corstorphine-hill-belt-w' }, { face: 2, id: 'corstorphine-hill-belt-n' }, { face: 3, id: 'corstorphine-hill-belt-s' }, { face: 5, id: 'corstorphine-hill-belt-b' }] },
  'cramond-belt': { base: 'cramond-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cramond-belt-e' }, { face: 1, id: 'cramond-belt-w' }, { face: 2, id: 'cramond-belt-n' }, { face: 3, id: 'cramond-belt-s' }, { face: 5, id: 'cramond-belt-b' }] },
  'gwanghwamun-belt': { base: 'gwanghwamun-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gwanghwamun-belt-e' }, { face: 1, id: 'gwanghwamun-belt-w' }, { face: 2, id: 'gwanghwamun-belt-n' }, { face: 3, id: 'gwanghwamun-belt-s' }, { face: 5, id: 'gwanghwamun-belt-b' }] },
  'bukchon-belt': { base: 'bukchon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bukchon-belt-e' }, { face: 1, id: 'bukchon-belt-w' }, { face: 2, id: 'bukchon-belt-n' }, { face: 3, id: 'bukchon-belt-s' }, { face: 5, id: 'bukchon-belt-b' }] },
  'namsan-belt': { base: 'namsan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'namsan-belt-e' }, { face: 1, id: 'namsan-belt-w' }, { face: 2, id: 'namsan-belt-n' }, { face: 3, id: 'namsan-belt-s' }, { face: 5, id: 'namsan-belt-b' }] },
  'dongdaemun-belt': { base: 'dongdaemun-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dongdaemun-belt-e' }, { face: 1, id: 'dongdaemun-belt-w' }, { face: 2, id: 'dongdaemun-belt-n' }, { face: 3, id: 'dongdaemun-belt-s' }, { face: 5, id: 'dongdaemun-belt-b' }] },
  'namdaemun-belt': { base: 'namdaemun-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'namdaemun-belt-e' }, { face: 1, id: 'namdaemun-belt-w' }, { face: 2, id: 'namdaemun-belt-n' }, { face: 3, id: 'namdaemun-belt-s' }, { face: 5, id: 'namdaemun-belt-b' }] },
  'yeouido-belt': { base: 'yeouido-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yeouido-belt-e' }, { face: 1, id: 'yeouido-belt-w' }, { face: 2, id: 'yeouido-belt-n' }, { face: 3, id: 'yeouido-belt-s' }, { face: 5, id: 'yeouido-belt-b' }] },
  'yongsan-belt': { base: 'yongsan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yongsan-belt-e' }, { face: 1, id: 'yongsan-belt-w' }, { face: 2, id: 'yongsan-belt-n' }, { face: 3, id: 'yongsan-belt-s' }, { face: 5, id: 'yongsan-belt-b' }] },
  'gangnam-belt': { base: 'gangnam-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gangnam-belt-e' }, { face: 1, id: 'gangnam-belt-w' }, { face: 2, id: 'gangnam-belt-n' }, { face: 3, id: 'gangnam-belt-s' }, { face: 5, id: 'gangnam-belt-b' }] },
  'jamsil-belt': { base: 'jamsil-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jamsil-belt-e' }, { face: 1, id: 'jamsil-belt-w' }, { face: 2, id: 'jamsil-belt-n' }, { face: 3, id: 'jamsil-belt-s' }, { face: 5, id: 'jamsil-belt-b' }] },
  'mokdong-belt': { base: 'mokdong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mokdong-belt-e' }, { face: 1, id: 'mokdong-belt-w' }, { face: 2, id: 'mokdong-belt-n' }, { face: 3, id: 'mokdong-belt-s' }, { face: 5, id: 'mokdong-belt-b' }] },
  'seonyudo-belt': { base: 'seonyudo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'seonyudo-belt-e' }, { face: 1, id: 'seonyudo-belt-w' }, { face: 2, id: 'seonyudo-belt-n' }, { face: 3, id: 'seonyudo-belt-s' }, { face: 5, id: 'seonyudo-belt-b' }] },
  'sangam-belt': { base: 'sangam-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sangam-belt-e' }, { face: 1, id: 'sangam-belt-w' }, { face: 2, id: 'sangam-belt-n' }, { face: 3, id: 'sangam-belt-s' }, { face: 5, id: 'sangam-belt-b' }] },
  'bukhansan-belt': { base: 'bukhansan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bukhansan-belt-e' }, { face: 1, id: 'bukhansan-belt-w' }, { face: 2, id: 'bukhansan-belt-n' }, { face: 3, id: 'bukhansan-belt-s' }, { face: 5, id: 'bukhansan-belt-b' }] },
  'naksan-belt': { base: 'naksan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'naksan-belt-e' }, { face: 1, id: 'naksan-belt-w' }, { face: 2, id: 'naksan-belt-n' }, { face: 3, id: 'naksan-belt-s' }, { face: 5, id: 'naksan-belt-b' }] },
  'seongsu-belt': { base: 'seongsu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'seongsu-belt-e' }, { face: 1, id: 'seongsu-belt-w' }, { face: 2, id: 'seongsu-belt-n' }, { face: 3, id: 'seongsu-belt-s' }, { face: 5, id: 'seongsu-belt-b' }] },
  'bunker-hill-belt': { base: 'bunker-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bunker-hill-belt-e' }, { face: 1, id: 'bunker-hill-belt-w' }, { face: 2, id: 'bunker-hill-belt-n' }, { face: 3, id: 'bunker-hill-belt-s' }, { face: 5, id: 'bunker-hill-belt-b' }] },
  'arts-district-belt': { base: 'arts-district-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'arts-district-belt-e' }, { face: 1, id: 'arts-district-belt-w' }, { face: 2, id: 'arts-district-belt-n' }, { face: 3, id: 'arts-district-belt-s' }, { face: 5, id: 'arts-district-belt-b' }] },
  'el-pueblo-belt': { base: 'el-pueblo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'el-pueblo-belt-e' }, { face: 1, id: 'el-pueblo-belt-w' }, { face: 2, id: 'el-pueblo-belt-n' }, { face: 3, id: 'el-pueblo-belt-s' }, { face: 5, id: 'el-pueblo-belt-b' }] },
  'elysian-park-belt': { base: 'elysian-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'elysian-park-belt-e' }, { face: 1, id: 'elysian-park-belt-w' }, { face: 2, id: 'elysian-park-belt-n' }, { face: 3, id: 'elysian-park-belt-s' }, { face: 5, id: 'elysian-park-belt-b' }] },
  'koreatown-belt': { base: 'koreatown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'koreatown-belt-e' }, { face: 1, id: 'koreatown-belt-w' }, { face: 2, id: 'koreatown-belt-n' }, { face: 3, id: 'koreatown-belt-s' }, { face: 5, id: 'koreatown-belt-b' }] },
  'melrose-belt': { base: 'melrose-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'melrose-belt-e' }, { face: 1, id: 'melrose-belt-w' }, { face: 2, id: 'melrose-belt-n' }, { face: 3, id: 'melrose-belt-s' }, { face: 5, id: 'melrose-belt-b' }] },
  'hollywood-boulevard-belt': { base: 'hollywood-boulevard-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hollywood-boulevard-belt-e' }, { face: 1, id: 'hollywood-boulevard-belt-w' }, { face: 2, id: 'hollywood-boulevard-belt-n' }, { face: 3, id: 'hollywood-boulevard-belt-s' }, { face: 5, id: 'hollywood-boulevard-belt-b' }] },
  'cahuenga-pass-belt': { base: 'cahuenga-pass-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cahuenga-pass-belt-e' }, { face: 1, id: 'cahuenga-pass-belt-w' }, { face: 2, id: 'cahuenga-pass-belt-n' }, { face: 3, id: 'cahuenga-pass-belt-s' }, { face: 5, id: 'cahuenga-pass-belt-b' }] },
  'griffith-park-belt': { base: 'griffith-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'griffith-park-belt-e' }, { face: 1, id: 'griffith-park-belt-w' }, { face: 2, id: 'griffith-park-belt-n' }, { face: 3, id: 'griffith-park-belt-s' }, { face: 5, id: 'griffith-park-belt-b' }] },
  'mount-lee-belt': { base: 'mount-lee-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mount-lee-belt-e' }, { face: 1, id: 'mount-lee-belt-w' }, { face: 2, id: 'mount-lee-belt-n' }, { face: 3, id: 'mount-lee-belt-s' }, { face: 5, id: 'mount-lee-belt-b' }] },
  'glendale-narrows-belt': { base: 'glendale-narrows-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'glendale-narrows-belt-e' }, { face: 1, id: 'glendale-narrows-belt-w' }, { face: 2, id: 'glendale-narrows-belt-n' }, { face: 3, id: 'glendale-narrows-belt-s' }, { face: 5, id: 'glendale-narrows-belt-b' }] },
  'century-city-belt': { base: 'century-city-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'century-city-belt-e' }, { face: 1, id: 'century-city-belt-w' }, { face: 2, id: 'century-city-belt-n' }, { face: 3, id: 'century-city-belt-s' }, { face: 5, id: 'century-city-belt-b' }] },
  'miracle-mile-belt': { base: 'miracle-mile-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'miracle-mile-belt-e' }, { face: 1, id: 'miracle-mile-belt-w' }, { face: 2, id: 'miracle-mile-belt-n' }, { face: 3, id: 'miracle-mile-belt-s' }, { face: 5, id: 'miracle-mile-belt-b' }] },
  'boyle-heights-belt': { base: 'boyle-heights-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'boyle-heights-belt-e' }, { face: 1, id: 'boyle-heights-belt-w' }, { face: 2, id: 'boyle-heights-belt-n' }, { face: 3, id: 'boyle-heights-belt-s' }, { face: 5, id: 'boyle-heights-belt-b' }] },
  'watts-belt': { base: 'watts-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'watts-belt-e' }, { face: 1, id: 'watts-belt-w' }, { face: 2, id: 'watts-belt-n' }, { face: 3, id: 'watts-belt-s' }, { face: 5, id: 'watts-belt-b' }] },
  'downtown-belt': { base: 'downtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'downtown-belt-e' }, { face: 1, id: 'downtown-belt-w' }, { face: 2, id: 'downtown-belt-n' }, { face: 3, id: 'downtown-belt-s' }, { face: 5, id: 'downtown-belt-b' }] },
  'business-bay-belt': { base: 'business-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'business-bay-belt-e' }, { face: 1, id: 'business-bay-belt-w' }, { face: 2, id: 'business-bay-belt-n' }, { face: 3, id: 'business-bay-belt-s' }, { face: 5, id: 'business-bay-belt-b' }] },
  'trade-centre-belt': { base: 'trade-centre-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'trade-centre-belt-e' }, { face: 1, id: 'trade-centre-belt-w' }, { face: 2, id: 'trade-centre-belt-n' }, { face: 3, id: 'trade-centre-belt-s' }, { face: 5, id: 'trade-centre-belt-b' }] },
  'al-safa-belt': { base: 'al-safa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'al-safa-belt-e' }, { face: 1, id: 'al-safa-belt-w' }, { face: 2, id: 'al-safa-belt-n' }, { face: 3, id: 'al-safa-belt-s' }, { face: 5, id: 'al-safa-belt-b' }] },
  'jumeira-belt': { base: 'jumeira-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jumeira-belt-e' }, { face: 1, id: 'jumeira-belt-w' }, { face: 2, id: 'jumeira-belt-n' }, { face: 3, id: 'jumeira-belt-s' }, { face: 5, id: 'jumeira-belt-b' }] },
  'al-mina-belt': { base: 'al-mina-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'al-mina-belt-e' }, { face: 1, id: 'al-mina-belt-w' }, { face: 2, id: 'al-mina-belt-n' }, { face: 3, id: 'al-mina-belt-s' }, { face: 5, id: 'al-mina-belt-b' }] },
  'al-shindagha-belt': { base: 'al-shindagha-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'al-shindagha-belt-e' }, { face: 1, id: 'al-shindagha-belt-w' }, { face: 2, id: 'al-shindagha-belt-n' }, { face: 3, id: 'al-shindagha-belt-s' }, { face: 5, id: 'al-shindagha-belt-b' }] },
  'al-fahidi-belt': { base: 'al-fahidi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'al-fahidi-belt-e' }, { face: 1, id: 'al-fahidi-belt-w' }, { face: 2, id: 'al-fahidi-belt-n' }, { face: 3, id: 'al-fahidi-belt-s' }, { face: 5, id: 'al-fahidi-belt-b' }] },
  'al-jaddaf-belt': { base: 'al-jaddaf-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'al-jaddaf-belt-e' }, { face: 1, id: 'al-jaddaf-belt-w' }, { face: 2, id: 'al-jaddaf-belt-n' }, { face: 3, id: 'al-jaddaf-belt-s' }, { face: 5, id: 'al-jaddaf-belt-b' }] },
  'ras-al-khor-belt': { base: 'ras-al-khor-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ras-al-khor-belt-e' }, { face: 1, id: 'ras-al-khor-belt-w' }, { face: 2, id: 'ras-al-khor-belt-n' }, { face: 3, id: 'ras-al-khor-belt-s' }, { face: 5, id: 'ras-al-khor-belt-b' }] },
  'nad-al-sheba-belt': { base: 'nad-al-sheba-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nad-al-sheba-belt-e' }, { face: 1, id: 'nad-al-sheba-belt-w' }, { face: 2, id: 'nad-al-sheba-belt-n' }, { face: 3, id: 'nad-al-sheba-belt-s' }, { face: 5, id: 'nad-al-sheba-belt-b' }] },
  'umm-suqeim-belt': { base: 'umm-suqeim-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'umm-suqeim-belt-e' }, { face: 1, id: 'umm-suqeim-belt-w' }, { face: 2, id: 'umm-suqeim-belt-n' }, { face: 3, id: 'umm-suqeim-belt-s' }, { face: 5, id: 'umm-suqeim-belt-b' }] },
  'palm-jumeirah-belt': { base: 'palm-jumeirah-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'palm-jumeirah-belt-e' }, { face: 1, id: 'palm-jumeirah-belt-w' }, { face: 2, id: 'palm-jumeirah-belt-n' }, { face: 3, id: 'palm-jumeirah-belt-s' }, { face: 5, id: 'palm-jumeirah-belt-b' }] },
  'marsa-dubai-belt': { base: 'marsa-dubai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marsa-dubai-belt-e' }, { face: 1, id: 'marsa-dubai-belt-w' }, { face: 2, id: 'marsa-dubai-belt-n' }, { face: 3, id: 'marsa-dubai-belt-s' }, { face: 5, id: 'marsa-dubai-belt-b' }] },
  'bluewaters-belt': { base: 'bluewaters-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bluewaters-belt-e' }, { face: 1, id: 'bluewaters-belt-w' }, { face: 2, id: 'bluewaters-belt-n' }, { face: 3, id: 'bluewaters-belt-s' }, { face: 5, id: 'bluewaters-belt-b' }] },
  'rattanakosin-belt': { base: 'rattanakosin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rattanakosin-belt-e' }, { face: 1, id: 'rattanakosin-belt-w' }, { face: 2, id: 'rattanakosin-belt-n' }, { face: 3, id: 'rattanakosin-belt-s' }, { face: 5, id: 'rattanakosin-belt-b' }] },
  'bangkok-noi-belt': { base: 'bangkok-noi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bangkok-noi-belt-e' }, { face: 1, id: 'bangkok-noi-belt-w' }, { face: 2, id: 'bangkok-noi-belt-n' }, { face: 3, id: 'bangkok-noi-belt-s' }, { face: 5, id: 'bangkok-noi-belt-b' }] },
  'kudi-chin-belt': { base: 'kudi-chin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kudi-chin-belt-e' }, { face: 1, id: 'kudi-chin-belt-w' }, { face: 2, id: 'kudi-chin-belt-n' }, { face: 3, id: 'kudi-chin-belt-s' }, { face: 5, id: 'kudi-chin-belt-b' }] },
  'wongwian-yai-belt': { base: 'wongwian-yai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wongwian-yai-belt-e' }, { face: 1, id: 'wongwian-yai-belt-w' }, { face: 2, id: 'wongwian-yai-belt-n' }, { face: 3, id: 'wongwian-yai-belt-s' }, { face: 5, id: 'wongwian-yai-belt-b' }] },
  'yaowarat-belt': { base: 'yaowarat-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yaowarat-belt-e' }, { face: 1, id: 'yaowarat-belt-w' }, { face: 2, id: 'yaowarat-belt-n' }, { face: 3, id: 'yaowarat-belt-s' }, { face: 5, id: 'yaowarat-belt-b' }] },
  'dusit-belt': { base: 'dusit-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dusit-belt-e' }, { face: 1, id: 'dusit-belt-w' }, { face: 2, id: 'dusit-belt-n' }, { face: 3, id: 'dusit-belt-s' }, { face: 5, id: 'dusit-belt-b' }] },
  'bang-rak-belt': { base: 'bang-rak-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bang-rak-belt-e' }, { face: 1, id: 'bang-rak-belt-w' }, { face: 2, id: 'bang-rak-belt-n' }, { face: 3, id: 'bang-rak-belt-s' }, { face: 5, id: 'bang-rak-belt-b' }] },
  'siam-belt': { base: 'siam-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'siam-belt-e' }, { face: 1, id: 'siam-belt-w' }, { face: 2, id: 'siam-belt-n' }, { face: 3, id: 'siam-belt-s' }, { face: 5, id: 'siam-belt-b' }] },
  'lumphini-belt': { base: 'lumphini-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lumphini-belt-e' }, { face: 1, id: 'lumphini-belt-w' }, { face: 2, id: 'lumphini-belt-n' }, { face: 3, id: 'lumphini-belt-s' }, { face: 5, id: 'lumphini-belt-b' }] },
  'chatuchak-belt': { base: 'chatuchak-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chatuchak-belt-e' }, { face: 1, id: 'chatuchak-belt-w' }, { face: 2, id: 'chatuchak-belt-n' }, { face: 3, id: 'chatuchak-belt-s' }, { face: 5, id: 'chatuchak-belt-b' }] },
  'asok-belt': { base: 'asok-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'asok-belt-e' }, { face: 1, id: 'asok-belt-w' }, { face: 2, id: 'asok-belt-n' }, { face: 3, id: 'asok-belt-s' }, { face: 5, id: 'asok-belt-b' }] },
  'khlong-toei-belt': { base: 'khlong-toei-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'khlong-toei-belt-e' }, { face: 1, id: 'khlong-toei-belt-w' }, { face: 2, id: 'khlong-toei-belt-n' }, { face: 3, id: 'khlong-toei-belt-s' }, { face: 5, id: 'khlong-toei-belt-b' }] },
  'bang-krachao-belt': { base: 'bang-krachao-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bang-krachao-belt-e' }, { face: 1, id: 'bang-krachao-belt-w' }, { face: 2, id: 'bang-krachao-belt-n' }, { face: 3, id: 'bang-krachao-belt-s' }, { face: 5, id: 'bang-krachao-belt-b' }] },
  'huai-khwang-belt': { base: 'huai-khwang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'huai-khwang-belt-e' }, { face: 1, id: 'huai-khwang-belt-w' }, { face: 2, id: 'huai-khwang-belt-n' }, { face: 3, id: 'huai-khwang-belt-s' }, { face: 5, id: 'huai-khwang-belt-b' }] },
  'phra-khanong-belt': { base: 'phra-khanong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'phra-khanong-belt-e' }, { face: 1, id: 'phra-khanong-belt-w' }, { face: 2, id: 'phra-khanong-belt-n' }, { face: 3, id: 'phra-khanong-belt-s' }, { face: 5, id: 'phra-khanong-belt-b' }] },
  'sea-point-belt': { base: 'sea-point-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sea-point-belt-e' }, { face: 1, id: 'sea-point-belt-w' }, { face: 2, id: 'sea-point-belt-n' }, { face: 3, id: 'sea-point-belt-s' }, { face: 5, id: 'sea-point-belt-b' }] },
  'clifton-belt': { base: 'clifton-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'clifton-belt-e' }, { face: 1, id: 'clifton-belt-w' }, { face: 2, id: 'clifton-belt-n' }, { face: 3, id: 'clifton-belt-s' }, { face: 5, id: 'clifton-belt-b' }] },
  'camps-bay-belt': { base: 'camps-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'camps-bay-belt-e' }, { face: 1, id: 'camps-bay-belt-w' }, { face: 2, id: 'camps-bay-belt-n' }, { face: 3, id: 'camps-bay-belt-s' }, { face: 5, id: 'camps-bay-belt-b' }] },
  'green-point-belt': { base: 'green-point-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'green-point-belt-e' }, { face: 1, id: 'green-point-belt-w' }, { face: 2, id: 'green-point-belt-n' }, { face: 3, id: 'green-point-belt-s' }, { face: 5, id: 'green-point-belt-b' }] },
  'table-bay-harbour-belt': { base: 'table-bay-harbour-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'table-bay-harbour-belt-e' }, { face: 1, id: 'table-bay-harbour-belt-w' }, { face: 2, id: 'table-bay-harbour-belt-n' }, { face: 3, id: 'table-bay-harbour-belt-s' }, { face: 5, id: 'table-bay-harbour-belt-b' }] },
  'paarden-eiland-belt': { base: 'paarden-eiland-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'paarden-eiland-belt-e' }, { face: 1, id: 'paarden-eiland-belt-w' }, { face: 2, id: 'paarden-eiland-belt-n' }, { face: 3, id: 'paarden-eiland-belt-s' }, { face: 5, id: 'paarden-eiland-belt-b' }] },
  'bo-kaap-belt': { base: 'bo-kaap-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bo-kaap-belt-e' }, { face: 1, id: 'bo-kaap-belt-w' }, { face: 2, id: 'bo-kaap-belt-n' }, { face: 3, id: 'bo-kaap-belt-s' }, { face: 5, id: 'bo-kaap-belt-b' }] },
  'cape-town-cbd-belt': { base: 'cape-town-cbd-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cape-town-cbd-belt-e' }, { face: 1, id: 'cape-town-cbd-belt-w' }, { face: 2, id: 'cape-town-cbd-belt-n' }, { face: 3, id: 'cape-town-cbd-belt-s' }, { face: 5, id: 'cape-town-cbd-belt-b' }] },
  'gardens-belt': { base: 'gardens-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gardens-belt-e' }, { face: 1, id: 'gardens-belt-w' }, { face: 2, id: 'gardens-belt-n' }, { face: 3, id: 'gardens-belt-s' }, { face: 5, id: 'gardens-belt-b' }] },
  'woodstock-belt': { base: 'woodstock-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'woodstock-belt-e' }, { face: 1, id: 'woodstock-belt-w' }, { face: 2, id: 'woodstock-belt-n' }, { face: 3, id: 'woodstock-belt-s' }, { face: 5, id: 'woodstock-belt-b' }] },
  'salt-river-belt': { base: 'salt-river-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'salt-river-belt-e' }, { face: 1, id: 'salt-river-belt-w' }, { face: 2, id: 'salt-river-belt-n' }, { face: 3, id: 'salt-river-belt-s' }, { face: 5, id: 'salt-river-belt-b' }] },
  'observatory-belt': { base: 'observatory-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'observatory-belt-e' }, { face: 1, id: 'observatory-belt-w' }, { face: 2, id: 'observatory-belt-n' }, { face: 3, id: 'observatory-belt-s' }, { face: 5, id: 'observatory-belt-b' }] },
  'rondebosch-belt': { base: 'rondebosch-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rondebosch-belt-e' }, { face: 1, id: 'rondebosch-belt-w' }, { face: 2, id: 'rondebosch-belt-n' }, { face: 3, id: 'rondebosch-belt-s' }, { face: 5, id: 'rondebosch-belt-b' }] },
  'newlands-belt': { base: 'newlands-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'newlands-belt-e' }, { face: 1, id: 'newlands-belt-w' }, { face: 2, id: 'newlands-belt-n' }, { face: 3, id: 'newlands-belt-s' }, { face: 5, id: 'newlands-belt-b' }] },
  'kirstenbosch-belt': { base: 'kirstenbosch-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kirstenbosch-belt-e' }, { face: 1, id: 'kirstenbosch-belt-w' }, { face: 2, id: 'kirstenbosch-belt-n' }, { face: 3, id: 'kirstenbosch-belt-s' }, { face: 5, id: 'kirstenbosch-belt-b' }] },
  'rocinha-belt': { base: 'rocinha-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rocinha-belt-e' }, { face: 1, id: 'rocinha-belt-w' }, { face: 2, id: 'rocinha-belt-n' }, { face: 3, id: 'rocinha-belt-s' }, { face: 5, id: 'rocinha-belt-b' }] },
  'ipanema-belt': { base: 'ipanema-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ipanema-belt-e' }, { face: 1, id: 'ipanema-belt-w' }, { face: 2, id: 'ipanema-belt-n' }, { face: 3, id: 'ipanema-belt-s' }, { face: 5, id: 'ipanema-belt-b' }] },
  'leblon-belt': { base: 'leblon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'leblon-belt-e' }, { face: 1, id: 'leblon-belt-w' }, { face: 2, id: 'leblon-belt-n' }, { face: 3, id: 'leblon-belt-s' }, { face: 5, id: 'leblon-belt-b' }] },
  'tijuca-belt': { base: 'tijuca-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tijuca-belt-e' }, { face: 1, id: 'tijuca-belt-w' }, { face: 2, id: 'tijuca-belt-n' }, { face: 3, id: 'tijuca-belt-s' }, { face: 5, id: 'tijuca-belt-b' }] },
  'gamboa-belt': { base: 'gamboa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gamboa-belt-e' }, { face: 1, id: 'gamboa-belt-w' }, { face: 2, id: 'gamboa-belt-n' }, { face: 3, id: 'gamboa-belt-s' }, { face: 5, id: 'gamboa-belt-b' }] },
  'copacabana-belt': { base: 'copacabana-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'copacabana-belt-e' }, { face: 1, id: 'copacabana-belt-w' }, { face: 2, id: 'copacabana-belt-n' }, { face: 3, id: 'copacabana-belt-s' }, { face: 5, id: 'copacabana-belt-b' }] },
  'sao-cristovao-belt': { base: 'sao-cristovao-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sao-cristovao-belt-e' }, { face: 1, id: 'sao-cristovao-belt-w' }, { face: 2, id: 'sao-cristovao-belt-n' }, { face: 3, id: 'sao-cristovao-belt-s' }, { face: 5, id: 'sao-cristovao-belt-b' }] },
  'maracana-belt': { base: 'maracana-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'maracana-belt-e' }, { face: 1, id: 'maracana-belt-w' }, { face: 2, id: 'maracana-belt-n' }, { face: 3, id: 'maracana-belt-s' }, { face: 5, id: 'maracana-belt-b' }] },
  'centro-belt': { base: 'centro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'centro-belt-e' }, { face: 1, id: 'centro-belt-w' }, { face: 2, id: 'centro-belt-n' }, { face: 3, id: 'centro-belt-s' }, { face: 5, id: 'centro-belt-b' }] },
  'jardim-botanico-belt': { base: 'jardim-botanico-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jardim-botanico-belt-e' }, { face: 1, id: 'jardim-botanico-belt-w' }, { face: 2, id: 'jardim-botanico-belt-n' }, { face: 3, id: 'jardim-botanico-belt-s' }, { face: 5, id: 'jardim-botanico-belt-b' }] },
  'urca-belt': { base: 'urca-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'urca-belt-e' }, { face: 1, id: 'urca-belt-w' }, { face: 2, id: 'urca-belt-n' }, { face: 3, id: 'urca-belt-s' }, { face: 5, id: 'urca-belt-b' }] },
  'botafogo-belt': { base: 'botafogo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'botafogo-belt-e' }, { face: 1, id: 'botafogo-belt-w' }, { face: 2, id: 'botafogo-belt-n' }, { face: 3, id: 'botafogo-belt-s' }, { face: 5, id: 'botafogo-belt-b' }] },
  'flamengo-belt': { base: 'flamengo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'flamengo-belt-e' }, { face: 1, id: 'flamengo-belt-w' }, { face: 2, id: 'flamengo-belt-n' }, { face: 3, id: 'flamengo-belt-s' }, { face: 5, id: 'flamengo-belt-b' }] },
  'santa-teresa-belt': { base: 'santa-teresa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'santa-teresa-belt-e' }, { face: 1, id: 'santa-teresa-belt-w' }, { face: 2, id: 'santa-teresa-belt-n' }, { face: 3, id: 'santa-teresa-belt-s' }, { face: 5, id: 'santa-teresa-belt-b' }] },
  'cosme-velho-belt': { base: 'cosme-velho-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cosme-velho-belt-e' }, { face: 1, id: 'cosme-velho-belt-w' }, { face: 2, id: 'cosme-velho-belt-n' }, { face: 3, id: 'cosme-velho-belt-s' }, { face: 5, id: 'cosme-velho-belt-b' }] },
  'rio-cube': { base: 'rio-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'rio-cube-e' }, { face: 1, id: 'rio-cube-w' }, { face: 2, id: 'rio-cube-n' }, { face: 3, id: 'rio-cube-s' }, { face: 5, id: 'rio-cube-b' }] },
  'eyup-belt': { base: 'eyup-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'eyup-belt-e' }, { face: 1, id: 'eyup-belt-w' }, { face: 2, id: 'eyup-belt-n' }, { face: 3, id: 'eyup-belt-s' }, { face: 5, id: 'eyup-belt-b' }] },
  'fenerbahce-kalamis-belt': { base: 'fenerbahce-kalamis-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fenerbahce-kalamis-belt-e' }, { face: 1, id: 'fenerbahce-kalamis-belt-w' }, { face: 2, id: 'fenerbahce-kalamis-belt-n' }, { face: 3, id: 'fenerbahce-kalamis-belt-s' }, { face: 5, id: 'fenerbahce-kalamis-belt-b' }] },
  'fener-balat-belt': { base: 'fener-balat-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fener-balat-belt-e' }, { face: 1, id: 'fener-balat-belt-w' }, { face: 2, id: 'fener-balat-belt-n' }, { face: 3, id: 'fener-balat-belt-s' }, { face: 5, id: 'fener-balat-belt-b' }] },
  'sisli-nisantasi-belt': { base: 'sisli-nisantasi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sisli-nisantasi-belt-e' }, { face: 1, id: 'sisli-nisantasi-belt-w' }, { face: 2, id: 'sisli-nisantasi-belt-n' }, { face: 3, id: 'sisli-nisantasi-belt-s' }, { face: 5, id: 'sisli-nisantasi-belt-b' }] },
  'ortakoy-bogazici-belt': { base: 'ortakoy-bogazici-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ortakoy-bogazici-belt-e' }, { face: 1, id: 'ortakoy-bogazici-belt-w' }, { face: 2, id: 'ortakoy-bogazici-belt-n' }, { face: 3, id: 'ortakoy-bogazici-belt-s' }, { face: 5, id: 'ortakoy-bogazici-belt-b' }] },
  'kuzguncuk-beylerbeyi-belt': { base: 'kuzguncuk-beylerbeyi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kuzguncuk-beylerbeyi-belt-e' }, { face: 1, id: 'kuzguncuk-beylerbeyi-belt-w' }, { face: 2, id: 'kuzguncuk-beylerbeyi-belt-n' }, { face: 3, id: 'kuzguncuk-beylerbeyi-belt-s' }, { face: 5, id: 'kuzguncuk-beylerbeyi-belt-b' }] },
  'kadikoy-belt': { base: 'kadikoy-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kadikoy-belt-e' }, { face: 1, id: 'kadikoy-belt-w' }, { face: 2, id: 'kadikoy-belt-n' }, { face: 3, id: 'kadikoy-belt-s' }, { face: 5, id: 'kadikoy-belt-b' }] },
  'suleymaniye-belt': { base: 'suleymaniye-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'suleymaniye-belt-e' }, { face: 1, id: 'suleymaniye-belt-w' }, { face: 2, id: 'suleymaniye-belt-n' }, { face: 3, id: 'suleymaniye-belt-s' }, { face: 5, id: 'suleymaniye-belt-b' }] },
  'kasimpasa-halic-belt': { base: 'kasimpasa-halic-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kasimpasa-halic-belt-e' }, { face: 1, id: 'kasimpasa-halic-belt-w' }, { face: 2, id: 'kasimpasa-halic-belt-n' }, { face: 3, id: 'kasimpasa-halic-belt-s' }, { face: 5, id: 'kasimpasa-halic-belt-b' }] },
  'besiktas-dolmabahce-belt': { base: 'besiktas-dolmabahce-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'besiktas-dolmabahce-belt-e' }, { face: 1, id: 'besiktas-dolmabahce-belt-w' }, { face: 2, id: 'besiktas-dolmabahce-belt-n' }, { face: 3, id: 'besiktas-dolmabahce-belt-s' }, { face: 5, id: 'besiktas-dolmabahce-belt-b' }] },
  'beyoglu-taksim-belt': { base: 'beyoglu-taksim-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beyoglu-taksim-belt-e' }, { face: 1, id: 'beyoglu-taksim-belt-w' }, { face: 2, id: 'beyoglu-taksim-belt-n' }, { face: 3, id: 'beyoglu-taksim-belt-s' }, { face: 5, id: 'beyoglu-taksim-belt-b' }] },
  'karakoy-galata-belt': { base: 'karakoy-galata-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'karakoy-galata-belt-e' }, { face: 1, id: 'karakoy-galata-belt-w' }, { face: 2, id: 'karakoy-galata-belt-n' }, { face: 3, id: 'karakoy-galata-belt-s' }, { face: 5, id: 'karakoy-galata-belt-b' }] },
  'haydarpasa-harem-belt': { base: 'haydarpasa-harem-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'haydarpasa-harem-belt-e' }, { face: 1, id: 'haydarpasa-harem-belt-w' }, { face: 2, id: 'haydarpasa-harem-belt-n' }, { face: 3, id: 'haydarpasa-harem-belt-s' }, { face: 5, id: 'haydarpasa-harem-belt-b' }] },
  'sultanahmet-belt': { base: 'sultanahmet-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sultanahmet-belt-e' }, { face: 1, id: 'sultanahmet-belt-w' }, { face: 2, id: 'sultanahmet-belt-n' }, { face: 3, id: 'sultanahmet-belt-s' }, { face: 5, id: 'sultanahmet-belt-b' }] },
  'uskudar-belt': { base: 'uskudar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'uskudar-belt-e' }, { face: 1, id: 'uskudar-belt-w' }, { face: 2, id: 'uskudar-belt-n' }, { face: 3, id: 'uskudar-belt-s' }, { face: 5, id: 'uskudar-belt-b' }] },
  'istanbul-cube': { base: 'istanbul-cube-z', worldScale: 4.0, faces: [{ face: 0, id: 'istanbul-cube-e' }, { face: 1, id: 'istanbul-cube-w' }, { face: 2, id: 'istanbul-cube-n' }, { face: 3, id: 'istanbul-cube-s' }, { face: 5, id: 'istanbul-cube-b' }] },
  'futian-cbd-belt': { base: 'futian-cbd-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'futian-cbd-belt-e' }, { face: 1, id: 'futian-cbd-belt-w' }, { face: 2, id: 'futian-cbd-belt-n' }, { face: 3, id: 'futian-cbd-belt-s' }, { face: 5, id: 'futian-cbd-belt-b' }] },
  'huaqiangbei-belt': { base: 'huaqiangbei-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'huaqiangbei-belt-e' }, { face: 1, id: 'huaqiangbei-belt-w' }, { face: 2, id: 'huaqiangbei-belt-n' }, { face: 3, id: 'huaqiangbei-belt-s' }, { face: 5, id: 'huaqiangbei-belt-b' }] },
  'luohu-belt': { base: 'luohu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'luohu-belt-e' }, { face: 1, id: 'luohu-belt-w' }, { face: 2, id: 'luohu-belt-n' }, { face: 3, id: 'luohu-belt-s' }, { face: 5, id: 'luohu-belt-b' }] },
  'houhai-belt': { base: 'houhai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'houhai-belt-e' }, { face: 1, id: 'houhai-belt-w' }, { face: 2, id: 'houhai-belt-n' }, { face: 3, id: 'houhai-belt-s' }, { face: 5, id: 'houhai-belt-b' }] },
  'nanshan-belt': { base: 'nanshan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nanshan-belt-e' }, { face: 1, id: 'nanshan-belt-w' }, { face: 2, id: 'nanshan-belt-n' }, { face: 3, id: 'nanshan-belt-s' }, { face: 5, id: 'nanshan-belt-b' }] },
  'shekou-belt': { base: 'shekou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shekou-belt-e' }, { face: 1, id: 'shekou-belt-w' }, { face: 2, id: 'shekou-belt-n' }, { face: 3, id: 'shekou-belt-s' }, { face: 5, id: 'shekou-belt-b' }] },
  'huaqiaocheng-belt': { base: 'huaqiaocheng-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'huaqiaocheng-belt-e' }, { face: 1, id: 'huaqiaocheng-belt-w' }, { face: 2, id: 'huaqiaocheng-belt-n' }, { face: 3, id: 'huaqiaocheng-belt-s' }, { face: 5, id: 'huaqiaocheng-belt-b' }] },
  'baoan-belt': { base: 'baoan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'baoan-belt-e' }, { face: 1, id: 'baoan-belt-w' }, { face: 2, id: 'baoan-belt-n' }, { face: 3, id: 'baoan-belt-s' }, { face: 5, id: 'baoan-belt-b' }] },
  'xixiang-belt': { base: 'xixiang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xixiang-belt-e' }, { face: 1, id: 'xixiang-belt-w' }, { face: 2, id: 'xixiang-belt-n' }, { face: 3, id: 'xixiang-belt-s' }, { face: 5, id: 'xixiang-belt-b' }] },
  'longhua-belt': { base: 'longhua-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'longhua-belt-e' }, { face: 1, id: 'longhua-belt-w' }, { face: 2, id: 'longhua-belt-n' }, { face: 3, id: 'longhua-belt-s' }, { face: 5, id: 'longhua-belt-b' }] },
  'bantian-belt': { base: 'bantian-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bantian-belt-e' }, { face: 1, id: 'bantian-belt-w' }, { face: 2, id: 'bantian-belt-n' }, { face: 3, id: 'bantian-belt-s' }, { face: 5, id: 'bantian-belt-b' }] },
  'buji-belt': { base: 'buji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'buji-belt-e' }, { face: 1, id: 'buji-belt-w' }, { face: 2, id: 'buji-belt-n' }, { face: 3, id: 'buji-belt-s' }, { face: 5, id: 'buji-belt-b' }] },
  'santa-croce-belt': { base: 'santa-croce-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'santa-croce-belt-e' }, { face: 1, id: 'santa-croce-belt-w' }, { face: 2, id: 'santa-croce-belt-n' }, { face: 3, id: 'santa-croce-belt-s' }, { face: 5, id: 'santa-croce-belt-b' }] },
  'cannaregio-belt': { base: 'cannaregio-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cannaregio-belt-e' }, { face: 1, id: 'cannaregio-belt-w' }, { face: 2, id: 'cannaregio-belt-n' }, { face: 3, id: 'cannaregio-belt-s' }, { face: 5, id: 'cannaregio-belt-b' }] },
  'rialto-belt': { base: 'rialto-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rialto-belt-e' }, { face: 1, id: 'rialto-belt-w' }, { face: 2, id: 'rialto-belt-n' }, { face: 3, id: 'rialto-belt-s' }, { face: 5, id: 'rialto-belt-b' }] },
  'san-marco-belt': { base: 'san-marco-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'san-marco-belt-e' }, { face: 1, id: 'san-marco-belt-w' }, { face: 2, id: 'san-marco-belt-n' }, { face: 3, id: 'san-marco-belt-s' }, { face: 5, id: 'san-marco-belt-b' }] },
  'castello-belt': { base: 'castello-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'castello-belt-e' }, { face: 1, id: 'castello-belt-w' }, { face: 2, id: 'castello-belt-n' }, { face: 3, id: 'castello-belt-s' }, { face: 5, id: 'castello-belt-b' }] },
  'dorsoduro-belt': { base: 'dorsoduro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dorsoduro-belt-e' }, { face: 1, id: 'dorsoduro-belt-w' }, { face: 2, id: 'dorsoduro-belt-n' }, { face: 3, id: 'dorsoduro-belt-s' }, { face: 5, id: 'dorsoduro-belt-b' }] },
  'giudecca-belt': { base: 'giudecca-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'giudecca-belt-e' }, { face: 1, id: 'giudecca-belt-w' }, { face: 2, id: 'giudecca-belt-n' }, { face: 3, id: 'giudecca-belt-s' }, { face: 5, id: 'giudecca-belt-b' }] },
  'sacca-fisola-belt': { base: 'sacca-fisola-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sacca-fisola-belt-e' }, { face: 1, id: 'sacca-fisola-belt-w' }, { face: 2, id: 'sacca-fisola-belt-n' }, { face: 3, id: 'sacca-fisola-belt-s' }, { face: 5, id: 'sacca-fisola-belt-b' }] },
  'murano-belt': { base: 'murano-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'murano-belt-e' }, { face: 1, id: 'murano-belt-w' }, { face: 2, id: 'murano-belt-n' }, { face: 3, id: 'murano-belt-s' }, { face: 5, id: 'murano-belt-b' }] },
  'lido-belt': { base: 'lido-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lido-belt-e' }, { face: 1, id: 'lido-belt-w' }, { face: 2, id: 'lido-belt-n' }, { face: 3, id: 'lido-belt-s' }, { face: 5, id: 'lido-belt-b' }] },
  'mestre-belt': { base: 'mestre-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mestre-belt-e' }, { face: 1, id: 'mestre-belt-w' }, { face: 2, id: 'mestre-belt-n' }, { face: 3, id: 'mestre-belt-s' }, { face: 5, id: 'mestre-belt-b' }] },
  'taipei-main-belt': { base: 'taipei-main-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'taipei-main-belt-e' }, { face: 1, id: 'taipei-main-belt-w' }, { face: 2, id: 'taipei-main-belt-n' }, { face: 3, id: 'taipei-main-belt-s' }, { face: 5, id: 'taipei-main-belt-b' }] },
  'zhongshan-belt': { base: 'zhongshan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zhongshan-belt-e' }, { face: 1, id: 'zhongshan-belt-w' }, { face: 2, id: 'zhongshan-belt-n' }, { face: 3, id: 'zhongshan-belt-s' }, { face: 5, id: 'zhongshan-belt-b' }] },
  'datong-belt': { base: 'datong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'datong-belt-e' }, { face: 1, id: 'datong-belt-w' }, { face: 2, id: 'datong-belt-n' }, { face: 3, id: 'datong-belt-s' }, { face: 5, id: 'datong-belt-b' }] },
  'ximending-belt': { base: 'ximending-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ximending-belt-e' }, { face: 1, id: 'ximending-belt-w' }, { face: 2, id: 'ximending-belt-n' }, { face: 3, id: 'ximending-belt-s' }, { face: 5, id: 'ximending-belt-b' }] },
  'wanhua-belt': { base: 'wanhua-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wanhua-belt-e' }, { face: 1, id: 'wanhua-belt-w' }, { face: 2, id: 'wanhua-belt-n' }, { face: 3, id: 'wanhua-belt-s' }, { face: 5, id: 'wanhua-belt-b' }] },
  'guting-belt': { base: 'guting-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'guting-belt-e' }, { face: 1, id: 'guting-belt-w' }, { face: 2, id: 'guting-belt-n' }, { face: 3, id: 'guting-belt-s' }, { face: 5, id: 'guting-belt-b' }] },
  'daan-belt': { base: 'daan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'daan-belt-e' }, { face: 1, id: 'daan-belt-w' }, { face: 2, id: 'daan-belt-n' }, { face: 3, id: 'daan-belt-s' }, { face: 5, id: 'daan-belt-b' }] },
  'xinyi-belt': { base: 'xinyi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xinyi-belt-e' }, { face: 1, id: 'xinyi-belt-w' }, { face: 2, id: 'xinyi-belt-n' }, { face: 3, id: 'xinyi-belt-s' }, { face: 5, id: 'xinyi-belt-b' }] },
  'songshan-belt': { base: 'songshan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'songshan-belt-e' }, { face: 1, id: 'songshan-belt-w' }, { face: 2, id: 'songshan-belt-n' }, { face: 3, id: 'songshan-belt-s' }, { face: 5, id: 'songshan-belt-b' }] },
  'nangang-belt': { base: 'nangang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nangang-belt-e' }, { face: 1, id: 'nangang-belt-w' }, { face: 2, id: 'nangang-belt-n' }, { face: 3, id: 'nangang-belt-s' }, { face: 5, id: 'nangang-belt-b' }] },
  'neihu-belt': { base: 'neihu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'neihu-belt-e' }, { face: 1, id: 'neihu-belt-w' }, { face: 2, id: 'neihu-belt-n' }, { face: 3, id: 'neihu-belt-s' }, { face: 5, id: 'neihu-belt-b' }] },
  'shilin-belt': { base: 'shilin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shilin-belt-e' }, { face: 1, id: 'shilin-belt-w' }, { face: 2, id: 'shilin-belt-n' }, { face: 3, id: 'shilin-belt-s' }, { face: 5, id: 'shilin-belt-b' }] },
  'beitou-belt': { base: 'beitou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beitou-belt-e' }, { face: 1, id: 'beitou-belt-w' }, { face: 2, id: 'beitou-belt-n' }, { face: 3, id: 'beitou-belt-s' }, { face: 5, id: 'beitou-belt-b' }] },
  'banqiao-belt': { base: 'banqiao-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'banqiao-belt-e' }, { face: 1, id: 'banqiao-belt-w' }, { face: 2, id: 'banqiao-belt-n' }, { face: 3, id: 'banqiao-belt-s' }, { face: 5, id: 'banqiao-belt-b' }] },
  'muzha-belt': { base: 'muzha-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'muzha-belt-e' }, { face: 1, id: 'muzha-belt-w' }, { face: 2, id: 'muzha-belt-n' }, { face: 3, id: 'muzha-belt-s' }, { face: 5, id: 'muzha-belt-b' }] },
  'bell-tower-belt': { base: 'bell-tower-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bell-tower-belt-e' }, { face: 1, id: 'bell-tower-belt-w' }, { face: 2, id: 'bell-tower-belt-n' }, { face: 3, id: 'bell-tower-belt-s' }, { face: 5, id: 'bell-tower-belt-b' }] },
  'beiyuanmen-belt': { base: 'beiyuanmen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beiyuanmen-belt-e' }, { face: 1, id: 'beiyuanmen-belt-w' }, { face: 2, id: 'beiyuanmen-belt-n' }, { face: 3, id: 'beiyuanmen-belt-s' }, { face: 5, id: 'beiyuanmen-belt-b' }] },
  'yongningmen-belt': { base: 'yongningmen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yongningmen-belt-e' }, { face: 1, id: 'yongningmen-belt-w' }, { face: 2, id: 'yongningmen-belt-n' }, { face: 3, id: 'yongningmen-belt-s' }, { face: 5, id: 'yongningmen-belt-b' }] },
  'changlemen-belt': { base: 'changlemen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'changlemen-belt-e' }, { face: 1, id: 'changlemen-belt-w' }, { face: 2, id: 'changlemen-belt-n' }, { face: 3, id: 'changlemen-belt-s' }, { face: 5, id: 'changlemen-belt-b' }] },
  'wulukou-belt': { base: 'wulukou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wulukou-belt-e' }, { face: 1, id: 'wulukou-belt-w' }, { face: 2, id: 'wulukou-belt-n' }, { face: 3, id: 'wulukou-belt-s' }, { face: 5, id: 'wulukou-belt-b' }] },
  'longshouyuan-belt': { base: 'longshouyuan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'longshouyuan-belt-e' }, { face: 1, id: 'longshouyuan-belt-w' }, { face: 2, id: 'longshouyuan-belt-n' }, { face: 3, id: 'longshouyuan-belt-s' }, { face: 5, id: 'longshouyuan-belt-b' }] },
  'tumen-belt': { base: 'tumen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tumen-belt-e' }, { face: 1, id: 'tumen-belt-w' }, { face: 2, id: 'tumen-belt-n' }, { face: 3, id: 'tumen-belt-s' }, { face: 5, id: 'tumen-belt-b' }] },
  'hansenzhai-belt': { base: 'hansenzhai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hansenzhai-belt-e' }, { face: 1, id: 'hansenzhai-belt-w' }, { face: 2, id: 'hansenzhai-belt-n' }, { face: 3, id: 'hansenzhai-belt-s' }, { face: 5, id: 'hansenzhai-belt-b' }] },
  'xingqing-belt': { base: 'xingqing-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xingqing-belt-e' }, { face: 1, id: 'xingqing-belt-w' }, { face: 2, id: 'xingqing-belt-n' }, { face: 3, id: 'xingqing-belt-s' }, { face: 5, id: 'xingqing-belt-b' }] },
  'xiaoyanta-belt': { base: 'xiaoyanta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xiaoyanta-belt-e' }, { face: 1, id: 'xiaoyanta-belt-w' }, { face: 2, id: 'xiaoyanta-belt-n' }, { face: 3, id: 'xiaoyanta-belt-s' }, { face: 5, id: 'xiaoyanta-belt-b' }] },
  'xiaozhai-belt': { base: 'xiaozhai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xiaozhai-belt-e' }, { face: 1, id: 'xiaozhai-belt-w' }, { face: 2, id: 'xiaozhai-belt-n' }, { face: 3, id: 'xiaozhai-belt-s' }, { face: 5, id: 'xiaozhai-belt-b' }] },
  'dayanta-belt': { base: 'dayanta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dayanta-belt-e' }, { face: 1, id: 'dayanta-belt-w' }, { face: 2, id: 'dayanta-belt-n' }, { face: 3, id: 'dayanta-belt-s' }, { face: 5, id: 'dayanta-belt-b' }] },
  'dianzicheng-belt': { base: 'dianzicheng-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dianzicheng-belt-e' }, { face: 1, id: 'dianzicheng-belt-w' }, { face: 2, id: 'dianzicheng-belt-n' }, { face: 3, id: 'dianzicheng-belt-s' }, { face: 5, id: 'dianzicheng-belt-b' }] },
  'qujiang-belt': { base: 'qujiang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'qujiang-belt-e' }, { face: 1, id: 'qujiang-belt-w' }, { face: 2, id: 'qujiang-belt-n' }, { face: 3, id: 'qujiang-belt-s' }, { face: 5, id: 'qujiang-belt-b' }] },
  'capitol-hill-belt': { base: 'capitol-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'capitol-hill-belt-e' }, { face: 1, id: 'capitol-hill-belt-w' }, { face: 2, id: 'capitol-hill-belt-n' }, { face: 3, id: 'capitol-hill-belt-s' }, { face: 5, id: 'capitol-hill-belt-b' }] },
  'farragut-square-belt': { base: 'farragut-square-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'farragut-square-belt-e' }, { face: 1, id: 'farragut-square-belt-w' }, { face: 2, id: 'farragut-square-belt-n' }, { face: 3, id: 'farragut-square-belt-s' }, { face: 5, id: 'farragut-square-belt-b' }] },
  'georgetown-belt': { base: 'georgetown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'georgetown-belt-e' }, { face: 1, id: 'georgetown-belt-w' }, { face: 2, id: 'georgetown-belt-n' }, { face: 3, id: 'georgetown-belt-s' }, { face: 5, id: 'georgetown-belt-b' }] },
  'dupont-circle-belt': { base: 'dupont-circle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dupont-circle-belt-e' }, { face: 1, id: 'dupont-circle-belt-w' }, { face: 2, id: 'dupont-circle-belt-n' }, { face: 3, id: 'dupont-circle-belt-s' }, { face: 5, id: 'dupont-circle-belt-b' }] },
  'logan-circle-belt': { base: 'logan-circle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'logan-circle-belt-e' }, { face: 1, id: 'logan-circle-belt-w' }, { face: 2, id: 'logan-circle-belt-n' }, { face: 3, id: 'logan-circle-belt-s' }, { face: 5, id: 'logan-circle-belt-b' }] },
  'shaw-belt': { base: 'shaw-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shaw-belt-e' }, { face: 1, id: 'shaw-belt-w' }, { face: 2, id: 'shaw-belt-n' }, { face: 3, id: 'shaw-belt-s' }, { face: 5, id: 'shaw-belt-b' }] },
  'adams-morgan-belt': { base: 'adams-morgan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'adams-morgan-belt-e' }, { face: 1, id: 'adams-morgan-belt-w' }, { face: 2, id: 'adams-morgan-belt-n' }, { face: 3, id: 'adams-morgan-belt-s' }, { face: 5, id: 'adams-morgan-belt-b' }] },
  'columbia-heights-belt': { base: 'columbia-heights-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'columbia-heights-belt-e' }, { face: 1, id: 'columbia-heights-belt-w' }, { face: 2, id: 'columbia-heights-belt-n' }, { face: 3, id: 'columbia-heights-belt-s' }, { face: 5, id: 'columbia-heights-belt-b' }] },
  'petworth-belt': { base: 'petworth-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'petworth-belt-e' }, { face: 1, id: 'petworth-belt-w' }, { face: 2, id: 'petworth-belt-n' }, { face: 3, id: 'petworth-belt-s' }, { face: 5, id: 'petworth-belt-b' }] },
  'tenleytown-belt': { base: 'tenleytown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tenleytown-belt-e' }, { face: 1, id: 'tenleytown-belt-w' }, { face: 2, id: 'tenleytown-belt-n' }, { face: 3, id: 'tenleytown-belt-s' }, { face: 5, id: 'tenleytown-belt-b' }] },
  'brookland-belt': { base: 'brookland-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'brookland-belt-e' }, { face: 1, id: 'brookland-belt-w' }, { face: 2, id: 'brookland-belt-n' }, { face: 3, id: 'brookland-belt-s' }, { face: 5, id: 'brookland-belt-b' }] },
  'h-street-belt': { base: 'h-street-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'h-street-belt-e' }, { face: 1, id: 'h-street-belt-w' }, { face: 2, id: 'h-street-belt-n' }, { face: 3, id: 'h-street-belt-s' }, { face: 5, id: 'h-street-belt-b' }] },
  'eastern-market-belt': { base: 'eastern-market-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'eastern-market-belt-e' }, { face: 1, id: 'eastern-market-belt-w' }, { face: 2, id: 'eastern-market-belt-n' }, { face: 3, id: 'eastern-market-belt-s' }, { face: 5, id: 'eastern-market-belt-b' }] },
  'navy-yard-belt': { base: 'navy-yard-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'navy-yard-belt-e' }, { face: 1, id: 'navy-yard-belt-w' }, { face: 2, id: 'navy-yard-belt-n' }, { face: 3, id: 'navy-yard-belt-s' }, { face: 5, id: 'navy-yard-belt-b' }] },
  'anacostia-belt': { base: 'anacostia-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'anacostia-belt-e' }, { face: 1, id: 'anacostia-belt-w' }, { face: 2, id: 'anacostia-belt-n' }, { face: 3, id: 'anacostia-belt-s' }, { face: 5, id: 'anacostia-belt-b' }] },
  'masjid-jamek-belt': { base: 'masjid-jamek-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'masjid-jamek-belt-e' }, { face: 1, id: 'masjid-jamek-belt-w' }, { face: 2, id: 'masjid-jamek-belt-n' }, { face: 3, id: 'masjid-jamek-belt-s' }, { face: 5, id: 'masjid-jamek-belt-b' }] },
  'petaling-street-belt': { base: 'petaling-street-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'petaling-street-belt-e' }, { face: 1, id: 'petaling-street-belt-w' }, { face: 2, id: 'petaling-street-belt-n' }, { face: 3, id: 'petaling-street-belt-s' }, { face: 5, id: 'petaling-street-belt-b' }] },
  'bukit-bintang-belt': { base: 'bukit-bintang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bukit-bintang-belt-e' }, { face: 1, id: 'bukit-bintang-belt-w' }, { face: 2, id: 'bukit-bintang-belt-n' }, { face: 3, id: 'bukit-bintang-belt-s' }, { face: 5, id: 'bukit-bintang-belt-b' }] },
  'klcc-belt': { base: 'klcc-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'klcc-belt-e' }, { face: 1, id: 'klcc-belt-w' }, { face: 2, id: 'klcc-belt-n' }, { face: 3, id: 'klcc-belt-s' }, { face: 5, id: 'klcc-belt-b' }] },
  'kampung-baru-belt': { base: 'kampung-baru-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kampung-baru-belt-e' }, { face: 1, id: 'kampung-baru-belt-w' }, { face: 2, id: 'kampung-baru-belt-n' }, { face: 3, id: 'kampung-baru-belt-s' }, { face: 5, id: 'kampung-baru-belt-b' }] },
  'chow-kit-belt': { base: 'chow-kit-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chow-kit-belt-e' }, { face: 1, id: 'chow-kit-belt-w' }, { face: 2, id: 'chow-kit-belt-n' }, { face: 3, id: 'chow-kit-belt-s' }, { face: 5, id: 'chow-kit-belt-b' }] },
  'pudu-belt': { base: 'pudu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pudu-belt-e' }, { face: 1, id: 'pudu-belt-w' }, { face: 2, id: 'pudu-belt-n' }, { face: 3, id: 'pudu-belt-s' }, { face: 5, id: 'pudu-belt-b' }] },
  'brickfields-belt': { base: 'brickfields-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'brickfields-belt-e' }, { face: 1, id: 'brickfields-belt-w' }, { face: 2, id: 'brickfields-belt-n' }, { face: 3, id: 'brickfields-belt-s' }, { face: 5, id: 'brickfields-belt-b' }] },
  'bangsar-belt': { base: 'bangsar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bangsar-belt-e' }, { face: 1, id: 'bangsar-belt-w' }, { face: 2, id: 'bangsar-belt-n' }, { face: 3, id: 'bangsar-belt-s' }, { face: 5, id: 'bangsar-belt-b' }] },
  'titiwangsa-belt': { base: 'titiwangsa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'titiwangsa-belt-e' }, { face: 1, id: 'titiwangsa-belt-w' }, { face: 2, id: 'titiwangsa-belt-n' }, { face: 3, id: 'titiwangsa-belt-s' }, { face: 5, id: 'titiwangsa-belt-b' }] },
  'sentul-belt': { base: 'sentul-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sentul-belt-e' }, { face: 1, id: 'sentul-belt-w' }, { face: 2, id: 'sentul-belt-n' }, { face: 3, id: 'sentul-belt-s' }, { face: 5, id: 'sentul-belt-b' }] },
  'ttdi-belt': { base: 'ttdi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ttdi-belt-e' }, { face: 1, id: 'ttdi-belt-w' }, { face: 2, id: 'ttdi-belt-n' }, { face: 3, id: 'ttdi-belt-s' }, { face: 5, id: 'ttdi-belt-b' }] },
  'kepong-belt': { base: 'kepong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kepong-belt-e' }, { face: 1, id: 'kepong-belt-w' }, { face: 2, id: 'kepong-belt-n' }, { face: 3, id: 'kepong-belt-s' }, { face: 5, id: 'kepong-belt-b' }] },
  'pandan-indah-belt': { base: 'pandan-indah-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pandan-indah-belt-e' }, { face: 1, id: 'pandan-indah-belt-w' }, { face: 2, id: 'pandan-indah-belt-n' }, { face: 3, id: 'pandan-indah-belt-s' }, { face: 5, id: 'pandan-indah-belt-b' }] },
  'cheras-belt': { base: 'cheras-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cheras-belt-e' }, { face: 1, id: 'cheras-belt-w' }, { face: 2, id: 'cheras-belt-n' }, { face: 3, id: 'cheras-belt-s' }, { face: 5, id: 'cheras-belt-b' }] },
  'circular-quay-belt': { base: 'circular-quay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'circular-quay-belt-e' }, { face: 1, id: 'circular-quay-belt-w' }, { face: 2, id: 'circular-quay-belt-n' }, { face: 3, id: 'circular-quay-belt-s' }, { face: 5, id: 'circular-quay-belt-b' }] },
  'sydney-cbd-belt': { base: 'sydney-cbd-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sydney-cbd-belt-e' }, { face: 1, id: 'sydney-cbd-belt-w' }, { face: 2, id: 'sydney-cbd-belt-n' }, { face: 3, id: 'sydney-cbd-belt-s' }, { face: 5, id: 'sydney-cbd-belt-b' }] },
  'barangaroo-belt': { base: 'barangaroo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'barangaroo-belt-e' }, { face: 1, id: 'barangaroo-belt-w' }, { face: 2, id: 'barangaroo-belt-n' }, { face: 3, id: 'barangaroo-belt-s' }, { face: 5, id: 'barangaroo-belt-b' }] },
  'pyrmont-belt': { base: 'pyrmont-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pyrmont-belt-e' }, { face: 1, id: 'pyrmont-belt-w' }, { face: 2, id: 'pyrmont-belt-n' }, { face: 3, id: 'pyrmont-belt-s' }, { face: 5, id: 'pyrmont-belt-b' }] },
  'haymarket-belt': { base: 'haymarket-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'haymarket-belt-e' }, { face: 1, id: 'haymarket-belt-w' }, { face: 2, id: 'haymarket-belt-n' }, { face: 3, id: 'haymarket-belt-s' }, { face: 5, id: 'haymarket-belt-b' }] },
  'surry-hills-belt': { base: 'surry-hills-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'surry-hills-belt-e' }, { face: 1, id: 'surry-hills-belt-w' }, { face: 2, id: 'surry-hills-belt-n' }, { face: 3, id: 'surry-hills-belt-s' }, { face: 5, id: 'surry-hills-belt-b' }] },
  'kings-cross-belt': { base: 'kings-cross-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kings-cross-belt-e' }, { face: 1, id: 'kings-cross-belt-w' }, { face: 2, id: 'kings-cross-belt-n' }, { face: 3, id: 'kings-cross-belt-s' }, { face: 5, id: 'kings-cross-belt-b' }] },
  'paddington-belt': { base: 'paddington-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'paddington-belt-e' }, { face: 1, id: 'paddington-belt-w' }, { face: 2, id: 'paddington-belt-n' }, { face: 3, id: 'paddington-belt-s' }, { face: 5, id: 'paddington-belt-b' }] },
  'redfern-belt': { base: 'redfern-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'redfern-belt-e' }, { face: 1, id: 'redfern-belt-w' }, { face: 2, id: 'redfern-belt-n' }, { face: 3, id: 'redfern-belt-s' }, { face: 5, id: 'redfern-belt-b' }] },
  'newtown-belt': { base: 'newtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'newtown-belt-e' }, { face: 1, id: 'newtown-belt-w' }, { face: 2, id: 'newtown-belt-n' }, { face: 3, id: 'newtown-belt-s' }, { face: 5, id: 'newtown-belt-b' }] },
  'milsons-point-belt': { base: 'milsons-point-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'milsons-point-belt-e' }, { face: 1, id: 'milsons-point-belt-w' }, { face: 2, id: 'milsons-point-belt-n' }, { face: 3, id: 'milsons-point-belt-s' }, { face: 5, id: 'milsons-point-belt-b' }] },
  'north-sydney-belt': { base: 'north-sydney-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'north-sydney-belt-e' }, { face: 1, id: 'north-sydney-belt-w' }, { face: 2, id: 'north-sydney-belt-n' }, { face: 3, id: 'north-sydney-belt-s' }, { face: 5, id: 'north-sydney-belt-b' }] },
  'bondi-beach-belt': { base: 'bondi-beach-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bondi-beach-belt-e' }, { face: 1, id: 'bondi-beach-belt-w' }, { face: 2, id: 'bondi-beach-belt-n' }, { face: 3, id: 'bondi-beach-belt-s' }, { face: 5, id: 'bondi-beach-belt-b' }] },
  'manly-belt': { base: 'manly-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'manly-belt-e' }, { face: 1, id: 'manly-belt-w' }, { face: 2, id: 'manly-belt-n' }, { face: 3, id: 'manly-belt-s' }, { face: 5, id: 'manly-belt-b' }] },
  'parramatta-belt': { base: 'parramatta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'parramatta-belt-e' }, { face: 1, id: 'parramatta-belt-w' }, { face: 2, id: 'parramatta-belt-n' }, { face: 3, id: 'parramatta-belt-s' }, { face: 5, id: 'parramatta-belt-b' }] },
  'the-bund-belt': { base: 'the-bund-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'the-bund-belt-e' }, { face: 1, id: 'the-bund-belt-w' }, { face: 2, id: 'the-bund-belt-n' }, { face: 3, id: 'the-bund-belt-s' }, { face: 5, id: 'the-bund-belt-b' }] },
  'lujiazui-belt': { base: 'lujiazui-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lujiazui-belt-e' }, { face: 1, id: 'lujiazui-belt-w' }, { face: 2, id: 'lujiazui-belt-n' }, { face: 3, id: 'lujiazui-belt-s' }, { face: 5, id: 'lujiazui-belt-b' }] },
  'renmin-square-belt': { base: 'renmin-square-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'renmin-square-belt-e' }, { face: 1, id: 'renmin-square-belt-w' }, { face: 2, id: 'renmin-square-belt-n' }, { face: 3, id: 'renmin-square-belt-s' }, { face: 5, id: 'renmin-square-belt-b' }] },
  'yuyuan-belt': { base: 'yuyuan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yuyuan-belt-e' }, { face: 1, id: 'yuyuan-belt-w' }, { face: 2, id: 'yuyuan-belt-n' }, { face: 3, id: 'yuyuan-belt-s' }, { face: 5, id: 'yuyuan-belt-b' }] },
  'xintiandi-belt': { base: 'xintiandi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xintiandi-belt-e' }, { face: 1, id: 'xintiandi-belt-w' }, { face: 2, id: 'xintiandi-belt-n' }, { face: 3, id: 'xintiandi-belt-s' }, { face: 5, id: 'xintiandi-belt-b' }] },
  'wukang-belt': { base: 'wukang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wukang-belt-e' }, { face: 1, id: 'wukang-belt-w' }, { face: 2, id: 'wukang-belt-n' }, { face: 3, id: 'wukang-belt-s' }, { face: 5, id: 'wukang-belt-b' }] },
  'jingan-belt': { base: 'jingan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jingan-belt-e' }, { face: 1, id: 'jingan-belt-w' }, { face: 2, id: 'jingan-belt-n' }, { face: 3, id: 'jingan-belt-s' }, { face: 5, id: 'jingan-belt-b' }] },
  'zhongshan-park-belt': { base: 'zhongshan-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zhongshan-park-belt-e' }, { face: 1, id: 'zhongshan-park-belt-w' }, { face: 2, id: 'zhongshan-park-belt-n' }, { face: 3, id: 'zhongshan-park-belt-s' }, { face: 5, id: 'zhongshan-park-belt-b' }] },
  'xujiahui-belt': { base: 'xujiahui-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xujiahui-belt-e' }, { face: 1, id: 'xujiahui-belt-w' }, { face: 2, id: 'xujiahui-belt-n' }, { face: 3, id: 'xujiahui-belt-s' }, { face: 5, id: 'xujiahui-belt-b' }] },
  'shanghai-station-belt': { base: 'shanghai-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shanghai-station-belt-e' }, { face: 1, id: 'shanghai-station-belt-w' }, { face: 2, id: 'shanghai-station-belt-n' }, { face: 3, id: 'shanghai-station-belt-s' }, { face: 5, id: 'shanghai-station-belt-b' }] },
  'moganshan-belt': { base: 'moganshan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'moganshan-belt-e' }, { face: 1, id: 'moganshan-belt-w' }, { face: 2, id: 'moganshan-belt-n' }, { face: 3, id: 'moganshan-belt-s' }, { face: 5, id: 'moganshan-belt-b' }] },
  'north-bund-belt': { base: 'north-bund-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'north-bund-belt-e' }, { face: 1, id: 'north-bund-belt-w' }, { face: 2, id: 'north-bund-belt-n' }, { face: 3, id: 'north-bund-belt-s' }, { face: 5, id: 'north-bund-belt-b' }] },
  'century-avenue-belt': { base: 'century-avenue-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'century-avenue-belt-e' }, { face: 1, id: 'century-avenue-belt-w' }, { face: 2, id: 'century-avenue-belt-n' }, { face: 3, id: 'century-avenue-belt-s' }, { face: 5, id: 'century-avenue-belt-b' }] },
  'wujiaochang-belt': { base: 'wujiaochang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wujiaochang-belt-e' }, { face: 1, id: 'wujiaochang-belt-w' }, { face: 2, id: 'wujiaochang-belt-n' }, { face: 3, id: 'wujiaochang-belt-s' }, { face: 5, id: 'wujiaochang-belt-b' }] },
  'hongqiao-gubei-belt': { base: 'hongqiao-gubei-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hongqiao-gubei-belt-e' }, { face: 1, id: 'hongqiao-gubei-belt-w' }, { face: 2, id: 'hongqiao-gubei-belt-n' }, { face: 3, id: 'hongqiao-gubei-belt-s' }, { face: 5, id: 'hongqiao-gubei-belt-b' }] },
  'qianmen-dashilan-belt': { base: 'qianmen-dashilan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'qianmen-dashilan-belt-e' }, { face: 1, id: 'qianmen-dashilan-belt-w' }, { face: 2, id: 'qianmen-dashilan-belt-n' }, { face: 3, id: 'qianmen-dashilan-belt-s' }, { face: 5, id: 'qianmen-dashilan-belt-b' }] },
  'wangfujing-dongdan-belt': { base: 'wangfujing-dongdan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wangfujing-dongdan-belt-e' }, { face: 1, id: 'wangfujing-dongdan-belt-w' }, { face: 2, id: 'wangfujing-dongdan-belt-n' }, { face: 3, id: 'wangfujing-dongdan-belt-s' }, { face: 5, id: 'wangfujing-dongdan-belt-b' }] },
  'xisi-baitasi-belt': { base: 'xisi-baitasi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xisi-baitasi-belt-e' }, { face: 1, id: 'xisi-baitasi-belt-w' }, { face: 2, id: 'xisi-baitasi-belt-n' }, { face: 3, id: 'xisi-baitasi-belt-s' }, { face: 5, id: 'xisi-baitasi-belt-b' }] },
  'shichahai-gulou-belt': { base: 'shichahai-gulou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shichahai-gulou-belt-e' }, { face: 1, id: 'shichahai-gulou-belt-w' }, { face: 2, id: 'shichahai-gulou-belt-n' }, { face: 3, id: 'shichahai-gulou-belt-s' }, { face: 5, id: 'shichahai-gulou-belt-b' }] },
  'beijing-station-belt': { base: 'beijing-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beijing-station-belt-e' }, { face: 1, id: 'beijing-station-belt-w' }, { face: 2, id: 'beijing-station-belt-n' }, { face: 3, id: 'beijing-station-belt-s' }, { face: 5, id: 'beijing-station-belt-b' }] },
  'guomao-cbd-belt': { base: 'guomao-cbd-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'guomao-cbd-belt-e' }, { face: 1, id: 'guomao-cbd-belt-w' }, { face: 2, id: 'guomao-cbd-belt-n' }, { face: 3, id: 'guomao-cbd-belt-s' }, { face: 5, id: 'guomao-cbd-belt-b' }] },
  'sanlitun-gongti-belt': { base: 'sanlitun-gongti-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sanlitun-gongti-belt-e' }, { face: 1, id: 'sanlitun-gongti-belt-w' }, { face: 2, id: 'sanlitun-gongti-belt-n' }, { face: 3, id: 'sanlitun-gongti-belt-s' }, { face: 5, id: 'sanlitun-gongti-belt-b' }] },
  'yonghegong-dongzhimen-belt': { base: 'yonghegong-dongzhimen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yonghegong-dongzhimen-belt-e' }, { face: 1, id: 'yonghegong-dongzhimen-belt-w' }, { face: 2, id: 'yonghegong-dongzhimen-belt-n' }, { face: 3, id: 'yonghegong-dongzhimen-belt-s' }, { face: 5, id: 'yonghegong-dongzhimen-belt-b' }] },
  'hepingli-anzhen-belt': { base: 'hepingli-anzhen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hepingli-anzhen-belt-e' }, { face: 1, id: 'hepingli-anzhen-belt-w' }, { face: 2, id: 'hepingli-anzhen-belt-n' }, { face: 3, id: 'hepingli-anzhen-belt-s' }, { face: 5, id: 'hepingli-anzhen-belt-b' }] },
  'wangjing-belt': { base: 'wangjing-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wangjing-belt-e' }, { face: 1, id: 'wangjing-belt-w' }, { face: 2, id: 'wangjing-belt-n' }, { face: 3, id: 'wangjing-belt-s' }, { face: 5, id: 'wangjing-belt-b' }] },
  'xizhimen-beijing-north-belt': { base: 'xizhimen-beijing-north-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xizhimen-beijing-north-belt-e' }, { face: 1, id: 'xizhimen-beijing-north-belt-w' }, { face: 2, id: 'xizhimen-beijing-north-belt-n' }, { face: 3, id: 'xizhimen-beijing-north-belt-s' }, { face: 5, id: 'xizhimen-beijing-north-belt-b' }] },
  'zhongguancun-belt': { base: 'zhongguancun-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zhongguancun-belt-e' }, { face: 1, id: 'zhongguancun-belt-w' }, { face: 2, id: 'zhongguancun-belt-n' }, { face: 3, id: 'zhongguancun-belt-s' }, { face: 5, id: 'zhongguancun-belt-b' }] },
  'yuetan-muxidi-belt': { base: 'yuetan-muxidi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yuetan-muxidi-belt-e' }, { face: 1, id: 'yuetan-muxidi-belt-w' }, { face: 2, id: 'yuetan-muxidi-belt-n' }, { face: 3, id: 'yuetan-muxidi-belt-s' }, { face: 5, id: 'yuetan-muxidi-belt-b' }] },
  'beijing-west-station-belt': { base: 'beijing-west-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beijing-west-station-belt-e' }, { face: 1, id: 'beijing-west-station-belt-w' }, { face: 2, id: 'beijing-west-station-belt-n' }, { face: 3, id: 'beijing-west-station-belt-s' }, { face: 5, id: 'beijing-west-station-belt-b' }] },
  'fangzhuang-belt': { base: 'fangzhuang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fangzhuang-belt-e' }, { face: 1, id: 'fangzhuang-belt-w' }, { face: 2, id: 'fangzhuang-belt-n' }, { face: 3, id: 'fangzhuang-belt-s' }, { face: 5, id: 'fangzhuang-belt-b' }] },
  'fuzimiao-belt': { base: 'fuzimiao-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fuzimiao-belt-e' }, { face: 1, id: 'fuzimiao-belt-w' }, { face: 2, id: 'fuzimiao-belt-n' }, { face: 3, id: 'fuzimiao-belt-s' }, { face: 5, id: 'fuzimiao-belt-b' }] },
  'xinjiekou-belt': { base: 'xinjiekou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xinjiekou-belt-e' }, { face: 1, id: 'xinjiekou-belt-w' }, { face: 2, id: 'xinjiekou-belt-n' }, { face: 3, id: 'xinjiekou-belt-s' }, { face: 5, id: 'xinjiekou-belt-b' }] },
  'gulou-belt': { base: 'gulou-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gulou-belt-e' }, { face: 1, id: 'gulou-belt-w' }, { face: 2, id: 'gulou-belt-n' }, { face: 3, id: 'gulou-belt-s' }, { face: 5, id: 'gulou-belt-b' }] },
  'shanxi-road-belt': { base: 'shanxi-road-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shanxi-road-belt-e' }, { face: 1, id: 'shanxi-road-belt-w' }, { face: 2, id: 'shanxi-road-belt-n' }, { face: 3, id: 'shanxi-road-belt-s' }, { face: 5, id: 'shanxi-road-belt-b' }] },
  'xiaguan-belt': { base: 'xiaguan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'xiaguan-belt-e' }, { face: 1, id: 'xiaguan-belt-w' }, { face: 2, id: 'xiaguan-belt-n' }, { face: 3, id: 'xiaguan-belt-s' }, { face: 5, id: 'xiaguan-belt-b' }] },
  'yueyahu-belt': { base: 'yueyahu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yueyahu-belt-e' }, { face: 1, id: 'yueyahu-belt-w' }, { face: 2, id: 'yueyahu-belt-n' }, { face: 3, id: 'yueyahu-belt-s' }, { face: 5, id: 'yueyahu-belt-b' }] },
  'guanghuamen-belt': { base: 'guanghuamen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'guanghuamen-belt-e' }, { face: 1, id: 'guanghuamen-belt-w' }, { face: 2, id: 'guanghuamen-belt-n' }, { face: 3, id: 'guanghuamen-belt-s' }, { face: 5, id: 'guanghuamen-belt-b' }] },
  'zhonghuamen-belt': { base: 'zhonghuamen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zhonghuamen-belt-e' }, { face: 1, id: 'zhonghuamen-belt-w' }, { face: 2, id: 'zhonghuamen-belt-n' }, { face: 3, id: 'zhonghuamen-belt-s' }, { face: 5, id: 'zhonghuamen-belt-b' }] },
  'nanhu-belt': { base: 'nanhu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nanhu-belt-e' }, { face: 1, id: 'nanhu-belt-w' }, { face: 2, id: 'nanhu-belt-n' }, { face: 3, id: 'nanhu-belt-s' }, { face: 5, id: 'nanhu-belt-b' }] },
  'chaotiangong-belt': { base: 'chaotiangong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chaotiangong-belt-e' }, { face: 1, id: 'chaotiangong-belt-w' }, { face: 2, id: 'chaotiangong-belt-n' }, { face: 3, id: 'chaotiangong-belt-s' }, { face: 5, id: 'chaotiangong-belt-b' }] },
  'longjiang-belt': { base: 'longjiang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'longjiang-belt-e' }, { face: 1, id: 'longjiang-belt-w' }, { face: 2, id: 'longjiang-belt-n' }, { face: 3, id: 'longjiang-belt-s' }, { face: 5, id: 'longjiang-belt-b' }] },
  'hexi-belt': { base: 'hexi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hexi-belt-e' }, { face: 1, id: 'hexi-belt-w' }, { face: 2, id: 'hexi-belt-n' }, { face: 3, id: 'hexi-belt-s' }, { face: 5, id: 'hexi-belt-b' }] },
  'zamoskvorechye-belt': { base: 'zamoskvorechye-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zamoskvorechye-belt-e' }, { face: 1, id: 'zamoskvorechye-belt-w' }, { face: 2, id: 'zamoskvorechye-belt-n' }, { face: 3, id: 'zamoskvorechye-belt-s' }, { face: 5, id: 'zamoskvorechye-belt-b' }] },
  'arbat-belt': { base: 'arbat-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'arbat-belt-e' }, { face: 1, id: 'arbat-belt-w' }, { face: 2, id: 'arbat-belt-n' }, { face: 3, id: 'arbat-belt-s' }, { face: 5, id: 'arbat-belt-b' }] },
  'chistye-prudy-belt': { base: 'chistye-prudy-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chistye-prudy-belt-e' }, { face: 1, id: 'chistye-prudy-belt-w' }, { face: 2, id: 'chistye-prudy-belt-n' }, { face: 3, id: 'chistye-prudy-belt-s' }, { face: 5, id: 'chistye-prudy-belt-b' }] },
  'sretenka-belt': { base: 'sretenka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sretenka-belt-e' }, { face: 1, id: 'sretenka-belt-w' }, { face: 2, id: 'sretenka-belt-n' }, { face: 3, id: 'sretenka-belt-s' }, { face: 5, id: 'sretenka-belt-b' }] },
  'ostozhenka-belt': { base: 'ostozhenka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ostozhenka-belt-e' }, { face: 1, id: 'ostozhenka-belt-w' }, { face: 2, id: 'ostozhenka-belt-n' }, { face: 3, id: 'ostozhenka-belt-s' }, { face: 5, id: 'ostozhenka-belt-b' }] },
  'patriarshiye-belt': { base: 'patriarshiye-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'patriarshiye-belt-e' }, { face: 1, id: 'patriarshiye-belt-w' }, { face: 2, id: 'patriarshiye-belt-n' }, { face: 3, id: 'patriarshiye-belt-s' }, { face: 5, id: 'patriarshiye-belt-b' }] },
  'taganka-belt': { base: 'taganka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'taganka-belt-e' }, { face: 1, id: 'taganka-belt-w' }, { face: 2, id: 'taganka-belt-n' }, { face: 3, id: 'taganka-belt-s' }, { face: 5, id: 'taganka-belt-b' }] },
  'tverskaya-belt': { base: 'tverskaya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tverskaya-belt-e' }, { face: 1, id: 'tverskaya-belt-w' }, { face: 2, id: 'tverskaya-belt-n' }, { face: 3, id: 'tverskaya-belt-s' }, { face: 5, id: 'tverskaya-belt-b' }] },
  'baumanskaya-belt': { base: 'baumanskaya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'baumanskaya-belt-e' }, { face: 1, id: 'baumanskaya-belt-w' }, { face: 2, id: 'baumanskaya-belt-n' }, { face: 3, id: 'baumanskaya-belt-s' }, { face: 5, id: 'baumanskaya-belt-b' }] },
  'paveletskaya-belt': { base: 'paveletskaya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'paveletskaya-belt-e' }, { face: 1, id: 'paveletskaya-belt-w' }, { face: 2, id: 'paveletskaya-belt-n' }, { face: 3, id: 'paveletskaya-belt-s' }, { face: 5, id: 'paveletskaya-belt-b' }] },
  'semyonovskaya-belt': { base: 'semyonovskaya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'semyonovskaya-belt-e' }, { face: 1, id: 'semyonovskaya-belt-w' }, { face: 2, id: 'semyonovskaya-belt-n' }, { face: 3, id: 'semyonovskaya-belt-s' }, { face: 5, id: 'semyonovskaya-belt-b' }] },
  'sokol-belt': { base: 'sokol-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sokol-belt-e' }, { face: 1, id: 'sokol-belt-w' }, { face: 2, id: 'sokol-belt-n' }, { face: 3, id: 'sokol-belt-s' }, { face: 5, id: 'sokol-belt-b' }] },
  'tulskaya-belt': { base: 'tulskaya-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tulskaya-belt-e' }, { face: 1, id: 'tulskaya-belt-w' }, { face: 2, id: 'tulskaya-belt-n' }, { face: 3, id: 'tulskaya-belt-s' }, { face: 5, id: 'tulskaya-belt-b' }] },
  'khamovniki-belt': { base: 'khamovniki-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'khamovniki-belt-e' }, { face: 1, id: 'khamovniki-belt-w' }, { face: 2, id: 'khamovniki-belt-n' }, { face: 3, id: 'khamovniki-belt-s' }, { face: 5, id: 'khamovniki-belt-b' }] },
  'kitay-gorod-belt': { base: 'kitay-gorod-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kitay-gorod-belt-e' }, { face: 1, id: 'kitay-gorod-belt-w' }, { face: 2, id: 'kitay-gorod-belt-n' }, { face: 3, id: 'kitay-gorod-belt-s' }, { face: 5, id: 'kitay-gorod-belt-b' }] },
  'colaba-belt': { base: 'colaba-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'colaba-belt-e' }, { face: 1, id: 'colaba-belt-w' }, { face: 2, id: 'colaba-belt-n' }, { face: 3, id: 'colaba-belt-s' }, { face: 5, id: 'colaba-belt-b' }] },
  'fort-mumbai-belt': { base: 'fort-mumbai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fort-mumbai-belt-e' }, { face: 1, id: 'fort-mumbai-belt-w' }, { face: 2, id: 'fort-mumbai-belt-n' }, { face: 3, id: 'fort-mumbai-belt-s' }, { face: 5, id: 'fort-mumbai-belt-b' }] },
  'marine-lines-belt': { base: 'marine-lines-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marine-lines-belt-e' }, { face: 1, id: 'marine-lines-belt-w' }, { face: 2, id: 'marine-lines-belt-n' }, { face: 3, id: 'marine-lines-belt-s' }, { face: 5, id: 'marine-lines-belt-b' }] },
  'girgaon-belt': { base: 'girgaon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'girgaon-belt-e' }, { face: 1, id: 'girgaon-belt-w' }, { face: 2, id: 'girgaon-belt-n' }, { face: 3, id: 'girgaon-belt-s' }, { face: 5, id: 'girgaon-belt-b' }] },
  'byculla-belt': { base: 'byculla-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'byculla-belt-e' }, { face: 1, id: 'byculla-belt-w' }, { face: 2, id: 'byculla-belt-n' }, { face: 3, id: 'byculla-belt-s' }, { face: 5, id: 'byculla-belt-b' }] },
  'worli-belt': { base: 'worli-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'worli-belt-e' }, { face: 1, id: 'worli-belt-w' }, { face: 2, id: 'worli-belt-n' }, { face: 3, id: 'worli-belt-s' }, { face: 5, id: 'worli-belt-b' }] },
  'dadar-belt': { base: 'dadar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dadar-belt-e' }, { face: 1, id: 'dadar-belt-w' }, { face: 2, id: 'dadar-belt-n' }, { face: 3, id: 'dadar-belt-s' }, { face: 5, id: 'dadar-belt-b' }] },
  'matunga-belt': { base: 'matunga-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'matunga-belt-e' }, { face: 1, id: 'matunga-belt-w' }, { face: 2, id: 'matunga-belt-n' }, { face: 3, id: 'matunga-belt-s' }, { face: 5, id: 'matunga-belt-b' }] },
  'mahim-belt': { base: 'mahim-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mahim-belt-e' }, { face: 1, id: 'mahim-belt-w' }, { face: 2, id: 'mahim-belt-n' }, { face: 3, id: 'mahim-belt-s' }, { face: 5, id: 'mahim-belt-b' }] },
  'bandra-west-belt': { base: 'bandra-west-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bandra-west-belt-e' }, { face: 1, id: 'bandra-west-belt-w' }, { face: 2, id: 'bandra-west-belt-n' }, { face: 3, id: 'bandra-west-belt-s' }, { face: 5, id: 'bandra-west-belt-b' }] },
  'vile-parle-belt': { base: 'vile-parle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vile-parle-belt-e' }, { face: 1, id: 'vile-parle-belt-w' }, { face: 2, id: 'vile-parle-belt-n' }, { face: 3, id: 'vile-parle-belt-s' }, { face: 5, id: 'vile-parle-belt-b' }] },
  'andheri-west-belt': { base: 'andheri-west-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'andheri-west-belt-e' }, { face: 1, id: 'andheri-west-belt-w' }, { face: 2, id: 'andheri-west-belt-n' }, { face: 3, id: 'andheri-west-belt-s' }, { face: 5, id: 'andheri-west-belt-b' }] },
  'ghatkopar-belt': { base: 'ghatkopar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ghatkopar-belt-e' }, { face: 1, id: 'ghatkopar-belt-w' }, { face: 2, id: 'ghatkopar-belt-n' }, { face: 3, id: 'ghatkopar-belt-s' }, { face: 5, id: 'ghatkopar-belt-b' }] },
  'chembur-belt': { base: 'chembur-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chembur-belt-e' }, { face: 1, id: 'chembur-belt-w' }, { face: 2, id: 'chembur-belt-n' }, { face: 3, id: 'chembur-belt-s' }, { face: 5, id: 'chembur-belt-b' }] },
  'borivali-belt': { base: 'borivali-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'borivali-belt-e' }, { face: 1, id: 'borivali-belt-w' }, { face: 2, id: 'borivali-belt-n' }, { face: 3, id: 'borivali-belt-s' }, { face: 5, id: 'borivali-belt-b' }] },
  'back-bay-belt': { base: 'back-bay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'back-bay-belt-e' }, { face: 1, id: 'back-bay-belt-w' }, { face: 2, id: 'back-bay-belt-n' }, { face: 3, id: 'back-bay-belt-s' }, { face: 5, id: 'back-bay-belt-b' }] },
  'beacon-hill-belt': { base: 'beacon-hill-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'beacon-hill-belt-e' }, { face: 1, id: 'beacon-hill-belt-w' }, { face: 2, id: 'beacon-hill-belt-n' }, { face: 3, id: 'beacon-hill-belt-s' }, { face: 5, id: 'beacon-hill-belt-b' }] },
  'north-end-belt': { base: 'north-end-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'north-end-belt-e' }, { face: 1, id: 'north-end-belt-w' }, { face: 2, id: 'north-end-belt-n' }, { face: 3, id: 'north-end-belt-s' }, { face: 5, id: 'north-end-belt-b' }] },
  'downtown-crossing-belt': { base: 'downtown-crossing-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'downtown-crossing-belt-e' }, { face: 1, id: 'downtown-crossing-belt-w' }, { face: 2, id: 'downtown-crossing-belt-n' }, { face: 3, id: 'downtown-crossing-belt-s' }, { face: 5, id: 'downtown-crossing-belt-b' }] },
  'chinatown-boston-belt': { base: 'chinatown-boston-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chinatown-boston-belt-e' }, { face: 1, id: 'chinatown-boston-belt-w' }, { face: 2, id: 'chinatown-boston-belt-n' }, { face: 3, id: 'chinatown-boston-belt-s' }, { face: 5, id: 'chinatown-boston-belt-b' }] },
  'south-end-belt': { base: 'south-end-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'south-end-belt-e' }, { face: 1, id: 'south-end-belt-w' }, { face: 2, id: 'south-end-belt-n' }, { face: 3, id: 'south-end-belt-s' }, { face: 5, id: 'south-end-belt-b' }] },
  'fenway-belt': { base: 'fenway-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fenway-belt-e' }, { face: 1, id: 'fenway-belt-w' }, { face: 2, id: 'fenway-belt-n' }, { face: 3, id: 'fenway-belt-s' }, { face: 5, id: 'fenway-belt-b' }] },
  'charlestown-belt': { base: 'charlestown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'charlestown-belt-e' }, { face: 1, id: 'charlestown-belt-w' }, { face: 2, id: 'charlestown-belt-n' }, { face: 3, id: 'charlestown-belt-s' }, { face: 5, id: 'charlestown-belt-b' }] },
  'east-boston-belt': { base: 'east-boston-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'east-boston-belt-e' }, { face: 1, id: 'east-boston-belt-w' }, { face: 2, id: 'east-boston-belt-n' }, { face: 3, id: 'east-boston-belt-s' }, { face: 5, id: 'east-boston-belt-b' }] },
  'south-boston-belt': { base: 'south-boston-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'south-boston-belt-e' }, { face: 1, id: 'south-boston-belt-w' }, { face: 2, id: 'south-boston-belt-n' }, { face: 3, id: 'south-boston-belt-s' }, { face: 5, id: 'south-boston-belt-b' }] },
  'dorchester-belt': { base: 'dorchester-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dorchester-belt-e' }, { face: 1, id: 'dorchester-belt-w' }, { face: 2, id: 'dorchester-belt-n' }, { face: 3, id: 'dorchester-belt-s' }, { face: 5, id: 'dorchester-belt-b' }] },
  'jamaica-plain-belt': { base: 'jamaica-plain-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jamaica-plain-belt-e' }, { face: 1, id: 'jamaica-plain-belt-w' }, { face: 2, id: 'jamaica-plain-belt-n' }, { face: 3, id: 'jamaica-plain-belt-s' }, { face: 5, id: 'jamaica-plain-belt-b' }] },
  'harvard-square-belt': { base: 'harvard-square-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'harvard-square-belt-e' }, { face: 1, id: 'harvard-square-belt-w' }, { face: 2, id: 'harvard-square-belt-n' }, { face: 3, id: 'harvard-square-belt-s' }, { face: 5, id: 'harvard-square-belt-b' }] },
  'kendall-belt': { base: 'kendall-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kendall-belt-e' }, { face: 1, id: 'kendall-belt-w' }, { face: 2, id: 'kendall-belt-n' }, { face: 3, id: 'kendall-belt-s' }, { face: 5, id: 'kendall-belt-b' }] },
  'davis-square-belt': { base: 'davis-square-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'davis-square-belt-e' }, { face: 1, id: 'davis-square-belt-w' }, { face: 2, id: 'davis-square-belt-n' }, { face: 3, id: 'davis-square-belt-s' }, { face: 5, id: 'davis-square-belt-b' }] },
  'the-loop-belt': { base: 'the-loop-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'the-loop-belt-e' }, { face: 1, id: 'the-loop-belt-w' }, { face: 2, id: 'the-loop-belt-n' }, { face: 3, id: 'the-loop-belt-s' }, { face: 5, id: 'the-loop-belt-b' }] },
  'river-north-belt': { base: 'river-north-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'river-north-belt-e' }, { face: 1, id: 'river-north-belt-w' }, { face: 2, id: 'river-north-belt-n' }, { face: 3, id: 'river-north-belt-s' }, { face: 5, id: 'river-north-belt-b' }] },
  'gold-coast-belt': { base: 'gold-coast-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gold-coast-belt-e' }, { face: 1, id: 'gold-coast-belt-w' }, { face: 2, id: 'gold-coast-belt-n' }, { face: 3, id: 'gold-coast-belt-s' }, { face: 5, id: 'gold-coast-belt-b' }] },
  'lincoln-park-belt': { base: 'lincoln-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lincoln-park-belt-e' }, { face: 1, id: 'lincoln-park-belt-w' }, { face: 2, id: 'lincoln-park-belt-n' }, { face: 3, id: 'lincoln-park-belt-s' }, { face: 5, id: 'lincoln-park-belt-b' }] },
  'wrigleyville-belt': { base: 'wrigleyville-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wrigleyville-belt-e' }, { face: 1, id: 'wrigleyville-belt-w' }, { face: 2, id: 'wrigleyville-belt-n' }, { face: 3, id: 'wrigleyville-belt-s' }, { face: 5, id: 'wrigleyville-belt-b' }] },
  'uptown-belt': { base: 'uptown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'uptown-belt-e' }, { face: 1, id: 'uptown-belt-w' }, { face: 2, id: 'uptown-belt-n' }, { face: 3, id: 'uptown-belt-s' }, { face: 5, id: 'uptown-belt-b' }] },
  'logan-square-belt': { base: 'logan-square-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'logan-square-belt-e' }, { face: 1, id: 'logan-square-belt-w' }, { face: 2, id: 'logan-square-belt-n' }, { face: 3, id: 'logan-square-belt-s' }, { face: 5, id: 'logan-square-belt-b' }] },
  'wicker-park-belt': { base: 'wicker-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wicker-park-belt-e' }, { face: 1, id: 'wicker-park-belt-w' }, { face: 2, id: 'wicker-park-belt-n' }, { face: 3, id: 'wicker-park-belt-s' }, { face: 5, id: 'wicker-park-belt-b' }] },
  'west-loop-belt': { base: 'west-loop-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'west-loop-belt-e' }, { face: 1, id: 'west-loop-belt-w' }, { face: 2, id: 'west-loop-belt-n' }, { face: 3, id: 'west-loop-belt-s' }, { face: 5, id: 'west-loop-belt-b' }] },
  'garfield-park-belt': { base: 'garfield-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'garfield-park-belt-e' }, { face: 1, id: 'garfield-park-belt-w' }, { face: 2, id: 'garfield-park-belt-n' }, { face: 3, id: 'garfield-park-belt-s' }, { face: 5, id: 'garfield-park-belt-b' }] },
  'pilsen-belt': { base: 'pilsen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pilsen-belt-e' }, { face: 1, id: 'pilsen-belt-w' }, { face: 2, id: 'pilsen-belt-n' }, { face: 3, id: 'pilsen-belt-s' }, { face: 5, id: 'pilsen-belt-b' }] },
  'chinatown-belt': { base: 'chinatown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chinatown-belt-e' }, { face: 1, id: 'chinatown-belt-w' }, { face: 2, id: 'chinatown-belt-n' }, { face: 3, id: 'chinatown-belt-s' }, { face: 5, id: 'chinatown-belt-b' }] },
  'bridgeport-belt': { base: 'bridgeport-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bridgeport-belt-e' }, { face: 1, id: 'bridgeport-belt-w' }, { face: 2, id: 'bridgeport-belt-n' }, { face: 3, id: 'bridgeport-belt-s' }, { face: 5, id: 'bridgeport-belt-b' }] },
  'bronzeville-belt': { base: 'bronzeville-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bronzeville-belt-e' }, { face: 1, id: 'bronzeville-belt-w' }, { face: 2, id: 'bronzeville-belt-n' }, { face: 3, id: 'bronzeville-belt-s' }, { face: 5, id: 'bronzeville-belt-b' }] },
  'hyde-park-belt': { base: 'hyde-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hyde-park-belt-e' }, { face: 1, id: 'hyde-park-belt-w' }, { face: 2, id: 'hyde-park-belt-n' }, { face: 3, id: 'hyde-park-belt-s' }, { face: 5, id: 'hyde-park-belt-b' }] },
  'lev-hair-belt': { base: 'lev-hair-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lev-hair-belt-e' }, { face: 1, id: 'lev-hair-belt-w' }, { face: 2, id: 'lev-hair-belt-n' }, { face: 3, id: 'lev-hair-belt-s' }, { face: 5, id: 'lev-hair-belt-b' }] },
  'florentin-belt': { base: 'florentin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'florentin-belt-e' }, { face: 1, id: 'florentin-belt-w' }, { face: 2, id: 'florentin-belt-n' }, { face: 3, id: 'florentin-belt-s' }, { face: 5, id: 'florentin-belt-b' }] },
  'old-jaffa-belt': { base: 'old-jaffa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'old-jaffa-belt-e' }, { face: 1, id: 'old-jaffa-belt-w' }, { face: 2, id: 'old-jaffa-belt-n' }, { face: 3, id: 'old-jaffa-belt-s' }, { face: 5, id: 'old-jaffa-belt-b' }] },
  'ajami-belt': { base: 'ajami-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ajami-belt-e' }, { face: 1, id: 'ajami-belt-w' }, { face: 2, id: 'ajami-belt-n' }, { face: 3, id: 'ajami-belt-s' }, { face: 5, id: 'ajami-belt-b' }] },
  'neve-tzedek-belt': { base: 'neve-tzedek-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'neve-tzedek-belt-e' }, { face: 1, id: 'neve-tzedek-belt-w' }, { face: 2, id: 'neve-tzedek-belt-n' }, { face: 3, id: 'neve-tzedek-belt-s' }, { face: 5, id: 'neve-tzedek-belt-b' }] },
  'dizengoff-belt': { base: 'dizengoff-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dizengoff-belt-e' }, { face: 1, id: 'dizengoff-belt-w' }, { face: 2, id: 'dizengoff-belt-n' }, { face: 3, id: 'dizengoff-belt-s' }, { face: 5, id: 'dizengoff-belt-b' }] },
  'old-north-belt': { base: 'old-north-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'old-north-belt-e' }, { face: 1, id: 'old-north-belt-w' }, { face: 2, id: 'old-north-belt-n' }, { face: 3, id: 'old-north-belt-s' }, { face: 5, id: 'old-north-belt-b' }] },
  'tel-aviv-port-belt': { base: 'tel-aviv-port-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tel-aviv-port-belt-e' }, { face: 1, id: 'tel-aviv-port-belt-w' }, { face: 2, id: 'tel-aviv-port-belt-n' }, { face: 3, id: 'tel-aviv-port-belt-s' }, { face: 5, id: 'tel-aviv-port-belt-b' }] },
  'sarona-belt': { base: 'sarona-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sarona-belt-e' }, { face: 1, id: 'sarona-belt-w' }, { face: 2, id: 'sarona-belt-n' }, { face: 3, id: 'sarona-belt-s' }, { face: 5, id: 'sarona-belt-b' }] },
  'neve-shaanan-belt': { base: 'neve-shaanan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'neve-shaanan-belt-e' }, { face: 1, id: 'neve-shaanan-belt-w' }, { face: 2, id: 'neve-shaanan-belt-n' }, { face: 3, id: 'neve-shaanan-belt-s' }, { face: 5, id: 'neve-shaanan-belt-b' }] },
  'yad-eliyahu-belt': { base: 'yad-eliyahu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yad-eliyahu-belt-e' }, { face: 1, id: 'yad-eliyahu-belt-w' }, { face: 2, id: 'yad-eliyahu-belt-n' }, { face: 3, id: 'yad-eliyahu-belt-s' }, { face: 5, id: 'yad-eliyahu-belt-b' }] },
  'ramat-gan-bursa-belt': { base: 'ramat-gan-bursa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ramat-gan-bursa-belt-e' }, { face: 1, id: 'ramat-gan-bursa-belt-w' }, { face: 2, id: 'ramat-gan-bursa-belt-n' }, { face: 3, id: 'ramat-gan-bursa-belt-s' }, { face: 5, id: 'ramat-gan-bursa-belt-b' }] },
  'givatayim-belt': { base: 'givatayim-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'givatayim-belt-e' }, { face: 1, id: 'givatayim-belt-w' }, { face: 2, id: 'givatayim-belt-n' }, { face: 3, id: 'givatayim-belt-s' }, { face: 5, id: 'givatayim-belt-b' }] },
  'bnei-brak-belt': { base: 'bnei-brak-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bnei-brak-belt-e' }, { face: 1, id: 'bnei-brak-belt-w' }, { face: 2, id: 'bnei-brak-belt-n' }, { face: 3, id: 'bnei-brak-belt-s' }, { face: 5, id: 'bnei-brak-belt-b' }] },
  'bat-yam-belt': { base: 'bat-yam-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bat-yam-belt-e' }, { face: 1, id: 'bat-yam-belt-w' }, { face: 2, id: 'bat-yam-belt-n' }, { face: 3, id: 'bat-yam-belt-s' }, { face: 5, id: 'bat-yam-belt-b' }] },
  'san-jose-downtown-belt': { base: 'san-jose-downtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'san-jose-downtown-belt-e' }, { face: 1, id: 'san-jose-downtown-belt-w' }, { face: 2, id: 'san-jose-downtown-belt-n' }, { face: 3, id: 'san-jose-downtown-belt-s' }, { face: 5, id: 'san-jose-downtown-belt-b' }] },
  'sofa-district-belt': { base: 'sofa-district-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sofa-district-belt-e' }, { face: 1, id: 'sofa-district-belt-w' }, { face: 2, id: 'sofa-district-belt-n' }, { face: 3, id: 'sofa-district-belt-s' }, { face: 5, id: 'sofa-district-belt-b' }] },
  'japantown-san-jose-belt': { base: 'japantown-san-jose-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'japantown-san-jose-belt-e' }, { face: 1, id: 'japantown-san-jose-belt-w' }, { face: 2, id: 'japantown-san-jose-belt-n' }, { face: 3, id: 'japantown-san-jose-belt-s' }, { face: 5, id: 'japantown-san-jose-belt-b' }] },
  'midtown-san-jose-belt': { base: 'midtown-san-jose-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'midtown-san-jose-belt-e' }, { face: 1, id: 'midtown-san-jose-belt-w' }, { face: 2, id: 'midtown-san-jose-belt-n' }, { face: 3, id: 'midtown-san-jose-belt-s' }, { face: 5, id: 'midtown-san-jose-belt-b' }] },
  'naglee-park-belt': { base: 'naglee-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'naglee-park-belt-e' }, { face: 1, id: 'naglee-park-belt-w' }, { face: 2, id: 'naglee-park-belt-n' }, { face: 3, id: 'naglee-park-belt-s' }, { face: 5, id: 'naglee-park-belt-b' }] },
  'spartan-keyes-belt': { base: 'spartan-keyes-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'spartan-keyes-belt-e' }, { face: 1, id: 'spartan-keyes-belt-w' }, { face: 2, id: 'spartan-keyes-belt-n' }, { face: 3, id: 'spartan-keyes-belt-s' }, { face: 5, id: 'spartan-keyes-belt-b' }] },
  'willow-glen-belt': { base: 'willow-glen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'willow-glen-belt-e' }, { face: 1, id: 'willow-glen-belt-w' }, { face: 2, id: 'willow-glen-belt-n' }, { face: 3, id: 'willow-glen-belt-s' }, { face: 5, id: 'willow-glen-belt-b' }] },
  'rose-garden-belt': { base: 'rose-garden-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'rose-garden-belt-e' }, { face: 1, id: 'rose-garden-belt-w' }, { face: 2, id: 'rose-garden-belt-n' }, { face: 3, id: 'rose-garden-belt-s' }, { face: 5, id: 'rose-garden-belt-b' }] },
  'burbank-belt': { base: 'burbank-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'burbank-belt-e' }, { face: 1, id: 'burbank-belt-w' }, { face: 2, id: 'burbank-belt-n' }, { face: 3, id: 'burbank-belt-s' }, { face: 5, id: 'burbank-belt-b' }] },
  'santana-row-belt': { base: 'santana-row-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'santana-row-belt-e' }, { face: 1, id: 'santana-row-belt-w' }, { face: 2, id: 'santana-row-belt-n' }, { face: 3, id: 'santana-row-belt-s' }, { face: 5, id: 'santana-row-belt-b' }] },
  'santa-clara-downtown-belt': { base: 'santa-clara-downtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'santa-clara-downtown-belt-e' }, { face: 1, id: 'santa-clara-downtown-belt-w' }, { face: 2, id: 'santa-clara-downtown-belt-n' }, { face: 3, id: 'santa-clara-downtown-belt-s' }, { face: 5, id: 'santa-clara-downtown-belt-b' }] },
  'little-portugal-belt': { base: 'little-portugal-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'little-portugal-belt-e' }, { face: 1, id: 'little-portugal-belt-w' }, { face: 2, id: 'little-portugal-belt-n' }, { face: 3, id: 'little-portugal-belt-s' }, { face: 5, id: 'little-portugal-belt-b' }] },
  'little-saigon-belt': { base: 'little-saigon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'little-saigon-belt-e' }, { face: 1, id: 'little-saigon-belt-w' }, { face: 2, id: 'little-saigon-belt-n' }, { face: 3, id: 'little-saigon-belt-s' }, { face: 5, id: 'little-saigon-belt-b' }] },
  'alum-rock-belt': { base: 'alum-rock-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'alum-rock-belt-e' }, { face: 1, id: 'alum-rock-belt-w' }, { face: 2, id: 'alum-rock-belt-n' }, { face: 3, id: 'alum-rock-belt-s' }, { face: 5, id: 'alum-rock-belt-b' }] },
  'berryessa-belt': { base: 'berryessa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'berryessa-belt-e' }, { face: 1, id: 'berryessa-belt-w' }, { face: 2, id: 'berryessa-belt-n' }, { face: 3, id: 'berryessa-belt-s' }, { face: 5, id: 'berryessa-belt-b' }] },
  'centro-historico-belt': { base: 'centro-historico-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'centro-historico-belt-e' }, { face: 1, id: 'centro-historico-belt-w' }, { face: 2, id: 'centro-historico-belt-n' }, { face: 3, id: 'centro-historico-belt-s' }, { face: 5, id: 'centro-historico-belt-b' }] },
  'tepito-belt': { base: 'tepito-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tepito-belt-e' }, { face: 1, id: 'tepito-belt-w' }, { face: 2, id: 'tepito-belt-n' }, { face: 3, id: 'tepito-belt-s' }, { face: 5, id: 'tepito-belt-b' }] },
  'tlatelolco-belt': { base: 'tlatelolco-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tlatelolco-belt-e' }, { face: 1, id: 'tlatelolco-belt-w' }, { face: 2, id: 'tlatelolco-belt-n' }, { face: 3, id: 'tlatelolco-belt-s' }, { face: 5, id: 'tlatelolco-belt-b' }] },
  'buenavista-belt': { base: 'buenavista-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'buenavista-belt-e' }, { face: 1, id: 'buenavista-belt-w' }, { face: 2, id: 'buenavista-belt-n' }, { face: 3, id: 'buenavista-belt-s' }, { face: 5, id: 'buenavista-belt-b' }] },
  'la-villa-belt': { base: 'la-villa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'la-villa-belt-e' }, { face: 1, id: 'la-villa-belt-w' }, { face: 2, id: 'la-villa-belt-n' }, { face: 3, id: 'la-villa-belt-s' }, { face: 5, id: 'la-villa-belt-b' }] },
  'reforma-belt': { base: 'reforma-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'reforma-belt-e' }, { face: 1, id: 'reforma-belt-w' }, { face: 2, id: 'reforma-belt-n' }, { face: 3, id: 'reforma-belt-s' }, { face: 5, id: 'reforma-belt-b' }] },
  'polanco-belt': { base: 'polanco-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'polanco-belt-e' }, { face: 1, id: 'polanco-belt-w' }, { face: 2, id: 'polanco-belt-n' }, { face: 3, id: 'polanco-belt-s' }, { face: 5, id: 'polanco-belt-b' }] },
  'doctores-belt': { base: 'doctores-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'doctores-belt-e' }, { face: 1, id: 'doctores-belt-w' }, { face: 2, id: 'doctores-belt-n' }, { face: 3, id: 'doctores-belt-s' }, { face: 5, id: 'doctores-belt-b' }] },
  'roma-norte-belt': { base: 'roma-norte-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'roma-norte-belt-e' }, { face: 1, id: 'roma-norte-belt-w' }, { face: 2, id: 'roma-norte-belt-n' }, { face: 3, id: 'roma-norte-belt-s' }, { face: 5, id: 'roma-norte-belt-b' }] },
  'condesa-belt': { base: 'condesa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'condesa-belt-e' }, { face: 1, id: 'condesa-belt-w' }, { face: 2, id: 'condesa-belt-n' }, { face: 3, id: 'condesa-belt-s' }, { face: 5, id: 'condesa-belt-b' }] },
  'narvarte-belt': { base: 'narvarte-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'narvarte-belt-e' }, { face: 1, id: 'narvarte-belt-w' }, { face: 2, id: 'narvarte-belt-n' }, { face: 3, id: 'narvarte-belt-s' }, { face: 5, id: 'narvarte-belt-b' }] },
  'del-valle-belt': { base: 'del-valle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'del-valle-belt-e' }, { face: 1, id: 'del-valle-belt-w' }, { face: 2, id: 'del-valle-belt-n' }, { face: 3, id: 'del-valle-belt-s' }, { face: 5, id: 'del-valle-belt-b' }] },
  'san-angel-belt': { base: 'san-angel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'san-angel-belt-e' }, { face: 1, id: 'san-angel-belt-w' }, { face: 2, id: 'san-angel-belt-n' }, { face: 3, id: 'san-angel-belt-s' }, { face: 5, id: 'san-angel-belt-b' }] },
  'coyoacan-belt': { base: 'coyoacan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'coyoacan-belt-e' }, { face: 1, id: 'coyoacan-belt-w' }, { face: 2, id: 'coyoacan-belt-n' }, { face: 3, id: 'coyoacan-belt-s' }, { face: 5, id: 'coyoacan-belt-b' }] },
  'ciudad-universitaria-belt': { base: 'ciudad-universitaria-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ciudad-universitaria-belt-e' }, { face: 1, id: 'ciudad-universitaria-belt-w' }, { face: 2, id: 'ciudad-universitaria-belt-n' }, { face: 3, id: 'ciudad-universitaria-belt-s' }, { face: 5, id: 'ciudad-universitaria-belt-b' }] },
  'kota-tua-belt': { base: 'kota-tua-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kota-tua-belt-e' }, { face: 1, id: 'kota-tua-belt-w' }, { face: 2, id: 'kota-tua-belt-n' }, { face: 3, id: 'kota-tua-belt-s' }, { face: 5, id: 'kota-tua-belt-b' }] },
  'glodok-belt': { base: 'glodok-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'glodok-belt-e' }, { face: 1, id: 'glodok-belt-w' }, { face: 2, id: 'glodok-belt-n' }, { face: 3, id: 'glodok-belt-s' }, { face: 5, id: 'glodok-belt-b' }] },
  'tambora-belt': { base: 'tambora-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tambora-belt-e' }, { face: 1, id: 'tambora-belt-w' }, { face: 2, id: 'tambora-belt-n' }, { face: 3, id: 'tambora-belt-s' }, { face: 5, id: 'tambora-belt-b' }] },
  'pluit-belt': { base: 'pluit-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pluit-belt-e' }, { face: 1, id: 'pluit-belt-w' }, { face: 2, id: 'pluit-belt-n' }, { face: 3, id: 'pluit-belt-s' }, { face: 5, id: 'pluit-belt-b' }] },
  'tanjung-priok-belt': { base: 'tanjung-priok-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tanjung-priok-belt-e' }, { face: 1, id: 'tanjung-priok-belt-w' }, { face: 2, id: 'tanjung-priok-belt-n' }, { face: 3, id: 'tanjung-priok-belt-s' }, { face: 5, id: 'tanjung-priok-belt-b' }] },
  'kelapa-gading-belt': { base: 'kelapa-gading-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kelapa-gading-belt-e' }, { face: 1, id: 'kelapa-gading-belt-w' }, { face: 2, id: 'kelapa-gading-belt-n' }, { face: 3, id: 'kelapa-gading-belt-s' }, { face: 5, id: 'kelapa-gading-belt-b' }] },
  'grogol-belt': { base: 'grogol-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'grogol-belt-e' }, { face: 1, id: 'grogol-belt-w' }, { face: 2, id: 'grogol-belt-n' }, { face: 3, id: 'grogol-belt-s' }, { face: 5, id: 'grogol-belt-b' }] },
  'gambir-belt': { base: 'gambir-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gambir-belt-e' }, { face: 1, id: 'gambir-belt-w' }, { face: 2, id: 'gambir-belt-n' }, { face: 3, id: 'gambir-belt-s' }, { face: 5, id: 'gambir-belt-b' }] },
  'senen-belt': { base: 'senen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'senen-belt-e' }, { face: 1, id: 'senen-belt-w' }, { face: 2, id: 'senen-belt-n' }, { face: 3, id: 'senen-belt-s' }, { face: 5, id: 'senen-belt-b' }] },
  'tanah-abang-belt': { base: 'tanah-abang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tanah-abang-belt-e' }, { face: 1, id: 'tanah-abang-belt-w' }, { face: 2, id: 'tanah-abang-belt-n' }, { face: 3, id: 'tanah-abang-belt-s' }, { face: 5, id: 'tanah-abang-belt-b' }] },
  'menteng-belt': { base: 'menteng-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'menteng-belt-e' }, { face: 1, id: 'menteng-belt-w' }, { face: 2, id: 'menteng-belt-n' }, { face: 3, id: 'menteng-belt-s' }, { face: 5, id: 'menteng-belt-b' }] },
  'sudirman-belt': { base: 'sudirman-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sudirman-belt-e' }, { face: 1, id: 'sudirman-belt-w' }, { face: 2, id: 'sudirman-belt-n' }, { face: 3, id: 'sudirman-belt-s' }, { face: 5, id: 'sudirman-belt-b' }] },
  'jatinegara-belt': { base: 'jatinegara-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jatinegara-belt-e' }, { face: 1, id: 'jatinegara-belt-w' }, { face: 2, id: 'jatinegara-belt-n' }, { face: 3, id: 'jatinegara-belt-s' }, { face: 5, id: 'jatinegara-belt-b' }] },
  'tebet-belt': { base: 'tebet-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tebet-belt-e' }, { face: 1, id: 'tebet-belt-w' }, { face: 2, id: 'tebet-belt-n' }, { face: 3, id: 'tebet-belt-s' }, { face: 5, id: 'tebet-belt-b' }] },
  'blok-m-belt': { base: 'blok-m-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'blok-m-belt-e' }, { face: 1, id: 'blok-m-belt-w' }, { face: 2, id: 'blok-m-belt-n' }, { face: 3, id: 'blok-m-belt-s' }, { face: 5, id: 'blok-m-belt-b' }] },
  'paddington-london-belt': { base: 'paddington-london-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'paddington-london-belt-e' }, { face: 1, id: 'paddington-london-belt-w' }, { face: 2, id: 'paddington-london-belt-n' }, { face: 3, id: 'paddington-london-belt-s' }, { face: 5, id: 'paddington-london-belt-b' }] },
  'south-kensington-belt': { base: 'south-kensington-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'south-kensington-belt-e' }, { face: 1, id: 'south-kensington-belt-w' }, { face: 2, id: 'south-kensington-belt-n' }, { face: 3, id: 'south-kensington-belt-s' }, { face: 5, id: 'south-kensington-belt-b' }] },
  'marylebone-belt': { base: 'marylebone-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marylebone-belt-e' }, { face: 1, id: 'marylebone-belt-w' }, { face: 2, id: 'marylebone-belt-n' }, { face: 3, id: 'marylebone-belt-s' }, { face: 5, id: 'marylebone-belt-b' }] },
  'camden-town-belt': { base: 'camden-town-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'camden-town-belt-e' }, { face: 1, id: 'camden-town-belt-w' }, { face: 2, id: 'camden-town-belt-n' }, { face: 3, id: 'camden-town-belt-s' }, { face: 5, id: 'camden-town-belt-b' }] },
  'soho-belt': { base: 'soho-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'soho-belt-e' }, { face: 1, id: 'soho-belt-w' }, { face: 2, id: 'soho-belt-n' }, { face: 3, id: 'soho-belt-s' }, { face: 5, id: 'soho-belt-b' }] },
  'westminster-belt': { base: 'westminster-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'westminster-belt-e' }, { face: 1, id: 'westminster-belt-w' }, { face: 2, id: 'westminster-belt-n' }, { face: 3, id: 'westminster-belt-s' }, { face: 5, id: 'westminster-belt-b' }] },
  'bloomsbury-belt': { base: 'bloomsbury-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bloomsbury-belt-e' }, { face: 1, id: 'bloomsbury-belt-w' }, { face: 2, id: 'bloomsbury-belt-n' }, { face: 3, id: 'bloomsbury-belt-s' }, { face: 5, id: 'bloomsbury-belt-b' }] },
  'kings-cross-london-belt': { base: 'kings-cross-london-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kings-cross-london-belt-e' }, { face: 1, id: 'kings-cross-london-belt-w' }, { face: 2, id: 'kings-cross-london-belt-n' }, { face: 3, id: 'kings-cross-london-belt-s' }, { face: 5, id: 'kings-cross-london-belt-b' }] },
  'waterloo-belt': { base: 'waterloo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'waterloo-belt-e' }, { face: 1, id: 'waterloo-belt-w' }, { face: 2, id: 'waterloo-belt-n' }, { face: 3, id: 'waterloo-belt-s' }, { face: 5, id: 'waterloo-belt-b' }] },
  'clerkenwell-belt': { base: 'clerkenwell-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'clerkenwell-belt-e' }, { face: 1, id: 'clerkenwell-belt-w' }, { face: 2, id: 'clerkenwell-belt-n' }, { face: 3, id: 'clerkenwell-belt-s' }, { face: 5, id: 'clerkenwell-belt-b' }] },
  'city-of-london-belt': { base: 'city-of-london-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'city-of-london-belt-e' }, { face: 1, id: 'city-of-london-belt-w' }, { face: 2, id: 'city-of-london-belt-n' }, { face: 3, id: 'city-of-london-belt-s' }, { face: 5, id: 'city-of-london-belt-b' }] },
  'borough-belt': { base: 'borough-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'borough-belt-e' }, { face: 1, id: 'borough-belt-w' }, { face: 2, id: 'borough-belt-n' }, { face: 3, id: 'borough-belt-s' }, { face: 5, id: 'borough-belt-b' }] },
  'shoreditch-belt': { base: 'shoreditch-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shoreditch-belt-e' }, { face: 1, id: 'shoreditch-belt-w' }, { face: 2, id: 'shoreditch-belt-n' }, { face: 3, id: 'shoreditch-belt-s' }, { face: 5, id: 'shoreditch-belt-b' }] },
  'canary-wharf-belt': { base: 'canary-wharf-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'canary-wharf-belt-e' }, { face: 1, id: 'canary-wharf-belt-w' }, { face: 2, id: 'canary-wharf-belt-n' }, { face: 3, id: 'canary-wharf-belt-s' }, { face: 5, id: 'canary-wharf-belt-b' }] },
  'greenwich-belt': { base: 'greenwich-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'greenwich-belt-e' }, { face: 1, id: 'greenwich-belt-w' }, { face: 2, id: 'greenwich-belt-n' }, { face: 3, id: 'greenwich-belt-s' }, { face: 5, id: 'greenwich-belt-b' }] },
  'centro-storico-belt': { base: 'centro-storico-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'centro-storico-belt-e' }, { face: 1, id: 'centro-storico-belt-w' }, { face: 2, id: 'centro-storico-belt-n' }, { face: 3, id: 'centro-storico-belt-s' }, { face: 5, id: 'centro-storico-belt-b' }] },
  'monti-belt': { base: 'monti-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'monti-belt-e' }, { face: 1, id: 'monti-belt-w' }, { face: 2, id: 'monti-belt-n' }, { face: 3, id: 'monti-belt-s' }, { face: 5, id: 'monti-belt-b' }] },
  'trastevere-belt': { base: 'trastevere-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'trastevere-belt-e' }, { face: 1, id: 'trastevere-belt-w' }, { face: 2, id: 'trastevere-belt-n' }, { face: 3, id: 'trastevere-belt-s' }, { face: 5, id: 'trastevere-belt-b' }] },
  'pigneto-belt': { base: 'pigneto-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pigneto-belt-e' }, { face: 1, id: 'pigneto-belt-w' }, { face: 2, id: 'pigneto-belt-n' }, { face: 3, id: 'pigneto-belt-s' }, { face: 5, id: 'pigneto-belt-b' }] },
  'esquilino-belt': { base: 'esquilino-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'esquilino-belt-e' }, { face: 1, id: 'esquilino-belt-w' }, { face: 2, id: 'esquilino-belt-n' }, { face: 3, id: 'esquilino-belt-s' }, { face: 5, id: 'esquilino-belt-b' }] },
  'aventino-belt': { base: 'aventino-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'aventino-belt-e' }, { face: 1, id: 'aventino-belt-w' }, { face: 2, id: 'aventino-belt-n' }, { face: 3, id: 'aventino-belt-s' }, { face: 5, id: 'aventino-belt-b' }] },
  'garbatella-belt': { base: 'garbatella-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'garbatella-belt-e' }, { face: 1, id: 'garbatella-belt-w' }, { face: 2, id: 'garbatella-belt-n' }, { face: 3, id: 'garbatella-belt-s' }, { face: 5, id: 'garbatella-belt-b' }] },
  'borgo-belt': { base: 'borgo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'borgo-belt-e' }, { face: 1, id: 'borgo-belt-w' }, { face: 2, id: 'borgo-belt-n' }, { face: 3, id: 'borgo-belt-s' }, { face: 5, id: 'borgo-belt-b' }] },
  'balduina-belt': { base: 'balduina-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'balduina-belt-e' }, { face: 1, id: 'balduina-belt-w' }, { face: 2, id: 'balduina-belt-n' }, { face: 3, id: 'balduina-belt-s' }, { face: 5, id: 'balduina-belt-b' }] },
  'san-lorenzo-belt': { base: 'san-lorenzo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'san-lorenzo-belt-e' }, { face: 1, id: 'san-lorenzo-belt-w' }, { face: 2, id: 'san-lorenzo-belt-n' }, { face: 3, id: 'san-lorenzo-belt-s' }, { face: 5, id: 'san-lorenzo-belt-b' }] },
  'monteverde-belt': { base: 'monteverde-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'monteverde-belt-e' }, { face: 1, id: 'monteverde-belt-w' }, { face: 2, id: 'monteverde-belt-n' }, { face: 3, id: 'monteverde-belt-s' }, { face: 5, id: 'monteverde-belt-b' }] },
  'san-giovanni-belt': { base: 'san-giovanni-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'san-giovanni-belt-e' }, { face: 1, id: 'san-giovanni-belt-w' }, { face: 2, id: 'san-giovanni-belt-n' }, { face: 3, id: 'san-giovanni-belt-s' }, { face: 5, id: 'san-giovanni-belt-b' }] },
  'testaccio-belt': { base: 'testaccio-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'testaccio-belt-e' }, { face: 1, id: 'testaccio-belt-w' }, { face: 2, id: 'testaccio-belt-n' }, { face: 3, id: 'testaccio-belt-s' }, { face: 5, id: 'testaccio-belt-b' }] },
  'parioli-belt': { base: 'parioli-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'parioli-belt-e' }, { face: 1, id: 'parioli-belt-w' }, { face: 2, id: 'parioli-belt-n' }, { face: 3, id: 'parioli-belt-s' }, { face: 5, id: 'parioli-belt-b' }] },
  'prati-belt': { base: 'prati-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'prati-belt-e' }, { face: 1, id: 'prati-belt-w' }, { face: 2, id: 'prati-belt-n' }, { face: 3, id: 'prati-belt-s' }, { face: 5, id: 'prati-belt-b' }] },
  'raffles-place-belt': { base: 'raffles-place-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'raffles-place-belt-e' }, { face: 1, id: 'raffles-place-belt-w' }, { face: 2, id: 'raffles-place-belt-n' }, { face: 3, id: 'raffles-place-belt-s' }, { face: 5, id: 'raffles-place-belt-b' }] },
  'chinatown-singapore-belt': { base: 'chinatown-singapore-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chinatown-singapore-belt-e' }, { face: 1, id: 'chinatown-singapore-belt-w' }, { face: 2, id: 'chinatown-singapore-belt-n' }, { face: 3, id: 'chinatown-singapore-belt-s' }, { face: 5, id: 'chinatown-singapore-belt-b' }] },
  'tanjong-pagar-belt': { base: 'tanjong-pagar-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tanjong-pagar-belt-e' }, { face: 1, id: 'tanjong-pagar-belt-w' }, { face: 2, id: 'tanjong-pagar-belt-n' }, { face: 3, id: 'tanjong-pagar-belt-s' }, { face: 5, id: 'tanjong-pagar-belt-b' }] },
  'clarke-quay-belt': { base: 'clarke-quay-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'clarke-quay-belt-e' }, { face: 1, id: 'clarke-quay-belt-w' }, { face: 2, id: 'clarke-quay-belt-n' }, { face: 3, id: 'clarke-quay-belt-s' }, { face: 5, id: 'clarke-quay-belt-b' }] },
  'bugis-belt': { base: 'bugis-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bugis-belt-e' }, { face: 1, id: 'bugis-belt-w' }, { face: 2, id: 'bugis-belt-n' }, { face: 3, id: 'bugis-belt-s' }, { face: 5, id: 'bugis-belt-b' }] },
  'little-india-belt': { base: 'little-india-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'little-india-belt-e' }, { face: 1, id: 'little-india-belt-w' }, { face: 2, id: 'little-india-belt-n' }, { face: 3, id: 'little-india-belt-s' }, { face: 5, id: 'little-india-belt-b' }] },
  'orchard-belt': { base: 'orchard-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'orchard-belt-e' }, { face: 1, id: 'orchard-belt-w' }, { face: 2, id: 'orchard-belt-n' }, { face: 3, id: 'orchard-belt-s' }, { face: 5, id: 'orchard-belt-b' }] },
  'novena-belt': { base: 'novena-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'novena-belt-e' }, { face: 1, id: 'novena-belt-w' }, { face: 2, id: 'novena-belt-n' }, { face: 3, id: 'novena-belt-s' }, { face: 5, id: 'novena-belt-b' }] },
  'geylang-belt': { base: 'geylang-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'geylang-belt-e' }, { face: 1, id: 'geylang-belt-w' }, { face: 2, id: 'geylang-belt-n' }, { face: 3, id: 'geylang-belt-s' }, { face: 5, id: 'geylang-belt-b' }] },
  'katong-belt': { base: 'katong-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'katong-belt-e' }, { face: 1, id: 'katong-belt-w' }, { face: 2, id: 'katong-belt-n' }, { face: 3, id: 'katong-belt-s' }, { face: 5, id: 'katong-belt-b' }] },
  'bedok-belt': { base: 'bedok-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bedok-belt-e' }, { face: 1, id: 'bedok-belt-w' }, { face: 2, id: 'bedok-belt-n' }, { face: 3, id: 'bedok-belt-s' }, { face: 5, id: 'bedok-belt-b' }] },
  'holland-village-belt': { base: 'holland-village-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'holland-village-belt-e' }, { face: 1, id: 'holland-village-belt-w' }, { face: 2, id: 'holland-village-belt-n' }, { face: 3, id: 'holland-village-belt-s' }, { face: 5, id: 'holland-village-belt-b' }] },
  'clementi-belt': { base: 'clementi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'clementi-belt-e' }, { face: 1, id: 'clementi-belt-w' }, { face: 2, id: 'clementi-belt-n' }, { face: 3, id: 'clementi-belt-s' }, { face: 5, id: 'clementi-belt-b' }] },
  'bishan-belt': { base: 'bishan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bishan-belt-e' }, { face: 1, id: 'bishan-belt-w' }, { face: 2, id: 'bishan-belt-n' }, { face: 3, id: 'bishan-belt-s' }, { face: 5, id: 'bishan-belt-b' }] },
  'serangoon-belt': { base: 'serangoon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'serangoon-belt-e' }, { face: 1, id: 'serangoon-belt-w' }, { face: 2, id: 'serangoon-belt-n' }, { face: 3, id: 'serangoon-belt-s' }, { face: 5, id: 'serangoon-belt-b' }] },
  'olympic-park-london-belt': { base: 'olympic-park-london-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'olympic-park-london-belt-e' }, { face: 1, id: 'olympic-park-london-belt-w' }, { face: 2, id: 'olympic-park-london-belt-n' }, { face: 3, id: 'olympic-park-london-belt-s' }, { face: 5, id: 'olympic-park-london-belt-b' }] },
  'wimbledon-belt': { base: 'wimbledon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wimbledon-belt-e' }, { face: 1, id: 'wimbledon-belt-w' }, { face: 2, id: 'wimbledon-belt-n' }, { face: 3, id: 'wimbledon-belt-s' }, { face: 5, id: 'wimbledon-belt-b' }] },
  'st-andrews-links-belt': { base: 'st-andrews-links-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'st-andrews-links-belt-e' }, { face: 1, id: 'st-andrews-links-belt-w' }, { face: 2, id: 'st-andrews-links-belt-n' }, { face: 3, id: 'st-andrews-links-belt-s' }, { face: 5, id: 'st-andrews-links-belt-b' }] },
  'sydney-olympic-park-belt': { base: 'sydney-olympic-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sydney-olympic-park-belt-e' }, { face: 1, id: 'sydney-olympic-park-belt-w' }, { face: 2, id: 'sydney-olympic-park-belt-n' }, { face: 3, id: 'sydney-olympic-park-belt-s' }, { face: 5, id: 'sydney-olympic-park-belt-b' }] },
  'olympic-green-belt': { base: 'olympic-green-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'olympic-green-belt-e' }, { face: 1, id: 'olympic-green-belt-w' }, { face: 2, id: 'olympic-green-belt-n' }, { face: 3, id: 'olympic-green-belt-s' }, { face: 5, id: 'olympic-green-belt-b' }] },
  'jingu-gaien-belt': { base: 'jingu-gaien-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'jingu-gaien-belt-e' }, { face: 1, id: 'jingu-gaien-belt-w' }, { face: 2, id: 'jingu-gaien-belt-n' }, { face: 3, id: 'jingu-gaien-belt-s' }, { face: 5, id: 'jingu-gaien-belt-b' }] },
  'olympiapark-munich-belt': { base: 'olympiapark-munich-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'olympiapark-munich-belt-e' }, { face: 1, id: 'olympiapark-munich-belt-w' }, { face: 2, id: 'olympiapark-munich-belt-n' }, { face: 3, id: 'olympiapark-munich-belt-s' }, { face: 5, id: 'olympiapark-munich-belt-b' }] },
  'olympic-park-montreal-belt': { base: 'olympic-park-montreal-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'olympic-park-montreal-belt-e' }, { face: 1, id: 'olympic-park-montreal-belt-w' }, { face: 2, id: 'olympic-park-montreal-belt-n' }, { face: 3, id: 'olympic-park-montreal-belt-s' }, { face: 5, id: 'olympic-park-montreal-belt-b' }] },
  'oaka-belt': { base: 'oaka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'oaka-belt-e' }, { face: 1, id: 'oaka-belt-w' }, { face: 2, id: 'oaka-belt-n' }, { face: 3, id: 'oaka-belt-s' }, { face: 5, id: 'oaka-belt-b' }] },
  'speedway-belt': { base: 'speedway-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'speedway-belt-e' }, { face: 1, id: 'speedway-belt-w' }, { face: 2, id: 'speedway-belt-n' }, { face: 3, id: 'speedway-belt-s' }, { face: 5, id: 'speedway-belt-b' }] },
  'barra-olympic-park-belt': { base: 'barra-olympic-park-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'barra-olympic-park-belt-e' }, { face: 1, id: 'barra-olympic-park-belt-w' }, { face: 2, id: 'barra-olympic-park-belt-n' }, { face: 3, id: 'barra-olympic-park-belt-s' }, { face: 5, id: 'barra-olympic-park-belt-b' }] },
  'saint-denis-belt': { base: 'saint-denis-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'saint-denis-belt-e' }, { face: 1, id: 'saint-denis-belt-w' }, { face: 2, id: 'saint-denis-belt-n' }, { face: 3, id: 'saint-denis-belt-s' }, { face: 5, id: 'saint-denis-belt-b' }] },
  // Computed game worlds (ADR-0272). Same CubeBeltSpec shape as a real city's belt —
  // the renderer binds six faces the same way whatever produced them.
  'grounds-complex': { base: 'grounds-complex-z', faces: [{ face: 0, id: 'grounds-complex-e' }, { face: 1, id: 'grounds-complex-w' }, { face: 2, id: 'grounds-complex-n' }, { face: 3, id: 'grounds-complex-s' }, { face: 5, id: 'grounds-complex-b' }] },
  'province-snow-peaks': { base: 'province-snow-peaks-z', faces: [{ face: 0, id: 'province-snow-peaks-e' }, { face: 1, id: 'province-snow-peaks-w' }, { face: 2, id: 'province-snow-peaks-n' }, { face: 3, id: 'province-snow-peaks-s' }, { face: 5, id: 'province-snow-peaks-b' }] },
  'cite-belt': { base: 'cite-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cite-belt-e' }, { face: 1, id: 'cite-belt-w' }, { face: 2, id: 'cite-belt-n' }, { face: 3, id: 'cite-belt-s' }, { face: 5, id: 'cite-belt-b' }] },
  'paquis-belt': { base: 'paquis-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'paquis-belt-e' }, { face: 1, id: 'paquis-belt-w' }, { face: 2, id: 'paquis-belt-n' }, { face: 3, id: 'paquis-belt-s' }, { face: 5, id: 'paquis-belt-b' }] },
  'eaux-vives-belt': { base: 'eaux-vives-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'eaux-vives-belt-e' }, { face: 1, id: 'eaux-vives-belt-w' }, { face: 2, id: 'eaux-vives-belt-n' }, { face: 3, id: 'eaux-vives-belt-s' }, { face: 5, id: 'eaux-vives-belt-b' }] },
  'plainpalais-belt': { base: 'plainpalais-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'plainpalais-belt-e' }, { face: 1, id: 'plainpalais-belt-w' }, { face: 2, id: 'plainpalais-belt-n' }, { face: 3, id: 'plainpalais-belt-s' }, { face: 5, id: 'plainpalais-belt-b' }] },
  'champel-belt': { base: 'champel-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'champel-belt-e' }, { face: 1, id: 'champel-belt-w' }, { face: 2, id: 'champel-belt-n' }, { face: 3, id: 'champel-belt-s' }, { face: 5, id: 'champel-belt-b' }] },
  'servette-belt': { base: 'servette-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'servette-belt-e' }, { face: 1, id: 'servette-belt-w' }, { face: 2, id: 'servette-belt-n' }, { face: 3, id: 'servette-belt-s' }, { face: 5, id: 'servette-belt-b' }] },
  'charmilles-belt': { base: 'charmilles-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'charmilles-belt-e' }, { face: 1, id: 'charmilles-belt-w' }, { face: 2, id: 'charmilles-belt-n' }, { face: 3, id: 'charmilles-belt-s' }, { face: 5, id: 'charmilles-belt-b' }] },
  'carouge-belt': { base: 'carouge-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'carouge-belt-e' }, { face: 1, id: 'carouge-belt-w' }, { face: 2, id: 'carouge-belt-n' }, { face: 3, id: 'carouge-belt-s' }, { face: 5, id: 'carouge-belt-b' }] },
  'lancy-belt': { base: 'lancy-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lancy-belt-e' }, { face: 1, id: 'lancy-belt-w' }, { face: 2, id: 'lancy-belt-n' }, { face: 3, id: 'lancy-belt-s' }, { face: 5, id: 'lancy-belt-b' }] },
  'vernier-belt': { base: 'vernier-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vernier-belt-e' }, { face: 1, id: 'vernier-belt-w' }, { face: 2, id: 'vernier-belt-n' }, { face: 3, id: 'vernier-belt-s' }, { face: 5, id: 'vernier-belt-b' }] },
  'grand-saconnex-belt': { base: 'grand-saconnex-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'grand-saconnex-belt-e' }, { face: 1, id: 'grand-saconnex-belt-w' }, { face: 2, id: 'grand-saconnex-belt-n' }, { face: 3, id: 'grand-saconnex-belt-s' }, { face: 5, id: 'grand-saconnex-belt-b' }] },
  'meyrin-belt': { base: 'meyrin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'meyrin-belt-e' }, { face: 1, id: 'meyrin-belt-w' }, { face: 2, id: 'meyrin-belt-n' }, { face: 3, id: 'meyrin-belt-s' }, { face: 5, id: 'meyrin-belt-b' }] },
  'chene-belt': { base: 'chene-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chene-belt-e' }, { face: 1, id: 'chene-belt-w' }, { face: 2, id: 'chene-belt-n' }, { face: 3, id: 'chene-belt-s' }, { face: 5, id: 'chene-belt-b' }] },
  'cologny-belt': { base: 'cologny-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cologny-belt-e' }, { face: 1, id: 'cologny-belt-w' }, { face: 2, id: 'cologny-belt-n' }, { face: 3, id: 'cologny-belt-s' }, { face: 5, id: 'cologny-belt-b' }] },
  'versoix-belt': { base: 'versoix-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'versoix-belt-e' }, { face: 1, id: 'versoix-belt-w' }, { face: 2, id: 'versoix-belt-n' }, { face: 3, id: 'versoix-belt-s' }, { face: 5, id: 'versoix-belt-b' }] },
  'onex-belt': { base: 'onex-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'onex-belt-e' }, { face: 1, id: 'onex-belt-w' }, { face: 2, id: 'onex-belt-n' }, { face: 3, id: 'onex-belt-s' }, { face: 5, id: 'onex-belt-b' }] },
  'se-belt': { base: 'se-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'se-belt-e' }, { face: 1, id: 'se-belt-w' }, { face: 2, id: 'se-belt-n' }, { face: 3, id: 'se-belt-s' }, { face: 5, id: 'se-belt-b' }] },
  'santo-antonio-belt': { base: 'santo-antonio-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'santo-antonio-belt-e' }, { face: 1, id: 'santo-antonio-belt-w' }, { face: 2, id: 'santo-antonio-belt-n' }, { face: 3, id: 'santo-antonio-belt-s' }, { face: 5, id: 'santo-antonio-belt-b' }] },
  'sao-lazaro-belt': { base: 'sao-lazaro-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sao-lazaro-belt-e' }, { face: 1, id: 'sao-lazaro-belt-w' }, { face: 2, id: 'sao-lazaro-belt-n' }, { face: 3, id: 'sao-lazaro-belt-s' }, { face: 5, id: 'sao-lazaro-belt-b' }] },
  'mong-ha-belt': { base: 'mong-ha-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mong-ha-belt-e' }, { face: 1, id: 'mong-ha-belt-w' }, { face: 2, id: 'mong-ha-belt-n' }, { face: 3, id: 'mong-ha-belt-s' }, { face: 5, id: 'mong-ha-belt-b' }] },
  'fatima-belt': { base: 'fatima-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fatima-belt-e' }, { face: 1, id: 'fatima-belt-w' }, { face: 2, id: 'fatima-belt-n' }, { face: 3, id: 'fatima-belt-s' }, { face: 5, id: 'fatima-belt-b' }] },
  'ilha-verde-belt': { base: 'ilha-verde-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ilha-verde-belt-e' }, { face: 1, id: 'ilha-verde-belt-w' }, { face: 2, id: 'ilha-verde-belt-n' }, { face: 3, id: 'ilha-verde-belt-s' }, { face: 5, id: 'ilha-verde-belt-b' }] },
  'barra-belt': { base: 'barra-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'barra-belt-e' }, { face: 1, id: 'barra-belt-w' }, { face: 2, id: 'barra-belt-n' }, { face: 3, id: 'barra-belt-s' }, { face: 5, id: 'barra-belt-b' }] },
  'nam-van-belt': { base: 'nam-van-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nam-van-belt-e' }, { face: 1, id: 'nam-van-belt-w' }, { face: 2, id: 'nam-van-belt-n' }, { face: 3, id: 'nam-van-belt-s' }, { face: 5, id: 'nam-van-belt-b' }] },
  'nape-belt': { base: 'nape-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nape-belt-e' }, { face: 1, id: 'nape-belt-w' }, { face: 2, id: 'nape-belt-n' }, { face: 3, id: 'nape-belt-s' }, { face: 5, id: 'nape-belt-b' }] },
  'taipa-belt': { base: 'taipa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'taipa-belt-e' }, { face: 1, id: 'taipa-belt-w' }, { face: 2, id: 'taipa-belt-n' }, { face: 3, id: 'taipa-belt-s' }, { face: 5, id: 'taipa-belt-b' }] },
  'cotai-belt': { base: 'cotai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'cotai-belt-e' }, { face: 1, id: 'cotai-belt-w' }, { face: 2, id: 'cotai-belt-n' }, { face: 3, id: 'cotai-belt-s' }, { face: 5, id: 'cotai-belt-b' }] },
  'coloane-belt': { base: 'coloane-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'coloane-belt-e' }, { face: 1, id: 'coloane-belt-w' }, { face: 2, id: 'coloane-belt-n' }, { face: 3, id: 'coloane-belt-s' }, { face: 5, id: 'coloane-belt-b' }] },
  'fontvieille-belt': { base: 'fontvieille-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fontvieille-belt-e' }, { face: 1, id: 'fontvieille-belt-w' }, { face: 2, id: 'fontvieille-belt-n' }, { face: 3, id: 'fontvieille-belt-s' }, { face: 5, id: 'fontvieille-belt-b' }] },
  'monaco-ville-belt': { base: 'monaco-ville-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'monaco-ville-belt-e' }, { face: 1, id: 'monaco-ville-belt-w' }, { face: 2, id: 'monaco-ville-belt-n' }, { face: 3, id: 'monaco-ville-belt-s' }, { face: 5, id: 'monaco-ville-belt-b' }] },
  'condamine-belt': { base: 'condamine-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'condamine-belt-e' }, { face: 1, id: 'condamine-belt-w' }, { face: 2, id: 'condamine-belt-n' }, { face: 3, id: 'condamine-belt-s' }, { face: 5, id: 'condamine-belt-b' }] },
  'monte-carlo-belt': { base: 'monte-carlo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'monte-carlo-belt-e' }, { face: 1, id: 'monte-carlo-belt-w' }, { face: 2, id: 'monte-carlo-belt-n' }, { face: 3, id: 'monte-carlo-belt-s' }, { face: 5, id: 'monte-carlo-belt-b' }] },
  'larvotto-belt': { base: 'larvotto-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'larvotto-belt-e' }, { face: 1, id: 'larvotto-belt-w' }, { face: 2, id: 'larvotto-belt-n' }, { face: 3, id: 'larvotto-belt-s' }, { face: 5, id: 'larvotto-belt-b' }] },
  'vaticano-belt': { base: 'vaticano-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vaticano-belt-e' }, { face: 1, id: 'vaticano-belt-w' }, { face: 2, id: 'vaticano-belt-n' }, { face: 3, id: 'vaticano-belt-s' }, { face: 5, id: 'vaticano-belt-b' }] },
  'gion-belt': { base: 'gion-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gion-belt-e' }, { face: 1, id: 'gion-belt-w' }, { face: 2, id: 'gion-belt-n' }, { face: 3, id: 'gion-belt-s' }, { face: 5, id: 'gion-belt-b' }] },
  'kawaramachi-belt': { base: 'kawaramachi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kawaramachi-belt-e' }, { face: 1, id: 'kawaramachi-belt-w' }, { face: 2, id: 'kawaramachi-belt-n' }, { face: 3, id: 'kawaramachi-belt-s' }, { face: 5, id: 'kawaramachi-belt-b' }] },
  'karasuma-belt': { base: 'karasuma-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'karasuma-belt-e' }, { face: 1, id: 'karasuma-belt-w' }, { face: 2, id: 'karasuma-belt-n' }, { face: 3, id: 'karasuma-belt-s' }, { face: 5, id: 'karasuma-belt-b' }] },
  'kyoto-station-belt': { base: 'kyoto-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kyoto-station-belt-e' }, { face: 1, id: 'kyoto-station-belt-w' }, { face: 2, id: 'kyoto-station-belt-n' }, { face: 3, id: 'kyoto-station-belt-s' }, { face: 5, id: 'kyoto-station-belt-b' }] },
  'toji-belt': { base: 'toji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'toji-belt-e' }, { face: 1, id: 'toji-belt-w' }, { face: 2, id: 'toji-belt-n' }, { face: 3, id: 'toji-belt-s' }, { face: 5, id: 'toji-belt-b' }] },
  'nijo-belt': { base: 'nijo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nijo-belt-e' }, { face: 1, id: 'nijo-belt-w' }, { face: 2, id: 'nijo-belt-n' }, { face: 3, id: 'nijo-belt-s' }, { face: 5, id: 'nijo-belt-b' }] },
  'gosho-belt': { base: 'gosho-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gosho-belt-e' }, { face: 1, id: 'gosho-belt-w' }, { face: 2, id: 'gosho-belt-n' }, { face: 3, id: 'gosho-belt-s' }, { face: 5, id: 'gosho-belt-b' }] },
  'nishijin-belt': { base: 'nishijin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nishijin-belt-e' }, { face: 1, id: 'nishijin-belt-w' }, { face: 2, id: 'nishijin-belt-n' }, { face: 3, id: 'nishijin-belt-s' }, { face: 5, id: 'nishijin-belt-b' }] },
  'demachiyanagi-belt': { base: 'demachiyanagi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'demachiyanagi-belt-e' }, { face: 1, id: 'demachiyanagi-belt-w' }, { face: 2, id: 'demachiyanagi-belt-n' }, { face: 3, id: 'demachiyanagi-belt-s' }, { face: 5, id: 'demachiyanagi-belt-b' }] },
  'heian-belt': { base: 'heian-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'heian-belt-e' }, { face: 1, id: 'heian-belt-w' }, { face: 2, id: 'heian-belt-n' }, { face: 3, id: 'heian-belt-s' }, { face: 5, id: 'heian-belt-b' }] },
  'ginkakuji-belt': { base: 'ginkakuji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ginkakuji-belt-e' }, { face: 1, id: 'ginkakuji-belt-w' }, { face: 2, id: 'ginkakuji-belt-n' }, { face: 3, id: 'ginkakuji-belt-s' }, { face: 5, id: 'ginkakuji-belt-b' }] },
  'kiyomizu-belt': { base: 'kiyomizu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kiyomizu-belt-e' }, { face: 1, id: 'kiyomizu-belt-w' }, { face: 2, id: 'kiyomizu-belt-n' }, { face: 3, id: 'kiyomizu-belt-s' }, { face: 5, id: 'kiyomizu-belt-b' }] },
  'tofukuji-belt': { base: 'tofukuji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tofukuji-belt-e' }, { face: 1, id: 'tofukuji-belt-w' }, { face: 2, id: 'tofukuji-belt-n' }, { face: 3, id: 'tofukuji-belt-s' }, { face: 5, id: 'tofukuji-belt-b' }] },
  'fushimi-inari-belt': { base: 'fushimi-inari-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fushimi-inari-belt-e' }, { face: 1, id: 'fushimi-inari-belt-w' }, { face: 2, id: 'fushimi-inari-belt-n' }, { face: 3, id: 'fushimi-inari-belt-s' }, { face: 5, id: 'fushimi-inari-belt-b' }] },
  'kinkakuji-belt': { base: 'kinkakuji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kinkakuji-belt-e' }, { face: 1, id: 'kinkakuji-belt-w' }, { face: 2, id: 'kinkakuji-belt-n' }, { face: 3, id: 'kinkakuji-belt-s' }, { face: 5, id: 'kinkakuji-belt-b' }] },
  'arashiyama-belt': { base: 'arashiyama-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'arashiyama-belt-e' }, { face: 1, id: 'arashiyama-belt-w' }, { face: 2, id: 'arashiyama-belt-n' }, { face: 3, id: 'arashiyama-belt-s' }, { face: 5, id: 'arashiyama-belt-b' }] },
  'old-town-belt': { base: 'old-town-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'old-town-belt-e' }, { face: 1, id: 'old-town-belt-w' }, { face: 2, id: 'old-town-belt-n' }, { face: 3, id: 'old-town-belt-s' }, { face: 5, id: 'old-town-belt-b' }] },
  'mala-strana-belt': { base: 'mala-strana-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'mala-strana-belt-e' }, { face: 1, id: 'mala-strana-belt-w' }, { face: 2, id: 'mala-strana-belt-n' }, { face: 3, id: 'mala-strana-belt-s' }, { face: 5, id: 'mala-strana-belt-b' }] },
  'hradcany-belt': { base: 'hradcany-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hradcany-belt-e' }, { face: 1, id: 'hradcany-belt-w' }, { face: 2, id: 'hradcany-belt-n' }, { face: 3, id: 'hradcany-belt-s' }, { face: 5, id: 'hradcany-belt-b' }] },
  'nove-mesto-belt': { base: 'nove-mesto-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nove-mesto-belt-e' }, { face: 1, id: 'nove-mesto-belt-w' }, { face: 2, id: 'nove-mesto-belt-n' }, { face: 3, id: 'nove-mesto-belt-s' }, { face: 5, id: 'nove-mesto-belt-b' }] },
  'vinohrady-belt': { base: 'vinohrady-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vinohrady-belt-e' }, { face: 1, id: 'vinohrady-belt-w' }, { face: 2, id: 'vinohrady-belt-n' }, { face: 3, id: 'vinohrady-belt-s' }, { face: 5, id: 'vinohrady-belt-b' }] },
  'zizkov-belt': { base: 'zizkov-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'zizkov-belt-e' }, { face: 1, id: 'zizkov-belt-w' }, { face: 2, id: 'zizkov-belt-n' }, { face: 3, id: 'zizkov-belt-s' }, { face: 5, id: 'zizkov-belt-b' }] },
  'karlin-belt': { base: 'karlin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'karlin-belt-e' }, { face: 1, id: 'karlin-belt-w' }, { face: 2, id: 'karlin-belt-n' }, { face: 3, id: 'karlin-belt-s' }, { face: 5, id: 'karlin-belt-b' }] },
  'holesovice-belt': { base: 'holesovice-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'holesovice-belt-e' }, { face: 1, id: 'holesovice-belt-w' }, { face: 2, id: 'holesovice-belt-n' }, { face: 3, id: 'holesovice-belt-s' }, { face: 5, id: 'holesovice-belt-b' }] },
  'letna-belt': { base: 'letna-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'letna-belt-e' }, { face: 1, id: 'letna-belt-w' }, { face: 2, id: 'letna-belt-n' }, { face: 3, id: 'letna-belt-s' }, { face: 5, id: 'letna-belt-b' }] },
  'smichov-belt': { base: 'smichov-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'smichov-belt-e' }, { face: 1, id: 'smichov-belt-w' }, { face: 2, id: 'smichov-belt-n' }, { face: 3, id: 'smichov-belt-s' }, { face: 5, id: 'smichov-belt-b' }] },
  'vysehrad-belt': { base: 'vysehrad-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vysehrad-belt-e' }, { face: 1, id: 'vysehrad-belt-w' }, { face: 2, id: 'vysehrad-belt-n' }, { face: 3, id: 'vysehrad-belt-s' }, { face: 5, id: 'vysehrad-belt-b' }] },
  'dejvice-belt': { base: 'dejvice-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'dejvice-belt-e' }, { face: 1, id: 'dejvice-belt-w' }, { face: 2, id: 'dejvice-belt-n' }, { face: 3, id: 'dejvice-belt-s' }, { face: 5, id: 'dejvice-belt-b' }] },
  'nusle-belt': { base: 'nusle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nusle-belt-e' }, { face: 1, id: 'nusle-belt-w' }, { face: 2, id: 'nusle-belt-n' }, { face: 3, id: 'nusle-belt-s' }, { face: 5, id: 'nusle-belt-b' }] },
  'vrsovice-belt': { base: 'vrsovice-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vrsovice-belt-e' }, { face: 1, id: 'vrsovice-belt-w' }, { face: 2, id: 'vrsovice-belt-n' }, { face: 3, id: 'vrsovice-belt-s' }, { face: 5, id: 'vrsovice-belt-b' }] },
  'altstadt-belt': { base: 'altstadt-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'altstadt-belt-e' }, { face: 1, id: 'altstadt-belt-w' }, { face: 2, id: 'altstadt-belt-n' }, { face: 3, id: 'altstadt-belt-s' }, { face: 5, id: 'altstadt-belt-b' }] },
  'bahnhofstrasse-belt': { base: 'bahnhofstrasse-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'bahnhofstrasse-belt-e' }, { face: 1, id: 'bahnhofstrasse-belt-w' }, { face: 2, id: 'bahnhofstrasse-belt-n' }, { face: 3, id: 'bahnhofstrasse-belt-s' }, { face: 5, id: 'bahnhofstrasse-belt-b' }] },
  'seefeld-belt': { base: 'seefeld-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'seefeld-belt-e' }, { face: 1, id: 'seefeld-belt-w' }, { face: 2, id: 'seefeld-belt-n' }, { face: 3, id: 'seefeld-belt-s' }, { face: 5, id: 'seefeld-belt-b' }] },
  'enge-belt': { base: 'enge-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'enge-belt-e' }, { face: 1, id: 'enge-belt-w' }, { face: 2, id: 'enge-belt-n' }, { face: 3, id: 'enge-belt-s' }, { face: 5, id: 'enge-belt-b' }] },
  'kreis-4-belt': { base: 'kreis-4-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kreis-4-belt-e' }, { face: 1, id: 'kreis-4-belt-w' }, { face: 2, id: 'kreis-4-belt-n' }, { face: 3, id: 'kreis-4-belt-s' }, { face: 5, id: 'kreis-4-belt-b' }] },
  'kreis-5-belt': { base: 'kreis-5-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kreis-5-belt-e' }, { face: 1, id: 'kreis-5-belt-w' }, { face: 2, id: 'kreis-5-belt-n' }, { face: 3, id: 'kreis-5-belt-s' }, { face: 5, id: 'kreis-5-belt-b' }] },
  'wiedikon-belt': { base: 'wiedikon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wiedikon-belt-e' }, { face: 1, id: 'wiedikon-belt-w' }, { face: 2, id: 'wiedikon-belt-n' }, { face: 3, id: 'wiedikon-belt-s' }, { face: 5, id: 'wiedikon-belt-b' }] },
  'hottingen-belt': { base: 'hottingen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hottingen-belt-e' }, { face: 1, id: 'hottingen-belt-w' }, { face: 2, id: 'hottingen-belt-n' }, { face: 3, id: 'hottingen-belt-s' }, { face: 5, id: 'hottingen-belt-b' }] },
  'unterstrass-belt': { base: 'unterstrass-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'unterstrass-belt-e' }, { face: 1, id: 'unterstrass-belt-w' }, { face: 2, id: 'unterstrass-belt-n' }, { face: 3, id: 'unterstrass-belt-s' }, { face: 5, id: 'unterstrass-belt-b' }] },
  'fluntern-belt': { base: 'fluntern-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'fluntern-belt-e' }, { face: 1, id: 'fluntern-belt-w' }, { face: 2, id: 'fluntern-belt-n' }, { face: 3, id: 'fluntern-belt-s' }, { face: 5, id: 'fluntern-belt-b' }] },
  'wollishofen-belt': { base: 'wollishofen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wollishofen-belt-e' }, { face: 1, id: 'wollishofen-belt-w' }, { face: 2, id: 'wollishofen-belt-n' }, { face: 3, id: 'wollishofen-belt-s' }, { face: 5, id: 'wollishofen-belt-b' }] },
  'altstetten-belt': { base: 'altstetten-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'altstetten-belt-e' }, { face: 1, id: 'altstetten-belt-w' }, { face: 2, id: 'altstetten-belt-n' }, { face: 3, id: 'altstetten-belt-s' }, { face: 5, id: 'altstetten-belt-b' }] },
  'hoengg-belt': { base: 'hoengg-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hoengg-belt-e' }, { face: 1, id: 'hoengg-belt-w' }, { face: 2, id: 'hoengg-belt-n' }, { face: 3, id: 'hoengg-belt-s' }, { face: 5, id: 'hoengg-belt-b' }] },
  'oerlikon-belt': { base: 'oerlikon-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'oerlikon-belt-e' }, { face: 1, id: 'oerlikon-belt-w' }, { face: 2, id: 'oerlikon-belt-n' }, { face: 3, id: 'oerlikon-belt-s' }, { face: 5, id: 'oerlikon-belt-b' }] },
  'keskusta-belt': { base: 'keskusta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'keskusta-belt-e' }, { face: 1, id: 'keskusta-belt-w' }, { face: 2, id: 'keskusta-belt-n' }, { face: 3, id: 'keskusta-belt-s' }, { face: 5, id: 'keskusta-belt-b' }] },
  'kamppi-belt': { base: 'kamppi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kamppi-belt-e' }, { face: 1, id: 'kamppi-belt-w' }, { face: 2, id: 'kamppi-belt-n' }, { face: 3, id: 'kamppi-belt-s' }, { face: 5, id: 'kamppi-belt-b' }] },
  'kruununhaka-belt': { base: 'kruununhaka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kruununhaka-belt-e' }, { face: 1, id: 'kruununhaka-belt-w' }, { face: 2, id: 'kruununhaka-belt-n' }, { face: 3, id: 'kruununhaka-belt-s' }, { face: 5, id: 'kruununhaka-belt-b' }] },
  'punavuori-belt': { base: 'punavuori-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'punavuori-belt-e' }, { face: 1, id: 'punavuori-belt-w' }, { face: 2, id: 'punavuori-belt-n' }, { face: 3, id: 'punavuori-belt-s' }, { face: 5, id: 'punavuori-belt-b' }] },
  'toolo-belt': { base: 'toolo-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'toolo-belt-e' }, { face: 1, id: 'toolo-belt-w' }, { face: 2, id: 'toolo-belt-n' }, { face: 3, id: 'toolo-belt-s' }, { face: 5, id: 'toolo-belt-b' }] },
  'kallio-belt': { base: 'kallio-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kallio-belt-e' }, { face: 1, id: 'kallio-belt-w' }, { face: 2, id: 'kallio-belt-n' }, { face: 3, id: 'kallio-belt-s' }, { face: 5, id: 'kallio-belt-b' }] },
  'sornainen-belt': { base: 'sornainen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sornainen-belt-e' }, { face: 1, id: 'sornainen-belt-w' }, { face: 2, id: 'sornainen-belt-n' }, { face: 3, id: 'sornainen-belt-s' }, { face: 5, id: 'sornainen-belt-b' }] },
  'vallila-belt': { base: 'vallila-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'vallila-belt-e' }, { face: 1, id: 'vallila-belt-w' }, { face: 2, id: 'vallila-belt-n' }, { face: 3, id: 'vallila-belt-s' }, { face: 5, id: 'vallila-belt-b' }] },
  'pasila-belt': { base: 'pasila-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pasila-belt-e' }, { face: 1, id: 'pasila-belt-w' }, { face: 2, id: 'pasila-belt-n' }, { face: 3, id: 'pasila-belt-s' }, { face: 5, id: 'pasila-belt-b' }] },
  'lauttasaari-belt': { base: 'lauttasaari-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lauttasaari-belt-e' }, { face: 1, id: 'lauttasaari-belt-w' }, { face: 2, id: 'lauttasaari-belt-n' }, { face: 3, id: 'lauttasaari-belt-s' }, { face: 5, id: 'lauttasaari-belt-b' }] },
  'arabianranta-belt': { base: 'arabianranta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'arabianranta-belt-e' }, { face: 1, id: 'arabianranta-belt-w' }, { face: 2, id: 'arabianranta-belt-n' }, { face: 3, id: 'arabianranta-belt-s' }, { face: 5, id: 'arabianranta-belt-b' }] },
  'munkkiniemi-belt': { base: 'munkkiniemi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'munkkiniemi-belt-e' }, { face: 1, id: 'munkkiniemi-belt-w' }, { face: 2, id: 'munkkiniemi-belt-n' }, { face: 3, id: 'munkkiniemi-belt-s' }, { face: 5, id: 'munkkiniemi-belt-b' }] },
  'baixa-belt': { base: 'baixa-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'baixa-belt-e' }, { face: 1, id: 'baixa-belt-w' }, { face: 2, id: 'baixa-belt-n' }, { face: 3, id: 'baixa-belt-s' }, { face: 5, id: 'baixa-belt-b' }] },
  'alfama-belt': { base: 'alfama-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'alfama-belt-e' }, { face: 1, id: 'alfama-belt-w' }, { face: 2, id: 'alfama-belt-n' }, { face: 3, id: 'alfama-belt-s' }, { face: 5, id: 'alfama-belt-b' }] },
  'chiado-belt': { base: 'chiado-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'chiado-belt-e' }, { face: 1, id: 'chiado-belt-w' }, { face: 2, id: 'chiado-belt-n' }, { face: 3, id: 'chiado-belt-s' }, { face: 5, id: 'chiado-belt-b' }] },
  'principe-real-belt': { base: 'principe-real-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'principe-real-belt-e' }, { face: 1, id: 'principe-real-belt-w' }, { face: 2, id: 'principe-real-belt-n' }, { face: 3, id: 'principe-real-belt-s' }, { face: 5, id: 'principe-real-belt-b' }] },
  'avenida-belt': { base: 'avenida-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'avenida-belt-e' }, { face: 1, id: 'avenida-belt-w' }, { face: 2, id: 'avenida-belt-n' }, { face: 3, id: 'avenida-belt-s' }, { face: 5, id: 'avenida-belt-b' }] },
  'graca-belt': { base: 'graca-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'graca-belt-e' }, { face: 1, id: 'graca-belt-w' }, { face: 2, id: 'graca-belt-n' }, { face: 3, id: 'graca-belt-s' }, { face: 5, id: 'graca-belt-b' }] },
  'intendente-belt': { base: 'intendente-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'intendente-belt-e' }, { face: 1, id: 'intendente-belt-w' }, { face: 2, id: 'intendente-belt-n' }, { face: 3, id: 'intendente-belt-s' }, { face: 5, id: 'intendente-belt-b' }] },
  'estrela-belt': { base: 'estrela-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'estrela-belt-e' }, { face: 1, id: 'estrela-belt-w' }, { face: 2, id: 'estrela-belt-n' }, { face: 3, id: 'estrela-belt-s' }, { face: 5, id: 'estrela-belt-b' }] },
  'campo-de-ourique-belt': { base: 'campo-de-ourique-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'campo-de-ourique-belt-e' }, { face: 1, id: 'campo-de-ourique-belt-w' }, { face: 2, id: 'campo-de-ourique-belt-n' }, { face: 3, id: 'campo-de-ourique-belt-s' }, { face: 5, id: 'campo-de-ourique-belt-b' }] },
  'alcantara-belt': { base: 'alcantara-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'alcantara-belt-e' }, { face: 1, id: 'alcantara-belt-w' }, { face: 2, id: 'alcantara-belt-n' }, { face: 3, id: 'alcantara-belt-s' }, { face: 5, id: 'alcantara-belt-b' }] },
  'belem-belt': { base: 'belem-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'belem-belt-e' }, { face: 1, id: 'belem-belt-w' }, { face: 2, id: 'belem-belt-n' }, { face: 3, id: 'belem-belt-s' }, { face: 5, id: 'belem-belt-b' }] },
  'saldanha-belt': { base: 'saldanha-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'saldanha-belt-e' }, { face: 1, id: 'saldanha-belt-w' }, { face: 2, id: 'saldanha-belt-n' }, { face: 3, id: 'saldanha-belt-s' }, { face: 5, id: 'saldanha-belt-b' }] },
  'alvalade-belt': { base: 'alvalade-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'alvalade-belt-e' }, { face: 1, id: 'alvalade-belt-w' }, { face: 2, id: 'alvalade-belt-n' }, { face: 3, id: 'alvalade-belt-s' }, { face: 5, id: 'alvalade-belt-b' }] },
  'benfica-belt': { base: 'benfica-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'benfica-belt-e' }, { face: 1, id: 'benfica-belt-w' }, { face: 2, id: 'benfica-belt-n' }, { face: 3, id: 'benfica-belt-s' }, { face: 5, id: 'benfica-belt-b' }] },
  'plaka-belt': { base: 'plaka-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'plaka-belt-e' }, { face: 1, id: 'plaka-belt-w' }, { face: 2, id: 'plaka-belt-n' }, { face: 3, id: 'plaka-belt-s' }, { face: 5, id: 'plaka-belt-b' }] },
  'syntagma-belt': { base: 'syntagma-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'syntagma-belt-e' }, { face: 1, id: 'syntagma-belt-w' }, { face: 2, id: 'syntagma-belt-n' }, { face: 3, id: 'syntagma-belt-s' }, { face: 5, id: 'syntagma-belt-b' }] },
  'psyrri-belt': { base: 'psyrri-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'psyrri-belt-e' }, { face: 1, id: 'psyrri-belt-w' }, { face: 2, id: 'psyrri-belt-n' }, { face: 3, id: 'psyrri-belt-s' }, { face: 5, id: 'psyrri-belt-b' }] },
  'kolonaki-belt': { base: 'kolonaki-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kolonaki-belt-e' }, { face: 1, id: 'kolonaki-belt-w' }, { face: 2, id: 'kolonaki-belt-n' }, { face: 3, id: 'kolonaki-belt-s' }, { face: 5, id: 'kolonaki-belt-b' }] },
  'exarcheia-belt': { base: 'exarcheia-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'exarcheia-belt-e' }, { face: 1, id: 'exarcheia-belt-w' }, { face: 2, id: 'exarcheia-belt-n' }, { face: 3, id: 'exarcheia-belt-s' }, { face: 5, id: 'exarcheia-belt-b' }] },
  'pangrati-belt': { base: 'pangrati-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'pangrati-belt-e' }, { face: 1, id: 'pangrati-belt-w' }, { face: 2, id: 'pangrati-belt-n' }, { face: 3, id: 'pangrati-belt-s' }, { face: 5, id: 'pangrati-belt-b' }] },
  'koukaki-belt': { base: 'koukaki-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'koukaki-belt-e' }, { face: 1, id: 'koukaki-belt-w' }, { face: 2, id: 'koukaki-belt-n' }, { face: 3, id: 'koukaki-belt-s' }, { face: 5, id: 'koukaki-belt-b' }] },
  'petralona-belt': { base: 'petralona-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'petralona-belt-e' }, { face: 1, id: 'petralona-belt-w' }, { face: 2, id: 'petralona-belt-n' }, { face: 3, id: 'petralona-belt-s' }, { face: 5, id: 'petralona-belt-b' }] },
  'gazi-belt': { base: 'gazi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'gazi-belt-e' }, { face: 1, id: 'gazi-belt-w' }, { face: 2, id: 'gazi-belt-n' }, { face: 3, id: 'gazi-belt-s' }, { face: 5, id: 'gazi-belt-b' }] },
  'kypseli-belt': { base: 'kypseli-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kypseli-belt-e' }, { face: 1, id: 'kypseli-belt-w' }, { face: 2, id: 'kypseli-belt-n' }, { face: 3, id: 'kypseli-belt-s' }, { face: 5, id: 'kypseli-belt-b' }] },
  'ampelokipoi-belt': { base: 'ampelokipoi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ampelokipoi-belt-e' }, { face: 1, id: 'ampelokipoi-belt-w' }, { face: 2, id: 'ampelokipoi-belt-n' }, { face: 3, id: 'ampelokipoi-belt-s' }, { face: 5, id: 'ampelokipoi-belt-b' }] },
  'neos-kosmos-belt': { base: 'neos-kosmos-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'neos-kosmos-belt-e' }, { face: 1, id: 'neos-kosmos-belt-w' }, { face: 2, id: 'neos-kosmos-belt-n' }, { face: 3, id: 'neos-kosmos-belt-s' }, { face: 5, id: 'neos-kosmos-belt-b' }] },
  'piraeus-belt': { base: 'piraeus-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'piraeus-belt-e' }, { face: 1, id: 'piraeus-belt-w' }, { face: 2, id: 'piraeus-belt-n' }, { face: 3, id: 'piraeus-belt-s' }, { face: 5, id: 'piraeus-belt-b' }] },
  'glyfada-belt': { base: 'glyfada-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'glyfada-belt-e' }, { face: 1, id: 'glyfada-belt-w' }, { face: 2, id: 'glyfada-belt-n' }, { face: 3, id: 'glyfada-belt-s' }, { face: 5, id: 'glyfada-belt-b' }] },
  'kifisia-belt': { base: 'kifisia-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kifisia-belt-e' }, { face: 1, id: 'kifisia-belt-w' }, { face: 2, id: 'kifisia-belt-n' }, { face: 3, id: 'kifisia-belt-s' }, { face: 5, id: 'kifisia-belt-b' }] },
  'marousi-belt': { base: 'marousi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'marousi-belt-e' }, { face: 1, id: 'marousi-belt-w' }, { face: 2, id: 'marousi-belt-n' }, { face: 3, id: 'marousi-belt-s' }, { face: 5, id: 'marousi-belt-b' }] },
  'umeda-belt': { base: 'umeda-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'umeda-belt-e' }, { face: 1, id: 'umeda-belt-w' }, { face: 2, id: 'umeda-belt-n' }, { face: 3, id: 'umeda-belt-s' }, { face: 5, id: 'umeda-belt-b' }] },
  'nakanoshima-belt': { base: 'nakanoshima-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nakanoshima-belt-e' }, { face: 1, id: 'nakanoshima-belt-w' }, { face: 2, id: 'nakanoshima-belt-n' }, { face: 3, id: 'nakanoshima-belt-s' }, { face: 5, id: 'nakanoshima-belt-b' }] },
  'osaka-castle-belt': { base: 'osaka-castle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'osaka-castle-belt-e' }, { face: 1, id: 'osaka-castle-belt-w' }, { face: 2, id: 'osaka-castle-belt-n' }, { face: 3, id: 'osaka-castle-belt-s' }, { face: 5, id: 'osaka-castle-belt-b' }] },
  'honmachi-belt': { base: 'honmachi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'honmachi-belt-e' }, { face: 1, id: 'honmachi-belt-w' }, { face: 2, id: 'honmachi-belt-n' }, { face: 3, id: 'honmachi-belt-s' }, { face: 5, id: 'honmachi-belt-b' }] },
  'shinsaibashi-belt': { base: 'shinsaibashi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shinsaibashi-belt-e' }, { face: 1, id: 'shinsaibashi-belt-w' }, { face: 2, id: 'shinsaibashi-belt-n' }, { face: 3, id: 'shinsaibashi-belt-s' }, { face: 5, id: 'shinsaibashi-belt-b' }] },
  'namba-belt': { base: 'namba-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'namba-belt-e' }, { face: 1, id: 'namba-belt-w' }, { face: 2, id: 'namba-belt-n' }, { face: 3, id: 'namba-belt-s' }, { face: 5, id: 'namba-belt-b' }] },
  'shinsekai-belt': { base: 'shinsekai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shinsekai-belt-e' }, { face: 1, id: 'shinsekai-belt-w' }, { face: 2, id: 'shinsekai-belt-n' }, { face: 3, id: 'shinsekai-belt-s' }, { face: 5, id: 'shinsekai-belt-b' }] },
  'tennoji-belt': { base: 'tennoji-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tennoji-belt-e' }, { face: 1, id: 'tennoji-belt-w' }, { face: 2, id: 'tennoji-belt-n' }, { face: 3, id: 'tennoji-belt-s' }, { face: 5, id: 'tennoji-belt-b' }] },
  'tempozan-belt': { base: 'tempozan-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tempozan-belt-e' }, { face: 1, id: 'tempozan-belt-w' }, { face: 2, id: 'tempozan-belt-n' }, { face: 3, id: 'tempozan-belt-s' }, { face: 5, id: 'tempozan-belt-b' }] },
  'minato-mirai-belt': { base: 'minato-mirai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'minato-mirai-belt-e' }, { face: 1, id: 'minato-mirai-belt-w' }, { face: 2, id: 'minato-mirai-belt-n' }, { face: 3, id: 'minato-mirai-belt-s' }, { face: 5, id: 'minato-mirai-belt-b' }] },
  'yokohama-station-belt': { base: 'yokohama-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yokohama-station-belt-e' }, { face: 1, id: 'yokohama-station-belt-w' }, { face: 2, id: 'yokohama-station-belt-n' }, { face: 3, id: 'yokohama-station-belt-s' }, { face: 5, id: 'yokohama-station-belt-b' }] },
  'kannai-belt': { base: 'kannai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kannai-belt-e' }, { face: 1, id: 'kannai-belt-w' }, { face: 2, id: 'kannai-belt-n' }, { face: 3, id: 'kannai-belt-s' }, { face: 5, id: 'kannai-belt-b' }] },
  'sakuragicho-belt': { base: 'sakuragicho-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sakuragicho-belt-e' }, { face: 1, id: 'sakuragicho-belt-w' }, { face: 2, id: 'sakuragicho-belt-n' }, { face: 3, id: 'sakuragicho-belt-s' }, { face: 5, id: 'sakuragicho-belt-b' }] },
  'osanbashi-belt': { base: 'osanbashi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'osanbashi-belt-e' }, { face: 1, id: 'osanbashi-belt-w' }, { face: 2, id: 'osanbashi-belt-n' }, { face: 3, id: 'osanbashi-belt-s' }, { face: 5, id: 'osanbashi-belt-b' }] },
  'motomachi-belt': { base: 'motomachi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'motomachi-belt-e' }, { face: 1, id: 'motomachi-belt-w' }, { face: 2, id: 'motomachi-belt-n' }, { face: 3, id: 'motomachi-belt-s' }, { face: 5, id: 'motomachi-belt-b' }] },
  'yamate-belt': { base: 'yamate-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yamate-belt-e' }, { face: 1, id: 'yamate-belt-w' }, { face: 2, id: 'yamate-belt-n' }, { face: 3, id: 'yamate-belt-s' }, { face: 5, id: 'yamate-belt-b' }] },
  'shin-yokohama-belt': { base: 'shin-yokohama-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'shin-yokohama-belt-e' }, { face: 1, id: 'shin-yokohama-belt-w' }, { face: 2, id: 'shin-yokohama-belt-n' }, { face: 3, id: 'shin-yokohama-belt-s' }, { face: 5, id: 'shin-yokohama-belt-b' }] },
  'honmoku-belt': { base: 'honmoku-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'honmoku-belt-e' }, { face: 1, id: 'honmoku-belt-w' }, { face: 2, id: 'honmoku-belt-n' }, { face: 3, id: 'honmoku-belt-s' }, { face: 5, id: 'honmoku-belt-b' }] },
  'meieki-belt': { base: 'meieki-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'meieki-belt-e' }, { face: 1, id: 'meieki-belt-w' }, { face: 2, id: 'meieki-belt-n' }, { face: 3, id: 'meieki-belt-s' }, { face: 5, id: 'meieki-belt-b' }] },
  'nagoya-fushimi-belt': { base: 'nagoya-fushimi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nagoya-fushimi-belt-e' }, { face: 1, id: 'nagoya-fushimi-belt-w' }, { face: 2, id: 'nagoya-fushimi-belt-n' }, { face: 3, id: 'nagoya-fushimi-belt-s' }, { face: 5, id: 'nagoya-fushimi-belt-b' }] },
  'sakae-belt': { base: 'sakae-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'sakae-belt-e' }, { face: 1, id: 'sakae-belt-w' }, { face: 2, id: 'sakae-belt-n' }, { face: 3, id: 'sakae-belt-s' }, { face: 5, id: 'sakae-belt-b' }] },
  'nagoya-castle-belt': { base: 'nagoya-castle-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nagoya-castle-belt-e' }, { face: 1, id: 'nagoya-castle-belt-w' }, { face: 2, id: 'nagoya-castle-belt-n' }, { face: 3, id: 'nagoya-castle-belt-s' }, { face: 5, id: 'nagoya-castle-belt-b' }] },
  'osu-belt': { base: 'osu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'osu-belt-e' }, { face: 1, id: 'osu-belt-w' }, { face: 2, id: 'osu-belt-n' }, { face: 3, id: 'osu-belt-s' }, { face: 5, id: 'osu-belt-b' }] },
  'kanayama-belt': { base: 'kanayama-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'kanayama-belt-e' }, { face: 1, id: 'kanayama-belt-w' }, { face: 2, id: 'kanayama-belt-n' }, { face: 3, id: 'kanayama-belt-s' }, { face: 5, id: 'kanayama-belt-b' }] },
  'tsurumai-belt': { base: 'tsurumai-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tsurumai-belt-e' }, { face: 1, id: 'tsurumai-belt-w' }, { face: 2, id: 'tsurumai-belt-n' }, { face: 3, id: 'tsurumai-belt-s' }, { face: 5, id: 'tsurumai-belt-b' }] },
  'atsuta-belt': { base: 'atsuta-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'atsuta-belt-e' }, { face: 1, id: 'atsuta-belt-w' }, { face: 2, id: 'atsuta-belt-n' }, { face: 3, id: 'atsuta-belt-s' }, { face: 5, id: 'atsuta-belt-b' }] },
  'nagoya-marunouchi-belt': { base: 'nagoya-marunouchi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nagoya-marunouchi-belt-e' }, { face: 1, id: 'nagoya-marunouchi-belt-w' }, { face: 2, id: 'nagoya-marunouchi-belt-n' }, { face: 3, id: 'nagoya-marunouchi-belt-s' }, { face: 5, id: 'nagoya-marunouchi-belt-b' }] },
  'hakata-station-belt': { base: 'hakata-station-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hakata-station-belt-e' }, { face: 1, id: 'hakata-station-belt-w' }, { face: 2, id: 'hakata-station-belt-n' }, { face: 3, id: 'hakata-station-belt-s' }, { face: 5, id: 'hakata-station-belt-b' }] },
  'tenjin-belt': { base: 'tenjin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'tenjin-belt-e' }, { face: 1, id: 'tenjin-belt-w' }, { face: 2, id: 'tenjin-belt-n' }, { face: 3, id: 'tenjin-belt-s' }, { face: 5, id: 'tenjin-belt-b' }] },
  'nakasu-belt': { base: 'nakasu-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'nakasu-belt-e' }, { face: 1, id: 'nakasu-belt-w' }, { face: 2, id: 'nakasu-belt-n' }, { face: 3, id: 'nakasu-belt-s' }, { face: 5, id: 'nakasu-belt-b' }] },
  'canal-city-belt': { base: 'canal-city-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'canal-city-belt-e' }, { face: 1, id: 'canal-city-belt-w' }, { face: 2, id: 'canal-city-belt-n' }, { face: 3, id: 'canal-city-belt-s' }, { face: 5, id: 'canal-city-belt-b' }] },
  'ohori-belt': { base: 'ohori-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'ohori-belt-e' }, { face: 1, id: 'ohori-belt-w' }, { face: 2, id: 'ohori-belt-n' }, { face: 3, id: 'ohori-belt-s' }, { face: 5, id: 'ohori-belt-b' }] },
  'momochi-belt': { base: 'momochi-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'momochi-belt-e' }, { face: 1, id: 'momochi-belt-w' }, { face: 2, id: 'momochi-belt-n' }, { face: 3, id: 'momochi-belt-s' }, { face: 5, id: 'momochi-belt-b' }] },
  'hakata-port-belt': { base: 'hakata-port-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hakata-port-belt-e' }, { face: 1, id: 'hakata-port-belt-w' }, { face: 2, id: 'hakata-port-belt-n' }, { face: 3, id: 'hakata-port-belt-s' }, { face: 5, id: 'hakata-port-belt-b' }] },
  'yakuin-belt': { base: 'yakuin-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'yakuin-belt-e' }, { face: 1, id: 'yakuin-belt-w' }, { face: 2, id: 'yakuin-belt-n' }, { face: 3, id: 'yakuin-belt-s' }, { face: 5, id: 'yakuin-belt-b' }] },
  'hakata-gion-belt': { base: 'hakata-gion-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'hakata-gion-belt-e' }, { face: 1, id: 'hakata-gion-belt-w' }, { face: 2, id: 'hakata-gion-belt-n' }, { face: 3, id: 'hakata-gion-belt-s' }, { face: 5, id: 'hakata-gion-belt-b' }] },
  'lijingmen-belt': { base: 'lijingmen-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'lijingmen-belt-e' }, { face: 1, id: 'lijingmen-belt-w' }, { face: 2, id: 'lijingmen-belt-n' }, { face: 3, id: 'lijingmen-belt-s' }, { face: 5, id: 'lijingmen-belt-b' }] },
  'south-beach-belt': { base: 'south-beach-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'south-beach-belt-e' }, { face: 1, id: 'south-beach-belt-w' }, { face: 2, id: 'south-beach-belt-n' }, { face: 3, id: 'south-beach-belt-s' }, { face: 5, id: 'south-beach-belt-b' }] },
  'miami-beach-belt': { base: 'miami-beach-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'miami-beach-belt-e' }, { face: 1, id: 'miami-beach-belt-w' }, { face: 2, id: 'miami-beach-belt-n' }, { face: 3, id: 'miami-beach-belt-s' }, { face: 5, id: 'miami-beach-belt-b' }] },
  'north-beach-belt': { base: 'north-beach-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'north-beach-belt-e' }, { face: 1, id: 'north-beach-belt-w' }, { face: 2, id: 'north-beach-belt-n' }, { face: 3, id: 'north-beach-belt-s' }, { face: 5, id: 'north-beach-belt-b' }] },
  'downtown-miami-belt': { base: 'downtown-miami-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'downtown-miami-belt-e' }, { face: 1, id: 'downtown-miami-belt-w' }, { face: 2, id: 'downtown-miami-belt-n' }, { face: 3, id: 'downtown-miami-belt-s' }, { face: 5, id: 'downtown-miami-belt-b' }] },
  'brickell-belt': { base: 'brickell-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'brickell-belt-e' }, { face: 1, id: 'brickell-belt-w' }, { face: 2, id: 'brickell-belt-n' }, { face: 3, id: 'brickell-belt-s' }, { face: 5, id: 'brickell-belt-b' }] },
  'wynwood-belt': { base: 'wynwood-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'wynwood-belt-e' }, { face: 1, id: 'wynwood-belt-w' }, { face: 2, id: 'wynwood-belt-n' }, { face: 3, id: 'wynwood-belt-s' }, { face: 5, id: 'wynwood-belt-b' }] },
  'design-district-belt': { base: 'design-district-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'design-district-belt-e' }, { face: 1, id: 'design-district-belt-w' }, { face: 2, id: 'design-district-belt-n' }, { face: 3, id: 'design-district-belt-s' }, { face: 5, id: 'design-district-belt-b' }] },
  'little-havana-belt': { base: 'little-havana-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'little-havana-belt-e' }, { face: 1, id: 'little-havana-belt-w' }, { face: 2, id: 'little-havana-belt-n' }, { face: 3, id: 'little-havana-belt-s' }, { face: 5, id: 'little-havana-belt-b' }] },
  'coral-gables-belt': { base: 'coral-gables-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'coral-gables-belt-e' }, { face: 1, id: 'coral-gables-belt-w' }, { face: 2, id: 'coral-gables-belt-n' }, { face: 3, id: 'coral-gables-belt-s' }, { face: 5, id: 'coral-gables-belt-b' }] },
  'coconut-grove-belt': { base: 'coconut-grove-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'coconut-grove-belt-e' }, { face: 1, id: 'coconut-grove-belt-w' }, { face: 2, id: 'coconut-grove-belt-n' }, { face: 3, id: 'coconut-grove-belt-s' }, { face: 5, id: 'coconut-grove-belt-b' }] },
  'overtown-belt': { base: 'overtown-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'overtown-belt-e' }, { face: 1, id: 'overtown-belt-w' }, { face: 2, id: 'overtown-belt-n' }, { face: 3, id: 'overtown-belt-s' }, { face: 5, id: 'overtown-belt-b' }] },
  'edgewater-belt': { base: 'edgewater-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'edgewater-belt-e' }, { face: 1, id: 'edgewater-belt-w' }, { face: 2, id: 'edgewater-belt-n' }, { face: 3, id: 'edgewater-belt-s' }, { face: 5, id: 'edgewater-belt-b' }] },
  'little-haiti-belt': { base: 'little-haiti-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'little-haiti-belt-e' }, { face: 1, id: 'little-haiti-belt-w' }, { face: 2, id: 'little-haiti-belt-n' }, { face: 3, id: 'little-haiti-belt-s' }, { face: 5, id: 'little-haiti-belt-b' }] },
  'key-biscayne-belt': { base: 'key-biscayne-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'key-biscayne-belt-e' }, { face: 1, id: 'key-biscayne-belt-w' }, { face: 2, id: 'key-biscayne-belt-n' }, { face: 3, id: 'key-biscayne-belt-s' }, { face: 5, id: 'key-biscayne-belt-b' }] },
  'miami-river-belt': { base: 'miami-river-belt-z', worldScale: 4.0, faces: [{ face: 0, id: 'miami-river-belt-e' }, { face: 1, id: 'miami-river-belt-w' }, { face: 2, id: 'miami-river-belt-n' }, { face: 3, id: 'miami-river-belt-s' }, { face: 5, id: 'miami-river-belt-b' }] },
};

export const META_PLANETS: Readonly<Record<string, MetaPlanetSpec>> = {
  // The HK Island street-meta (the good one) — fine ground patches + per-crop
  // roads + the island's single building cloud, all street-level.
  'hk-island-meta': {
    base: 'hk-island-ultra',
    members: [
      'central-ultra',
      'sheung-wan-ultra',
      'wan-chai-ultra',
      'causeway-bay-ultra',
      'north-point-ultra',
      'kennedy-town-ultra',
      'aberdeen-ultra',
      'stanley-ultra',
      'the-peak-ultra',
    ],
  },
  // The whole HKSAR overview-meta — the entire territory on one giant sphere, every
  // town's building cloud stitched at its true position. Overview showcase: per-member
  // clouds (dense towers everywhere), NO fine patches/roads (invisible from far, heavy).
  'hksar-meta': {
    base: 'whole-hk', // territory ground + coastline + all 14 landmarks
    memberClouds: true,
    groundPatches: false,
    memberRoads: false,
    equirect: true, // whole-ball plate-carrée: lon wraps 360°, lat pole-to-pole → all real HK
    worldScale: 5.0,
    members: [
      'hk-island', // whole island (28k)
      'kowloon', // whole peninsula (30k)
      'sha-tin',
      'tai-po',
      'fanling-sheung-shui',
      'yuen-long',
      'tuen-mun',
      'tsuen-wan',
      'tseung-kwan-o',
      'sai-kung',
      'cuhk',
      'tung-chung', // Lantau north (airport)
      'hk-disneyland', // Lantau NE
      'tian-tan-buddha', // Lantau S
      'cheung-chau', // outlying island
    ],
  },
  // The GLOBE variant of the whole-HKSAR meta: same territory + member clouds, but a
  // LARGE azimuthal CONFORMAL stereographic cap instead of the equirect wrap. Trades
  // the equirect's 100%-fill (which pays a ~1.8× E-W stretch + a north-pole pinch) for
  // CORRECT GIS shape (conformal — zero stretch), no pole distortion (the only
  // singularity is the antipode, which is open sea) and no seam. The back cap fills
  // with the tidy sea. Same reused azimuthal-meta path as hk-island-meta.
  'hksar-globe-meta': {
    base: 'whole-hk',
    memberClouds: true,
    groundPatches: false,
    memberRoads: false,
    capDeg: 120, // fills ~75% of the ball with true-shape HK; back cap = open sea
    capCenter: { lon: 114.165, lat: 22.3 }, // Victoria Harbour — Island+Kowloon skylines crown the ball
    worldScale: 5.0,
    members: [
      'hk-island',
      'kowloon',
      'sha-tin',
      'tai-po',
      'fanling-sheung-shui',
      'yuen-long',
      'tuen-mun',
      'tsuen-wan',
      'tseung-kwan-o',
      'sai-kung',
      'cuhk',
      'tung-chung',
      'hk-disneyland',
      'tian-tan-buddha',
      'cheung-chau',
    ],
  },
};

export function getHdDistrict(id: string | null | undefined): HdDistrict | null {
  if (!id) return null;
  return HD_DISTRICTS.find((d) => d.id === id) ?? null;
}

// ── Computed game worlds ─────────────────────────────────────────────────────
/**
 * The `games` world, which is NOT a `RegionId`.
 *
 * Every RegionId must satisfy `regions.test`: at least ten planets, every one named
 * `-belt`/`-cube` and carrying a CubeBeltSpec. Those rules encode what a CITY world is,
 * and they are right — a city with three planets is a thin city. A computed-worlds
 * collection has no such obligation: three well-made generated worlds is a real offering,
 * and one of them is deliberately a flat tile because an arena is one readable place.
 * So `games` is its own WorldId with its own spec, rather than a region bending those
 * rules until they mean nothing.
 */
export const GAMES_WORLD = {
  id: 'games' as const,
  place: 'Game worlds',
  label: 'Game worlds · computed from a seed, no map data',
  defaultDistrict: 'grounds-complex',
} as const;

/** Every computed game planet, in registry order. */
export const GAME_PLANETS: ReadonlyArray<HdDistrict> = HD_DISTRICTS.filter((d) => d.game);

/** Every sport-collection belt. AllSportsBoard lists these; there is no extra OSM cube. */
export const SPORT_BELTS: ReadonlyArray<HdDistrict> = HD_DISTRICTS.filter((d) => d.region === 'sport');

/**
 * Picker kinds Siege is offered on — ONE list, used at every site that gates it.
 *
 * It exists because the gate was duplicated: two separate `kind === 'belt' || …` checks
 * in PlanetRoute. A new PickerGroupKey silently fails such a check, so adding `games`
 * would have shipped a world with the Siege button quietly missing. Exported so the test
 * can pin it and so both sites read the same source.
 */
export const SIEGE_KINDS: ReadonlySet<PickerGroupKey> = new Set<PickerGroupKey>([
  'belt',
  'ultra',
  'special',
  'games',
]);
